import React, { useCallback, useState, useEffect, useRef, useContext, Fragment, Component } from 'react';
import { useLocation, Route, Redirect, Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Layout, Button, Dropdown, Menu, Select, Input, Tag, List, Typography, Modal, message, Switch, Badge, Table, Tooltip, Collapse, Divider, InputNumber, Spin, Form, Alert, TreeSelect } from 'antd';
import { MenuOutlined, LeftOutlined, RightOutlined, UserOutlined, FilterOutlined, CloseOutlined, CheckOutlined, LoadingOutlined, WarningFilled, BellFilled, CaretRightOutlined } from '@ant-design/icons';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { FaCog, FaQuestionCircle, FaPen, FaSignOutAlt, FaBell, FaCalendarAlt } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import { selectors, actions, hooks, getCropStateByStress, calculateGroupStateForPresentationModes } from 'farmx-redux-core';
import { StyleSheet, css } from 'aphrodite';
import 'antd/dist/antd.css';
import { sensorApi, constants, controlApi, authService, userApi } from 'farmx-api';
import Moment from 'react-moment';
import moment from 'moment';
import CircularSlider from '@fseehawer/react-circular-slider';
import Highcharts from 'highcharts';
import HighchartsGantt from 'highcharts/highcharts-gantt.src';
import HighchartsReact from 'highcharts-react-official';
import NoDataToDisplay from 'highcharts/modules/no-data-to-display';
import ReactDOMServer from 'react-dom/server';
import ReactResizeDetector from 'react-resize-detector';
import L$1 from 'leaflet';
import { useLeaflet, GeoJSON, Marker, Map as Map$2, TileLayer } from 'react-leaflet';
import 'react-leaflet-draw';
import 'leaflet-draw/dist/leaflet.draw-src.css';
import WebMercatorViewport from 'viewport-mercator-project';
import bbox from '@turf/bbox';
import 'dhtmlx-scheduler';
import 'dhtmlx-scheduler/codebase/dhtmlxscheduler_material.css';
import 'dhtmlx-scheduler/codebase/ext/dhtmlxscheduler_limit';
import 'dhtmlx-scheduler/codebase/ext/dhtmlxscheduler_multiselection';
import 'dhtmlx-scheduler/codebase/ext/dhtmlxscheduler_timeline';
import 'dhtmlx-scheduler/codebase/ext/dhtmlxscheduler_tooltip';
import 'dhtmlx-scheduler/codebase/ext/dhtmlxscheduler_multiselect';
import 'dhtmlx-scheduler/codebase/sources/skins/Timeline.css';
import 'dhtmlx-scheduler/codebase/ext/dhtmlxscheduler_minical';
import 'dhtmlx-scheduler/codebase/ext/dhtmlxscheduler_all_timed';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _inheritsLoose(subClass, superClass) {
  subClass.prototype = Object.create(superClass.prototype);
  subClass.prototype.constructor = subClass;
  subClass.__proto__ = superClass;
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function PrivateRoute(_ref) {
  var Component = _ref.component,
      authService = _ref.authService,
      adminRequired = _ref.adminRequired,
      rest = _objectWithoutPropertiesLoose(_ref, ["component", "authService", "adminRequired"]);

  var userInfo = authService.useAuth();
  var authorized = userInfo && (!adminRequired || userInfo.admin);
  var location = useLocation();

  var render = function render(props) {
    return /*#__PURE__*/React.createElement(Component, props);
  };

  return authorized ? /*#__PURE__*/React.createElement(Route, _extends({
    render: render
  }, rest)) : /*#__PURE__*/React.createElement(Redirect, {
    to: {
      pathname: '/login',
      state: {
        from: location
      }
    }
  });
}

PrivateRoute.propTypes = {
  component: PropTypes.elementType.isRequired,
  authService: PropTypes.shape({
    useAuth: PropTypes.func.isRequired
  }).isRequired,
  adminRequired: PropTypes.bool
};
PrivateRoute.defaultProps = {
  adminRequired: false
};

function useWindowSize() {
  var isClient = typeof window === 'object';
  var getSize = useCallback(function () {
    return {
      width: isClient ? window.innerWidth : undefined,
      height: isClient ? window.innerHeight : undefined
    };
  }, [isClient]);

  var _useState = useState(getSize),
      windowSize = _useState[0],
      setWindowSize = _useState[1];

  var handleResize = useCallback(function () {
    setWindowSize(getSize());
  }, [setWindowSize, getSize]);
  useEffect(function () {
    if (!isClient) return false;
    window.addEventListener('resize', handleResize);
    return function () {
      return window.removeEventListener('resize', handleResize);
    };
  }, [handleResize, isClient]);
  return windowSize;
}

var Header = Layout.Header,
    Content = Layout.Content;
function AppTemplate(props) {
  var size = useWindowSize();
  var scrollRef = useRef(null);
  var isMobile = size.width <= 600;

  var _useState = useState(isMobile),
      collapsed = _useState[0],
      setCollapsed = _useState[1];

  var SidebarComponent = props.sidebarComponent,
      NavBarComponent = props.navBarComponent,
      EntitySideBarComponent = props.entitySideBarComponent,
      children = props.children,
      _props$title = props.title,
      title = _props$title === void 0 ? 'FarmX' : _props$title,
      logo = props.logo,
      headerMenu = props.headerMenu,
      theme = props.theme,
      RanchBlockBreadcrumb = props.ranchBlockBreadcrumb;
  var onCollapse = useCallback(function (collapse) {
    setCollapsed(collapse);
  }, [setCollapsed]);
  var fontColor = theme === 'dark' ? 'white' : '#555';

  var scroll = function scroll(scrollOffset) {
    scrollRef.current.scrollLeft += scrollOffset;
  };

  return /*#__PURE__*/React.createElement(Layout, {
    className: "layout"
  }, /*#__PURE__*/React.createElement(Helmet, null, /*#__PURE__*/React.createElement("title", null, title)), /*#__PURE__*/React.createElement(Header, null, /*#__PURE__*/React.createElement("div", {
    className: "menu-trigger"
  }, /*#__PURE__*/React.createElement(MenuOutlined, {
    className: "trigger",
    style: {
      fontSize: '25px',
      color: fontColor
    },
    onClick: function onClick() {
      return setCollapsed(!collapsed);
    }
  })), /*#__PURE__*/React.createElement("div", {
    className: "logo"
  }, /*#__PURE__*/React.createElement("img", {
    className: "header-logo",
    alt: "FarmX",
    src: logo
  })), NavBarComponent && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Button, {
    className: "scroll-button",
    type: "primary",
    icon: /*#__PURE__*/React.createElement(LeftOutlined, null),
    onClick: function onClick() {
      return scroll(-25);
    }
  }), /*#__PURE__*/React.createElement("div", {
    className: "middle-menu",
    ref: scrollRef
  }, /*#__PURE__*/React.createElement(NavBarComponent, {
    isMobile: isMobile
  })), /*#__PURE__*/React.createElement(Button, {
    className: "scroll-button",
    type: "primary",
    icon: /*#__PURE__*/React.createElement(RightOutlined, null),
    onClick: function onClick() {
      return scroll(25);
    }
  })), RanchBlockBreadcrumb && /*#__PURE__*/React.createElement("div", {
    className: collapsed ? 'middle-menu' : 'breadcrumb-select-container-width middle-menu'
  }, /*#__PURE__*/React.createElement(RanchBlockBreadcrumb, null)), /*#__PURE__*/React.createElement("div", {
    className: "right-menu"
  }, headerMenu)), /*#__PURE__*/React.createElement(Layout, null, SidebarComponent && /*#__PURE__*/React.createElement(SidebarComponent, {
    isMobile: isMobile,
    onCollapse: onCollapse,
    collapsed: collapsed
  }), EntitySideBarComponent && /*#__PURE__*/React.createElement(EntitySideBarComponent, {
    isMobile: isMobile,
    onCollapse: onCollapse,
    collapsed: collapsed
  }), /*#__PURE__*/React.createElement(Content, {
    className: collapsed ? 'content-container collapsed' : 'content-container'
  }, children)));
}
AppTemplate.propTypes = {
  sidebarComponent: PropTypes.elementType,
  navBarComponent: PropTypes.elementType,
  entitySideBarComponent: PropTypes.elementType,
  children: PropTypes.element.isRequired,
  title: PropTypes.string,
  logo: PropTypes.string.isRequired,
  headerMenu: PropTypes.element.isRequired,
  theme: PropTypes.oneOf(['dark', 'light']),
  ranchBlockBreadcrumb: PropTypes.elementType
};
AppTemplate.defaultProps = {
  sidebarComponent: null,
  navBarComponent: null,
  entitySideBarComponent: null,
  title: 'FarmX',
  theme: 'light',
  ranchBlockBreadcrumb: null
};

function UserMenu(props) {
  var userInfo = props.userInfo,
      onFeedbackMenuClick = props.onFeedbackMenuClick;

  var _useTranslation = useTranslation(),
      t = _useTranslation.t;

  if (!userInfo) return null;
  return /*#__PURE__*/React.createElement(Dropdown, {
    placement: "bottomRight",
    trigger: ['click'],
    overlay: /*#__PURE__*/React.createElement(Menu, null, /*#__PURE__*/React.createElement(Menu.Item, null, /*#__PURE__*/React.createElement(Link, {
      to: "/settings"
    }, /*#__PURE__*/React.createElement(FaCog, null), /*#__PURE__*/React.createElement("span", null, t('Settings')))), /*#__PURE__*/React.createElement(Menu.Divider, null), /*#__PURE__*/React.createElement(Menu.Item, null, /*#__PURE__*/React.createElement(Link, {
      to: "/help"
    }, /*#__PURE__*/React.createElement(FaQuestionCircle, null), /*#__PURE__*/React.createElement("span", null, t('Resources And Help')))), /*#__PURE__*/React.createElement(Menu.Divider, null), /*#__PURE__*/React.createElement(Menu.Item, {
      onClick: onFeedbackMenuClick
    }, /*#__PURE__*/React.createElement(FaPen, null), /*#__PURE__*/React.createElement("span", {
      className: "menu-title margin-left-10"
    }, t('Submit Feedback'))), /*#__PURE__*/React.createElement(Menu.Divider, null), /*#__PURE__*/React.createElement(Menu.Item, null, /*#__PURE__*/React.createElement(Link, {
      to: "/logout"
    }, /*#__PURE__*/React.createElement(FaSignOutAlt, null), /*#__PURE__*/React.createElement("span", null, t('Logout')))))
  }, userInfo ? /*#__PURE__*/React.createElement(Button, {
    type: "link",
    className: "ant-dropdown-link user-menu"
  }, /*#__PURE__*/React.createElement("span", {
    className: "user-title"
  }, userInfo.username), /*#__PURE__*/React.createElement(UserOutlined, null)) : /*#__PURE__*/React.createElement(Button, null));
}

UserMenu.propTypes = {
  userInfo: PropTypes.shape({
    username: PropTypes.string.isRequired
  }),
  onFeedbackMenuClick: PropTypes.func
};
UserMenu.defaultProps = {
  userInfo: null,
  onFeedbackMenuClick: null
};

var Sider = Layout.Sider;

var CollapsableSidebar = /*#__PURE__*/function (_React$Component) {
  _inheritsLoose(CollapsableSidebar, _React$Component);

  function CollapsableSidebar() {
    var _this;

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _React$Component.call.apply(_React$Component, [this].concat(args)) || this;

    _this.collapse = function () {
      var onCollapse = _this.props.onCollapse;
      onCollapse(true);
    };

    _this.toggle = function () {
      var _this$props = _this.props,
          collapsed = _this$props.collapsed,
          onCollapse = _this$props.onCollapse;
      onCollapse(!collapsed);
    };

    _this.toggleIfNotMobile = function () {
      var isMobile = _this.props.isMobile;

      if (!isMobile) {
        _this.toggle();
      }
    };

    return _this;
  }

  var _proto = CollapsableSidebar.prototype;

  _proto.render = function render() {
    var _this$props2 = this.props,
        collapsed = _this$props2.collapsed,
        onCollapse = _this$props2.onCollapse,
        children = _this$props2.children,
        theme = _this$props2.theme;
    return /*#__PURE__*/React.createElement(Sider, {
      className: "nav",
      collapsible: true,
      collapsed: collapsed,
      onCollapse: onCollapse,
      collapsedWidth: 50,
      theme: theme
    }, children, /*#__PURE__*/React.createElement("div", {
      className: "sider-filler",
      role: "button",
      tabIndex: 0,
      onKeyDown: this.toggleIfNotMobile,
      onClick: this.toggleIfNotMobile
    }));
  };

  return CollapsableSidebar;
}(React.Component);

CollapsableSidebar.propTypes = {
  collapsed: PropTypes.bool,
  onCollapse: PropTypes.func,
  children: PropTypes.element,
  theme: PropTypes.oneOf(['light', 'dark']),
  isMobile: PropTypes.bool
};
CollapsableSidebar.defaultProps = {
  onCollapse: function onCollapse() {},
  collapsed: false,
  children: null,
  theme: 'dark',
  isMobile: false
};

var deviceIsWindowsPhone = navigator.userAgent.indexOf('Windows Phone') >= 0;
var deviceIsAndroid = navigator.userAgent.indexOf('Android') > 0 && !deviceIsWindowsPhone;
var deviceIsIOS = /iP(ad|hone|od)/.test(navigator.userAgent) && !deviceIsWindowsPhone;
var deviceIsIOS4 = deviceIsIOS && /OS 4_\d(_\d)?/.test(navigator.userAgent);
var deviceIsIOSWithBadTarget = deviceIsIOS && /OS [6-7]_\d/.test(navigator.userAgent);
var deviceIsBlackBerry10 = navigator.userAgent.indexOf('BB10') > 0;
var isMobile = deviceIsIOS || deviceIsAndroid || deviceIsBlackBerry10 || deviceIsWindowsPhone;

var commonjsGlobal = typeof globalThis !== 'undefined' ? globalThis : typeof window !== 'undefined' ? window : typeof global !== 'undefined' ? global : typeof self !== 'undefined' ? self : {};

function createCommonjsModule(fn, module) {
	return module = { exports: {} }, fn(module, module.exports), module.exports;
}

var freeGlobal = typeof commonjsGlobal == 'object' && commonjsGlobal && commonjsGlobal.Object === Object && commonjsGlobal;
var _freeGlobal = freeGlobal;

var freeSelf = typeof self == 'object' && self && self.Object === Object && self;
var root = _freeGlobal || freeSelf || Function('return this')();
var _root = root;

var Symbol$1 = _root.Symbol;
var _Symbol = Symbol$1;

var objectProto = Object.prototype;
var hasOwnProperty = objectProto.hasOwnProperty;
var nativeObjectToString = objectProto.toString;
var symToStringTag = _Symbol ? _Symbol.toStringTag : undefined;

function getRawTag(value) {
  var isOwn = hasOwnProperty.call(value, symToStringTag),
      tag = value[symToStringTag];

  try {
    value[symToStringTag] = undefined;
    var unmasked = true;
  } catch (e) {}

  var result = nativeObjectToString.call(value);

  if (unmasked) {
    if (isOwn) {
      value[symToStringTag] = tag;
    } else {
      delete value[symToStringTag];
    }
  }

  return result;
}

var _getRawTag = getRawTag;

var objectProto$1 = Object.prototype;
var nativeObjectToString$1 = objectProto$1.toString;

function objectToString(value) {
  return nativeObjectToString$1.call(value);
}

var _objectToString = objectToString;

var nullTag = '[object Null]',
    undefinedTag = '[object Undefined]';
var symToStringTag$1 = _Symbol ? _Symbol.toStringTag : undefined;

function baseGetTag(value) {
  if (value == null) {
    return value === undefined ? undefinedTag : nullTag;
  }

  return symToStringTag$1 && symToStringTag$1 in Object(value) ? _getRawTag(value) : _objectToString(value);
}

var _baseGetTag = baseGetTag;

function isObjectLike(value) {
  return value != null && typeof value == 'object';
}

var isObjectLike_1 = isObjectLike;

var numberTag = '[object Number]';

function isNumber(value) {
  return typeof value == 'number' || isObjectLike_1(value) && _baseGetTag(value) == numberTag;
}

var isNumber_1 = isNumber;

var stateTagColorCodes = {
  critical: '#e8101e',
  bad: '#F26F30',
  poor: '#faad14',
  good: 'rgb(61, 200, 115)',
  unknown: '#989898'
};
var stateTagTextColorCodes = {
  critical: 'white',
  bad: 'white',
  poor: 'black',
  good: 'black'
};
function getAnomalyIconColor(type) {
  if (type === 'disease') {
    return '#000000';
  }

  if (type === 'missing') {
    return '#3dc873';
  }

  if (type === 'irrigation') {
    return '#429aff';
  }

  return '#999999';
}
function getAnomalyIcon(type) {
  if (type === 'disease') {
    return 'icon-air';
  }

  if (type === 'missing') {
    return 'icon-tree';
  }

  if (type === 'under_irrigated' || type === 'over_irrigated' || type === 'standing_water') {
    return 'icon-waterdrop';
  }

  return 'icon-sound';
}
var getColorCodeForIrrigationState = function getColorCodeForIrrigationState(irrigationState) {
  if (irrigationState === 'over_irrigated') {
    return stateTagColorCodes.bad;
  }

  if (irrigationState === 'well_irrigated') {
    return stateTagColorCodes.good;
  }

  if (irrigationState === 'drying') {
    return stateTagColorCodes.poor;
  }

  if (irrigationState === 'dry') {
    return stateTagColorCodes.bad;
  }

  if (irrigationState === 'very_dry') {
    return stateTagColorCodes.critical;
  }

  return stateTagColorCodes.unknown;
};
function getAnomalyIconColorBySeverity(type) {
  if (type === 'low') {
    return getColorCodeForIrrigationState('well_irrigated');
  }

  if (type === 'moderate') {
    return getColorCodeForIrrigationState('drying');
  }

  if (type === 'major') {
    return getColorCodeForIrrigationState('dry');
  }

  if (type === 'critical') {
    return getColorCodeForIrrigationState('very_dry');
  }

  return '#999999';
}
var soilStateColorCodes = {
  critical: '#e8101e',
  bad: '#F26F30',
  poor: '#faad14',
  good: 'rgb(61, 200, 115)',
  unknown: '#989898',
  over: '#1890FF'
};
var getColorCodeForSoilState = function getColorCodeForSoilState(soilState) {
  if (soilState === 'over_irrigated') {
    return soilStateColorCodes.over;
  }

  if (soilState === 'well_irrigated') {
    return soilStateColorCodes.good;
  }

  if (soilState === 'drying') {
    return soilStateColorCodes.poor;
  }

  if (soilState === 'dry') {
    return soilStateColorCodes.bad;
  }

  if (soilState === 'very_dry') {
    return soilStateColorCodes.critical;
  }

  return soilStateColorCodes.unknown;
};
var stateColorCode = [{
  iconColor: '#ffffff',
  markerColor: '#cccccc',
  fillColor: '#cccccc',
  textColor: '#000000'
}, {
  iconColor: '#ffffff',
  markerColor: '#cccccc',
  fillColor: '#cccccc',
  textColor: '#000000'
}, {
  iconColor: '#ff98a4',
  markerColor: '#e23549',
  fillColor: '#e23549',
  textColor: '#35e2ce'
}, {
  iconColor: '#fff4cc',
  markerColor: '#FFCC00',
  fillColor: '#FFCC00',
  textColor: '#0033ff'
}, {
  iconColor: '#a0ccff',
  markerColor: '#429aff',
  fillColor: '#429aff',
  textColor: '#fa742'
}, {
  iconColor: '#4DFA90',
  markerColor: '#3dc873',
  fillColor: '#3dc873',
  textColor: '#c83d92'
}];
var icons = {
  dendrometer: 'icon-leaf',
  farmx_dendrometer: 'icon-leaf',
  ir_plant: 'icon-camera',
  farmx_infrared: 'icon-camera',
  weather_station: 'icon-ios-rainy',
  water_pressure: 'icon-gauge',
  water_flow: 'icon-water-meter',
  water_flow_analog: 'icon-water-meter',
  gateway: 'icon-radio-tower',
  boxer_station: 'icon-radio-tower',
  aquacheck_soil: 'icon-eyedropper',
  pixl_soil: 'icon-eyedropper',
  valve: 'icon-pipe-valve',
  vfd: 'icon-water-pump',
  cavalier: 'icon-radio-waves'
};
var sensorTitles = {
  dendrometer: {
    singular: 'Dendrometer',
    plural: 'Dendrometers'
  },
  farmx_dendrometer: {
    singular: 'Dendrometer',
    plural: 'Dendrometers'
  },
  ir_plant: {
    singular: 'IR Sensor',
    plural: 'IR Sensors'
  },
  weather_station: {
    singular: 'Weather Station',
    plural: 'Weather Stations'
  },
  water_pressure: {
    singular: 'Pressure Sensor',
    plural: 'Pressure Sensors'
  },
  water_flow: {
    singular: 'Flow Meter',
    plural: 'Flow Meters'
  },
  water_flow_analog: {
    singular: 'Flow Meter',
    plural: 'Flow Meters'
  },
  gateway: {
    singular: 'Gateway',
    plural: 'Gateways'
  },
  boxer_station: {
    singular: 'Boxer Station',
    plural: 'Boxer Stations'
  },
  aquacheck_soil: {
    singular: 'Soil Sensor',
    plural: 'Soil Sensors'
  },
  pixl_soil: {
    singular: 'Pixl Soil Sensor',
    plural: 'Pixl Soil Sensors'
  },
  cavalier: {
    singular: 'Cavalier',
    plural: 'Cavaliers'
  },
  valve: {
    singular: 'Valve',
    plural: 'Valves'
  },
  vfd: {
    singular: 'VFD',
    plural: 'VFDs'
  }
};
var mapPresentationModes = {
  connectivity: {
    title: 'Connectivity',
    excludedSensors: ['cavalier']
  },
  waterpressure: {
    title: 'Water pressure',
    includedSensors: ['water_pressure'],
    psiLevel: 5
  },
  provision: {
    title: 'Provision',
    excludedSensors: []
  },
  soilstatus: {
    title: 'Soil Status',
    excludedSensors: []
  },
  control: {
    title: 'Control',
    includedSensors: ['valve', 'vfd', 'water_flow', 'water_flow_analog', 'water_pressure']
  },
  irrigationRecommendation: {
    title: 'Irrigation Recommendation',
    includedSensors: []
  },
  anomaly: {
    title: 'Anomalies',
    excludedSensors: []
  },
  cropHealth: {
    title: 'Crop Health',
    includedSensors: ['farmx_dendrometer', 'farmx_infrared']
  }
};
function sensorTypeAllowedForMode(presentationMode, sensorProperties, isSensorByTypeOnly) {
  var sensorType = sensorProperties.type,
      block = sensorProperties.block;

  if (presentationMode === 'soilstatus' && isSensorByTypeOnly) {
    return ['aquacheck_soil', 'pixl_soil'].includes(sensorType) && block;
  }

  if (presentationMode === 'waterpressure' && isSensorByTypeOnly) {
    return sensorType === 'water_pressure';
  }

  if (mapPresentationModes[presentationMode].excludedSensors && !mapPresentationModes[presentationMode].includedSensors) {
    return !mapPresentationModes[presentationMode].excludedSensors.includes(sensorType);
  }

  if (mapPresentationModes[presentationMode].includedSensors && !mapPresentationModes[presentationMode].excludedSensors) {
    return mapPresentationModes[presentationMode].includedSensors.includes(sensorType);
  }

  if (!mapPresentationModes[presentationMode]) {
    throw new Error(presentationMode + " is not defined in mapPresentationModes");
  }

  throw new Error('mapPresentationModes can not have both includedSensors and excludedSensors');
}
function prepareNumberForDisplay(n) {
  if (!isNumber_1(n)) {
    return n;
  }

  if (Math.abs(n) < 1) {
    return n.toFixed(3);
  }

  return n.toFixed(1);
}
function getMarkerTooltipByPresentationMode(feature, presentationMode) {
  if (presentationMode === 'waterpressure') {
    return prepareNumberForDisplay(feature.properties.psi);
  }

  return feature.properties.identifier.slice(-5);
}

var selectPressureDetails = selectors.selectPressureDetails;
function calculatedHeightForMobile() {
  var mainDivElement = document.querySelector('.main-content');
  var footerDivElement = document.querySelector('.bottom-tabbar-container');
  var mainDivHeight = mainDivElement ? mainDivElement.offsetHeight : 0;
  var footerHeight = footerDivElement ? footerDivElement.offsetHeight : 0;
  return mainDivHeight || footerHeight ? mainDivHeight + footerHeight : window.innerHeight;
}
function getPressureDetailsForBlockIds(state, blockIds) {
  var pressureSensors = [];

  if (blockIds && blockIds.length) {
    blockIds.forEach(function (blockId) {
      var pressureDetail = selectPressureDetails(state, blockId);
      var averagePressureCutOff = pressureDetail.pressureCutoff.reduce(function (p, c) {
        return p + c;
      }, 0) / pressureDetail.pressureCutoff.length;
      var averagePressure = Number(pressureDetail.pressure);
      var waterPressureState = 0;

      if (averagePressure >= averagePressureCutOff) {
        waterPressureState = 4;
      } else if (averagePressure < averagePressureCutOff) {
        waterPressureState = 1;
      }

      var pressureDetailWithState = pressureDetail;
      pressureDetail.waterPressureState = waterPressureState;
      pressureSensors.push({
        blockId: blockId,
        pressureDetail: pressureDetailWithState
      });
    });
  }

  var pressureSensorsObj = pressureSensors.reduce(function (acc, obj) {
    acc[obj.blockId] = obj.pressureDetail;
    return acc;
  }, {});
  return pressureSensorsObj;
}
function getBlockIrrigationStateByWaterPressureState(waterPressureState) {
  var irrigationState = 'unknown';
  var color = stateColorCode[waterPressureState].markerColor;
  if (waterPressureState === 4) irrigationState = 'irrigating';
  if (waterPressureState === 1) irrigationState = 'not irrigating';
  return {
    irrigationState: irrigationState,
    color: color
  };
}

function ItemSelect(props) {
  var onChange = props.onChange,
      onSelect = props.onSelect,
      onBlur = props.onBlur,
      mode = props.mode,
      renderItem = props.renderItem,
      items = props.items,
      tagRender = props.tagRender,
      placeholder = props.placeholder,
      defaultOption = props.defaultOption,
      filterOption = props.filterOption,
      value = props.value,
      dropdownRender = props.dropdownRender,
      dropdownStyle = props.dropdownStyle,
      menuIsOpen = props.menuIsOpen,
      dropdownStyleValues = props.dropdownStyleValues,
      loading = props.loading,
      showSearch = props.showSearch,
      listHeightOffset = props.listHeightOffset,
      onDropdownVisibleChange = props.onDropdownVisibleChange,
      bordered = props.bordered,
      size = props.size,
      listItemHeight = props.listItemHeight;

  var _useTranslation = useTranslation(),
      t = _useTranslation.t;

  var children = [];

  if (items && items.length > 0) {
    items.forEach(function (d) {
      children.push(renderItem(d));
    });
  }

  var getDropDownClass = function getDropDownClass(dropDownStyle) {
    var sideBar = document.querySelector('.ant-layout-sider-trigger');
    var styleClassName;

    if (window.innerWidth > 950) {
      if (dropDownStyle && dropDownStyle.leftValue) {
        var createdStyles = StyleSheet.create({
          leftposition: {
            left: dropDownStyle.leftValue.current + " !important",
            width: dropDownStyle.minWidthValue.current + " !important"
          }
        });
        styleClassName = "" + css(createdStyles.leftposition);
      }
    }

    if (isMobile && window.innerWidth <= 950) {
      if (sideBar && sideBar.offsetWidth > 50) {
        var _createdStyles = StyleSheet.create({
          leftposition: {
            left: sideBar.offsetWidth + 20 + "px !important"
          }
        });

        styleClassName = "" + css(_createdStyles.leftposition);
      }
    }

    if (isMobile) {
      var _createdStyles2 = StyleSheet.create({
        leftposition: {
          left: '0px !important',
          width: '100% !important',
          top: '0px !important'
        }
      });

      styleClassName = "" + css(_createdStyles2.leftposition);
    }

    return styleClassName;
  };

  var divHeight = calculatedHeightForMobile();
  return /*#__PURE__*/React.createElement("div", {
    id: "select-component-wrapper"
  }, value ? /*#__PURE__*/React.createElement(Select, {
    defaultActiveFirstOption: isMobile,
    showSearch: showSearch,
    filterOption: filterOption,
    mode: mode,
    style: {
      width: '100%'
    },
    placeholder: placeholder || t('Please select'),
    defaultValue: defaultOption || [],
    value: value || undefined,
    onSelect: onSelect,
    onChange: onChange,
    onBlur: onBlur,
    allowClear: true,
    tagRender: tagRender,
    dropdownRender: dropdownRender,
    onMouseDown: function onMouseDown(e) {
      e.stopPropagation();
    },
    dropdownStyle: dropdownStyle,
    optionLabelProp: "label",
    dropdownClassName: getDropDownClass(dropdownStyleValues),
    open: menuIsOpen != null ? menuIsOpen : undefined,
    listHeight: isMobile ? divHeight - (listHeightOffset || 70) : 330,
    loading: loading,
    onFocus: onDropdownVisibleChange,
    getPopupContainer: isMobile ? null : function () {
      return document.getElementById('select-component-wrapper');
    },
    bordered: bordered,
    size: size,
    listItemHeight: listItemHeight
  }, children) : /*#__PURE__*/React.createElement(Select, {
    defaultActiveFirstOption: isMobile,
    showSearch: showSearch,
    filterOption: filterOption,
    mode: mode,
    style: {
      width: '100%'
    },
    placeholder: t(placeholder || 'Please select'),
    defaultValue: defaultOption || [],
    onSelect: onSelect,
    onChange: onChange,
    onBlur: onBlur,
    allowClear: true,
    tagRender: tagRender,
    dropdownRender: dropdownRender,
    onMouseDown: function onMouseDown(e) {
      e.stopPropagation();
    },
    dropdownStyle: dropdownStyle,
    optionLabelProp: "label",
    dropdownClassName: getDropDownClass(dropdownStyleValues),
    open: menuIsOpen != null ? menuIsOpen : undefined,
    listHeight: isMobile ? divHeight - (listHeightOffset || 70) : 330,
    loading: loading,
    onFocus: onDropdownVisibleChange,
    getPopupContainer: isMobile ? null : function () {
      return document.getElementById('select-component-wrapper');
    },
    bordered: bordered,
    size: size,
    listItemHeight: listItemHeight
  }, children));
}

ItemSelect.propTypes = {
  onChange: PropTypes.func,
  onSelect: PropTypes.func,
  onBlur: PropTypes.func,
  mode: PropTypes.string,
  renderItem: PropTypes.func,
  items: PropTypes.arrayOf(PropTypes.any),
  tagRender: PropTypes.func,
  placeholder: PropTypes.string,
  defaultOption: PropTypes.arrayOf(PropTypes.any),
  filterOption: PropTypes.func,
  value: PropTypes.arrayOf(PropTypes.any),
  dropdownRender: PropTypes.func,
  dropdownStyle: PropTypes.shape({}),
  menuIsOpen: PropTypes.bool,
  dropdownStyleValues: PropTypes.oneOfType([PropTypes.number, PropTypes.shape({})]),
  loading: PropTypes.bool,
  showSearch: PropTypes.bool,
  listHeightOffset: PropTypes.number,
  onDropdownVisibleChange: PropTypes.func,
  bordered: PropTypes.bool,
  size: PropTypes.string,
  listItemHeight: PropTypes.number
};
ItemSelect.defaultProps = {
  onChange: null,
  onSelect: null,
  onBlur: null,
  mode: null,
  renderItem: null,
  items: [],
  tagRender: null,
  placeholder: null,
  defaultOption: null,
  filterOption: null,
  value: null,
  dropdownRender: null,
  dropdownStyle: {},
  menuIsOpen: null,
  dropdownStyleValues: null,
  loading: false,
  showSearch: true,
  listHeightOffset: null,
  onDropdownVisibleChange: null,
  bordered: true,
  size: 'middle',
  listItemHeight: null
};

var styles = {"left-align":"_B5SkY","right-align":"_2Wsl8","extra-list-items":"_1wLl4","chart-container-div":"_3P_xS","loader-style":"_djLYN","select-dropdown":"_fz0U6","dropdown-optiondiv":"_rRLj7","collapse-content":"_1Eppv","collapse-content-collapse-selected":"_2fE0F","header":"_2d_-o","selected-block":"_2zQyk","selected-block-text":"_12vYp","selected-ranch-header":"_3t_l3","collapsible-list-selected":"_1BFq2","collapsible-list":"_1s2Io","ant-collapse":"_2ERLJ","ant-collapse-item":"_19Mfc","ant-collapse-header":"_2bx_o","list-content":"_1QkEB","ant-collapse-content":"_1lNDt","ant-collapse-content-box":"_2D_vV","selected-value":"_znUmr","panel-statusbox":"_1rH8_","status-box":"_2L6Pb","ant-tooltip-inner":"_2ALx6","list-name":"_YQbVw","loading-icon":"_3rvC5","block-select-tag":"_3MpMl","add-color-to-even-rows":"_2yl_d"};

var BlockOption = function BlockOption(_ref) {
  var block = _ref.block;
  return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("span", {
    className: styles['left-align']
  }, block.name), /*#__PURE__*/React.createElement("span", {
    className: styles['right-align']
  }, block.ranch && block.ranch.name));
};

BlockOption.propTypes = {
  block: PropTypes.shape({
    name: PropTypes.string,
    ranch: PropTypes.shape({
      name: PropTypes.string
    })
  }).isRequired
};

var styles$1 = {"color-state-unknown":"_2tAkI","color-state-over":"_qvKl5","color-state-ok":"__th2x","color-state-warning":"_1Tye4","color-state-error":"_2brbR","color-state-critical":"_1zyZu","color-state-not-irrigating":"_1pmkc","color-state-disease":"_1Zt5p","color-state-missing":"_22Jyv","color-state-irrigation":"_3G_2D","text-color-normal":"_3S447","text-color-unknown":"_1eb9Z","ant-typography":"_3Yura","text-color-over":"_1QjjA","text-color-ok":"_11wGX","text-color-warning":"_3eE8j","text-color-error":"_1GVG6","text-color-critical":"_CV0xj","modal-select-header":"_1iCKl","mobile-select-dropdown-style":"_22kTn","mobile-select-scrollable-div":"_QW-Db","cancel-button-color":"_2g1Vb","search-box":"_1JwQT","flex-container":"_3ckWs","button-centered":"_2iwtO","modal-select-header-text":"_1t5or","sensor-select-outer":"_3Y4n-","sensor-option-div":"_2ass1","sensor-option-scrolldiv-outer":"_2lR1V","sensor-option-scrolldiv-inner":"_1m4DN","filter-outerdiv":"_2jjsw","scrollbar":"_2cWDT","rc-virtual-list-scrollbar":"_1Qt_F","select-filter":"_2l_FM","filter-icon":"_2p-7u","select-filter-dropdown":"_2DjeH","ant-select-selection-item":"_1D2_J","ant-select":"_3VbiX","item-select":"_1MXnH","add-button-style":"_q5xEM","add-text-button-style":"_3kH59","sensor-options":"_1EvSf","filter-options":"_3kT5r","before-add-button":"_2Ra1z","option-button":"_15QqT","option-or-button":"_1nXMc","option-one":"_rzXCV","option-two":"_PPCTf","option-three":"_2jtdm","option-four":"_JRcmk","sensor-name":"_3YX3P","ranch-name":"_1qrGd","identifier":"_19Snh","block-name":"_3YOfW","ant-select-item-option-state":"_Ncgty","close-button-style":"_3mGpE","sensor-select-scroll":"_1z9Rj","sensor-add-button-style":"_2tC6g","sensor-filter-color":"_3uwrV","sensor-filter-color-after":"_3ud3x","sensor-button-color":"_1w9q7","sensor-button-color-after":"_1JCTk","sensor-select-filter-background":"_Bu0zY","sensor-select-filter-background-after":"_3gDGj","tree-select-dropdown":"_1uP3U","mobile-select-modal":"_1jZ1F","mobile-dropdown-modal-button":"_2GZSW","modal-custom-button":"_3kg3J","go-back":"_3awaf","custom-modal":"_24a5F","ant-modal-content":"_lz-Oi","ant-modal-body":"_1rjCx"};

var Searchbox = function Searchbox(_ref) {
  var onChange = _ref.onChange;

  var _useTranslation = useTranslation(),
      t = _useTranslation.t;

  return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Input, {
    className: styles$1['search-box'],
    placeholder: t('Type to search'),
    allowClear: true,
    onChange: onChange,
    onKeyDown: function onKeyDown(e) {
      e.stopPropagation();
    },
    ref: function ref(input) {
      return input && input.focus();
    }
  }));
};

Searchbox.propTypes = {
  onChange: PropTypes.func
};
Searchbox.defaultProps = {
  onChange: null
};

var DropdownModal = function DropdownModal(_ref) {
  var title = _ref.title,
      displayOptions = _ref.displayOptions,
      mobileScreen = _ref.mobileScreen,
      _onClick = _ref.onClick,
      onChange = _ref.onChange,
      displaySearch = _ref.displaySearch,
      buttonText = _ref.buttonText,
      size = _ref.size,
      showFullHeight = _ref.showFullHeight;

  var _useTranslation = useTranslation(),
      t = _useTranslation.t;

  var listHeight = calculatedHeightForMobile();
  var btnHeight = 110;
  return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
    className: styles$1['flex-container'],
    style: {
      height: mobileScreen && !showFullHeight ? listHeight + "px" : listHeight - btnHeight + "px"
    }
  }, mobileScreen ? /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
    className: styles$1['modal-select-header']
  }, /*#__PURE__*/React.createElement("div", {
    className: styles$1['modal-select-header-text']
  }, t(title)), /*#__PURE__*/React.createElement("div", null)), displaySearch ? /*#__PURE__*/React.createElement(Searchbox, {
    onChange: onChange
  }) : null) : null, /*#__PURE__*/React.createElement("div", {
    className: styles$1['sensor-option-div'],
    onClick: function onClick(d) {
      return buttonText === 'cancel' ? _onClick(d) : null;
    },
    onKeyPress: function onKeyPress(d) {
      return buttonText === 'cancel' ? _onClick(d) : null;
    },
    role: "button",
    tabIndex: "0"
  }, displayOptions)), mobileScreen ? /*#__PURE__*/React.createElement("div", {
    className: styles$1['button-centered'] + " custom-button"
  }, /*#__PURE__*/React.createElement(Button, {
    id: "goBack",
    type: "primary",
    className: styles$1['cancel-button-color'],
    size: size,
    onClick: _onClick,
    block: true
  }, t(buttonText))) : null);
};

DropdownModal.propTypes = {
  title: PropTypes.string,
  displayOptions: PropTypes.shape({}),
  mobileScreen: PropTypes.bool,
  onClick: PropTypes.func,
  onChange: PropTypes.func,
  displaySearch: PropTypes.bool,
  buttonText: PropTypes.string,
  size: PropTypes.string,
  showFullHeight: PropTypes.bool
};
DropdownModal.defaultProps = {
  title: null,
  displayOptions: null,
  mobileScreen: false,
  onClick: null,
  onChange: null,
  displaySearch: false,
  buttonText: 'done',
  size: 'middle',
  showFullHeight: null
};

var SensorSelectContext = React.createContext(3);

var Option = Select.Option;
var loadFarmData = actions.loadFarmData;
var selectAllBlocks = selectors.selectAllBlocks;

var blockTagRender = function blockTagRender(args) {
  var closable = args.closable,
      onClose = args.onClose,
      label = args.label;
  return /*#__PURE__*/React.createElement(Tag, {
    closable: closable,
    onClose: onClose,
    className: styles['block-select-tag']
  }, label.props ? label.props.block.name : label);
};

var defaultItemRenderer = function defaultItemRenderer(d) {
  return /*#__PURE__*/React.createElement(Option, {
    className: styles['add-color-to-even-rows'],
    key: d.id,
    value: d.id,
    label: d.name,
    "data-testid": "" + d.name
  }, /*#__PURE__*/React.createElement(BlockOption, {
    block: d
  }));
};

var BlockSelect = function BlockSelect(_ref) {
  var loadItems = _ref.loadItems,
      items = _ref.items,
      mode = _ref.mode,
      renderItem = _ref.renderItem,
      onSelect = _ref.onSelect,
      onChange = _ref.onChange,
      tagRender = _ref.tagRender,
      placeholder = _ref.placeholder,
      defaultOption = _ref.defaultOption,
      value = _ref.value,
      bordered = _ref.bordered,
      title = _ref.title,
      applyFilter = _ref.applyFilter;
  var dispatch = useDispatch();
  var data = useSelector(selectAllBlocks);
  var blocks = items || data;

  var _useState = useState(false),
      modal = _useState[0],
      setModalState = _useState[1];

  var _useState2 = useState(''),
      searchText = _useState2[0],
      setSearchText = _useState2[1];

  var dropdownStyleValues = useContext(SensorSelectContext);
  useEffect(function () {
    if (loadItems) loadItems();else dispatch(loadFarmData(applyFilter));
  }, [applyFilter, dispatch, loadItems]);
  var dropdownStyle = null;
  if (isMobile) dropdownStyle = {
    minWidth: '60%'
  };

  function onSelectAction(d) {
    if (onSelect) onSelect(d);

    if (!isMobile) {
      setModalState(false);
    }
  }

  function customFilterMobile(searchStr) {
    var filterResult = blocks.filter(function (d) {
      return d && d.name && d.name.toLowerCase().indexOf(searchStr.toLowerCase()) >= 0 || d && d.ranch && d.ranch.name.toLowerCase().indexOf(searchStr.toLowerCase()) >= 0;
    });
    return filterResult;
  }

  return /*#__PURE__*/React.createElement(ItemSelect, {
    mode: mode,
    renderItem: renderItem,
    items: !isMobile ? blocks : customFilterMobile(searchText),
    onSelect: onSelectAction,
    onChange: onChange,
    tagRender: tagRender || blockTagRender,
    placeholder: placeholder,
    defaultOption: defaultOption,
    value: value,
    filterOption: function filterOption(input, option) {
      return option.children.props.block.name.toLowerCase().indexOf(input.toLowerCase()) >= 0 || option.children.props.block.ranch && option.children.props.block.ranch.name.toLowerCase().indexOf(input.toLowerCase()) >= 0;
    },
    dropdownStyle: dropdownStyle,
    menuIsOpen: isMobile ? modal : undefined,
    dropdownRender: isMobile ? function (menu) {
      return /*#__PURE__*/React.createElement(DropdownModal, {
        title: title,
        displayOptions: menu,
        mobileScreen: isMobile,
        onClick: function onClick() {
          return setModalState(false);
        },
        onChange: function onChange(d) {
          return setSearchText(d.target.value);
        },
        displaySearch: true,
        size: isMobile ? 'large' : 'middle'
      });
    } : undefined,
    dropdownStyleValues: dropdownStyleValues,
    onDropdownVisibleChange: isMobile ? function () {
      return setModalState(true);
    } : undefined,
    showSearch: !isMobile,
    bordered: bordered,
    listItemHeight: 40
  });
};

BlockSelect.propTypes = {
  mode: PropTypes.string,
  loadItems: PropTypes.func,
  renderItem: PropTypes.func,
  items: PropTypes.arrayOf(PropTypes.object),
  onSelect: PropTypes.func,
  onChange: PropTypes.func,
  tagRender: PropTypes.func,
  placeholder: PropTypes.string,
  defaultOption: PropTypes.arrayOf(PropTypes.any),
  value: PropTypes.arrayOf(PropTypes.any),
  bordered: PropTypes.bool,
  title: PropTypes.string,
  applyFilter: PropTypes.bool
};
BlockSelect.defaultProps = {
  mode: null,
  loadItems: null,
  items: null,
  renderItem: defaultItemRenderer,
  onSelect: null,
  onChange: null,
  tagRender: null,
  placeholder: null,
  defaultOption: [],
  value: null,
  bordered: true,
  title: 'Block Select',
  applyFilter: true
};

var RanchOption = function RanchOption(_ref) {
  var ranch = _ref.ranch;
  return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("span", {
    className: styles['left-align']
  }, ranch.name), /*#__PURE__*/React.createElement("span", {
    className: styles['right-align']
  }, ranch.entity && ranch.entity.name));
};

RanchOption.propTypes = {
  ranch: PropTypes.shape({
    name: PropTypes.string,
    entity: PropTypes.shape({
      name: PropTypes.string
    })
  }).isRequired
};

var mobileScreen = window.innerWidth < 599;
var Option$1 = Select.Option;
var loadFarmData$1 = actions.loadFarmData;
var selectAllRanches = selectors.selectAllRanches;

var ranchTagRender = function ranchTagRender(args) {
  var label = args.label,
      closable = args.closable,
      onClose = args.onClose;
  return /*#__PURE__*/React.createElement(Tag, {
    closable: closable,
    onClose: onClose,
    style: {
      marginRight: 3
    }
  }, label.props ? label.props.ranch.name : label);
};

var defaultItemRenderer$1 = function defaultItemRenderer(d) {
  return /*#__PURE__*/React.createElement(Option$1, {
    className: styles['add-color-to-even-rows'],
    key: d.id,
    value: d.id,
    label: d.name,
    "data-testid": "" + d.name
  }, /*#__PURE__*/React.createElement(RanchOption, {
    ranch: d
  }));
};

var RanchSelect = function RanchSelect(_ref) {
  var loadItems = _ref.loadItems,
      items = _ref.items,
      mode = _ref.mode,
      renderItem = _ref.renderItem,
      onSelect = _ref.onSelect,
      onChange = _ref.onChange,
      tagRender = _ref.tagRender,
      placeholder = _ref.placeholder,
      defaultOption = _ref.defaultOption,
      value = _ref.value,
      applyFilter = _ref.applyFilter;
  var dispatch = useDispatch();
  var data = useSelector(selectAllRanches);
  var ranches = items || data;

  var _useState = useState(false),
      modal = _useState[0],
      setModalState = _useState[1];

  var _useState2 = useState(''),
      searchText = _useState2[0],
      setSearchText = _useState2[1];

  var dropdownStyleValues = useContext(SensorSelectContext);
  useEffect(function () {
    if (loadItems) loadItems();else dispatch(loadFarmData$1(applyFilter));
  }, [applyFilter, dispatch, loadItems]);
  var dropdownStyle = null;
  if (window.innerWidth < 599) dropdownStyle = {
    minWidth: '60%'
  };

  function onSelectAction(d) {
    if (onSelect) onSelect(d);
    if (!mobileScreen) setModalState(false);
  }

  function customFilterMobile(searchStr) {
    var filterResult = ranches.filter(function (d) {
      return d && d.name && d.name.toLowerCase().indexOf(searchStr.toLowerCase()) >= 0 || d && d.entity && d.entity.name.toLowerCase().indexOf(searchStr.toLowerCase()) >= 0;
    });
    return filterResult;
  }

  return /*#__PURE__*/React.createElement(ItemSelect, {
    mode: mode,
    renderItem: renderItem,
    items: !mobileScreen ? ranches : customFilterMobile(searchText),
    onSelect: onSelectAction,
    onChange: onChange,
    tagRender: tagRender || ranchTagRender,
    placeholder: placeholder,
    defaultOption: defaultOption,
    value: value,
    filterOption: function filterOption(input, option) {
      return option.children.props.ranch.name.toLowerCase().indexOf(input.toLowerCase()) >= 0 || option.children.props.ranch.entity && option.children.props.ranch.entity.name.toLowerCase().indexOf(input.toLowerCase()) >= 0;
    },
    dropdownStyle: dropdownStyle,
    menuIsOpen: mobileScreen ? modal : undefined,
    dropdownRender: mobileScreen ? function (menu) {
      return /*#__PURE__*/React.createElement(DropdownModal, {
        title: "Ranch Select",
        displayOptions: menu,
        mobileScreen: mobileScreen,
        onClick: function onClick() {
          return setModalState(false);
        },
        onChange: function onChange(d) {
          return setSearchText(d.target.value);
        },
        displaySearch: true,
        size: mobileScreen ? 'large' : 'middle'
      });
    } : undefined,
    dropdownStyleValues: dropdownStyleValues,
    onDropdownVisibleChange: mobileScreen ? function () {
      return setModalState(true);
    } : undefined,
    showSearch: !mobileScreen
  });
};

RanchSelect.propTypes = {
  mode: PropTypes.string,
  loadItems: PropTypes.func,
  renderItem: PropTypes.func,
  items: PropTypes.arrayOf(PropTypes.object),
  onSelect: PropTypes.func,
  onChange: PropTypes.func,
  tagRender: PropTypes.func,
  placeholder: PropTypes.string,
  defaultOption: PropTypes.arrayOf(PropTypes.any),
  value: PropTypes.arrayOf(PropTypes.any),
  applyFilter: PropTypes.bool
};
RanchSelect.defaultProps = {
  mode: null,
  loadItems: null,
  items: null,
  renderItem: defaultItemRenderer$1,
  onSelect: null,
  onChange: null,
  tagRender: null,
  placeholder: null,
  defaultOption: [],
  value: null,
  applyFilter: true
};

var EntityOption = function EntityOption(_ref) {
  var entity = _ref.entity;
  return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("span", {
    className: styles['left-align']
  }, entity.name));
};

EntityOption.propTypes = {
  entity: PropTypes.shape({
    name: PropTypes.string
  }).isRequired
};

var mobileScreen$1 = window.innerWidth < 599;
var Option$2 = Select.Option;
var loadFarmData$2 = actions.loadFarmData;
var selectAllEntities = selectors.selectAllEntities;

var entityTagRender = function entityTagRender(args) {
  var label = args.label,
      closable = args.closable,
      onClose = args.onClose;
  return /*#__PURE__*/React.createElement(Tag, {
    closable: closable,
    onClose: onClose,
    style: {
      marginRight: 3
    }
  }, label.props ? label.props.entity.name : label);
};

var defaultItemRenderer$2 = function defaultItemRenderer(d) {
  return /*#__PURE__*/React.createElement(Option$2, {
    className: styles['add-color-to-even-rows'],
    key: d.id,
    value: d.id,
    label: d.name,
    "data-testid": "" + d.name
  }, /*#__PURE__*/React.createElement(EntityOption, {
    entity: d
  }));
};

var EntitySelect = function EntitySelect(_ref) {
  var loadItems = _ref.loadItems,
      items = _ref.items,
      mode = _ref.mode,
      renderItem = _ref.renderItem,
      onSelect = _ref.onSelect,
      onChange = _ref.onChange,
      tagRender = _ref.tagRender,
      placeholder = _ref.placeholder,
      defaultOption = _ref.defaultOption,
      applyFilter = _ref.applyFilter;
  var dispatch = useDispatch();
  var data = useSelector(selectAllEntities);
  var ranches = items || data;

  var _useState = useState(false),
      modal = _useState[0],
      setModalState = _useState[1];

  var _useState2 = useState(''),
      searchText = _useState2[0],
      setSearchText = _useState2[1];

  var dropdownStyle = null;
  if (mobileScreen$1) dropdownStyle = {
    minWidth: '60%'
  };
  var dropdownStyleValues = useContext(SensorSelectContext);
  useEffect(function () {
    if (loadItems) loadItems();else dispatch(loadFarmData$2(applyFilter));
  }, [applyFilter, dispatch, loadItems]);

  function customFilterMobile(searchStr) {
    var filterResult = ranches.filter(function (d) {
      return d && d.name && d.name.toLowerCase().indexOf(searchStr.toLowerCase()) >= 0;
    });
    return filterResult;
  }

  return /*#__PURE__*/React.createElement(ItemSelect, {
    mode: mode,
    renderItem: renderItem,
    items: !mobileScreen$1 ? ranches : customFilterMobile(searchText),
    onSelect: onSelect,
    onChange: onChange,
    tagRender: tagRender || entityTagRender,
    placeholder: placeholder,
    defaultOption: defaultOption,
    filterOption: function filterOption(input, option) {
      return option.children.props.entity.name.toLowerCase().indexOf(input.toLowerCase()) >= 0 || option.children.props.entity && option.children.props.entity.name.toLowerCase().indexOf(input.toLowerCase()) >= 0;
    },
    dropdownStyle: dropdownStyle,
    menuIsOpen: mobileScreen$1 ? modal : undefined,
    dropdownRender: mobileScreen$1 ? function (menu) {
      return /*#__PURE__*/React.createElement(DropdownModal, {
        title: "Entity Select",
        displayOptions: menu,
        mobileScreen: mobileScreen$1,
        onClick: function onClick() {
          return setModalState(false);
        },
        onChange: function onChange(d) {
          return setSearchText(d.target.value);
        },
        displaySearch: true,
        size: mobileScreen$1 ? 'large' : 'middle'
      });
    } : undefined,
    dropdownStyleValues: dropdownStyleValues,
    onDropdownVisibleChange: mobileScreen$1 ? function () {
      return setModalState(true);
    } : undefined,
    showSearch: !mobileScreen$1
  });
};

EntitySelect.propTypes = {
  mode: PropTypes.string,
  loadItems: PropTypes.func,
  renderItem: PropTypes.func,
  items: PropTypes.arrayOf(PropTypes.object),
  onSelect: PropTypes.func,
  onChange: PropTypes.func,
  tagRender: PropTypes.func,
  placeholder: PropTypes.string,
  defaultOption: PropTypes.arrayOf(PropTypes.any),
  applyFilter: PropTypes.bool
};
EntitySelect.defaultProps = {
  mode: null,
  loadItems: null,
  items: null,
  renderItem: defaultItemRenderer$2,
  onSelect: null,
  onChange: null,
  tagRender: null,
  placeholder: null,
  defaultOption: [],
  applyFilter: true
};

var getNameForSensorType = sensorApi.getNameForSensorType;
function SensorTypeTag(_ref) {
  var sensorType = _ref.sensorType;

  function getColorForType(type) {
    switch (type) {
      case 'weather_station':
        return 'geekblue';

      case 'cavalier':
        return 'purple';

      case 'aquacheck_soil':
        return 'volcano';

      case 'water_pressure':
        return 'blue';

      case 'farmx_dendrometer':
        return 'green';

      case 'pixl_soil':
        return 'orange';

      case 'farmx_infrared':
        return 'lime';

      case 'farmx_frost':
        return 'cyan';

      default:
        return null;
    }
  }

  var color = getColorForType(sensorType);
  return /*#__PURE__*/React.createElement(Tag, {
    color: color
  }, getNameForSensorType(sensorType));
}
SensorTypeTag.propTypes = {
  sensorType: PropTypes.string.isRequired
};

function InstallStateTag(_ref) {
  var state = _ref.state;

  function getColorForState(installState) {
    switch (installState) {
      case 'removed':
        return 'red';

      case 'installed':
        return 'green';

      case 'planned':
        return 'cyan';

      case 'withdrawn':
        return 'purple';

      case 'inventory':
        return 'blue';

      case 'unknown':
        return 'geekblue';

      case 'dead':
        return 'black';

      case 'maintenance':
        return 'magenta';

      default:
        return null;
    }
  }

  return /*#__PURE__*/React.createElement(Tag, {
    color: getColorForState(state)
  }, state);
}

InstallStateTag.propTypes = {
  state: PropTypes.string
};
InstallStateTag.defaultProps = {
  state: null
};

var Text = Typography.Text;
var selectRanchById = selectors.selectRanchById,
    selectBlockById = selectors.selectBlockById;

var SensorOption = function SensorOption(_ref) {
  var sensor = _ref.sensor;
  var ranch = useSelector(function (state) {
    return selectRanchById(state, sensor.ranch);
  });
  var block = useSelector(function (state) {
    return selectBlockById(state, sensor.block);
  });
  return /*#__PURE__*/React.createElement("div", {
    id: "sensorOption"
  }, /*#__PURE__*/React.createElement(List, {
    className: styles$1['sensor-options']
  }, /*#__PURE__*/React.createElement(List.Item, {
    className: styles$1['list-sensor-options']
  }, /*#__PURE__*/React.createElement(List.Item.Meta, {
    className: styles$1['option-one'],
    title: sensor.name ? /*#__PURE__*/React.createElement(Text, {
      className: styles$1['sensor-name'],
      ellipsis: true,
      title: "" + sensor.name
    }, "" + sensor.name) : /*#__PURE__*/React.createElement(Text, {
      style: {
        color: 'lightgray'
      },
      title: "No Name"
    }, "No Name"),
    description: /*#__PURE__*/React.createElement(Text, {
      className: styles$1.identifier,
      ellipsis: true,
      title: "" + sensor.identifier
    }, "" + sensor.identifier)
  }), /*#__PURE__*/React.createElement(List.Item.Meta, {
    title: /*#__PURE__*/React.createElement(Text, {
      className: styles$1['ranch-name'],
      ellipsis: true,
      title: ranch && ranch.name ? ranch.name : ''
    }, ranch && ranch.name ? ranch.name : ''),
    description: /*#__PURE__*/React.createElement(Text, {
      className: styles$1['block-name'],
      ellipsis: true,
      title: block && block.name ? block.name : ''
    }, block && block.name ? block.name : ''),
    className: styles$1['option-two']
  }), /*#__PURE__*/React.createElement("div", {
    className: styles$1['option-three']
  }, /*#__PURE__*/React.createElement(InstallStateTag, {
    state: sensor.install_state
  })), /*#__PURE__*/React.createElement("div", {
    className: styles$1['option-four']
  }, /*#__PURE__*/React.createElement(SensorTypeTag, {
    styles: {
      "float": 'right'
    },
    sensorType: sensor.type
  })))));
};

SensorOption.propTypes = {
  sensor: PropTypes.shape({
    name: PropTypes.string,
    identifier: PropTypes.string,
    ranch: PropTypes.number,
    block: PropTypes.number,
    type: PropTypes.string,
    install_state: PropTypes.string
  }).isRequired
};

var Option$3 = Select.Option;
function InstallStateSelect(props) {
  var selectedState = props.selectedState,
      installStates = props.installStates,
      onChange = props.onChange,
      onSelect = props.onSelect;

  var _useTranslation = useTranslation(),
      t = _useTranslation.t;

  var _useState = useState(false),
      modal = _useState[0],
      setModalState = _useState[1];

  var mobileScreen = window.innerWidth < 599;
  var dropdownStyle = null;
  if (mobileScreen) dropdownStyle = {
    minWidth: '60%'
  };
  var dropdownStyleValues = useContext(SensorSelectContext);

  var getDropDownClass = function getDropDownClass(dropDownStyle) {
    var styleClassName;

    if (window.innerWidth > 599) {
      if (dropDownStyle && dropDownStyle.leftValue) {
        var createdStyles = StyleSheet.create({
          leftposition: {
            left: dropDownStyle.leftValue.current + " !important",
            width: dropDownStyle.minWidthValue.current + " !important"
          }
        });
        styleClassName = "" + css(createdStyles.leftposition);
      }
    }

    if (window.innerWidth < 599) {
      styleClassName = styles['select-dropdown'] + " ";
    }

    return styleClassName;
  };

  return /*#__PURE__*/React.createElement(Select, {
    disabled: installStates.length === 1 && selectedState,
    style: {
      width: '100%'
    },
    allowClear: true,
    value: selectedState,
    placeholder: t('Install State'),
    optionLabelProp: "label",
    dropdownMatchSelectWidth: false,
    onChange: onChange,
    onSelect: !mobileScreen ? function (d) {
      setModalState(false);
      if (onSelect) onSelect(d);
    } : null,
    dropdownStyle: dropdownStyle,
    open: mobileScreen ? modal : undefined,
    dropdownClassName: getDropDownClass(dropdownStyleValues),
    dropdownRender: mobileScreen ? function (menu) {
      return /*#__PURE__*/React.createElement(DropdownModal, {
        title: "InstallState Select",
        displayOptions: menu,
        mobileScreen: mobileScreen,
        onClick: function onClick() {
          return setModalState(false);
        }
      });
    } : undefined,
    onDropdownVisibleChange: mobileScreen ? function () {
      return setModalState(true);
    } : undefined
  }, installStates.map(function (value, i) {
    return /*#__PURE__*/React.createElement(Option$3, {
      key: String(i),
      label: value,
      value: value,
      "data-testid": "" + value
    }, value);
  }));
}
InstallStateSelect.propTypes = {
  selectedState: PropTypes.string,
  installStates: PropTypes.arrayOf(PropTypes.any),
  onChange: PropTypes.func,
  onSelect: PropTypes.func
};
InstallStateSelect.defaultProps = {
  selectedState: undefined,
  installStates: [],
  onChange: function onChange() {},
  onSelect: null
};

var Option$4 = Select.Option;
function SensorTypeSelect(props) {
  var selectedType = props.selectedType,
      sensorTypes = props.sensorTypes,
      onChange = props.onChange,
      disabled = props.disabled;

  var _useTranslation = useTranslation(),
      t = _useTranslation.t;

  var _useState = useState(false),
      modal = _useState[0],
      setModalState = _useState[1];

  var mobileScreen = window.innerWidth < 599;
  var dropdownStyle = null;
  if (mobileScreen) dropdownStyle = {
    minWidth: '60%'
  };
  var dropdownStyleValues = useContext(SensorSelectContext);

  var getDropDownClass = function getDropDownClass(dropDownStyle) {
    var styleClassName;

    if (window.innerWidth > 599) {
      if (dropDownStyle && dropDownStyle.leftValue) {
        var createdStyles = StyleSheet.create({
          leftposition: {
            left: dropDownStyle.leftValue.current + " !important",
            width: dropDownStyle.minWidthValue.current + " !important"
          }
        });
        styleClassName = "" + css(createdStyles.leftposition);
      }
    }

    if (window.innerWidth < 599) {
      styleClassName = styles['select-dropdown'] + " ";
    }

    return styleClassName;
  };

  return /*#__PURE__*/React.createElement(Select, {
    disabled: sensorTypes.length === 1 && selectedType || disabled,
    style: {
      width: '100%'
    },
    allowClear: true,
    value: selectedType,
    placeholder: t('Type'),
    optionLabelProp: "label",
    dropdownMatchSelectWidth: false,
    onChange: onChange,
    onSelect: !mobileScreen ? function () {
      return setModalState(false);
    } : null,
    dropdownStyle: dropdownStyle,
    open: mobileScreen ? modal : undefined,
    dropdownClassName: getDropDownClass(dropdownStyleValues),
    dropdownRender: mobileScreen ? function (menu) {
      return /*#__PURE__*/React.createElement("div", {
        className: styles['dropdown-option-div']
      }, /*#__PURE__*/React.createElement(DropdownModal, {
        title: "SensorType Select",
        displayOptions: menu,
        mobileScreen: mobileScreen,
        onClick: function onClick() {
          return setModalState(false);
        }
      }));
    } : undefined,
    onDropdownVisibleChange: mobileScreen ? function () {
      return setModalState(true);
    } : undefined
  }, sensorTypes.map(function (_ref) {
    var key = _ref[0],
        value = _ref[1];
    return /*#__PURE__*/React.createElement(Option$4, {
      key: key,
      label: value.name,
      "data-testid": "" + value.name
    }, value.name);
  }));
}
var sensorTypePropType = PropTypes.string;
SensorTypeSelect.propTypes = {
  selectedType: sensorTypePropType,
  sensorTypes: PropTypes.arrayOf(PropTypes.any),
  onChange: PropTypes.func,
  disabled: PropTypes.bool
};
SensorTypeSelect.defaultProps = {
  selectedType: undefined,
  sensorTypes: [],
  onChange: function onChange() {},
  disabled: false
};

var OP = {
  OR: 'OR',
  AND: 'AND'
};
var INSTALL_STATE = {
  unknown: 'unknown',
  inventory: 'inventory',
  planned: 'planned',
  installed: 'installed',
  removed: 'removed',
  withdrawn: 'withdrawn',
  maintenance: 'maintenance',
  dead: 'dead'
};

var FilterListItem = function FilterListItem(_ref) {
  var onAdd = _ref.onAdd,
      onChange = _ref.onChange,
      changeIndex = _ref.changeIndex,
      onRemove = _ref.onRemove,
      type = _ref.type,
      typeFilter = _ref.typeFilter,
      value = _ref.value,
      op = _ref.op,
      noEdit = _ref.noEdit,
      noDelete = _ref.noDelete,
      showFilter = _ref.showFilter,
      onSetShowFilter = _ref.onSetShowFilter;

  var _useState = useState(null),
      selectType = _useState[0],
      setSelectType = _useState[1];

  var _useState2 = useState(null),
      selectVal = _useState2[0],
      setSelectVal = _useState2[1];

  var _useState3 = useState(OP.AND),
      opVal = _useState3[0],
      setOpVal = _useState3[1];

  var _useTranslation = useTranslation(),
      t = _useTranslation.t;

  var _useState4 = useState(false),
      modal = _useState4[0],
      setModalState = _useState4[1];

  var mobileScreen = window.innerWidth < 599;
  var CONDITION_CHECK_1 = (!selectType || !type) && (!value || !selectVal);
  var CONDITION_CHECK_2 = !selectVal || !selectType;

  var onAutomaticAdd = function onAutomaticAdd(val) {
    if (onAdd) {
      setOpVal(OP.AND);
      onAdd({
        type: selectType,
        value: val,
        op: opVal
      });
      setSelectType(undefined);
      setSelectVal(undefined);
      onSetShowFilter(true);
    }
  };

  var onSensorTypeOrStateSelect = function onSensorTypeOrStateSelect(val) {
    if (onChange) {
      onChange({
        index: changeIndex,
        type: selectType,
        value: val,
        op: opVal
      });
    }

    onAutomaticAdd(val);
  };

  var getSensorTypes = function getSensorTypes() {
    var allTypes = Object.entries(constants.SENSOR_TYPES);
    if (typeFilter) return allTypes.filter(function (sType) {
      return typeFilter.indexOf(sType[0]) >= 0;
    });
    return allTypes;
  };

  var sensorTypes = getSensorTypes();
  var selectedType;

  if (sensorTypes.length === 1) {
    var selType = sensorTypes[0][0];
    selectedType = selType;
  } else if (value) selectedType = value;

  var installStates = Object.values(INSTALL_STATE);
  var selectedState;

  if (installStates.length === 1) {
    var _selType = installStates[0];
    selectedState = _selType;
  } else if (value) selectedState = value;

  useEffect(function () {
    if (type) setSelectType(type);
    if (value) setSelectVal(value);
    if (op) setOpVal(op);
  }, [type, value, op]);

  var onSelectType = function onSelectType(d) {
    setSelectType(d);

    if (changeIndex >= 0) {
      setSelectVal(undefined);
      onChange({
        index: changeIndex,
        type: d,
        value: undefined,
        op: opVal
      });
    }

    if (!mobileScreen) setModalState(false);
  };

  var onSelectVal = function onSelectVal(val) {
    setSelectVal(val);
  };

  var onClickIcon = function onClickIcon() {
    setSelectType(undefined);
    setSelectVal(undefined);
    setOpVal(OP.AND);

    if (!value && !type) {
      if (selectType && selectVal) onAdd({
        type: selectType,
        value: selectVal,
        op: opVal
      });
    } else onRemove({
      index: changeIndex
    });
  };

  var onChangeType = function onChangeType(d) {
    if (value) {
      if (changeIndex >= 0) setSelectVal(undefined);
      onChange({
        index: changeIndex,
        type: d,
        value: changeIndex >= 0 ? undefined : selectVal,
        op: opVal
      });
    }
  };

  var onChangeVal = function onChangeVal(val) {
    onChange({
      index: changeIndex,
      type: selectType,
      value: val,
      op: opVal
    });
    onAutomaticAdd(val);
  };

  var onChangeOp = function onChangeOp(e) {
    var opValue = OP.AND;

    if (e.target.textContent === OP.AND) {
      opValue = OP.OR;
      setOpVal(OP.OR);
    } else setOpVal(OP.AND);

    onChange({
      index: changeIndex,
      type: selectType,
      value: selectVal,
      op: opValue
    });
  };

  var displayOp = function displayOp() {
    return /*#__PURE__*/React.createElement("div", {
      "data-testid": (opVal || op) + "_" + (value || selectVal)
    }, op && /*#__PURE__*/React.createElement(Button, {
      disabled: noEdit,
      className: styles$1['option-or-button'],
      onClick: onChangeOp
    }, opVal || op));
  };

  function getComponentForType(selectedFilterType) {
    switch (selectedFilterType) {
      case 'ranch':
        return /*#__PURE__*/React.createElement(RanchSelect, {
          onSelect: onSelectVal,
          onChange: onChangeVal,
          defaultOption: [value],
          placeholder: "Select Ranch",
          loadItems: function loadItems() {
            return null;
          }
        });

      case 'block':
        return /*#__PURE__*/React.createElement(BlockSelect, {
          onSelect: onSelectVal,
          onChange: onChangeVal,
          defaultOption: [value],
          placeholder: "Select Block",
          loadItems: function loadItems() {
            return null;
          }
        });

      case 'entity':
        return /*#__PURE__*/React.createElement(EntitySelect, {
          onSelect: onSelectVal,
          onChange: onChangeVal,
          defaultOption: [value],
          placeholder: "Select Entity",
          loadItems: function loadItems() {
            return null;
          }
        });

      case 'install_state':
        return /*#__PURE__*/React.createElement(InstallStateSelect, {
          onSelect: onSelectVal,
          onChange: onSensorTypeOrStateSelect,
          selectedState: selectedState,
          installStates: installStates
        });

      case 'type':
        return /*#__PURE__*/React.createElement(SensorTypeSelect, {
          disabled: noEdit,
          onChange: onSensorTypeOrStateSelect,
          selectedType: selectedType,
          sensorTypes: sensorTypes
        });

      default:
        return /*#__PURE__*/React.createElement(Select, {
          style: {
            width: '100%'
          },
          options: [],
          placeholder: "--Select--",
          getPopupContainer: function getPopupContainer(trigger) {
            return trigger.parentNode;
          }
        });
    }
  }

  function renderFilterControls(argType) {
    return /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(FilterOutlined, {
      className: styles$1['filter-icon'] + " \n            " + styles$1['sensor-filter-color'] + " " + (type ? styles$1['sensor-filter-color-after'] : '')
    })), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(Select, {
      disabled: noEdit,
      className: styles$1['select-filter-dropdown'],
      onSelect: onSelectType,
      onChange: onChangeType,
      placeholder: t('Select type'),
      defaultValue: argType,
      value: argType,
      dropdownStyle: {
        minWidth: '100px'
      },
      options: [{
        label: 'ranch',
        value: 'ranch'
      }, {
        label: 'block',
        value: 'block'
      }, {
        label: 'entity',
        value: 'entity'
      }, {
        label: 'install state',
        value: 'install_state'
      }, {
        label: 'sensor type',
        value: 'type'
      }],
      onDropdownVisibleChange: mobileScreen ? function () {
        return setModalState(true);
      } : undefined,
      open: mobileScreen ? modal : undefined,
      dropdownClassName: styles['select-dropdown'],
      dropdownRender: mobileScreen ? function (menu) {
        return /*#__PURE__*/React.createElement(DropdownModal, {
          title: "Select Type",
          displayOptions: menu,
          mobileScreen: mobileScreen,
          onClick: function onClick() {
            return setModalState(false);
          }
        });
      } : undefined,
      getPopupContainer: mobileScreen ? null : function (trigger) {
        return trigger.parentNode;
      }
    })), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("div", {
      className: styles$1['item-select']
    }, getComponentForType(argType))), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("div", {
      className: styles$1['option-button']
    }, displayOp())), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("div", {
      className: styles$1['before-add-button']
    }, value || type ? !noDelete && /*#__PURE__*/React.createElement("div", {
      className: styles$1['close-button-style'] + " \n                        " + styles$1['sensor-add-button-style']
    }, /*#__PURE__*/React.createElement(CloseOutlined, {
      className: styles$1['sensor-button-color'] + " \n                          " + (type ? styles$1['sensor-button-color-after'] : ''),
      onClick: onClickIcon,
      "data-testid": (value || selectVal) + "_CloseIcon"
    })) : null)));
  }

  return /*#__PURE__*/React.createElement("div", null, showFilter && CONDITION_CHECK_1 && CONDITION_CHECK_2 ? /*#__PURE__*/React.createElement("div", {
    className: styles$1['add-text-button-style'],
    id: "sensor-settings-button-style"
  }, /*#__PURE__*/React.createElement(Button, {
    id: "add_filter",
    type: "primary",
    block: true,
    onClick: function onClick() {
      return onSetShowFilter(false);
    }
  }, t('Add Filter'))) : /*#__PURE__*/React.createElement("div", {
    className: styles$1['select-filter'] + " \n          " + (type ? styles$1['sensor-select-filter-background'] : styles$1['sensor-select-filter-background-after'])
  }, /*#__PURE__*/React.createElement("table", {
    className: styles$1['filter-options']
  }, /*#__PURE__*/React.createElement("tbody", null, type && renderFilterControls(type || selectType), !showFilter && CONDITION_CHECK_1 && renderFilterControls(selectType)))));
};

FilterListItem.propTypes = {
  onAdd: PropTypes.func,
  onChange: PropTypes.func,
  changeIndex: PropTypes.number,
  onRemove: PropTypes.func,
  type: PropTypes.string,
  typeFilter: PropTypes.arrayOf(PropTypes.string),
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  op: PropTypes.string,
  noEdit: PropTypes.bool,
  noDelete: PropTypes.bool,
  showFilter: PropTypes.bool,
  onSetShowFilter: PropTypes.func
};
FilterListItem.defaultProps = {
  onAdd: null,
  onChange: null,
  changeIndex: null,
  onRemove: null,
  type: null,
  typeFilter: null,
  value: null,
  op: null,
  noEdit: null,
  noDelete: null,
  showFilter: true,
  onSetShowFilter: null
};

var Option$5 = Select.Option;
var loadAllSensors = actions.loadAllSensors,
    loadFarmData$3 = actions.loadFarmData;
var selectAllSensors = selectors.selectAllSensors,
    selectLoadingSensors = selectors.selectLoadingSensors,
    selectSensor = selectors.selectSensor,
    selectSensorById = selectors.selectSensorById;

var sensorTagRender = function sensorTagRender(args) {
  var closable = args.closable,
      onClose = args.onClose,
      label = args.label;
  return /*#__PURE__*/React.createElement(Tag, {
    closable: closable,
    onClose: onClose,
    style: {
      marginRight: 3
    }
  }, label.props ? label.props.sensor.name : label);
};

var defaultItemRenderer$3 = function defaultItemRenderer(d) {
  var valueObject = JSON.stringify({
    type: d.type,
    identifier: d.identifier,
    id: d.id
  });

  if (!d.count && !d.filterItem) {
    return /*#__PURE__*/React.createElement(Option$5, {
      key: d.identifier + " " + d.name,
      value: valueObject,
      label: d.name ? d.name : 'No Name',
      "data-testid": "" + d.name
    }, /*#__PURE__*/React.createElement(SensorOption, {
      sensor: d
    }));
  }

  if (d.count && !d.filterItem) {
    return /*#__PURE__*/React.createElement(Option$5, {
      key: "" + d.name,
      label: d.name,
      "data-testid": "" + d.name,
      disabled: true
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        textAlign: 'center',
        color: 'rgba(0, 0, 0, 0.45)'
      }
    }, d.name));
  }

  if (d.filterItem) {
    return /*#__PURE__*/React.createElement(Option$5, {
      key: "" + d.key,
      label: d.name,
      "data-testid": "" + d.name,
      disabled: true
    }, d.item);
  }

  return null;
};

var SensorSelect = function SensorSelect(_ref) {
  var loadItems = _ref.loadItems,
      items = _ref.items,
      mode = _ref.mode,
      renderItem = _ref.renderItem,
      onSelect = _ref.onSelect,
      onChange = _ref.onChange,
      tagRender = _ref.tagRender,
      placeholder = _ref.placeholder,
      defaultOption = _ref.defaultOption,
      dropdownStyle = _ref.dropdownStyle,
      sensorTypeConstraint = _ref.sensorTypeConstraint,
      sensorType = _ref.sensorType,
      sensorIdentifier = _ref.sensorIdentifier,
      sensorId = _ref.sensorId,
      selectedFilters = _ref.selectedFilters,
      onChangeFilters = _ref.onChangeFilters,
      applyFilter = _ref.applyFilter;

  var _useTranslation = useTranslation(),
      t = _useTranslation.t;

  var dispatch = useDispatch();
  var data = useSelector(selectAllSensors);
  var sensors = items || data;

  var _useState = useState([]),
      filterItemList = _useState[0],
      setFilterItemList = _useState[1];

  var _useState2 = useState(''),
      searchText = _useState2[0],
      setSearchText = _useState2[1];

  var _useState3 = useState(false),
      modal = _useState3[0],
      setModalState = _useState3[1];

  var mobileScreen = window.innerWidth < 599;
  var leftValue = useRef(null);
  var minWidthValue = useRef(null);

  var _useState4 = useState(true),
      showFilter = _useState4[0],
      setShowFilter = _useState4[1];

  var sensorsByPropValues = useSelector(function (state) {
    return selectSensor(state, sensorType, sensorIdentifier);
  });
  var sensorsByPropId = useSelector(function (state) {
    return selectSensorById(state, sensorType, sensorId);
  });
  var sensorLoading = useSelector(function (state) {
    return selectLoadingSensors(state);
  });
  useEffect(function () {
    if (sensorType && !filterItemList.length) {
      var obj = {
        type: 'type',
        value: sensorType,
        op: 'AND'
      };
      setFilterItemList([obj]);
    }
  }, [sensorType, filterItemList.length]);
  useEffect(function () {
    if (selectedFilters && selectedFilters.length) {
      setFilterItemList(selectedFilters);
    }
  }, [selectedFilters]);
  useEffect(function () {
    if (sensorTypeConstraint && sensorTypeConstraint.length > 0 && !filterItemList.length) {
      var objList = sensorTypeConstraint.map(function (d) {
        return {
          type: 'type',
          value: d,
          op: 'OR',
          noEdit: true,
          noDelete: true
        };
      });
      setFilterItemList([].concat(objList));
    }
  }, [sensorTypeConstraint, filterItemList.length]);
  var sensorValue;

  if (sensorsByPropValues) {
    sensorValue = {
      type: sensorsByPropValues.type,
      identifier: sensorsByPropValues.identifier,
      id: sensorsByPropValues.id
    };
  } else if (sensorsByPropId) {
    sensorValue = {
      type: sensorsByPropId.type,
      identifier: sensorsByPropId.identifier,
      id: sensorsByPropId.id
    };
  } else if (sensorId && !sensorValue && sensorType && sensorIdentifier) {
    sensorValue = {
      type: sensorType,
      identifier: sensorIdentifier,
      id: sensorId
    };
  }

  useEffect(function () {
    var divElement = document.querySelector('#sensorSelect');

    if (divElement !== null && !leftValue.current) {
      setTimeout(function () {
        var declare = divElement.parentNode.parentNode.style;

        if (!declare.left.includes('-') && declare.left !== null) {
          leftValue.current = declare.left;
          minWidthValue.current = declare.minWidth;
        }
      }, 1000);
    }
  });

  function onChangeFilter(dat) {
    if (onChangeFilters) onChangeFilters(dat);
  }

  function renderFilterListItem() {
    var selectedFilterLists = filterItemList.map(function (item, index) {
      return /*#__PURE__*/React.createElement(FilterListItem, {
        key: String(index),
        type: item.type,
        value: item.value,
        onRemove: function onRemove(obj) {
          filterItemList.splice(obj.index, 1);
          setFilterItemList([].concat(filterItemList));
          onChangeFilter([].concat(filterItemList));
        },
        onChange: function onChange(obj) {
          filterItemList[obj.index] = {
            type: obj.type,
            value: obj.value,
            op: obj.op
          };
          setFilterItemList([].concat(filterItemList));
          onChangeFilter([].concat(filterItemList));
        },
        changeIndex: index,
        op: index !== 0 && item.op ? item.op : '',
        typeFilter: sensorTypeConstraint,
        noEdit: item.noEdit,
        noDelete: item.noDelete
      });
    });
    var defaultFilter = /*#__PURE__*/React.createElement(FilterListItem, {
      onAdd: function onAdd(obj) {
        setFilterItemList([].concat(filterItemList, [obj]));
        onChangeFilter([].concat(filterItemList, [obj]));
      },
      onChange: function onChange(obj) {
        filterItemList[obj.index] = {
          type: obj.type,
          value: obj.value,
          op: obj.op
        };
        setFilterItemList([].concat(filterItemList));
        onChangeFilter([].concat(filterItemList));
      },
      op: filterItemList.length > 0 ? OP.AND : '',
      typeFilter: sensorTypeConstraint,
      showFilter: showFilter,
      onSetShowFilter: function onSetShowFilter(d) {
        return setShowFilter(d);
      }
    });
    return [].concat(selectedFilterLists, [defaultFilter]);
  }

  var filteredSensorData = sensors.filter(function (sensor) {
    if (sensorTypeConstraint && sensorTypeConstraint.length > 0) {
      return sensorTypeConstraint.indexOf(sensor.type) >= 0;
    }

    return true;
  }).filter(function (sensor) {
    return filterItemList.filter(function (d) {
      return d.value;
    }).reduce(function (prev, curr) {
      if (curr.op === OP.OR) {
        return prev || sensor[curr.type] === curr.value;
      }

      return prev && sensor[curr.type] === curr.value;
    }, true);
  });
  var countOfFilteredSensorData = [{
    name: filteredSensorData.length + " results",
    count: true
  }];
  var filterItems = renderFilterListItem();
  var filterOptionsList = filterItems.map(function (d, index) {
    return {
      filterItem: true,
      item: d,
      key: "Item_" + index
    };
  });
  var sensorData = [].concat(filterOptionsList, countOfFilteredSensorData, filteredSensorData);

  function customFilterMobile(searchStr) {
    var filterResult = sensorData.filter(function (d) {
      return !d.count && !d.filterItem && (d && d.name && d.name.toLowerCase().indexOf(searchStr.toLowerCase()) >= 0 || d && d.type.toLowerCase().indexOf(searchStr.toLowerCase()) >= 0 || d && d.identifier && d.identifier.toLowerCase().indexOf(searchStr.toLowerCase()) >= 0 || d && d.install_state && d.install_state.toLowerCase().indexOf(searchStr.toLowerCase()) >= 0);
    });
    var countOfFilterResult = [{
      name: filterResult.length + " results",
      count: true
    }];
    return [].concat(filterOptionsList, countOfFilterResult, filterResult);
  }

  var listHeight = calculatedHeightForMobile();

  var dropdownRender = function dropdownRender(menu) {
    return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
      id: "sensorSelect",
      className: styles$1['flex-container'] + " " + styles$1['sensor-select-scroll'],
      style: {
        height: mobileScreen ? listHeight + "px" : 'auto'
      }
    }, mobileScreen ? /*#__PURE__*/React.createElement("div", {
      className: styles$1['modal-select-header']
    }, /*#__PURE__*/React.createElement("div", {
      className: styles$1['modal-select-header-text']
    }, "Sensor Select"), /*#__PURE__*/React.createElement("div", null)) : null, mobileScreen ? /*#__PURE__*/React.createElement(Searchbox, {
      onChange: function onChange(d) {
        return setSearchText(d.target.value);
      }
    }) : null, /*#__PURE__*/React.createElement("div", {
      className: styles$1['sensor-option-scrolldiv-outer']
    }, /*#__PURE__*/React.createElement("div", {
      className: styles$1['sensor-option-scrolldiv-inner']
    }, menu))), mobileScreen ? /*#__PURE__*/React.createElement("div", {
      className: styles$1['button-centered']
    }, /*#__PURE__*/React.createElement(Button, {
      id: "goBack",
      type: "primary",
      block: true,
      onClick: function onClick() {
        return setModalState(false);
      }
    }, t('done'))) : null);
  };

  useEffect(function () {
    if (loadItems) loadItems();else dispatch(loadAllSensors());
  }, [loadItems, dispatch]);
  useEffect(function () {
    dispatch(loadFarmData$3(applyFilter));
  }, [applyFilter, dispatch]);

  function onSelectAction(d) {
    if (onSelect) onSelect(d);
    if (!mobileScreen) setModalState(false);
  }

  var onChangeAction = function onChangeAction(d) {
    var multipleMode = mode === 'multiple';

    if (onChange && d && !multipleMode) {
      onChange(JSON.parse(d));
    } else if (onChange && d && multipleMode) {
      onChange(d.map(function (e) {
        return JSON.parse(e);
      }));
    } else if (onChange && !d) {
      onChange({
        id: undefined,
        type: undefined,
        identifier: undefined
      });
      onChangeFilter([]);
    }
  };

  var customFilter = function customFilter(input, option) {
    if (option.children.props.sensor && option.children.props.sensor.name && option.children.props.sensor.name.toLowerCase().indexOf(input.toLowerCase()) >= 0 || option.children.props.sensor && option.children.props.sensor.type.toLowerCase().indexOf(input.toLowerCase()) >= 0 || option.children.props.sensor && option.children.props.sensor.identifier && option.children.props.sensor.identifier.toLowerCase().indexOf(input.toLowerCase()) >= 0 || option.children.props.sensor && option.children.props.sensor.install_state && option.children.props.sensor.install_state.toLowerCase().indexOf(input.toLowerCase()) >= 0) {
      return true;
    }

    return false;
  };

  var minWidthForDropdown = window.innerWidth > 500 && window.innerWidth < 950 ? {
    minWidth: (window.innerWidth - 360 > 450 ? window.innerWidth - 360 : 450) + "px"
  } : {
    minWidth: '600px'
  };

  window.onresize = function onresize() {
    var _window = window,
        innerWidth = _window.innerWidth;
    mobileScreen = innerWidth < 599;

    if (innerWidth > 500 && innerWidth < 950) {
      minWidthForDropdown = {
        minWidth: innerWidth - 360 + "px"
      };
    }
  };

  var fixedStyle = window.innerWidth <= 500 && window.innerHeight > 600 ? {
    minWidth: window.innerWidth - 50 + "px"
  } : undefined;
  return /*#__PURE__*/React.createElement(SensorSelectContext.Provider, {
    value: {
      leftValue: leftValue,
      minWidthValue: minWidthValue
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: styles$1['sensor-select-outer'],
    id: "sensorScrollStyle"
  }, /*#__PURE__*/React.createElement(ItemSelect, {
    showSearch: !mobileScreen,
    mode: mode,
    renderItem: renderItem,
    items: !mobileScreen ? sensorData : customFilterMobile(searchText),
    onSelect: onSelectAction,
    onChange: onChangeAction,
    onBlur: function onBlur() {
      return !mobileScreen ? setModalState(false) : null;
    },
    tagRender: tagRender || sensorTagRender,
    placeholder: modal ? t('Type to search') : placeholder,
    filterOption: function filterOption(input, option) {
      return customFilter(input, option);
    },
    dropdownRender: dropdownRender,
    defaultOption: defaultOption,
    value: sensorValue ? [JSON.stringify(sensorValue)] : null,
    dropdownStyle: mobileScreen ? fixedStyle : dropdownStyle || minWidthForDropdown,
    menuIsOpen: mobileScreen ? modal : undefined,
    loading: sensorLoading.loading,
    listItemHeight: 54,
    onDropdownVisibleChange: mobileScreen ? function () {
      return setModalState(true);
    } : undefined
  })));
};

SensorSelect.propTypes = {
  mode: PropTypes.string,
  loadItems: PropTypes.func,
  items: PropTypes.arrayOf(PropTypes.object),
  renderItem: PropTypes.func,
  onSelect: PropTypes.func,
  onChange: PropTypes.func,
  tagRender: PropTypes.func,
  placeholder: PropTypes.string,
  defaultOption: PropTypes.arrayOf(PropTypes.any),
  dropdownStyle: PropTypes.shape({}),
  sensorTypeConstraint: PropTypes.arrayOf(PropTypes.string),
  sensorId: PropTypes.number,
  sensorIdentifier: PropTypes.string,
  sensorType: PropTypes.string,
  selectedFilters: PropTypes.arrayOf(PropTypes.object),
  onChangeFilters: PropTypes.func,
  applyFilter: PropTypes.bool
};
SensorSelect.defaultProps = {
  mode: null,
  loadItems: null,
  items: null,
  renderItem: defaultItemRenderer$3,
  onSelect: null,
  onChange: null,
  tagRender: null,
  placeholder: null,
  defaultOption: [],
  dropdownStyle: null,
  sensorTypeConstraint: null,
  sensorId: null,
  sensorIdentifier: null,
  sensorType: null,
  selectedFilters: null,
  onChangeFilters: null,
  applyFilter: false
};

var getColorForValue = function getColorForValue(value) {
  if (value) return 'green';
  return 'red';
};

function BooleanTag(_ref) {
  var value = _ref.value,
      labels = _ref.labels;
  if (value === undefined || value === null) return null;
  var labelTrue = labels[0],
      labelFalse = labels[1];
  return /*#__PURE__*/React.createElement(Tag, {
    color: getColorForValue(value)
  }, value ? labelTrue : labelFalse);
}

BooleanTag.propTypes = {
  value: PropTypes.bool,
  labels: PropTypes.arrayOf(PropTypes.string)
};
BooleanTag.defaultProps = {
  value: undefined,
  labels: ['yes', 'no']
};

var getColorForValue$1 = function getColorForValue(value) {
  if (value === 'ok') return 'green';
  if (value === 'warning') return 'yellow';
  if (value === 'error') return 'red';
  return undefined;
};

var values = ['ok', 'warning', 'error'];

function renderLabelForValue(value, labels) {
  var labelOk = labels[0],
      labelWarning = labels[1],
      labelError = labels[2];
  if (value === 'ok') return labelOk;
  if (value === 'warning') return labelWarning;
  if (value === 'error') return labelError;
  return 'unknown';
}

function StoplightTag(_ref) {
  var value = _ref.value,
      labels = _ref.labels;
  if (value === undefined || value === null) return null;
  return /*#__PURE__*/React.createElement(Tag, {
    color: getColorForValue$1(value)
  }, renderLabelForValue(value, labels));
}

StoplightTag.propTypes = {
  value: PropTypes.oneOf([].concat(values, [undefined])),
  labels: PropTypes.arrayOf(PropTypes.string)
};
StoplightTag.defaultProps = {
  value: undefined,
  labels: values
};

var styles$2 = {"custom-table":"_3Nr0O","configuration-message":"_3Eac4","schedule-item-header":"_152Zn","sensor-header":"_12Bwi","state-header":"_QHydv","alarms-header":"_wEjrM","sub-header":"_1vGGq","list-container":"_XzNZZ","space-between":"_2Vlcc","space-after":"_1feDX","anticon":"_AE10G","warning-icon":"_1Ko9w","indicator":"_3yEkF","schedule-header":"_2qdKu","blue":"_3P7nF","red":"_2VVYh","light-grey":"_2-bb4","red-border-text":"_3dTLR","inner-table":"_1b9s8","tag-icon":"_2mKxY","tag-icon-irrigation-state":"_1yD4t","tag-icon-override":"_3W41J","tag-icon-irrigating":"_1fhRM","icon-container":"_2ZxxO","red-message":"_1J-e8","schedule-content":"_2SLuV","normal-message":"_iVNcf","icon":"_2ntFS","switch":"_353UI","switch-left":"_3R0hE","switch-right":"_3W4g0","bell-icon-button":"_nyLIR","bell-icon-size":"_3HFTI","mobile-sensor-alarm-button":"_1DZMj","bell-icon-enabled":"_1PbxZ","bell-icon-disabled":"_2qcyO","status":"_1-1Rp","no-data-message":"_37UBG","hidden-column":"_1gXz3","override-header":"_3thua","anticon-warning":"_cyrUB","connect":"_1Th_M","last-data":"_nVeYr"};

var ConfirmValveOverrideDialog = function ConfirmValveOverrideDialog(_ref) {
  var title = _ref.title,
      displayText = _ref.displayText,
      buttonText = _ref.buttonText,
      onClick = _ref.onClick;

  var _useTranslation = useTranslation(),
      t = _useTranslation.t;

  var _useState = useState(true),
      modal = _useState[0],
      setModalState = _useState[1];

  function callOnClick(d) {
    if (d) setModalState(false);
    onClick(d.target.id);
  }

  return /*#__PURE__*/React.createElement("div", null, modal ? /*#__PURE__*/React.createElement(Modal, {
    title: t(title),
    centered: true,
    visible: modal,
    onCancel: callOnClick,
    footer: [/*#__PURE__*/React.createElement(Button, {
      key: "cancel",
      onClick: callOnClick
    }, t('Cancel')), /*#__PURE__*/React.createElement(Button, {
      id: buttonText[0],
      key: buttonText[0],
      value: buttonText[0],
      onClick: callOnClick,
      type: "primary",
      style: {
        background: 'red',
        borderColor: 'red'
      }
    }, /*#__PURE__*/React.createElement("span", {
      id: buttonText[0]
    }, t(buttonText[0]))), /*#__PURE__*/React.createElement(Button, {
      id: buttonText[1],
      key: buttonText[1],
      value: buttonText[1],
      onClick: callOnClick,
      type: "primary"
    }, /*#__PURE__*/React.createElement("span", {
      id: buttonText[1]
    }, t(buttonText[1])))],
    closable: true,
    maskClosable: false,
    destroyOnClose: true
  }, displayText.map(function (d, i) {
    return /*#__PURE__*/React.createElement("p", {
      key: String(i)
    }, t(d));
  })) : null);
};

ConfirmValveOverrideDialog.propTypes = {
  title: PropTypes.string,
  displayText: PropTypes.arrayOf(PropTypes.string),
  buttonText: PropTypes.arrayOf(PropTypes.string),
  onClick: PropTypes.func
};
ConfirmValveOverrideDialog.defaultProps = {
  title: null,
  displayText: null,
  buttonText: null,
  onClick: function onClick() {
    return null;
  }
};

var RefreshContext = React.createContext(3);

var VALVE_TYPE = 'valve';
var VFD_TYPE = 'vfd';
var loadSensorStatus = actions.loadSensorStatus;
var createOverride = controlApi.createOverride,
    endOverride = controlApi.endOverride;
function OverrideStatus(_ref) {
  var type = _ref.type,
      overrideStatus = _ref.overrideStatus,
      valveIdentifier = _ref.valveIdentifier,
      vfdIdentifier = _ref.vfdIdentifier,
      confirmDialogMessages = _ref.confirmDialogMessages,
      currentState = _ref.currentState;
  var dispatch = useDispatch();

  var _useState = useState(false),
      toggleChanged = _useState[0],
      setToggleChanged = _useState[1];

  var HTTP_SUCCESS_RES_CODE = 200;
  var OVERRIDE_STATE_CHECK_INTERVAL = 5000;
  var OVERRIDE_STATE_CHECK_TIMEOUT = 15000;
  var refreshStatusInterval = useRef(null);
  var valveOverrideSyncState = useRef('pending');
  var refreshId = useContext(RefreshContext);

  var _useTranslation = useTranslation(),
      t = _useTranslation.t;

  var OVERRIDE_CONFIRM_DIALOG_TITLE = type === VALVE_TYPE ? 'Confirm Valve Override' : 'Confirm Pump Override';
  var buttonText = type === VALVE_TYPE ? ['CLOSE', 'OPEN'] : ['OFF', 'ON'];
  useEffect(function () {
    if (refreshId && valveIdentifier) {
      dispatch(loadSensorStatus({
        type: VALVE_TYPE,
        identifier: valveIdentifier
      }));
    }

    if (refreshId && vfdIdentifier) {
      dispatch(loadSensorStatus({
        type: VFD_TYPE,
        identifier: vfdIdentifier
      }));
    }
  }, [dispatch, valveIdentifier, vfdIdentifier, refreshId]);

  function refreshSensorStatus(param) {
    dispatch(loadSensorStatus(param));
  }

  function clearRefreshInterval() {
    if (refreshStatusInterval.current) {
      clearInterval(refreshStatusInterval.current);
      refreshStatusInterval.current = null;
    }
  }

  function refreshUntilSynced() {
    refreshStatusInterval.current = setInterval(function () {
      if (valveOverrideSyncState.current !== 'pending') {
        clearRefreshInterval();
      } else {
        if (type === VALVE_TYPE) {
          refreshSensorStatus({
            type: type,
            identifier: valveIdentifier
          });
        }

        if (type === VFD_TYPE) {
          refreshSensorStatus({
            type: type,
            identifier: vfdIdentifier
          });
        }
      }
    }, OVERRIDE_STATE_CHECK_INTERVAL);
    setTimeout(clearRefreshInterval, OVERRIDE_STATE_CHECK_TIMEOUT);
  }

  var onConfirmValveOverride = function onConfirmValveOverride(overOp) {
    if (overOp === 'OPEN' || overOp === 'CLOSE' || overOp === 'OFF' || overOp === 'ON') {
      var newState;
      var paramObj;

      if (type === VALVE_TYPE) {
        newState = overOp === 'OPEN' ? 'open' : 'closed';
        paramObj = {
          sensorType: VALVE_TYPE,
          sensorIdentifier: valveIdentifier,
          state: newState
        };
      } else if (type === VFD_TYPE) {
        newState = overOp === 'ON' ? 'on' : 'off';
        paramObj = {
          sensorType: VFD_TYPE,
          sensorIdentifier: vfdIdentifier,
          state: newState
        };
      }

      if (paramObj) {
        var response = createOverride({
          sensorType: type === VALVE_TYPE ? VALVE_TYPE : VFD_TYPE,
          sensorIdentifier: type === VALVE_TYPE ? valveIdentifier : vfdIdentifier,
          state: newState
        });
        response.then(function (res) {
          if (res.status === HTTP_SUCCESS_RES_CODE) {
            if (type === VALVE_TYPE) {
              refreshSensorStatus({
                type: VALVE_TYPE,
                identifier: valveIdentifier
              });
            } else {
              refreshSensorStatus({
                type: VFD_TYPE,
                identifier: vfdIdentifier
              });
            }

            message.success(t('Created override'));
            refreshUntilSynced();
            setToggleChanged(false);
          } else {
            message.error(t('Failed to create override'));
          }
        });
      }
    } else {
      setToggleChanged(false);
    }
  };

  var onChangeToggle = function onChangeToggle(checked) {
    if (!checked) {
      var paramObj;

      if (type === VALVE_TYPE) {
        paramObj = {
          sensorType: VALVE_TYPE,
          sensorIdentifier: valveIdentifier
        };
      } else if (type === VFD_TYPE) {
        paramObj = {
          sensorType: VFD_TYPE,
          sensorIdentifier: vfdIdentifier
        };
      }

      if (paramObj) {
        var response = endOverride(paramObj);
        response.then(function (res) {
          if (res.status === HTTP_SUCCESS_RES_CODE) {
            if (type === VALVE_TYPE) {
              refreshSensorStatus({
                type: VALVE_TYPE,
                identifier: valveIdentifier
              });
              message.success(t('Ended valve override'));
            } else {
              refreshSensorStatus({
                type: VFD_TYPE,
                identifier: vfdIdentifier
              });
              message.success(t('Ended pump override'));
            }

            refreshUntilSynced();
          } else if (type === VALVE_TYPE) {
            message.error(t('Failed to end valve override'));
          } else {
            message.error(t('Failed to end pump override'));
          }
        });
      }
    } else {
      setToggleChanged(true);
    }
  };

  var overrideActive = !!overrideStatus;

  if (overrideActive) {
    valveOverrideSyncState.current = overrideStatus.syncStatus;
  }

  function getOverrideStateColor(overrideStatusObj) {
    if (overrideStatusObj && overrideStatusObj.state) {
      if (overrideStatusObj.state === 'open' || overrideStatusObj.state === 'on') {
        return 'green';
      }

      return 'red';
    }

    return null;
  }

  function getValveOverrideStateTitle(overrideStatusObj) {
    if (overrideStatusObj && overrideStatusObj.state) {
      return overrideStatusObj.state.toUpperCase();
    }

    return 'unknown';
  }

  function getSyncStatusIcon(overrideStatusObj) {
    var icons;
    if (!overrideStatus || !overrideStatus.syncStatus) icons = null;

    if (overrideStatusObj) {
      if (overrideStatusObj.syncStatus === 'success') icons = /*#__PURE__*/React.createElement(CheckOutlined, null);
      if (overrideStatusObj.syncStatus === 'pending') icons = /*#__PURE__*/React.createElement(LoadingOutlined, null);
      if (overrideStatusObj.syncStatus === 'error') icons = /*#__PURE__*/React.createElement(WarningFilled, null);
      if (overrideStatusObj.syncStatus === 'aborted') icons = /*#__PURE__*/React.createElement(WarningFilled, null);
    }

    return icons;
  }

  function getControlColumn() {
    if (currentState) {
      return /*#__PURE__*/React.createElement(Switch, {
        style: {
          background: overrideActive ? getOverrideStateColor(overrideStatus) : null,
          width: 'auto',
          minWidth: '57px'
        },
        checkedChildren: t(getValveOverrideStateTitle(overrideStatus)),
        unCheckedChildren: t('NONE'),
        "data-testid": valveIdentifier || vfdIdentifier,
        checked: overrideActive,
        onChange: onChangeToggle
      });
    }

    if (currentState === 'unknown' || !currentState) {
      return /*#__PURE__*/React.createElement(WarningFilled, {
        className: styles$2['tag-icon-override']
      });
    }

    return '';
  }

  return /*#__PURE__*/React.createElement("div", {
    className: styles$2["switch"]
  }, /*#__PURE__*/React.createElement("div", {
    className: styles$2['switch-left']
  }, getControlColumn()), /*#__PURE__*/React.createElement("div", {
    className: styles$2['switch-right']
  }, getSyncStatusIcon(overrideStatus)), /*#__PURE__*/React.createElement("div", null), toggleChanged ? /*#__PURE__*/React.createElement(ConfirmValveOverrideDialog, {
    title: OVERRIDE_CONFIRM_DIALOG_TITLE,
    displayText: confirmDialogMessages,
    buttonText: buttonText,
    onClick: onConfirmValveOverride
  }) : '');
}
OverrideStatus.propTypes = {
  type: PropTypes.string,
  overrideStatus: PropTypes.shape({
    state: PropTypes.string,
    syncStatus: PropTypes.string
  }),
  valveIdentifier: PropTypes.string,
  vfdIdentifier: PropTypes.string,
  confirmDialogMessages: PropTypes.arrayOf(PropTypes.string),
  currentState: PropTypes.string
};
OverrideStatus.defaultProps = {
  type: null,
  overrideStatus: null,
  valveIdentifier: null,
  vfdIdentifier: null,
  confirmDialogMessages: [],
  currentState: null
};

function getAlarmListUrl(_ref) {
  var type = _ref.type,
      identifier = _ref.identifier;
  var params = {
    sensorType: type,
    sensorIdentifier: identifier,
    logLevel: 'error',
    active: true
  };
  var paramStr = new URLSearchParams(params).toString();
  return "/events?" + paramStr;
}

function SensorAlarmButton(props) {
  var alarmCount = props.alarmCount,
      identifier = props.identifier,
      isMobileView = props.isMobileView,
      type = props.type;
  return /*#__PURE__*/React.createElement(Link, {
    to: getAlarmListUrl({
      type: type,
      identifier: identifier
    })
  }, /*#__PURE__*/React.createElement(Badge, {
    className: isMobileView ? styles$2['mobile-sensor-alarm-button'] : '',
    count: alarmCount
  }, /*#__PURE__*/React.createElement(Button, {
    className: styles$2['bell-icon-button'],
    icon: /*#__PURE__*/React.createElement(FaBell, {
      className: styles$2['bell-icon-size'] + ",\n              " + styles$2['bell-icon'] + ",\n              " + (alarmCount === 0 ? styles$2['bell-icon-disabled'] : styles$2['bell-icon-enabled'])
    })
  })));
}
SensorAlarmButton.propTypes = {
  type: PropTypes.string.isRequired,
  identifier: PropTypes.string.isRequired,
  alarmCount: PropTypes.number,
  isMobileView: PropTypes.bool
};
SensorAlarmButton.defaultProps = {
  alarmCount: null,
  isMobileView: false
};

function PumpStateTag(_ref) {
  var state = _ref.state,
      online = _ref.online;
  var color = state === 'on' ? '#1890FF' : '#D40202';

  if (state) {
    if (state !== 'unknown') {
      if (online && state === 'off') {
        return /*#__PURE__*/React.createElement(Tag, {
          color: "",
          key: state,
          className: styles$2['tag-icon']
        }, state.toUpperCase());
      }

      return /*#__PURE__*/React.createElement(Tag, {
        color: color,
        key: state,
        className: [styles$2['tag-icon'], styles$2['tag-icon-irrigating']]
      }, state.toUpperCase());
    }

    return /*#__PURE__*/React.createElement(WarningFilled, {
      className: styles$2['warning-icon']
    });
  }

  if (!state) {
    return /*#__PURE__*/React.createElement(WarningFilled, {
      className: styles$2['warning-icon']
    });
  }

  return '';
}
PumpStateTag.propTypes = {
  state: PropTypes.string,
  online: PropTypes.bool
};
PumpStateTag.defaultProps = {
  state: null,
  online: null
};

var selectPumpFlowMeter = selectors.selectPumpFlowMeter;
var loadSensorStatus$1 = actions.loadSensorStatus;
function PumpControlSensorList(_ref) {
  var pumpType = _ref.pumpType,
      pumpIdentifier = _ref.pumpIdentifier;

  var _useTranslation = useTranslation(),
      t = _useTranslation.t;

  var dispatch = useDispatch();
  var refreshId = useContext(RefreshContext);
  var flowMeter = useSelector(function (state) {
    return selectPumpFlowMeter(state, pumpType, pumpIdentifier);
  });

  var _ref2 = flowMeter || {},
      flowMeterIdentifier = _ref2.identifier,
      flowMeterType = _ref2.type;

  useEffect(function () {
    if (refreshId && flowMeterIdentifier && flowMeterType) {
      var sensorParams = {
        type: flowMeterType,
        identifier: flowMeterIdentifier
      };
      dispatch(loadSensorStatus$1(sensorParams));
    }
  }, [dispatch, refreshId, flowMeterType, flowMeterIdentifier]);

  function renderSensorName(sensor) {
    if (!sensor.name) {
      return /*#__PURE__*/React.createElement("span", null, t('No Name'));
    }

    return /*#__PURE__*/React.createElement(Tooltip, {
      title: sensor.name
    }, /*#__PURE__*/React.createElement("span", null, sensor.name));
  }

  function renderFlow(sensor) {
    var status = sensor.status;

    if (!status || status.flowRate === null || status.flowRate === undefined) {
      return null;
    }

    return /*#__PURE__*/React.createElement("div", null, status.flowRate.toFixed(1) + " GPM");
  }

  function renderSensorConnectivity(sensor) {
    if (!sensor.status) return null;
    return /*#__PURE__*/React.createElement(BooleanTag, {
      value: sensor.status.online,
      labels: [t('online'), t('offline')]
    });
  }

  function renderSensorAlarms(sensor) {
    return /*#__PURE__*/React.createElement(SensorAlarmButton, {
      type: sensor.type,
      identifier: sensor.identifier,
      alarmCount: sensor.status && sensor.status.alarmCount
    });
  }

  function renderLatestDate(sensor) {
    if (!sensor.status || !sensor.status.latestDate) return null;
    var date = sensor.status.latestDate;
    return /*#__PURE__*/React.createElement(Tooltip, {
      title: /*#__PURE__*/React.createElement(Moment, {
        format: "YYYY-MM-DD h:mm:ss A",
        local: true
      }, date),
      placement: "bottom"
    }, /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement(Moment, {
      fromNow: true
    }, date)));
  }

  function widthCalc(columns) {
    var maxWidth = 100;
    var result = columns().map(function (column) {
      if (column.key !== 'alarms') {
        return _extends({}, column, {
          width: (maxWidth - 10) / (columns().length - 1) + "%"
        });
      }

      return column;
    });
    return result;
  }

  var flowMeterColumns = function flowMeterColumns() {
    return [{
      title: t('Name'),
      key: 'name',
      className: styles$2['schedule-item-header'],
      render: function render(sensor) {
        return renderSensorName(sensor);
      }
    }, {
      title: t('Connectivity'),
      key: 'connectivity',
      width: 100,
      render: function render(sensor) {
        return renderSensorConnectivity(sensor);
      }
    }, {
      title: t('Flow'),
      key: 'flow',
      width: 100,
      render: function render(sensor) {
        return renderFlow(sensor);
      }
    }, {
      title: t('Last Data'),
      key: 'lastData',
      className: styles$2['last-data'],
      render: function render(sensor) {
        return renderLatestDate(sensor);
      }
    }, {
      title: t('Alarms'),
      key: 'alarms',
      className: styles$2['alarms-header'],
      width: 100,
      render: function render(sensor) {
        return renderSensorAlarms(sensor);
      }
    }];
  };

  return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("span", {
    className: styles$2['sub-header']
  }, t('Flow Meter')), flowMeter && /*#__PURE__*/React.createElement(Table, {
    dataSource: [flowMeter],
    columns: widthCalc(flowMeterColumns),
    rowKey: function rowKey(record) {
      return record.identifier;
    },
    size: "small",
    className: styles$2['inner-table'],
    pagination: false
  }), !flowMeter && /*#__PURE__*/React.createElement("div", {
    className: styles$2['no-data-message']
  }, t('No Flow Meter installed')));
}
PumpControlSensorList.propTypes = {
  pumpType: PropTypes.string.isRequired,
  pumpIdentifier: PropTypes.string.isRequired
};

function getTimeDiff(startDate, endDate) {
  var min = endDate.diff(startDate, 'minutes');
  var sec = endDate.diff(startDate, 'seconds');

  if (min < 0 || sec < 0) {
    return null;
  }

  if (min > 59) {
    var hr = Math.floor(min / 60);
    var m = min % 60;
    return hr + " hrs and " + m + " mins";
  }

  if (sec > 59) {
    return min + " mins";
  }

  return sec + " secs";
}

function getTimingAttr(openTime, closeTime) {
  var timeOpen;
  var timeClose;
  var opensFirst;

  if (openTime) {
    timeOpen = getTimeDiff(moment(), moment(openTime));
  }

  if (closeTime) {
    timeClose = getTimeDiff(moment(), moment(closeTime));
  }

  if (openTime && closeTime) {
    opensFirst = !!getTimeDiff(moment(openTime), moment(closeTime));
  }

  return [timeOpen, timeClose, opensFirst];
}

var getValveScheduleMessage = function getValveScheduleMessage(t, online, state, openTime, closeTime, overrideState) {
  var _getTimingAttr = getTimingAttr(openTime, closeTime),
      timeOpen = _getTimingAttr[0],
      timeClose = _getTimingAttr[1],
      opensFirst = _getTimingAttr[2];

  if (state) {
    if (timeOpen && opensFirst) {
      if (state !== 'closed' && overrideState !== state) {
        return t('Closing, scheduled to open in') + " " + timeOpen;
      }

      if (overrideState !== undefined) {
        return t('Scheduled to open in') + " " + timeOpen + ".";
      }

      return t('Opens in') + " " + timeOpen;
    }

    if (timeClose && !opensFirst) {
      if (state !== 'open' && overrideState !== state) {
        return t('Opening, scheduled to close in') + " " + timeClose;
      }

      if (overrideState !== undefined) {
        return t('Scheduled to close in') + " " + timeClose + ".";
      }

      return t('Closes in') + " " + timeClose;
    }

    if (state === 'unknown') {
      return t('Disconnected');
    }

    if (!online && state === 'closed') {
      return t('Disconnected (Closed)');
    }

    if (state !== 'closed' && !overrideState) {
      return t('Closing');
    }

    return t('Nothing scheduled');
  }

  return '';
};
var getPumpScheduleMessageBasic = function getPumpScheduleMessageBasic(t, startDate, stopDate) {
  var _getTimingAttr2 = getTimingAttr(startDate, stopDate),
      timeOn = _getTimingAttr2[0],
      timeOff = _getTimingAttr2[1],
      onFirst = _getTimingAttr2[2];

  if (timeOn && onFirst) {
    return t('On in') + " " + timeOn + ".";
  }

  if (timeOff && !onFirst) {
    return t('Off in') + " " + timeOff + ".";
  }

  return null;
};
var getPumpScheduleMessage = function getPumpScheduleMessage(t, online, state, startDate, stopDate, overrideState) {
  var _getTimingAttr3 = getTimingAttr(startDate, stopDate),
      timeOn = _getTimingAttr3[0],
      timeOff = _getTimingAttr3[1],
      onFirst = _getTimingAttr3[2];

  if (state) {
    if (timeOn && onFirst) {
      if (state !== 'off' && overrideState !== state) {
        return t('Turning off, scheduled to turn on in') + " " + timeOn;
      }

      if (overrideState !== undefined) {
        return t('Scheduled to turn on in') + " " + timeOn + ".";
      }

      return t('On in') + " " + timeOn + ".";
    }

    if (timeOff && !onFirst) {
      if (state !== 'on' && overrideState !== state) {
        return t('Turning on, scheduled to turn off in') + " " + timeOff;
      }

      if (overrideState !== undefined) {
        return t('Scheduled to turn off in') + " " + timeOff + ".";
      }

      return t('Off in') + " " + timeOff + ".";
    }

    if (state === 'unknown') {
      return t('Disconnected');
    }

    if (!online && state === 'off') {
      return t('Disconnected (off)');
    }

    if (state !== 'off' && !overrideState) {
      return t('Turning off');
    }

    return t('Nothing scheduled');
  }

  return '';
};

var Panel = Collapse.Panel;
var Text$1 = Typography.Text;
var selectPumpSensorStatus = selectors.selectPumpSensorStatus;
function PumpControlList(_ref) {
  var pumps = _ref.pumps,
      onClickCalendar = _ref.onClickCalendar,
      pumpLoading = _ref.pumpLoading;
  var pumpData = useSelector(function (state) {
    return selectPumpSensorStatus(state, pumps);
  });
  var spinner = pumpData.reduce(function (a, p) {
    return !p || a || p.pumpLoading;
  }, false);

  var _useTranslation = useTranslation(),
      t = _useTranslation.t;

  var getMessageClassName = function getMessageClassName(state, online) {
    var styleStr = styles$2['red-message'] + " ";
    var styleStrNot = styles$2['normal-message'] + " ";

    if (state === 'unknown' || !online && state === 'off') {
      return styleStr;
    }

    if (!state) return styleStr;
    return styleStrNot;
  };

  var getSchedule = function getSchedule(pump, text) {
    return /*#__PURE__*/React.createElement("div", {
      className: styles$2['icon-container'],
      onClick: function onClick() {
        return onClickCalendar({
          identifier: pump.pumpIdentifier
        });
      },
      role: "presentation"
    }, /*#__PURE__*/React.createElement("div", {
      className: styles$2.icon
    }, /*#__PURE__*/React.createElement(FaCalendarAlt, null)), /*#__PURE__*/React.createElement("div", {
      className: getMessageClassName(pump.pumpCurrentState, pump.pumpOnline)
    }, /*#__PURE__*/React.createElement("span", {
      className: styles$2['schedule-content']
    }, text)));
  };

  var getIndClass = function getIndClass(_ref2) {
    var online = _ref2.pumpOnline,
        status = _ref2.pumpCurrentState,
        loading = _ref2.pumpLoading;
    var styleStr = styles$2.indicator + " ";

    if (!loading) {
      if (!online) return styleStr + styles$2.red;
      if (online && status === 'off') return styleStr + styles$2['light-grey'];
      if (online && status === 'on') return styleStr + styles$2.blue;
    }

    return styleStr + styles$2['light-grey'];
  };

  var getCurrentState = function getCurrentState(pump) {
    return /*#__PURE__*/React.createElement(PumpStateTag, {
      state: pump.pumpCurrentState,
      online: pump.pumpOnline
    });
  };

  var getRowClassName = function getRowClassName(record) {
    if (record.pumpCurrentState) {
      if (record.pumpCurrentState === 'unknown' || !record.pumpOnline && (!record.pumpCurrentState || record.pumpCurrentState === 'off')) {
        return styles$2['red-border-text'];
      }
    }

    var className = getIndClass(record);
    return className;
  };

  function widthCalc(columns) {
    var maxwid = 100;
    var result = columns().map(function (element) {
      var values = element;

      if (element.title === 'Alarm') {
        values.width = 100;
      }

      if (element.title === 'Override') {
        values.width = 100;
      }

      if (element.title !== 'Override' && element.title !== 'Alarm') {
        values.width = (maxwid - 10) / (columns().length - 2) + "%";
      }

      return values;
    });
    return result;
  }

  var columns = function columns() {
    return [{
      title: t('Pump'),
      className: styles$2['schedule-item-header'],
      render: function render(pump) {
        if (pump.pumpName) {
          return /*#__PURE__*/React.createElement(Tooltip, {
            title: pump.pumpName
          }, /*#__PURE__*/React.createElement("span", null, pump.pumpName));
        }

        return /*#__PURE__*/React.createElement("span", {
          style: {
            color: 'lightgray'
          }
        }, t('No Name'));
      }
    }, {
      title: t('Schedule'),
      className: styles$2['schedule-header'],
      render: function render(pump) {
        return getSchedule(pump, getPumpScheduleMessage(t, pump.pumpOnline, pump.pumpCurrentState, pump.pumpStartDate, pump.pumpStopDate, pump.pumpOverrideStatus && pump.pumpOverrideStatus.state));
      }
    }, {
      title: t('Override'),
      className: styles$2['override-header'],
      render: function render(pump) {
        return /*#__PURE__*/React.createElement(OverrideStatus, {
          type: VFD_TYPE,
          overrideStatus: pump.pumpOverrideStatus,
          vfdIdentifier: pump.pumpIdentifier,
          currentState: pump.pumpCurrentState,
          confirmDialogMessages: [getPumpScheduleMessage(t, pump.pumpOnline, pump.pumpCurrentState, pump.pumpStartDate, pump.pumpStopDate)]
        });
      }
    }, {
      title: t('State'),
      className: styles$2['state-header'],
      render: function render(pump) {
        return /*#__PURE__*/React.createElement("div", {
          className: styles$2.status
        }, getCurrentState(pump));
      }
    }, {
      title: t('Alarms'),
      className: styles$2['alarms-header'],
      render: function render(pump) {
        return /*#__PURE__*/React.createElement(SensorAlarmButton, {
          type: "vfd",
          identifier: pump.pumpIdentifier,
          alarmCount: pump.alarmCount
        });
      }
    }];
  };

  return /*#__PURE__*/React.createElement("div", {
    className: styles$2['list-container']
  }, /*#__PURE__*/React.createElement(Collapse, {
    expandIconPosition: "right",
    defaultActiveKey: ['1']
  }, /*#__PURE__*/React.createElement(Panel, {
    header: /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Text$1, {
      style: {
        fontSize: '16px'
      }
    }, "Pumps"), pumpLoading || spinner ? /*#__PURE__*/React.createElement(LoadingOutlined, {
      style: {
        marginLeft: '10px',
        fontSize: '16px'
      }
    }) : ''),
    key: "1",
    className: styles$2['collapse-header']
  }, pumpData.length ? /*#__PURE__*/React.createElement(Table, {
    className: styles$2['custom-table'],
    rowClassName: getRowClassName,
    dataSource: pumpData,
    columns: widthCalc(columns),
    pagination: false,
    rowKey: function rowKey(record) {
      return String(record.pumpId);
    },
    expandable: {
      expandRowByClick: true,
      rowExpandable: function rowExpandable() {
        return true;
      },
      expandedRowRender: function expandedRowRender(pump) {
        return /*#__PURE__*/React.createElement(PumpControlSensorList, {
          pumpType: "vfd",
          pumpIdentifier: pump.pumpIdentifier
        });
      }
    }
  }) : /*#__PURE__*/React.createElement("section", {
    className: styles$2['configuration-message']
  }, "No Pumps Configured"))));
}
PumpControlList.propTypes = {
  pumps: PropTypes.arrayOf(PropTypes.object),
  onClickCalendar: PropTypes.func,
  pumpLoading: PropTypes.bool
};
PumpControlList.defaultProps = {
  pumps: [],
  onClickCalendar: null,
  pumpLoading: null
};

moment.updateLocale('en', {
  relativeTime: {
    future: 'in %s',
    past: '%s ago',
    s: 'a few secs',
    ss: '%d secs',
    m: 'a min',
    mm: '%d mins',
    h: 'an hr',
    hh: '%d hrs',
    d: 'a day',
    dd: '%d days',
    w: 'a week',
    ww: '%d weeks',
    M: 'a month',
    MM: '%d months',
    y: 'a year',
    yy: '%d years'
  }
});
var selectBlockPressureSensors = selectors.selectBlockPressureSensors,
    selectBlockPumpController = selectors.selectBlockPumpController,
    selectBlockFlowMeter = selectors.selectBlockFlowMeter,
    selectValveControllersForBlocks = selectors.selectValveControllersForBlocks;
var loadSensorStatus$2 = actions.loadSensorStatus;
var useBlockNames = hooks.useBlockNames;
function BlockControlSensorList(props) {
  var controlStatus = props.controlStatus,
      valves = props.valves,
      showBlockList = props.showBlockList;
  var blockId = controlStatus.blockId;

  var _useTranslation = useTranslation(),
      t = _useTranslation.t;

  var dispatch = useDispatch();
  var refreshId = useContext(RefreshContext);
  var pressureSensors = useSelector(function (state) {
    return selectBlockPressureSensors(state, blockId);
  });
  var pumpController = useSelector(function (state) {
    return selectBlockPumpController(state, blockId);
  });
  var flowMeter = useSelector(function (state) {
    return selectBlockFlowMeter(state, blockId);
  }) || [];
  var valvesIds = controlStatus.valves ? controlStatus.valves.valveIds || [] : [];
  var filteredValveObjArr = valves.filter(function (d) {
    return d && d.irrigation_blocks.includes(blockId) && valvesIds.includes(d.id);
  });
  var valveBlockIds = [];
  var vIdentifiers = [];
  filteredValveObjArr.forEach(function (d) {
    if (d) {
      vIdentifiers.push({
        type: d.type,
        identifier: d.identifier
      });
    }

    if (d && d.irrigation_blocks && d.irrigation_blocks.length && !vIdentifiers.includes({
      type: d.type,
      identifier: d.identifier
    })) {
      valveBlockIds.push.apply(valveBlockIds, d.irrigation_blocks);
    }
  });
  var uniqueValveBlockIds = valveBlockIds.filter(function (v, i, a) {
    return a.indexOf(v) === i;
  });
  var valveControllerObj = useSelector(function (state) {
    return selectValveControllersForBlocks(state, uniqueValveBlockIds.length ? uniqueValveBlockIds : [blockId]);
  });
  var valveControllersFromObj = Object.values(valveControllerObj).length ? Object.values(valveControllerObj) : [];
  var valveController = valveControllerObj[blockId];
  var valveControllers = [];
  valveControllersFromObj.forEach(function (items) {
    items.forEach(function (item) {
      if (item.irrigation_blocks && item.irrigation_blocks.includes(blockId)) {
        var i = valveControllers.findIndex(function (x) {
          return x.identifier === item.identifier;
        });

        if (i <= -1) {
          valveControllers.push(item);
        }
      }
    });
  });
  var blockIds = controlStatus && controlStatus.irrigation_blocks;
  var blockNames = useBlockNames(blockIds);
  var pressureSensorsObj = useSelector(function (state) {
    return getPressureDetailsForBlockIds(state, blockIds);
  });
  var blockArr = [];

  if (showBlockList) {
    blockIds.forEach(function (d, i) {
      var blockObj = {
        blockName: blockNames[d],
        blockId: d,
        type: controlStatus.irrigation_blocks[i] === d && controlStatus.type,
        identifier: controlStatus.irrigation_blocks[i] === d && controlStatus.identifier,
        status: {
          alarmCount: controlStatus.blockAlarmCount
        }
      };
      blockArr.push(blockObj);
    });
  }

  var pumpControllerIdentifier = pumpController.identifier,
      pumpControllerType = pumpController.type;

  var _ref = flowMeter || {},
      flowMeterIdentifier = _ref.identifier,
      flowMeterType = _ref.type;

  var valveControllerIdentifier = valveController.identifier,
      valveControllerType = valveController.type;
  var valveIdentifiers = '';

  if (!vIdentifiers.includes({
    identifier: valveControllerIdentifier,
    type: valveControllerType
  })) {
    valveIdentifiers = JSON.stringify([].concat(vIdentifiers, [{
      identifier: valveControllerIdentifier,
      type: valveControllerType
    }]));
  }

  var pressureSensorIdentifiers = JSON.stringify(pressureSensors.map(function (sensor) {
    return sensor.identifier;
  }));
  var refreshPressureSensorStatus = useCallback(function () {
    if (refreshId) {
      JSON.parse(pressureSensorIdentifiers).forEach(function (identifier) {
        var sensorParams = {
          type: 'water_pressure',
          identifier: identifier
        };
        dispatch(loadSensorStatus$2(sensorParams));
      });
    }
  }, [dispatch, refreshId, pressureSensorIdentifiers]);
  useEffect(function () {
    refreshPressureSensorStatus();
  }, [refreshPressureSensorStatus]);
  useEffect(function () {
    var sensorParams = {
      type: pumpControllerType,
      identifier: pumpControllerIdentifier
    };

    if (pumpControllerIdentifier) {
      dispatch(loadSensorStatus$2(sensorParams));
    }
  }, [dispatch, pumpControllerType, pumpControllerIdentifier]);
  useEffect(function () {
    if (refreshId && flowMeterIdentifier && flowMeterType) {
      var sensorParams = {
        type: flowMeterType,
        identifier: flowMeterIdentifier
      };
      dispatch(loadSensorStatus$2(sensorParams));
    }
  }, [dispatch, refreshId, flowMeterType, flowMeterIdentifier]);
  useEffect(function () {
    if (JSON.parse(valveIdentifiers).length) {
      JSON.parse(valveIdentifiers).forEach(function (sensorParams) {
        dispatch(loadSensorStatus$2(sensorParams));
      });
    }
  }, [dispatch, valveIdentifiers]);

  var getCurrentState = function getCurrentState(valveState, valveOnline) {
    var color = valveState === 'open' ? '#1890FF' : '#D40202';

    if (valveState) {
      if (valveState !== 'unknown') {
        if (valveOnline && valveState === 'closed') {
          return /*#__PURE__*/React.createElement(Tag, {
            color: "",
            key: valveState,
            className: styles$2['tag-icon']
          }, t('Closed').toUpperCase());
        }

        var state = valveState === 'open' ? t('Open_status').toUpperCase() : valveState;
        return /*#__PURE__*/React.createElement(Tag, {
          color: color,
          key: valveState,
          className: [styles$2['tag-icon'], styles$2['tag-icon-irrigating']]
        }, state.toUpperCase());
      }

      return /*#__PURE__*/React.createElement(WarningFilled, {
        className: styles$2['warning-icon']
      });
    }

    if (!valveState) {
      return /*#__PURE__*/React.createElement(WarningFilled, {
        className: styles$2['warning-icon']
      });
    }

    return '';
  };

  function renderSensorName(sensor) {
    if (!sensor.name) {
      return /*#__PURE__*/React.createElement("span", null, t('No Name'));
    }

    return /*#__PURE__*/React.createElement(Tooltip, {
      title: sensor.name
    }, /*#__PURE__*/React.createElement("span", null, sensor.name));
  }

  function renderPressure(sensor) {
    var status = sensor.status;
    if (!status || status.waterPressure === undefined || status.waterPressure === null) return null;
    return /*#__PURE__*/React.createElement("div", null, status.waterPressure.toFixed(1) + " psi");
  }

  function renderSensorConnectivity(sensor) {
    if (!sensor.status) return null;
    return /*#__PURE__*/React.createElement(BooleanTag, {
      value: sensor.status.online,
      labels: [t('online'), t('offline')]
    });
  }

  function renderLatestDate(sensor) {
    if (!sensor.status || !sensor.status.latestDate) return null;
    var date = sensor.status.latestDate;
    return /*#__PURE__*/React.createElement(Tooltip, {
      title: /*#__PURE__*/React.createElement(Moment, {
        format: "YYYY-MM-DD h:mm:ss A",
        local: true
      }, date),
      placement: "bottom"
    }, /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement(Moment, {
      fromNow: true
    }, date)));
  }

  function renderSensorAlarms(sensor) {
    return /*#__PURE__*/React.createElement(SensorAlarmButton, {
      type: sensor.type,
      identifier: sensor.identifier,
      alarmCount: sensor.status && sensor.status.alarmCount
    });
  }

  function renderFlow(sensor) {
    var status = sensor.status;

    if (!status || status.flowRate === undefined || status.flowRate === null) {
      return null;
    }

    return /*#__PURE__*/React.createElement("div", null, status.flowRate.toFixed(1) + " GPM");
  }

  function widthCalc(columns) {
    var maxWidth = 100;
    var result = columns().map(function (column) {
      if (column.key !== 'alarms') {
        return _extends({}, column, {
          width: (maxWidth - 10) / (columns().length - 1) + "%"
        });
      }

      return column;
    });
    return result;
  }

  var pressureColumns = function pressureColumns() {
    return [{
      title: t('Name'),
      key: 'name',
      className: styles$2['schedule-item-header'],
      render: function render(sensor) {
        return renderSensorName(sensor);
      }
    }, {
      title: t('Connectivity'),
      key: 'connectivity',
      className: styles$2.connect,
      render: function render(sensor) {
        return renderSensorConnectivity(sensor);
      }
    }, {
      title: t('Pressure'),
      key: 'pressure',
      className: styles$2['state-header'],
      render: function render(sensor) {
        return renderPressure(sensor);
      }
    }, {
      title: t('Last Data'),
      key: 'lastData',
      className: styles$2['last-data'],
      render: function render(sensor) {
        return renderLatestDate(sensor);
      }
    }, {
      title: t('Alarms'),
      key: 'alarms',
      className: styles$2['alarms-header'],
      width: 100,
      render: function render(sensor) {
        return renderSensorAlarms(sensor);
      }
    }];
  };

  var flowMeterColumns = function flowMeterColumns() {
    return [{
      title: t('Name'),
      key: 'name',
      className: styles$2['schedule-item-header'],
      render: function render(sensor) {
        return renderSensorName(sensor);
      }
    }, {
      title: t('Connectivity'),
      key: 'connectivity',
      className: styles$2.connect,
      render: function render(sensor) {
        return renderSensorConnectivity(sensor);
      }
    }, {
      title: t('Flow'),
      key: 'flow',
      className: styles$2['state-header'],
      render: function render(sensor) {
        return renderFlow(sensor);
      }
    }, {
      title: t('Last Data'),
      key: 'lastData',
      className: styles$2['last-data'],
      render: function render(sensor) {
        return renderLatestDate(sensor);
      }
    }, {
      title: t('Alarms'),
      key: 'alarms',
      className: styles$2['alarm-header'],
      width: 100,
      render: function render(sensor) {
        return renderSensorAlarms(sensor);
      }
    }];
  };

  var pumpControllerColumns = function pumpControllerColumns() {
    return [{
      title: t('Name'),
      key: 'name',
      className: styles$2['schedule-item-header'],
      render: function render(sensor) {
        return renderSensorName(sensor);
      }
    }, {
      title: t('Connectivity'),
      key: 'connectivity',
      className: styles$2.connect,
      render: function render(sensor) {
        return renderSensorConnectivity(sensor);
      }
    }, {
      title: t('State'),
      className: styles$2['state-header'],
      key: 'state',
      render: function render(sensor) {
        return /*#__PURE__*/React.createElement(PumpStateTag, {
          state: sensor.status && sensor.status.controlStatus && sensor.status.controlStatus.status,
          loading: sensor.status && sensor.status.loading,
          online: sensor.status && sensor.status.online
        });
      }
    }, {
      title: t('Last Data'),
      key: 'lastData',
      className: styles$2['last-data'],
      render: function render(sensor) {
        return renderLatestDate(sensor);
      }
    }, {
      title: t('Alarms'),
      key: 'alarms',
      className: styles$2['alarms-header'],
      width: 100,
      render: function render(sensor) {
        return renderSensorAlarms(sensor);
      }
    }];
  };

  var valveControllerColumns = function valveControllerColumns() {
    return [{
      title: t('Name'),
      key: 'name',
      className: styles$2['schedule-item-header'],
      render: function render(sensor) {
        return renderSensorName(sensor);
      }
    }, {
      title: t('Connectivity'),
      key: 'connectivity',
      className: styles$2.connect,
      render: function render(sensor) {
        return renderSensorConnectivity(sensor);
      }
    }, {
      title: t('State'),
      className: styles$2['state-header'],
      key: 'state',
      render: function render(sensor) {
        return getCurrentState(sensor.status && sensor.status.controlStatus && sensor.status.controlStatus.state, sensor.status && sensor.status.loading);
      }
    }, {
      title: t('Last Data'),
      key: 'lastData',
      className: styles$2['last-data'],
      render: function render(sensor) {
        return renderLatestDate(sensor);
      }
    }, {
      title: t('Alarms'),
      key: 'alarms',
      className: styles$2['alarms-header'],
      width: 100,
      render: function render(sensor) {
        return renderSensorAlarms(sensor);
      }
    }];
  };

  var blockList = function blockList() {
    return [{
      title: t('Name'),
      key: 'blockName',
      className: styles$2['schedule-item-header'],
      render: function render(d) {
        return d.blockName;
      }
    }, {
      title: t('Connectivity'),
      key: 'connectivity',
      className: styles$2['hidden-column'],
      render: null
    }, {
      title: t('Irrigation State'),
      className: styles$2['state-header'],
      render: function render(d) {
        var waterPressureState = pressureSensorsObj[d.blockId] && pressureSensorsObj[d.blockId].waterPressureState;
        var statusDetails = getBlockIrrigationStateByWaterPressureState(waterPressureState);
        return /*#__PURE__*/React.createElement(Tag, {
          color: statusDetails.color,
          className: waterPressureState > 1 ? [styles$2['tag-icon-irrigation-state'], styles$2['tag-icon-irrigating']] : styles$2['tag-icon-irrigation-state']
        }, statusDetails.irrigationState && statusDetails.irrigationState.toUpperCase());
      }
    }, {
      title: t('Last Data'),
      className: styles$2['hidden-column'],
      render: null
    }, {
      title: t('Alarms'),
      key: 'alarms',
      className: styles$2['alarm-header'],
      render: function render(d) {
        return renderSensorAlarms(d);
      }
    }];
  };

  var pumpControllers = pumpControllerIdentifier ? [pumpController] : null;
  return /*#__PURE__*/React.createElement("div", null, pumpControllers && [/*#__PURE__*/React.createElement("span", {
    className: styles$2['sub-header'],
    key: "header"
  }, t('Pump Control')), /*#__PURE__*/React.createElement(Table, {
    key: "table",
    dataSource: pumpControllers,
    columns: widthCalc(pumpControllerColumns),
    rowKey: function rowKey(record) {
      return record.identifier;
    },
    size: "small",
    className: styles$2['inner-table'],
    pagination: false
  })], valveController && [/*#__PURE__*/React.createElement("span", {
    className: styles$2['sub-header'],
    key: "header"
  }, showBlockList ? t('Blocks') : t('Valve Control')), /*#__PURE__*/React.createElement(Table, {
    key: "table",
    dataSource: showBlockList ? blockArr : valveControllers,
    columns: widthCalc(showBlockList ? blockList : valveControllerColumns),
    rowKey: function rowKey(record) {
      return record.identifier;
    },
    size: "small",
    className: styles$2['inner-table'],
    pagination: false
  })], flowMeter && [/*#__PURE__*/React.createElement("span", {
    className: styles$2['sub-header'],
    key: "header"
  }, t('Flow Meter')), /*#__PURE__*/React.createElement(Table, {
    key: "table",
    dataSource: [flowMeter],
    columns: widthCalc(flowMeterColumns),
    rowKey: function rowKey(record) {
      return record.identifier;
    },
    size: "small",
    className: styles$2['inner-table'],
    pagination: false
  })], /*#__PURE__*/React.createElement("span", {
    className: styles$2['sub-header']
  }, t('Pressure Sensors')), /*#__PURE__*/React.createElement(Table, {
    dataSource: pressureSensors,
    columns: widthCalc(pressureColumns),
    rowKey: function rowKey(record) {
      return record.identifier;
    },
    size: "small",
    className: styles$2['inner-table'],
    pagination: false
  }));
}
BlockControlSensorList.propTypes = {
  controlStatus: PropTypes.shape().isRequired,
  valves: PropTypes.arrayOf(PropTypes.any),
  showBlockList: PropTypes.bool
};
BlockControlSensorList.defaultProps = {
  valves: [],
  showBlockList: false
};

var Panel$1 = Collapse.Panel;
var Text$2 = Typography.Text;
var selectBlockSensorStatus = selectors.selectBlockSensorStatus;
var loadSensorStatus$3 = actions.loadSensorStatus;
function BlockControlList(_ref) {
  var blocks = _ref.blocks,
      valves = _ref.valves,
      onClickCalendar = _ref.onClickCalendar,
      blockLoading = _ref.blockLoading;
  var dispatch = useDispatch();
  var filteredBlocks = blocks ? blocks.filter(function (d) {
    return d.valve;
  }) : null;
  var blocksUnsorted = useSelector(function (state) {
    return selectBlockSensorStatus(state, filteredBlocks);
  });
  var blockDataSorted = blocksUnsorted.sort(function (a, b) {
    return a.blockName.localeCompare(b.blockName);
  });
  var spinner = blockDataSorted.reduce(function (a, p) {
    return !p || a || p.valveLoading;
  }, false);

  var _useTranslation = useTranslation(),
      t = _useTranslation.t;

  var blockIds = filteredBlocks && filteredBlocks.map(function (d) {
    return d && d.id;
  });
  var pressureSensorsObj = useSelector(function (state) {
    return getPressureDetailsForBlockIds(state, blockIds);
  });
  var sensorIdentifierArr = [];
  Object.values(pressureSensorsObj).forEach(function (pressureSensors) {
    var pressureSensorIdentifiers = pressureSensors.pressureSensorIdentifiers;
    JSON.parse(pressureSensorIdentifiers).forEach(function (identifier) {
      sensorIdentifierArr.push({
        type: 'water_pressure',
        identifier: identifier
      });
    });
  });
  var sensorIdentifiers = JSON.stringify(sensorIdentifierArr);
  useEffect(function () {
    JSON.parse(sensorIdentifiers).forEach(function (sensorParams) {
      dispatch(loadSensorStatus$3(sensorParams));
    });
  }, [dispatch, sensorIdentifiers]);
  var valveObj = blocks.reduce(function (acc, obj) {
    acc[obj.id] = {
      valveIds: obj.valves
    };
    return acc;
  }, {});
  var vfds = blocks.reduce(function (acc, obj) {
    acc[obj.id] = obj.vfds;
    return acc;
  }, {});
  var blockData = blockDataSorted.map(function (d) {
    var obj = d;
    obj.valves = valveObj[obj.blockId];
    obj.vfds = vfds[obj.blockId];
    return obj;
  });

  var getMessageClassName = function getMessageClassName(state, online) {
    var styleStr = styles$2['red-message'] + " ";
    var styleStrNot = styles$2['normal-message'] + " ";

    if (state === 'unknown' || !online && state === 'closed') {
      return styleStr;
    }

    if (!state) return styleStr;
    return styleStrNot;
  };

  var getSchedule = function getSchedule(block, text) {
    return /*#__PURE__*/React.createElement("div", {
      className: styles$2['icon-container'],
      onClick: function onClick() {
        return onClickCalendar({
          id: block.blockId
        });
      },
      role: "presentation"
    }, /*#__PURE__*/React.createElement("div", {
      className: styles$2.icon
    }, /*#__PURE__*/React.createElement(FaCalendarAlt, null)), /*#__PURE__*/React.createElement("div", {
      className: getMessageClassName(block.valveCurrentState, block.valveOnline)
    }, /*#__PURE__*/React.createElement("span", {
      className: styles$2['schedule-content']
    }, text)));
  };

  var getIndClass = function getIndClass(blockStatus) {
    var waterPressureState = pressureSensorsObj[blockStatus.blockId] && pressureSensorsObj[blockStatus.blockId].waterPressureState;
    var styleStr = styles$2.indicator + " ";

    if (!blockStatus.valveLoading) {
      if (blockStatus.valveControlEnabled) {
        if (!blockStatus.valveOnline) return styleStr + styles$2.red;
        if (blockStatus.valveOnline && waterPressureState === 1 && blockStatus.valveCurrentState === 'closed') return styleStr + styles$2['light-grey'];
        if (blockStatus.valveOnline && waterPressureState === 4 && (blockStatus.valveCurrentState === 'open' || blockStatus.pumpCurrentState === 'on')) return styleStr + styles$2.blue;
      }
    }

    return styleStr + styles$2['light-grey'];
  };

  var getRowClassName = function getRowClassName(record) {
    if (record.valveCurrentState) {
      if (!record.valveControlEnabled || record.valveCurrentState === 'unknown') {
        return styles$2['red-border-text'];
      }

      if (record.valveControlEnabled && !record.valveOnline && (record.valveCurrentState === 'closed' || !record.valveCurrentState)) {
        return styles$2['red-border-text'];
      }
    }

    if (!record.valveLoading && (!record.valveControlEnabled || !record.valveCurrentState)) {
      return styles$2['red-border-text'];
    }

    var className = getIndClass(record);
    return className;
  };

  function widthCalc(columns) {
    var maxwid = 100;
    var result = columns().map(function (element) {
      var values = element;

      if (element.title === 'Alarm') {
        values.width = 100;
      }

      if (element.title === 'Override') {
        values.width = 100;
      }

      if (element.title !== 'Override' && element.title !== 'Alarm') {
        values.width = (maxwid - 10) / (columns().length - 2) + "%";
      }

      return values;
    });
    return result;
  }

  function getAlarmListUrl(block) {
    var params = {
      blockId: block.blockId,
      logLevel: 'error',
      active: true
    };
    var paramStr = new URLSearchParams(params).toString();
    return "/events?" + paramStr;
  }

  var columns = function columns() {
    return [{
      title: t('Block'),
      className: styles$2['schedule-item-header'],
      render: function render(block) {
        if (block.blockName) {
          return /*#__PURE__*/React.createElement(Tooltip, {
            title: block.blockName
          }, /*#__PURE__*/React.createElement("span", null, block.blockName));
        }

        return /*#__PURE__*/React.createElement("span", {
          style: {
            color: 'lightgray'
          }
        }, t('No Name'));
      }
    }, {
      title: t('Schedule'),
      className: styles$2['schedule-header'],
      render: function render(block) {
        if (block.valveControlEnabled) {
          return getSchedule(block, getValveScheduleMessage(t, block.valveOnline, block.valveCurrentState, block.valveScheduledOpen, block.valveScheduledClose, block.valveOverrideStatus && block.valveOverrideStatus.state));
        }

        return t('Control not enabled');
      }
    }, {
      title: t('Override'),
      className: styles$2['hidden-column'],
      render: null
    }, {
      title: t('Irrigation State'),
      className: styles$2['state-header'],
      render: function render(block) {
        var waterPressureState = pressureSensorsObj[block.blockId] && pressureSensorsObj[block.blockId].waterPressureState;
        var statusDetails = getBlockIrrigationStateByWaterPressureState(waterPressureState);
        return /*#__PURE__*/React.createElement(Tag, {
          key: block.valveCurrentState,
          color: statusDetails.color,
          className: waterPressureState > 1 ? [styles$2['tag-icon-irrigation-state'], styles$2['tag-icon-irrigating']] : styles$2['tag-icon-irrigation-state']
        }, statusDetails.irrigationState && statusDetails.irrigationState.toUpperCase());
      }
    }, {
      title: t('Alarms'),
      className: styles$2['alarms-header'],
      render: function render(block) {
        return /*#__PURE__*/React.createElement(Link, {
          to: getAlarmListUrl(block)
        }, /*#__PURE__*/React.createElement(Badge, {
          count: block.blockAlarmCount
        }, /*#__PURE__*/React.createElement(Button, {
          className: styles$2['bell-icon-button'],
          icon: /*#__PURE__*/React.createElement(BellFilled, {
            className: styles$2['bell-icon-size']
          })
        })));
      }
    }];
  };

  function renderBlockDetail(blockControlStatus) {
    return /*#__PURE__*/React.createElement(BlockControlSensorList, {
      controlStatus: blockControlStatus,
      valves: valves
    });
  }

  return /*#__PURE__*/React.createElement("div", {
    className: styles$2['list-container']
  }, /*#__PURE__*/React.createElement(Collapse, {
    expandIconPosition: "right",
    defaultActiveKey: ['1']
  }, /*#__PURE__*/React.createElement(Panel$1, {
    header: /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Text$2, {
      style: {
        fontSize: '16px'
      }
    }, t('Blocks')), blockLoading || spinner ? /*#__PURE__*/React.createElement(LoadingOutlined, {
      style: {
        marginLeft: '10px',
        fontSize: '16px'
      }
    }) : ''),
    key: "1",
    className: styles$2['collapse-header']
  }, blockData.length ? /*#__PURE__*/React.createElement(Table, {
    className: styles$2['custom-table'],
    rowClassName: getRowClassName,
    dataSource: blockData,
    columns: widthCalc(columns),
    pagination: false,
    rowKey: function rowKey(record) {
      return String(record.blockId);
    },
    expandable: {
      expandRowByClick: true,
      rowExpandable: function rowExpandable() {
        return true;
      },
      expandedRowRender: function expandedRowRender(block) {
        return renderBlockDetail(block);
      }
    }
  }) : /*#__PURE__*/React.createElement("section", {
    className: styles$2['configuration-message']
  }, "No Blocks Configured"))));
}
BlockControlList.propTypes = {
  blocks: PropTypes.arrayOf(PropTypes.object),
  valves: PropTypes.arrayOf(PropTypes.object),
  onClickCalendar: PropTypes.func,
  blockLoading: PropTypes.bool
};
BlockControlList.defaultProps = {
  blocks: [],
  valves: [],
  onClickCalendar: null,
  blockLoading: null
};

var selectControlGatewaysForRanchId = selectors.selectControlGatewaysForRanchId,
    populateSensorStatus = selectors.populateSensorStatus;
var loadSensorStatus$4 = actions.loadSensorStatus;

function sortByName(a, b) {
  if (!a.name) return -1;
  if (!b.name) return 1;
  return a.name.localeCompare(b.name);
}

function ControlSystemStatus(_ref) {
  var ranchId = _ref.ranchId;

  var _useTranslation = useTranslation(),
      t = _useTranslation.t;

  var dispatch = useDispatch();
  var refreshId = useContext(RefreshContext);
  var gateways = useSelector(function (state) {
    return selectControlGatewaysForRanchId(state, ranchId);
  });
  var gatewaysWithStatus = useSelector(function (state) {
    return populateSensorStatus(state, gateways);
  });
  var gatewaysSorted = gatewaysWithStatus.sort(sortByName);
  var gatewayIdentifiers = JSON.stringify(gatewaysSorted.map(function (gateway) {
    return gateway.identifier;
  }));
  var statusLabels = [t('operational'), t('degraded'), t('offline')];
  useEffect(function () {
    if (refreshId) {
      JSON.parse(gatewayIdentifiers).forEach(function (identifier) {
        return dispatch(loadSensorStatus$4({
          identifier: identifier,
          type: 'gateway'
        }));
      });
    }
  }, [dispatch, gatewayIdentifiers, refreshId]);

  function getOverallStatus(sensors) {
    var numInternetOffline = 0;
    var numControlOffline = 0;
    var numDegraded = 0;
    var numStatus = 0;
    sensors.forEach(function (sensor) {
      if (sensor.status && sensor.status.controlStatus) {
        numStatus += 1;
        if (sensor.status.online === false) numInternetOffline += 1;
        if (sensor.status.controlStatus.controlServerState === 'offline') numControlOffline += 1;
        if (sensor.status.controlStatus.controlServerState === 'degraded') numDegraded += 1;
      }
    });
    if (numControlOffline > 0 || numInternetOffline > 0 || numStatus === 0) return 'error';
    if (numDegraded > 0) return 'warning';
    return 'ok';
  }

  function getStoplightStateFromControlState(value) {
    if (value === 'offline') return 'error';
    if (value === 'online') return 'ok';
    if (value === 'degraded') return 'warning';
    return undefined;
  }

  function renderPanelHeader() {
    var overallStatus = getOverallStatus(gatewaysWithStatus);
    return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Typography.Text, {
      className: styles$2['space-after'],
      style: {
        fontSize: '16px'
      }
    }, t('Gateway System Status')), /*#__PURE__*/React.createElement(StoplightTag, {
      value: overallStatus,
      labels: statusLabels
    }));
  }

  function renderGatewayStatus(gateway) {
    var online = gateway.status && gateway.status.online;
    var controlStatusState = gateway.status && gateway.status.controlStatus && gateway.status.controlStatus.controlServerState;
    var controlStoplightState = getStoplightStateFromControlState(controlStatusState);
    return /*#__PURE__*/React.createElement("div", {
      key: gateway.id
    }, /*#__PURE__*/React.createElement("div", {
      className: styles$2['gateway-header']
    }, /*#__PURE__*/React.createElement("strong", null, gateway.name)), /*#__PURE__*/React.createElement(Divider, {
      dashed: true
    }), /*#__PURE__*/React.createElement("div", {
      className: styles$2['space-between']
    }, /*#__PURE__*/React.createElement("div", null, t('Internet Connectivity')), /*#__PURE__*/React.createElement(BooleanTag, {
      value: online,
      labels: [statusLabels[0], statusLabels[2]]
    })), /*#__PURE__*/React.createElement(Divider, {
      dashed: true
    }), /*#__PURE__*/React.createElement("div", {
      className: styles$2['space-between']
    }, /*#__PURE__*/React.createElement("div", null, t('Gateway Control Software')), /*#__PURE__*/React.createElement(StoplightTag, {
      value: controlStoplightState,
      labels: statusLabels
    })), /*#__PURE__*/React.createElement(Divider, null));
  }

  return /*#__PURE__*/React.createElement("div", {
    className: styles$2['list-container']
  }, /*#__PURE__*/React.createElement(Collapse, {
    expandIconPosition: "right"
  }, /*#__PURE__*/React.createElement(Collapse.Panel, {
    header: renderPanelHeader(),
    key: "systemStatus",
    className: styles$2['collapse-header']
  }, gatewaysSorted.map(function (gateway) {
    return renderGatewayStatus(gateway);
  }))));
}
ControlSystemStatus.propTypes = {
  ranchId: PropTypes.number.isRequired
};

var Panel$2 = Collapse.Panel;
var Text$3 = Typography.Text;
var selectBlockSensorStatus$1 = selectors.selectBlockSensorStatus;

function getBlockNameForValve(blockNames, blockIds) {
  var blockName = '';

  if (blockIds && blockIds.length) {
    if (blockIds.length === 1) {
      blockName = blockNames[blockIds[0]];
    } else {
      blockName = blockIds.length + " blocks";
    }
  }

  return blockName;
}

function ValveControlList(_ref) {
  var valves = _ref.valves,
      blocks = _ref.blocks,
      onClickCalendar = _ref.onClickCalendar,
      valveLoading = _ref.valveLoading;
  var filteredBlocks = blocks ? blocks.filter(function (d) {
    return d.valve || d.valves;
  }) : null;
  var blocksUnsorted = useSelector(function (state) {
    return selectBlockSensorStatus$1(state, filteredBlocks);
  });
  var blockData = blocksUnsorted.sort(function (a, b) {
    return a.blockName.localeCompare(b.blockName);
  });
  var spinner = blockData.reduce(function (a, p) {
    return !p || a || p.valveLoading;
  }, false);

  var _useTranslation = useTranslation(),
      t = _useTranslation.t;

  var blockNames = filteredBlocks.reduce(function (acc, obj) {
    acc[obj.id] = obj.name;
    return acc;
  }, {});
  var blockDataObj = blockData.reduce(function (acc, b) {
    acc[b.blockId] = b;
    return acc;
  }, {});
  var valveData = [];
  valves.forEach(function (d) {
    var _d$irrigation_blocks;

    var blockId = d.irrigation_block || ((_d$irrigation_blocks = d.irrigation_blocks) === null || _d$irrigation_blocks === void 0 ? void 0 : _d$irrigation_blocks.length) && d.irrigation_blocks[0];
    if (blockId) valveData.push(_extends({}, d, {}, blockDataObj[blockId]));
  });

  var getMessageClassName = function getMessageClassName(state, online) {
    var styleStr = styles$2['red-message'] + " ";
    var styleStrNot = styles$2['normal-message'] + " ";

    if (state === 'unknown' || !online && state === 'closed') {
      return styleStr;
    }

    if (!state) return styleStr;
    return styleStrNot;
  };

  var getSchedule = function getSchedule(valve, text) {
    return /*#__PURE__*/React.createElement("div", {
      className: styles$2['icon-container'],
      onClick: function onClick() {
        return onClickCalendar({
          id: valve.blockId
        });
      },
      role: "presentation"
    }, /*#__PURE__*/React.createElement("div", {
      className: styles$2.icon
    }, /*#__PURE__*/React.createElement(FaCalendarAlt, null)), /*#__PURE__*/React.createElement("div", {
      className: getMessageClassName(valve.valveCurrentState, valve.valveOnline)
    }, /*#__PURE__*/React.createElement("span", {
      className: styles$2['schedule-content']
    }, text)));
  };

  var getIndClass = function getIndClass(blockStatus) {
    var styleStr = styles$2.indicator + " ";

    if (!blockStatus.valveLoading) {
      if (blockStatus.valveControlEnabled) {
        if (!blockStatus.valveOnline) return styleStr + styles$2.red;
        if (blockStatus.valveOnline && blockStatus.valveCurrentState === 'closed') return styleStr + styles$2['light-grey'];
        if (blockStatus.valveOnline && blockStatus.valveCurrentState === 'open') return styleStr + styles$2.blue;
      }
    }

    return styleStr + styles$2['light-grey'];
  };

  var getCurrentState = function getCurrentState(valve) {
    var color = valve.valveCurrentState === 'open' ? '#1890FF' : '#D40202';

    if (valve.valveCurrentState) {
      if (valve.valveCurrentState !== 'unknown') {
        if (valve.valveOnline && valve.valveCurrentState === 'closed') {
          return /*#__PURE__*/React.createElement(Tag, {
            color: "",
            key: valve.valveCurrentState,
            className: styles$2['tag-icon']
          }, t('Closed').toUpperCase());
        }

        var state = valve.valveCurrentState === 'open' ? t('Open_status').toUpperCase() : valve.valveCurrentState;
        return /*#__PURE__*/React.createElement(Tag, {
          color: color,
          key: valve.valveCurrentState,
          className: [styles$2['tag-icon'], styles$2['tag-icon-irrigating']]
        }, state.toUpperCase());
      }

      return /*#__PURE__*/React.createElement(WarningFilled, {
        className: styles$2['warning-icon']
      });
    }

    if (!valve.valveCurrentState) {
      return /*#__PURE__*/React.createElement(WarningFilled, {
        className: styles$2['warning-icon']
      });
    }

    return '';
  };

  var getRowClassName = function getRowClassName(record) {
    if (record.valveCurrentState) {
      if (!record.valveControlEnabled || record.valveCurrentState === 'unknown') {
        return styles$2['red-border-text'];
      }

      if (record.valveControlEnabled && !record.valveOnline && (record.valveCurrentState === 'closed' || !record.valveCurrentState)) {
        return styles$2['red-border-text'];
      }
    }

    if (!record.valveLoading && (!record.valveControlEnabled || !record.valveCurrentState)) {
      return styles$2['red-border-text'];
    }

    var className = getIndClass(record);
    return className;
  };

  function widthCalc(columns) {
    var maxwid = 100;
    var result = columns().map(function (element) {
      var values = element;

      if (element.title === 'Alarm') {
        values.width = 100;
      }

      if (element.title === 'Override') {
        values.width = 100;
      }

      if (element.title !== 'Override' && element.title !== 'Alarm') {
        values.width = (maxwid - 10) / (columns().length - 2) + "%";
      }

      return values;
    });
    return result;
  }

  function getAlarmListUrl(valve) {
    var params = {
      blockId: valve.blockId,
      logLevel: 'error',
      active: true
    };
    var paramStr = new URLSearchParams(params).toString();
    return "/events?" + paramStr;
  }

  var columns = function columns() {
    return [{
      title: t('Block(s)'),
      className: styles$2['schedule-item-header'],
      render: function render(valve) {
        var blockIds = valve.irrigation_blocks && valve.irrigation_blocks.length ? valve.irrigation_blocks : [valve.block];
        var blockName = getBlockNameForValve(blockNames, blockIds);

        if (blockName) {
          return /*#__PURE__*/React.createElement(Tooltip, {
            title: blockName
          }, /*#__PURE__*/React.createElement("span", null, blockName));
        }

        return /*#__PURE__*/React.createElement("span", {
          style: {
            color: 'lightgray'
          }
        }, t('No Name'));
      }
    }, {
      title: t('Valve'),
      className: styles$2['schedule-item-header'],
      render: function render(valve) {
        if (valve.name) {
          return /*#__PURE__*/React.createElement(Tooltip, {
            title: valve.name
          }, /*#__PURE__*/React.createElement("span", null, valve.name));
        }

        return /*#__PURE__*/React.createElement("span", {
          style: {
            color: 'lightgray'
          }
        }, t('No Name'));
      }
    }, {
      title: t('Schedule'),
      className: styles$2['schedule-header'],
      render: function render(valve) {
        if (valve.valveControlEnabled) {
          return getSchedule(valve, getValveScheduleMessage(t, valve.valveOnline, valve.valveCurrentState, valve.valveScheduledOpen, valve.valveScheduledClose, valve.valveOverrideStatus && valve.valveOverrideStatus.state));
        }

        return t('Control not enabled');
      }
    }, {
      title: t('Override'),
      className: styles$2['override-header'],
      render: function render(valve) {
        if (valve.valveControlEnabled) {
          return /*#__PURE__*/React.createElement(OverrideStatus, {
            type: VALVE_TYPE,
            overrideStatus: valve.valveOverrideStatus,
            valveIdentifier: valve.valveIdentifier,
            vfdIdentifier: valve.vfdIdentifier,
            currentState: valve.valveCurrentState,
            confirmDialogMessages: [getPumpScheduleMessageBasic(t, valve.pumpStartDate, valve.pumpStopDate), getValveScheduleMessage(t, valve.valveOnline, valve.valveCurrentState, valve.valveScheduledOpen, valve.valveScheduledClose)]
          });
        }

        return null;
      }
    }, {
      title: t('State'),
      className: styles$2['state-header'],
      render: function render(valve) {
        if (valve.valveControlEnabled) {
          return /*#__PURE__*/React.createElement("div", {
            className: styles$2.status
          }, getCurrentState(valve));
        }

        return null;
      }
    }, {
      title: t('Alarms'),
      className: styles$2['alarms-header'],
      render: function render(valve) {
        return /*#__PURE__*/React.createElement(Link, {
          to: getAlarmListUrl(valve)
        }, /*#__PURE__*/React.createElement(Badge, {
          count: valve.valveAlarmCount
        }, /*#__PURE__*/React.createElement(Button, {
          className: styles$2['bell-icon-button'],
          icon: /*#__PURE__*/React.createElement(BellFilled, {
            className: styles$2['bell-icon-size']
          })
        })));
      }
    }];
  };

  function renderValveDetail(valveControlStatus) {
    return /*#__PURE__*/React.createElement(BlockControlSensorList, {
      controlStatus: valveControlStatus,
      showBlockList: true
    });
  }

  return /*#__PURE__*/React.createElement("div", {
    className: styles$2['list-container']
  }, /*#__PURE__*/React.createElement(Collapse, {
    expandIconPosition: "right",
    defaultActiveKey: ['1']
  }, /*#__PURE__*/React.createElement(Panel$2, {
    header: /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Text$3, {
      style: {
        fontSize: '16px'
      }
    }, t('Valves')), valveLoading || spinner ? /*#__PURE__*/React.createElement(LoadingOutlined, {
      style: {
        marginLeft: '10px',
        fontSize: '16px'
      }
    }) : ''),
    key: "1",
    className: styles$2['collapse-header']
  }, valveData.length ? /*#__PURE__*/React.createElement(Table, {
    className: styles$2['custom-table'],
    rowClassName: getRowClassName,
    dataSource: valveData,
    columns: widthCalc(columns),
    pagination: false,
    rowKey: function rowKey(record) {
      return String(record.id);
    },
    expandable: {
      expandRowByClick: true,
      rowExpandable: function rowExpandable() {
        return true;
      },
      expandedRowRender: function expandedRowRender(valve) {
        return renderValveDetail(valve);
      }
    }
  }) : /*#__PURE__*/React.createElement("section", {
    className: styles$2['configuration-message']
  }, "No Valves Configured"))));
}
ValveControlList.propTypes = {
  blocks: PropTypes.arrayOf(PropTypes.object),
  valves: PropTypes.arrayOf(PropTypes.object),
  onClickCalendar: PropTypes.func,
  valveLoading: PropTypes.bool
};
ValveControlList.defaultProps = {
  blocks: [],
  valves: [],
  onClickCalendar: null,
  valveLoading: null
};

var styles$3 = {"root":"_1RMQI","custom-label-div":"_1kAHd","custom-label-div-default":"_gUKoy","water-value":"_3sSDw","outer-div":"_2kMca","inner-div":"_1glhn","time-background":"_2ZrAb","custom-input":"_2QhrQ","time-div-style":"_1YmQx","water-value-div-style":"_1Kg2-","span-text-value":"_1WMyQ","span-text-unit":"_3O_WH","container":"_3jwoj","card-style":"_3kVsq","card-div-style":"_3CO3Z","back-icon":"_1lkOB"};

function round(x, step) {
  return Math.ceil(x / step) * step;
}

var Slider = function Slider(props) {
  var sliderWidth = props.sliderWidth,
      trackSize = props.trackSize,
      hourMax = props.hourMax,
      hourStep = props.hourStep,
      minMax = props.minMax,
      minStep = props.minStep,
      value = props.value,
      knobSize = props.knobSize,
      colors = props.colors,
      onChange = props.onChange,
      cutoffValues = props.cutoffValues,
      waterValue = props.waterValue,
      waterUnit = props.waterUnit;

  var _useTranslation = useTranslation(),
      t = _useTranslation.t;

  var timeoutId = useRef(0);
  useEffect(function () {
    return function () {
      if (timeoutId.current) clearTimeout(timeoutId.current);
    };
  });

  function parseHours(minutes) {
    return Math.floor(minutes / 60);
  }

  function parseMinutes(minutes) {
    return minutes % 60;
  }

  var _useState = useState(parseHours(value)),
      hrs = _useState[0],
      setHrs = _useState[1];

  var _useState2 = useState(parseMinutes(value)),
      mins = _useState2[0],
      setMins = _useState2[1];

  useEffect(function () {
    if (mins !== parseMinutes(value)) setMins(parseMinutes(value));
  }, [value, mins]);
  var totalMinutes = value;
  var maxTotalMinutes = 60 * hourMax;

  function getSliderValues() {
    var allValues = [];

    for (var i = minStep; i <= maxTotalMinutes; i += minStep) {
      allValues.push(i);
    }

    return allValues;
  }

  var timeArr = getSliderValues();
  var currIndex = timeArr.indexOf(round(totalMinutes, minStep));
  var sliderIndex = currIndex >= 0 ? currIndex : 0;

  var onSliderChange = function onSliderChange(newValue) {
    if (newValue === maxTotalMinutes && totalMinutes > maxTotalMinutes) {
      return;
    }

    setHrs(parseHours(newValue));
    setMins(parseMinutes(newValue));
    onChange(newValue);
  };

  function onHoursInputChange(newHours) {
    setHrs(newHours);
    onChange(Number(newHours * 60 + mins));
  }

  function onMinutesInputChange(newMinutes) {
    var extraHours = Math.floor(newMinutes / 60);
    var minutes = newMinutes % 60;
    setMins(minutes);
    setHrs(hrs + extraHours);
    onChange(Number(newMinutes + 60 * hrs));
  }

  function appendAttributesToElement() {
    var circles = document.getElementsByTagName('circle');
    var svgElem = document.getElementsByTagName('svg');
    var divElem = document.querySelectorAll('.innerDiv div');

    if (svgElem.length && svgElem.length >= 3) {
      svgElem[0].setAttribute('id', 'sliderCircle');
      svgElem[1].setAttribute('id', 'knob');
      svgElem[0].setAttribute('shape-rendering', 'geometricPrecision');
      svgElem[1].setAttribute('shape-rendering', 'geometricPrecision');
      svgElem[2].childNodes.forEach(function (d) {
        return d.setAttribute('fill', 'none');
      });
    }

    if (divElem.length && divElem.length >= 1) {
      divElem[1].setAttribute('id', 'knobDiv');
    }

    return circles;
  }

  var filteredCutOffPoints = [];
  var filteredColor = [];
  var arrIndex;

  for (arrIndex = 0; arrIndex < cutoffValues.length; arrIndex += 1) {
    var cutoffValue = cutoffValues[arrIndex];

    if (cutoffValue && cutoffValue > 0 && cutoffValue <= hourMax) {
      filteredColor.push(colors[arrIndex]);
      filteredCutOffPoints.push(cutoffValues[arrIndex]);
    }
  }

  filteredColor.push(colors[arrIndex]);
  var mobileScreen = window.innerWidth < 600;
  var outerCircleWidth = sliderWidth;
  var cutOffValArray = filteredCutOffPoints;
  var x = sliderWidth / 2;
  var y = sliderWidth / 2;
  var r = x - 8;
  var maxValue = hourMax;
  var sin = Math.sin;
  var cos = Math.cos;
  var svg = document.getElementById('svgStrokeCircle');
  var radius = 7.5;
  var gap = 12;

  function getProportionateAngle(num) {
    return 2 * Math.PI * num / maxValue;
  }

  function getPointForAngle(ra, angle) {
    return [x + ra * sin(angle - Math.PI), y + ra * cos(angle - Math.PI)];
  }

  function plotCirclesOnArc(start, end, color) {
    var circle = document.getElementById('innerCircle');
    var angleUnit = Math.PI / 180;
    var angleBetweenCircles = gap * angleUnit;
    var currentAngle = start + angleBetweenCircles;
    var cutOffPointsArr = [].concat(cutOffValArray, [end]);

    for (var i = 0; i < cutOffPointsArr.length; i += 1) {
      var cutOffAngle = getProportionateAngle(cutOffPointsArr[i]);

      while (currentAngle <= cutOffAngle) {
        var point = getPointForAngle(r, currentAngle);
        var circleClone = circle.cloneNode(true);
        circleClone.setAttribute('id', 'arc-cir');
        circleClone.setAttribute('r', radius);
        circleClone.setAttribute('cx', point[0]);
        circleClone.setAttribute('cy', point[1]);
        circleClone.setAttribute('fill', color || filteredColor[i]);
        circleClone.setAttribute('stroke-width', 1);
        circleClone.setAttribute('shape-rendering', 'geometricPrecision');
        circleClone.setAttribute('stroke-linejoin', 'round');
        svg.appendChild(circleClone);
        currentAngle += angleBetweenCircles;
      }
    }
  }

  function removeCircles() {
    var dialCircles = document.querySelectorAll('#arc-cir');

    if (dialCircles === null || dialCircles === void 0 ? void 0 : dialCircles.length) {
      dialCircles.forEach(function (d) {
        return d.remove();
      });
    }
  }

  useEffect(function () {
    var circle = appendAttributesToElement();

    if (circle.length && circle.length >= 2) {
      circle[0].setAttribute('id', 'circle');
      circle[0].setAttribute('r', "" + (mobileScreen ? x - 20 : x - 30));
      circle[0].setAttribute('fill', '#e3e3e3');
      circle[1].setAttribute('fill', 'white');
      circle[1].setAttribute('fill-opacity', '0.8');
    }

    if (!document.getElementById('svgStrokeCircle')) {
      var newHtml = "<svg id=\"svgStrokeCircle\" class=\"outercircle\"\n        viewBox=\"0 0 " + outerCircleWidth + " " + outerCircleWidth + "\"\n        width=\"" + outerCircleWidth + "\" height=\"" + outerCircleWidth + "\">\n        <circle id='innerCircle' cx=\"" + x + "\" cy=\"" + y + "\" r=\"" + r + "\" fill=\"none\"\n        stroke-width=\"1\" shape-rendering=\"geometricPrecision stroke-linejoin=\"round\" />\n        </svg>";
      var newSvg = document.getElementById('strokeCircle');

      if (newSvg) {
        newSvg.innerHTML += newHtml;
      }
    }

    try {
      if (cutOffValArray && cutOffValArray.length) {
        removeCircles();
        plotCirclesOnArc(0, maxValue);
      } else {
        removeCircles();
        plotCirclesOnArc(0, maxValue, filteredColor[0] ? filteredColor[0] : 'orange');
      }
    } catch (_unused) {}
  });

  function renderInputs() {
    return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
      className: styles$3['custom-label-div'] + " \n          " + (!waterValue || !waterUnit ? styles$3['custom-label-div-default'] : '')
    }, /*#__PURE__*/React.createElement("div", {
      className: styles$3['time-div-style']
    }, /*#__PURE__*/React.createElement(InputNumber, {
      className: styles$3['custom-input'],
      min: 0,
      step: hourStep,
      max: hourMax,
      defaultValue: 3,
      value: hrs,
      onChange: onHoursInputChange,
      controls: false,
      size: "large",
      "data-testid": "input-hrs"
    }), /*#__PURE__*/React.createElement("span", {
      className: styles$3['span-text']
    }, t('hrs'))), /*#__PURE__*/React.createElement("div", {
      className: styles$3['time-div-style']
    }, /*#__PURE__*/React.createElement(InputNumber, {
      className: styles$3['custom-input'],
      min: 0,
      step: minStep,
      max: minMax,
      defaultValue: 0,
      value: mins,
      onChange: onMinutesInputChange,
      controls: false,
      size: "large",
      "data-testid": "input-mins"
    }), /*#__PURE__*/React.createElement("span", {
      className: styles$3['span-text']
    }, t('mins')))), waterValue && waterUnit ? /*#__PURE__*/React.createElement("div", {
      className: styles$3['custom-label-div'] + " " + styles$3['water-value']
    }, /*#__PURE__*/React.createElement("span", {
      className: styles$3['span-text-value']
    }, waterValue), /*#__PURE__*/React.createElement("span", {
      className: styles$3['span-text-unit']
    }, waterUnit)) : null);
  }

  return /*#__PURE__*/React.createElement("div", {
    className: styles$3['outer-div']
  }, /*#__PURE__*/React.createElement("div", {
    className: styles$3['inner-div'],
    id: "innerdiv"
  }, /*#__PURE__*/React.createElement(CircularSlider, {
    width: sliderWidth,
    renderLabelValue: renderInputs(),
    knobSize: knobSize || 40,
    labelFontSize: "1rem",
    valueFontSize: "2rem",
    knobPosition: "top",
    direction: -1,
    progressLineCap: "round",
    knobColor: "#1890ff",
    progressColorFrom: "#1890ff",
    progressColorTo: "#1890ff",
    progressSize: trackSize,
    trackColor: "transparent",
    trackSize: trackSize,
    data: timeArr,
    dataIndex: sliderIndex,
    onChange: onSliderChange
  })), /*#__PURE__*/React.createElement("div", {
    id: "strokeCircle",
    className: styles$3['inner-div']
  }));
};

Slider.propTypes = {
  strokeDashArray: PropTypes.string,
  sliderWidth: PropTypes.number,
  trackSize: PropTypes.number,
  hourMax: PropTypes.number,
  hourStep: PropTypes.number,
  minMax: PropTypes.number,
  minStep: PropTypes.number,
  value: PropTypes.number.isRequired,
  onChange: PropTypes.func,
  defaultValue: PropTypes.number,
  knobSize: PropTypes.number,
  colors: PropTypes.arrayOf(PropTypes.string),
  cutoffValues: PropTypes.arrayOf(PropTypes.any),
  waterValue: PropTypes.number,
  waterUnit: PropTypes.string
};
Slider.defaultProps = {
  strokeDashArray: '0,18.5',
  sliderWidth: 150,
  trackSize: 14,
  hourMax: 72,
  hourStep: 1,
  minMax: 60,
  minStep: 5,
  defaultValue: null,
  onChange: function onChange() {},
  knobSize: 30,
  colors: ['green', 'orange', 'blue'],
  cutoffValues: [],
  waterValue: null,
  waterUnit: null
};

var almonds = require("./almonds~diBVNtXO.png");

var grapes = require("./grapes~VvgQSJjC.png");

var none = require("./none~fggdWnqg.png");

var pistachios = require("./pistachios~DDbsjBIW.png");

var walnuts = require("./walnuts~VdYLyOvB.png");

var Panel$3 = Collapse.Panel;
var Text$4 = Typography.Text;

var EntityList = function EntityList(_ref) {
  var onChange = _ref.onChange,
      selected = _ref.selected,
      data = _ref.data,
      activeSelections = _ref.activeSelections;

  if (data.length) {
    data.map(function (d) {
      var obj = d;
      obj.ranches.sort(function (a, b) {
        return a && a.name.toLowerCase().localeCompare(b && b.name.toLowerCase());
      });
      obj.ranches.map(function (r) {
        var robj = r;
        robj.blocks.sort(function (a, b) {
          return a && a.name.toLowerCase().localeCompare(b && b.name.toLowerCase());
        });
        return robj;
      });
      return obj;
    });
    data.sort(function (a, b) {
      return a && a.name.toLowerCase().localeCompare(b && b.name.toLowerCase());
    });
  }

  function getImage(type) {
    switch (type.toLowerCase()) {
      case 'almonds':
      case 'almond':
        return almonds;

      case 'grapes':
      case 'grape':
        return grapes;

      case 'pistachios':
      case 'pistachio':
        return pistachios;

      case 'walnuts':
      case 'walnut':
        return walnuts;

      default:
        return none;
    }
  }

  useEffect(function () {
    if (selected.ranch.length && !selected.block.length) {
      var elmnt = document.getElementById(String(selected.ranch[0]));
      if (elmnt) elmnt.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    } else if (selected.block.length) {
      var _elmnt = document.getElementById(String(selected.block[0]));

      if (_elmnt) _elmnt.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  });
  return data.map(function (en, i) {
    if (en && en.id) {
      return /*#__PURE__*/React.createElement(Collapse, {
        expandIconPosition: "right",
        key: String(en.id) + "_" + String(i),
        collapsible: "header",
        defaultActiveKey: en.id
      }, /*#__PURE__*/React.createElement(Panel$3, {
        header: en.name,
        key: String(en.id),
        className: styles['collapse-content']
      }, en.ranches.length ? en.ranches.map(function (ra, j) {
        return /*#__PURE__*/React.createElement(Collapse, {
          className: selected.ranch && selected.ranch.includes(ra.id) && selected.block && !selected.block.length ? styles['collapsible-list-selected'] : styles['collapsible-list'],
          expandIcon: function expandIcon(_ref2) {
            var isActive = _ref2.isActive;
            return /*#__PURE__*/React.createElement(CaretRightOutlined, {
              rotate: isActive ? 90 : 0
            });
          },
          key: String(ra.id) + "_" + String(j),
          activeKey: activeSelections,
          onChange: onChange
        }, /*#__PURE__*/React.createElement(Panel$3, {
          header: selected.ranch && selected.ranch.includes(ra.id) && selected.block && !selected.block.length ? /*#__PURE__*/React.createElement(Tooltip, {
            title: ra.name
          }, /*#__PURE__*/React.createElement(Text$4, {
            id: String(ra.id),
            onClick: function onClick(d) {
              return onChange({
                ranch: d.target.id
              });
            },
            className: styles['selected-ranch-header']
          }, ra.name)) : /*#__PURE__*/React.createElement(Tooltip, {
            title: ra.name
          }, /*#__PURE__*/React.createElement(Text$4, {
            id: String(ra.id),
            onClick: function onClick(d) {
              return onChange({
                ranch: d.target.id
              });
            }
          }, ra.name)),
          key: String(ra.id)
        }, ra.blocks.length ? ra.blocks.map(function (bl, k) {
          return /*#__PURE__*/React.createElement("div", {
            key: bl.name + "_" + String(k),
            className: styles['list-content']
          }, /*#__PURE__*/React.createElement(List.Item, {
            key: bl.id,
            onClick: function onClick(d) {
              return onChange({
                block: d.target.id
              });
            },
            id: bl.id,
            className: selected.ranch && selected.ranch.length && selected.block && selected.block.includes(bl.id) ? styles['selected-block'] : styles['extra-list'],
            extra: [/*#__PURE__*/React.createElement("div", {
              id: bl.id,
              key: bl.id + "_" + bl.name,
              className: styles['extra-list-items']
            }, /*#__PURE__*/React.createElement("img", {
              id: bl.id,
              alt: bl.crop,
              src: getImage(bl.crop)
            }))]
          }, /*#__PURE__*/React.createElement(Tooltip, {
            title: bl.name
          }, /*#__PURE__*/React.createElement(Text$4, {
            id: bl.id,
            className: selected.ranch && selected.ranch.length && selected.block && selected.block.includes(bl.id) ? styles['selected-block-text'] : styles['list-name']
          }, bl.name))), /*#__PURE__*/React.createElement("div", {
            className: styles['status-box']
          }));
        }) : /*#__PURE__*/React.createElement("div", null, !ra.isLoading ? /*#__PURE__*/React.createElement(Text$4, {
          className: styles['loading-icon']
        }, "No Block") : /*#__PURE__*/React.createElement(LoadingOutlined, {
          className: styles['loading-icon']
        }))), /*#__PURE__*/React.createElement("div", {
          className: styles['panel-statusbox']
        }));
      }) : /*#__PURE__*/React.createElement("div", null, !en.isLoading ? /*#__PURE__*/React.createElement(Text$4, {
        className: styles['loading-icon']
      }, "No Ranch") : /*#__PURE__*/React.createElement(LoadingOutlined, {
        className: styles['loading-icon']
      }))));
    }

    return null;
  });
};

EntityList.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object),
  onChange: PropTypes.func,
  selected: PropTypes.shape({
    ranch: PropTypes.arrayOf(PropTypes.any),
    block: PropTypes.arrayOf(PropTypes.any)
  }),
  activeSelections: PropTypes.arrayOf(PropTypes.any)
};
EntityList.defaultProps = {
  data: [],
  onChange: null,
  selected: null,
  activeSelections: []
};

NoDataToDisplay(Highcharts);
var defaultOptions = {
  title: null,
  xAxis: {
    gridLineWidth: 1
  },
  yAxis: {
    gridLineWidth: 1
  },
  series: [],
  credits: {
    enabled: false
  },
  noData: {
    style: {
      fontWeight: 'bold',
      fontSize: '15px',
      color: '#303030'
    }
  },
  time: {
    useUTC: false
  }
};

var ChartComponent = /*#__PURE__*/function (_React$Component) {
  _inheritsLoose(ChartComponent, _React$Component);

  function ChartComponent(props) {
    var _this;

    _this = _React$Component.call(this, props) || this;

    _this.componentDidUpdate = function () {
      _this.forceReflow();
    };

    _this.forceReflow = function () {
      if (_this.highchartsComponent && _this.highchartsComponent.current) {
        _this.highchartsComponent.current.chart.reflow();
      }
    };

    _this.customCallback = function (param) {
      var callback = _this.props.callback;
      callback(param);
    };

    _this.highchartsComponent = React.createRef();
    return _this;
  }

  var _proto = ChartComponent.prototype;

  _proto.render = function render() {
    var _this$props = this.props,
        options = _this$props.options,
        constructorType = _this$props.constructorType,
        chartRef = _this$props.chartRef,
        loading = _this$props.loading;

    if (chartRef && this.highchartsComponent) {
      chartRef.current = this.highchartsComponent.current;
    }

    return /*#__PURE__*/React.createElement(HighchartsReact, {
      constructorType: constructorType || 'chart',
      containerProps: {
        style: {
          height: '100%'
        }
      },
      highcharts: constructorType === 'ganttChart' ? HighchartsGantt : Highcharts,
      options: _extends({}, defaultOptions, {}, options, {
        lang: {
          noData: loading ? '' : 'No data'
        }
      }),
      ref: this.highchartsComponent,
      callback: this.customCallback
    });
  };

  return ChartComponent;
}(React.Component);
ChartComponent.propTypes = {
  options: PropTypes.shape({}),
  constructorType: PropTypes.string,
  chartRef: PropTypes.shape({
    current: PropTypes.any
  }),
  callback: PropTypes.func,
  loading: PropTypes.bool
};
ChartComponent.defaultProps = {
  options: null,
  constructorType: null,
  chartRef: null,
  callback: function callback() {},
  loading: false
};

/* loaded by smart-asset */
var image = require("./loader~oKpOSUuf.gif");

var styles$4 = {"gantt":"_3sNiW","chart-div":"_3SZHP","loader":"_1PHn4","capitalize":"_GTQ1N","bell-icon-size":"_mdZF1","bell-icon-button":"_1_3fM","block-buttons":"_1eAT_","override-alert":"_1S5eO","loading-block":"_2I6aY","loading-data":"_2L-_L"};

var selectEntitySidebarCollapseState = selectors.selectEntitySidebarCollapseState;

var HighchartsSchedule = function HighchartsSchedule(_ref) {
  var chartData = _ref.chartData,
      onClickChart = _ref.onClickChart,
      dates = _ref.dates,
      configChange = _ref.configChange;
  var chartRef = useRef(null);
  var renderTrigger = useRef(true);

  var _useState = useState(null),
      options = _useState[0],
      setOptions = _useState[1];

  var _useState2 = useState([]),
      series = _useState2[0],
      setSeries = _useState2[1];

  var sidebarCollapsed = useSelector(function (state) {
    return selectEntitySidebarCollapseState(state);
  }).payload;

  var _useTranslation = useTranslation(),
      t = _useTranslation.t;

  var colors = {
    recommended: 'rgb(193, 218, 214)',
    past: 'rgb(232, 208, 169)',
    scheduled: 'lightblue'
  };
  var jsonKeys = {
    recommended_events: 'recommended',
    past_events: 'past',
    scheduled_events: 'scheduled'
  };

  var equals = function equals(x, y) {
    return x.map(function (d, i) {
      return moment(d).isSame(moment(y[i]));
    }).reduce(function (a, p) {
      return a && p;
    }, true);
  };

  var divElement = document.querySelector('.schedule-chart-inner');
  var divHeight = divElement ? divElement.offsetHeight : null;
  var height;

  if (divHeight && chartData && chartData[0] && chartData[0].selectedIrrigationTypes) {
    if (chartData[0].selectedIrrigationTypes.length) {
      var heightVal = chartData.length * chartData[0].selectedIrrigationTypes.length * 50;
      height = heightVal > divHeight ? heightVal : divHeight;
    } else height = divHeight;
  } else height = window.innerHeight / 1.6;

  var buildChartConfig = useCallback(function () {
    function getAlarmListUrl(blockId) {
      var params = {
        blockId: blockId,
        logLevel: 'error',
        active: true
      };
      var paramStr = new URLSearchParams(params).toString();
      return "/events?" + paramStr;
    }

    function getControlPageUrl(blockId) {
      var params = {
        blockId: blockId
      };
      var paramStr = new URLSearchParams(params).toString();
      return "/control?" + paramStr;
    }

    function renderAlarmButton(block) {
      return /*#__PURE__*/React.createElement("a", {
        href: getAlarmListUrl(block.blockId)
      }, /*#__PURE__*/React.createElement(Badge, {
        count: block.blockAlarmCount
      }, /*#__PURE__*/React.createElement(Button, {
        className: styles$4['bell-icon-button'],
        icon: /*#__PURE__*/React.createElement(BellFilled, {
          className: styles$4['bell-icon-size']
        })
      })));
    }

    function renderOverrideNotice(block) {
      if (!block.overrideActive) return null;
      return /*#__PURE__*/React.createElement("a", {
        className: styles$4['override-alert'],
        href: getControlPageUrl(block.blockId)
      }, t('Override Active'));
    }

    function renderCategory(seriesObj) {
      if (seriesObj.name.includes('<img')) {
        return seriesObj.name;
      }

      var links = ReactDOMServer.renderToStaticMarkup(renderOverrideNotice(seriesObj.current));
      var buttons = ReactDOMServer.renderToStaticMarkup( /*#__PURE__*/React.createElement("div", {
        className: styles$4['block-buttons']
      }, renderAlarmButton(seriesObj.current)));
      return seriesObj.name + "<br><span class=" + styles$4.capitalize + ">(" + seriesObj.current.irrigationType + ")</span><br>" + ("" + links + buttons);
    }

    var abbrDuration = function abbrDuration(duration) {
      var durationHr = duration.asHours();
      if (durationHr % 1 !== 0) return durationHr.toFixed(1) + " hrs";
      return durationHr.toFixed() + " hrs";
    };

    var abbrPeriod = function abbrPeriod(data) {
      var duration = moment.duration(moment(data[1]).diff(moment(data[0])));
      return abbrDuration(duration);
    };

    function configBuild(seriesData) {
      var chartConfig;

      if (chartData.length * chartData[0].selectedIrrigationTypes.length === seriesData.length) {
        var xaxisDateFormat;
        var dateDiff;

        if (dates) {
          dateDiff = dates[1].diff(dates[0], 'days');
          if (dateDiff > 1 && dateDiff < 7) xaxisDateFormat = '%e %b %Y';else if (dateDiff > 7 && dateDiff <= 14) xaxisDateFormat = '%e %b';else if (dateDiff > 14) xaxisDateFormat = '%e';else if (!dateDiff) xaxisDateFormat = '%H:%M';else xaxisDateFormat = '%e %b %Y';
        }

        var xaxisOption;

        if (dates) {
          xaxisOption = [{
            tickInterval: dateDiff ? 1000 * 60 * 60 * 24 : 3600 * 1000,
            type: 'datetime',
            min: dates[0].valueOf(),
            max: dates[1].valueOf(),
            currentDateIndicator: true,
            dateTimeLabelFormats: {
              day: xaxisDateFormat
            }
          }, {
            startOfWeek: 0
          }];
        } else {
          xaxisOption = [{
            currentDateIndicator: true,
            grid: {
              cellHeight: 60
            }
          }, {
            startOfWeek: 0
          }];
        }

        var numSeries = seriesData.length;
        var minHeight = numSeries * 100 + 120;
        var chartHeight = minHeight > height ? minHeight : height;
        chartConfig = {
          series: seriesData,
          chart: {
            marginLeft: 130,
            animation: false,
            height: height ? chartHeight + "px" : 'calc(100vh - 100%)',
            reflow: true,
            events: {
              click: function click(e) {
                if (global.chartYaxisCategories.length > 0) {
                  var clickObj = global.chartYaxisCategories[Math.abs(Math.round(e.yAxis[0].value))];

                  if (clickObj) {
                    clickObj.startDate = moment(e.xAxis[0].value);
                    clickObj.irrigationType = global.chartYaxisCategoryTypes[Math.abs(Math.round(e.yAxis[0].value))];
                    onClickChart(clickObj);
                  }
                }
              }
            }
          },
          plotOptions: {
            series: {
              animation: {
                defer: 0,
                duration: 0
              },
              dataLabels: {
                enabled: true,
                format: '{point.runtime}',
                style: {
                  cursor: 'default',
                  pointerEvents: 'none',
                  color: 'black'
                }
              },
              states: {
                select: {
                  color: '',
                  borderColor: 'none'
                }
              },
              allowPointSelect: true,
              point: {
                events: {
                  click: function click(e) {
                    onClickChart({
                      scheduleId: e.point.options.schId,
                      blockId: e.point.options.blkId
                    });

                    if (chartRef.current) {
                      var chart = chartRef.current.chart;
                      if (chart) chart.tooltip.hide();
                    }
                  }
                }
              }
            }
          },
          tooltip: {
            pointFormat: '<span>Run Time: {point.runtime}</span><br/>' + '<span>From: {point.start:%Y-%m-%d %H:%M:%S}</span><br/>' + '<span>To: {point.end:%Y-%m-%d %H:%M:%S}</span><br/>' + '<span>Total runtime (in view): {point.totalRuntime}</span><br/>'
          },
          xAxis: xaxisOption,
          yAxis: {
            type: 'category',
            labels: {
              useHTML: true,
              style: {
                width: '90px',
                minWidth: '90px',
                textAlign: 'center',
                paddingTop: '10px',
                paddingBottom: '10px'
              }
            },
            grid: {
              columns: [{
                title: {
                  text: t('Blocks')
                },
                categories: seriesData.map(function (s) {
                  return renderCategory(s);
                })
              }]
            }
          }
        };
      }

      return chartConfig;
    }

    var defaultOption;

    if (renderTrigger.current) {
      var seriesData = [];
      var count = 0;
      var yHeight = 0;
      global.chartYaxisCategories = [];
      global.chartYaxisCategoryTypes = [];
      chartData.forEach(function (block, i) {
        Object.keys(jsonKeys).forEach(function (irrgType, j) {
          var irrgEventArr = block[irrgType];
          var obj = {};
          obj.selectedDates = dates;
          obj.name = block.name ? block.name : "<img id=" + styles$4['loading-block'] + " src=" + image + " alt=\"Loading...\" />";

          if (block.selectedIrrigationTypes && block.selectedIrrigationTypes.includes(jsonKeys[irrgType])) {
            global.chartYaxisCategories.push({
              name: block.name,
              id: block.id,
              startDate: null
            });
            global.chartYaxisCategoryTypes.push(jsonKeys[irrgType]);
          }

          var pumpOverride = block.blockControlStatus && block.blockControlStatus.vfdStatus && block.blockControlStatus.vfdStatus.overrideStatus;
          var valveOverride = block.blockControlStatus && block.blockControlStatus.valveStatus && block.blockControlStatus.valveStatus.overrideStatus;
          var overrideActive = !!(valveOverride || pumpOverride);
          obj.current = {
            name: block.name,
            irrigationType: jsonKeys[irrgType],
            blockId: block.id,
            blockAlarmCount: block.blockControlStatus && block.blockControlStatus.valveStatus && block.blockControlStatus.valveStatus.blockAlarmCount,
            overrideActive: overrideActive
          };
          obj.current.irrigationType = jsonKeys[irrgType];

          if (irrgType === 'recommended_events') {
            obj.current.totalRuntime = block.totalRuntimeRecommended;
          } else if (irrgType === 'scheduled_events') {
            obj.current.totalRuntime = block.totalRuntimeScheduled;
          } else if (irrgType === 'past_events') {
            obj.current.totalRuntime = block.totalRuntimePast;
          }

          obj.data = [];

          if (!irrgEventArr || !irrgEventArr.length) {
            if (block.selectedIrrigationTypes && block.selectedIrrigationTypes.includes(jsonKeys[irrgType])) {
              obj.data.push({
                id: "block-" + count,
                y: yHeight
              });
              count += 1;
              seriesData.push(obj);
              yHeight += 1;
            }
          }

          if (irrgEventArr) {
            if (irrgEventArr.length > 0) {
              irrgEventArr.forEach(function (event) {
                obj.data.push({
                  id: "block-" + count,
                  schId: event.id,
                  blkId: block.id,
                  runtime: abbrPeriod([event.start_date, event.stop_date]),
                  flow: event.flow,
                  start: Date.parse(event.start_date),
                  end: Date.parse(event.stop_date),
                  y: yHeight,
                  color: colors[jsonKeys[irrgType]]
                });
                count += 1;
              });
              seriesData.push(obj);
              yHeight += 1;
            }
          }

          if (chartData.length === i + 1 && Object.keys(jsonKeys).length === j + 1) {
            setSeries(seriesData);
            defaultOption = configBuild(seriesData);
          }
        });
      });
    }

    return defaultOption;
  }, [chartData, t, jsonKeys, colors, dates, height, onClickChart]);
  useEffect(function () {
    if (!renderTrigger.current && (chartData.length * (chartData[0] && chartData[0].selectedIrrigationTypes || []).length !== series.length || series.length && !equals(series[0].selectedDates, dates))) {
      renderTrigger.current = true;
    }
  }, [chartData, dates, series]);
  useEffect(function () {
    if (renderTrigger.current && !configChange) {
      var config = buildChartConfig();

      if (config) {
        renderTrigger.current = false;
        setOptions(config);
      }
    }
  }, [buildChartConfig, configChange]);
  useEffect(function () {
    if (chartRef.current && divHeight && window.innerWidth > 599) {
      var width = window.innerWidth - 20;

      if (!sidebarCollapsed) {
        chartRef.current.chart.setSize(width - 320, divHeight, false);
      } else {
        chartRef.current.chart.setSize(width, divHeight, false);
      }
    }
  }, [divHeight, sidebarCollapsed]);
  useEffect(function () {
    var interval = setInterval(function () {
      if (chartRef.current && chartRef.current.chart) chartRef.current.chart.redraw();
    }, 60000);
    return function () {
      clearInterval(interval);
    };
  }, []);

  window.onresize = function onresize() {
    var chartContainer = document.querySelector('.schedule-chart-outer');

    if (chartContainer) {
      var width = chartContainer.clientWidth;
      var boxheight = chartContainer.clientHeight;

      if (chartRef.current) {
        var chart = chartRef.current.chart;

        if (boxheight > 400) {
          if (boxheight > height) chart.setSize(width, boxheight, false);else chart.setSize(width, height, false);
        }
      }
    }
  };

  return /*#__PURE__*/React.createElement("div", {
    className: styles$4['chart-div']
  }, options ? /*#__PURE__*/React.createElement(ChartComponent, {
    constructorType: "ganttChart",
    options: options,
    chartRef: chartRef
  }) : null, renderTrigger.current ? /*#__PURE__*/React.createElement(LoadingOutlined, {
    className: styles$4.loader
  }) : null);
};

HighchartsSchedule.propTypes = {
  chartData: PropTypes.arrayOf(PropTypes.any),
  dates: PropTypes.arrayOf(PropTypes.object),
  onClickChart: PropTypes.func,
  configChange: PropTypes.bool
};
HighchartsSchedule.defaultProps = {
  chartData: null,
  dates: null,
  onClickChart: function onClickChart() {
    return null;
  },
  configChange: null
};

var styles$5 = {"container":"_1Ma7E","left-half":"_1IjKl","right-half":"_30e5v","form-container":"_2UUpF","ant-btn":"_2pZwL","update-form-buttons":"_17gxE","error-text":"_2o2WZ"};

function InputNumberUnits(props) {
  var disabled = props.disabled,
      initialValue = props.initialValue,
      onChange = props.onChange,
      unitOptions = props.unitOptions;

  var _useState = useState(unitOptions && unitOptions[0] && unitOptions[0].key || ''),
      units = _useState[0],
      setUnits = _useState[1];

  var _useState2 = useState(initialValue || 0),
      value = _useState2[0],
      setValue = _useState2[1];

  function onUnitsChange(newUnits) {
    setUnits(newUnits);

    if (onChange) {
      onChange({
        value: value,
        units: newUnits
      });
    }
  }

  function onValueChange(newValue) {
    setValue(newValue);

    if (onChange) {
      onChange({
        value: newValue,
        units: units
      });
    }
  }

  return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("section", {
    className: styles$5.container
  }, /*#__PURE__*/React.createElement("div", {
    className: styles$5['left-half']
  }, /*#__PURE__*/React.createElement(InputNumber, {
    value: value,
    onChange: onValueChange,
    disabled: disabled,
    style: {
      width: '100%'
    }
  })), /*#__PURE__*/React.createElement("div", {
    className: styles$5['right-half']
  }, /*#__PURE__*/React.createElement(Select, {
    value: units,
    onChange: onUnitsChange,
    disabled: disabled
  }, unitOptions.map(function (option) {
    return /*#__PURE__*/React.createElement(Select.Option, {
      key: option.key,
      value: option.key
    }, option.name);
  })))));
}

InputNumberUnits.propTypes = {
  unitOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  disabled: PropTypes.bool,
  initialValue: PropTypes.number,
  onChange: PropTypes.func
};
InputNumberUnits.defaultProps = {
  disabled: false,
  initialValue: 0,
  onChange: null
};

var timeUnits = [{
  key: 'minutes',
  name: 'Minutes'
}, {
  key: 'hours',
  name: 'Hours'
}];

function InputTime(props) {
  var disabled = props.disabled,
      initialValue = props.initialValue,
      onChange = props.onChange;
  return /*#__PURE__*/React.createElement(InputNumberUnits, {
    unitOptions: timeUnits,
    onChange: onChange,
    disabled: disabled,
    initialValue: initialValue
  });
}

InputTime.propTypes = {
  disabled: PropTypes.bool,
  initialValue: PropTypes.number,
  onChange: PropTypes.func
};
InputTime.defaultProps = {
  onChange: null,
  disabled: false,
  initialValue: 0
};

var volumeUnits = [{
  key: 'acrein',
  name: 'acre inches'
}, {
  key: 'cuft',
  name: 'cubic feet'
}];

function InputVolume(props) {
  var disabled = props.disabled,
      initialValue = props.initialValue,
      onChange = props.onChange;
  return /*#__PURE__*/React.createElement(InputNumberUnits, {
    unitOptions: volumeUnits,
    onChange: onChange,
    disabled: disabled,
    initialValue: initialValue
  });
}

InputVolume.propTypes = {
  disabled: PropTypes.bool,
  initialValue: PropTypes.number,
  onChange: PropTypes.func
};
InputVolume.defaultProps = {
  disabled: false,
  initialValue: 0,
  onChange: null
};

function MapSettingsMenu(_ref) {
  var renderItemShowLabels = _ref.renderItemShowLabels,
      renderItemPresentationMode = _ref.renderItemPresentationMode,
      onChangePresentationMode = _ref.onChangePresentationMode,
      showLabels = _ref.showLabels,
      toggleShowLabels = _ref.toggleShowLabels;
  return /*#__PURE__*/React.createElement(Menu, {
    className: "settings-menu",
    selectable: false
  }, renderItemPresentationMode && /*#__PURE__*/React.createElement(Menu.Item, {
    key: "1"
  }, /*#__PURE__*/React.createElement(Select, {
    defaultValue: "connectivity",
    style: {
      width: 140
    },
    onChange: onChangePresentationMode,
    "data-test-id": "presentationmode-select"
  }, /*#__PURE__*/React.createElement(Select.Option, {
    value: "connectivity",
    className: "presentationmode-option-connectivity"
  }, "Connectivity"), /*#__PURE__*/React.createElement(Select.Option, {
    value: "waterpressure",
    className: "presentationmode-option-waterpressure"
  }, "Water Pressure"), /*#__PURE__*/React.createElement(Select.Option, {
    value: "provision",
    className: "presentationmode-option-provision"
  }, "Provision"))), renderItemShowLabels && /*#__PURE__*/React.createElement(Menu.Item, {
    key: "2"
  }, /*#__PURE__*/React.createElement("span", null, "Show Labels:"), /*#__PURE__*/React.createElement(Switch, {
    checked: showLabels,
    onChange: toggleShowLabels,
    className: "switch-toggle-labels"
  })));
}
MapSettingsMenu.propTypes = {
  renderItemShowLabels: PropTypes.bool.isRequired,
  renderItemPresentationMode: PropTypes.bool.isRequired,
  onChangePresentationMode: PropTypes.func.isRequired,
  showLabels: PropTypes.bool.isRequired,
  toggleShowLabels: PropTypes.func.isRequired
};

function listCacheClear() {
  this.__data__ = [];
  this.size = 0;
}

var _listCacheClear = listCacheClear;

function eq(value, other) {
  return value === other || value !== value && other !== other;
}

var eq_1 = eq;

function assocIndexOf(array, key) {
  var length = array.length;

  while (length--) {
    if (eq_1(array[length][0], key)) {
      return length;
    }
  }

  return -1;
}

var _assocIndexOf = assocIndexOf;

var arrayProto = Array.prototype;
var splice = arrayProto.splice;

function listCacheDelete(key) {
  var data = this.__data__,
      index = _assocIndexOf(data, key);

  if (index < 0) {
    return false;
  }

  var lastIndex = data.length - 1;

  if (index == lastIndex) {
    data.pop();
  } else {
    splice.call(data, index, 1);
  }

  --this.size;
  return true;
}

var _listCacheDelete = listCacheDelete;

function listCacheGet(key) {
  var data = this.__data__,
      index = _assocIndexOf(data, key);
  return index < 0 ? undefined : data[index][1];
}

var _listCacheGet = listCacheGet;

function listCacheHas(key) {
  return _assocIndexOf(this.__data__, key) > -1;
}

var _listCacheHas = listCacheHas;

function listCacheSet(key, value) {
  var data = this.__data__,
      index = _assocIndexOf(data, key);

  if (index < 0) {
    ++this.size;
    data.push([key, value]);
  } else {
    data[index][1] = value;
  }

  return this;
}

var _listCacheSet = listCacheSet;

function ListCache(entries) {
  var index = -1,
      length = entries == null ? 0 : entries.length;
  this.clear();

  while (++index < length) {
    var entry = entries[index];
    this.set(entry[0], entry[1]);
  }
}

ListCache.prototype.clear = _listCacheClear;
ListCache.prototype['delete'] = _listCacheDelete;
ListCache.prototype.get = _listCacheGet;
ListCache.prototype.has = _listCacheHas;
ListCache.prototype.set = _listCacheSet;
var _ListCache = ListCache;

function stackClear() {
  this.__data__ = new _ListCache();
  this.size = 0;
}

var _stackClear = stackClear;

function stackDelete(key) {
  var data = this.__data__,
      result = data['delete'](key);
  this.size = data.size;
  return result;
}

var _stackDelete = stackDelete;

function stackGet(key) {
  return this.__data__.get(key);
}

var _stackGet = stackGet;

function stackHas(key) {
  return this.__data__.has(key);
}

var _stackHas = stackHas;

function isObject(value) {
  var type = typeof value;
  return value != null && (type == 'object' || type == 'function');
}

var isObject_1 = isObject;

var asyncTag = '[object AsyncFunction]',
    funcTag = '[object Function]',
    genTag = '[object GeneratorFunction]',
    proxyTag = '[object Proxy]';

function isFunction(value) {
  if (!isObject_1(value)) {
    return false;
  }

  var tag = _baseGetTag(value);
  return tag == funcTag || tag == genTag || tag == asyncTag || tag == proxyTag;
}

var isFunction_1 = isFunction;

var coreJsData = _root['__core-js_shared__'];
var _coreJsData = coreJsData;

var maskSrcKey = function () {
  var uid = /[^.]+$/.exec(_coreJsData && _coreJsData.keys && _coreJsData.keys.IE_PROTO || '');
  return uid ? 'Symbol(src)_1.' + uid : '';
}();

function isMasked(func) {
  return !!maskSrcKey && maskSrcKey in func;
}

var _isMasked = isMasked;

var funcProto = Function.prototype;
var funcToString = funcProto.toString;

function toSource(func) {
  if (func != null) {
    try {
      return funcToString.call(func);
    } catch (e) {}

    try {
      return func + '';
    } catch (e) {}
  }

  return '';
}

var _toSource = toSource;

var reRegExpChar = /[\\^$.*+?()[\]{}|]/g;
var reIsHostCtor = /^\[object .+?Constructor\]$/;
var funcProto$1 = Function.prototype,
    objectProto$2 = Object.prototype;
var funcToString$1 = funcProto$1.toString;
var hasOwnProperty$1 = objectProto$2.hasOwnProperty;
var reIsNative = RegExp('^' + funcToString$1.call(hasOwnProperty$1).replace(reRegExpChar, '\\$&').replace(/hasOwnProperty|(function).*?(?=\\\()| for .+?(?=\\\])/g, '$1.*?') + '$');

function baseIsNative(value) {
  if (!isObject_1(value) || _isMasked(value)) {
    return false;
  }

  var pattern = isFunction_1(value) ? reIsNative : reIsHostCtor;
  return pattern.test(_toSource(value));
}

var _baseIsNative = baseIsNative;

function getValue(object, key) {
  return object == null ? undefined : object[key];
}

var _getValue = getValue;

function getNative(object, key) {
  var value = _getValue(object, key);
  return _baseIsNative(value) ? value : undefined;
}

var _getNative = getNative;

var Map = _getNative(_root, 'Map');
var _Map = Map;

var nativeCreate = _getNative(Object, 'create');
var _nativeCreate = nativeCreate;

function hashClear() {
  this.__data__ = _nativeCreate ? _nativeCreate(null) : {};
  this.size = 0;
}

var _hashClear = hashClear;

function hashDelete(key) {
  var result = this.has(key) && delete this.__data__[key];
  this.size -= result ? 1 : 0;
  return result;
}

var _hashDelete = hashDelete;

var HASH_UNDEFINED = '__lodash_hash_undefined__';
var objectProto$3 = Object.prototype;
var hasOwnProperty$2 = objectProto$3.hasOwnProperty;

function hashGet(key) {
  var data = this.__data__;

  if (_nativeCreate) {
    var result = data[key];
    return result === HASH_UNDEFINED ? undefined : result;
  }

  return hasOwnProperty$2.call(data, key) ? data[key] : undefined;
}

var _hashGet = hashGet;

var objectProto$4 = Object.prototype;
var hasOwnProperty$3 = objectProto$4.hasOwnProperty;

function hashHas(key) {
  var data = this.__data__;
  return _nativeCreate ? data[key] !== undefined : hasOwnProperty$3.call(data, key);
}

var _hashHas = hashHas;

var HASH_UNDEFINED$1 = '__lodash_hash_undefined__';

function hashSet(key, value) {
  var data = this.__data__;
  this.size += this.has(key) ? 0 : 1;
  data[key] = _nativeCreate && value === undefined ? HASH_UNDEFINED$1 : value;
  return this;
}

var _hashSet = hashSet;

function Hash(entries) {
  var index = -1,
      length = entries == null ? 0 : entries.length;
  this.clear();

  while (++index < length) {
    var entry = entries[index];
    this.set(entry[0], entry[1]);
  }
}

Hash.prototype.clear = _hashClear;
Hash.prototype['delete'] = _hashDelete;
Hash.prototype.get = _hashGet;
Hash.prototype.has = _hashHas;
Hash.prototype.set = _hashSet;
var _Hash = Hash;

function mapCacheClear() {
  this.size = 0;
  this.__data__ = {
    'hash': new _Hash(),
    'map': new (_Map || _ListCache)(),
    'string': new _Hash()
  };
}

var _mapCacheClear = mapCacheClear;

function isKeyable(value) {
  var type = typeof value;
  return type == 'string' || type == 'number' || type == 'symbol' || type == 'boolean' ? value !== '__proto__' : value === null;
}

var _isKeyable = isKeyable;

function getMapData(map, key) {
  var data = map.__data__;
  return _isKeyable(key) ? data[typeof key == 'string' ? 'string' : 'hash'] : data.map;
}

var _getMapData = getMapData;

function mapCacheDelete(key) {
  var result = _getMapData(this, key)['delete'](key);
  this.size -= result ? 1 : 0;
  return result;
}

var _mapCacheDelete = mapCacheDelete;

function mapCacheGet(key) {
  return _getMapData(this, key).get(key);
}

var _mapCacheGet = mapCacheGet;

function mapCacheHas(key) {
  return _getMapData(this, key).has(key);
}

var _mapCacheHas = mapCacheHas;

function mapCacheSet(key, value) {
  var data = _getMapData(this, key),
      size = data.size;
  data.set(key, value);
  this.size += data.size == size ? 0 : 1;
  return this;
}

var _mapCacheSet = mapCacheSet;

function MapCache(entries) {
  var index = -1,
      length = entries == null ? 0 : entries.length;
  this.clear();

  while (++index < length) {
    var entry = entries[index];
    this.set(entry[0], entry[1]);
  }
}

MapCache.prototype.clear = _mapCacheClear;
MapCache.prototype['delete'] = _mapCacheDelete;
MapCache.prototype.get = _mapCacheGet;
MapCache.prototype.has = _mapCacheHas;
MapCache.prototype.set = _mapCacheSet;
var _MapCache = MapCache;

var LARGE_ARRAY_SIZE = 200;

function stackSet(key, value) {
  var data = this.__data__;

  if (data instanceof _ListCache) {
    var pairs = data.__data__;

    if (!_Map || pairs.length < LARGE_ARRAY_SIZE - 1) {
      pairs.push([key, value]);
      this.size = ++data.size;
      return this;
    }

    data = this.__data__ = new _MapCache(pairs);
  }

  data.set(key, value);
  this.size = data.size;
  return this;
}

var _stackSet = stackSet;

function Stack(entries) {
  var data = this.__data__ = new _ListCache(entries);
  this.size = data.size;
}

Stack.prototype.clear = _stackClear;
Stack.prototype['delete'] = _stackDelete;
Stack.prototype.get = _stackGet;
Stack.prototype.has = _stackHas;
Stack.prototype.set = _stackSet;
var _Stack = Stack;

function arrayEach(array, iteratee) {
  var index = -1,
      length = array == null ? 0 : array.length;

  while (++index < length) {
    if (iteratee(array[index], index, array) === false) {
      break;
    }
  }

  return array;
}

var _arrayEach = arrayEach;

var defineProperty = function () {
  try {
    var func = _getNative(Object, 'defineProperty');
    func({}, '', {});
    return func;
  } catch (e) {}
}();

var _defineProperty = defineProperty;

function baseAssignValue(object, key, value) {
  if (key == '__proto__' && _defineProperty) {
    _defineProperty(object, key, {
      'configurable': true,
      'enumerable': true,
      'value': value,
      'writable': true
    });
  } else {
    object[key] = value;
  }
}

var _baseAssignValue = baseAssignValue;

var objectProto$5 = Object.prototype;
var hasOwnProperty$4 = objectProto$5.hasOwnProperty;

function assignValue(object, key, value) {
  var objValue = object[key];

  if (!(hasOwnProperty$4.call(object, key) && eq_1(objValue, value)) || value === undefined && !(key in object)) {
    _baseAssignValue(object, key, value);
  }
}

var _assignValue = assignValue;

function copyObject(source, props, object, customizer) {
  var isNew = !object;
  object || (object = {});
  var index = -1,
      length = props.length;

  while (++index < length) {
    var key = props[index];
    var newValue = customizer ? customizer(object[key], source[key], key, object, source) : undefined;

    if (newValue === undefined) {
      newValue = source[key];
    }

    if (isNew) {
      _baseAssignValue(object, key, newValue);
    } else {
      _assignValue(object, key, newValue);
    }
  }

  return object;
}

var _copyObject = copyObject;

function baseTimes(n, iteratee) {
  var index = -1,
      result = Array(n);

  while (++index < n) {
    result[index] = iteratee(index);
  }

  return result;
}

var _baseTimes = baseTimes;

var argsTag = '[object Arguments]';

function baseIsArguments(value) {
  return isObjectLike_1(value) && _baseGetTag(value) == argsTag;
}

var _baseIsArguments = baseIsArguments;

var objectProto$6 = Object.prototype;
var hasOwnProperty$5 = objectProto$6.hasOwnProperty;
var propertyIsEnumerable = objectProto$6.propertyIsEnumerable;
var isArguments = _baseIsArguments(function () {
  return arguments;
}()) ? _baseIsArguments : function (value) {
  return isObjectLike_1(value) && hasOwnProperty$5.call(value, 'callee') && !propertyIsEnumerable.call(value, 'callee');
};
var isArguments_1 = isArguments;

var isArray = Array.isArray;
var isArray_1 = isArray;

function stubFalse() {
  return false;
}

var stubFalse_1 = stubFalse;

var isBuffer_1 = createCommonjsModule(function (module, exports) {
  var freeExports =  exports && !exports.nodeType && exports;
  var freeModule = freeExports && 'object' == 'object' && module && !module.nodeType && module;
  var moduleExports = freeModule && freeModule.exports === freeExports;
  var Buffer = moduleExports ? _root.Buffer : undefined;
  var nativeIsBuffer = Buffer ? Buffer.isBuffer : undefined;
  var isBuffer = nativeIsBuffer || stubFalse_1;
  module.exports = isBuffer;
});

var MAX_SAFE_INTEGER = 9007199254740991;
var reIsUint = /^(?:0|[1-9]\d*)$/;

function isIndex(value, length) {
  var type = typeof value;
  length = length == null ? MAX_SAFE_INTEGER : length;
  return !!length && (type == 'number' || type != 'symbol' && reIsUint.test(value)) && value > -1 && value % 1 == 0 && value < length;
}

var _isIndex = isIndex;

var MAX_SAFE_INTEGER$1 = 9007199254740991;

function isLength(value) {
  return typeof value == 'number' && value > -1 && value % 1 == 0 && value <= MAX_SAFE_INTEGER$1;
}

var isLength_1 = isLength;

var argsTag$1 = '[object Arguments]',
    arrayTag = '[object Array]',
    boolTag = '[object Boolean]',
    dateTag = '[object Date]',
    errorTag = '[object Error]',
    funcTag$1 = '[object Function]',
    mapTag = '[object Map]',
    numberTag$1 = '[object Number]',
    objectTag = '[object Object]',
    regexpTag = '[object RegExp]',
    setTag = '[object Set]',
    stringTag = '[object String]',
    weakMapTag = '[object WeakMap]';
var arrayBufferTag = '[object ArrayBuffer]',
    dataViewTag = '[object DataView]',
    float32Tag = '[object Float32Array]',
    float64Tag = '[object Float64Array]',
    int8Tag = '[object Int8Array]',
    int16Tag = '[object Int16Array]',
    int32Tag = '[object Int32Array]',
    uint8Tag = '[object Uint8Array]',
    uint8ClampedTag = '[object Uint8ClampedArray]',
    uint16Tag = '[object Uint16Array]',
    uint32Tag = '[object Uint32Array]';
var typedArrayTags = {};
typedArrayTags[float32Tag] = typedArrayTags[float64Tag] = typedArrayTags[int8Tag] = typedArrayTags[int16Tag] = typedArrayTags[int32Tag] = typedArrayTags[uint8Tag] = typedArrayTags[uint8ClampedTag] = typedArrayTags[uint16Tag] = typedArrayTags[uint32Tag] = true;
typedArrayTags[argsTag$1] = typedArrayTags[arrayTag] = typedArrayTags[arrayBufferTag] = typedArrayTags[boolTag] = typedArrayTags[dataViewTag] = typedArrayTags[dateTag] = typedArrayTags[errorTag] = typedArrayTags[funcTag$1] = typedArrayTags[mapTag] = typedArrayTags[numberTag$1] = typedArrayTags[objectTag] = typedArrayTags[regexpTag] = typedArrayTags[setTag] = typedArrayTags[stringTag] = typedArrayTags[weakMapTag] = false;

function baseIsTypedArray(value) {
  return isObjectLike_1(value) && isLength_1(value.length) && !!typedArrayTags[_baseGetTag(value)];
}

var _baseIsTypedArray = baseIsTypedArray;

function baseUnary(func) {
  return function (value) {
    return func(value);
  };
}

var _baseUnary = baseUnary;

var _nodeUtil = createCommonjsModule(function (module, exports) {
  var freeExports =  exports && !exports.nodeType && exports;
  var freeModule = freeExports && 'object' == 'object' && module && !module.nodeType && module;
  var moduleExports = freeModule && freeModule.exports === freeExports;
  var freeProcess = moduleExports && _freeGlobal.process;

  var nodeUtil = function () {
    try {
      var types = freeModule && freeModule.require && freeModule.require('util').types;

      if (types) {
        return types;
      }

      return freeProcess && freeProcess.binding && freeProcess.binding('util');
    } catch (e) {}
  }();

  module.exports = nodeUtil;
});

var nodeIsTypedArray = _nodeUtil && _nodeUtil.isTypedArray;
var isTypedArray = nodeIsTypedArray ? _baseUnary(nodeIsTypedArray) : _baseIsTypedArray;
var isTypedArray_1 = isTypedArray;

var objectProto$7 = Object.prototype;
var hasOwnProperty$6 = objectProto$7.hasOwnProperty;

function arrayLikeKeys(value, inherited) {
  var isArr = isArray_1(value),
      isArg = !isArr && isArguments_1(value),
      isBuff = !isArr && !isArg && isBuffer_1(value),
      isType = !isArr && !isArg && !isBuff && isTypedArray_1(value),
      skipIndexes = isArr || isArg || isBuff || isType,
      result = skipIndexes ? _baseTimes(value.length, String) : [],
      length = result.length;

  for (var key in value) {
    if ((inherited || hasOwnProperty$6.call(value, key)) && !(skipIndexes && (key == 'length' || isBuff && (key == 'offset' || key == 'parent') || isType && (key == 'buffer' || key == 'byteLength' || key == 'byteOffset') || _isIndex(key, length)))) {
      result.push(key);
    }
  }

  return result;
}

var _arrayLikeKeys = arrayLikeKeys;

var objectProto$8 = Object.prototype;

function isPrototype(value) {
  var Ctor = value && value.constructor,
      proto = typeof Ctor == 'function' && Ctor.prototype || objectProto$8;
  return value === proto;
}

var _isPrototype = isPrototype;

function overArg(func, transform) {
  return function (arg) {
    return func(transform(arg));
  };
}

var _overArg = overArg;

var nativeKeys = _overArg(Object.keys, Object);
var _nativeKeys = nativeKeys;

var objectProto$9 = Object.prototype;
var hasOwnProperty$7 = objectProto$9.hasOwnProperty;

function baseKeys(object) {
  if (!_isPrototype(object)) {
    return _nativeKeys(object);
  }

  var result = [];

  for (var key in Object(object)) {
    if (hasOwnProperty$7.call(object, key) && key != 'constructor') {
      result.push(key);
    }
  }

  return result;
}

var _baseKeys = baseKeys;

function isArrayLike(value) {
  return value != null && isLength_1(value.length) && !isFunction_1(value);
}

var isArrayLike_1 = isArrayLike;

function keys(object) {
  return isArrayLike_1(object) ? _arrayLikeKeys(object) : _baseKeys(object);
}

var keys_1 = keys;

function baseAssign(object, source) {
  return object && _copyObject(source, keys_1(source), object);
}

var _baseAssign = baseAssign;

function nativeKeysIn(object) {
  var result = [];

  if (object != null) {
    for (var key in Object(object)) {
      result.push(key);
    }
  }

  return result;
}

var _nativeKeysIn = nativeKeysIn;

var objectProto$a = Object.prototype;
var hasOwnProperty$8 = objectProto$a.hasOwnProperty;

function baseKeysIn(object) {
  if (!isObject_1(object)) {
    return _nativeKeysIn(object);
  }

  var isProto = _isPrototype(object),
      result = [];

  for (var key in object) {
    if (!(key == 'constructor' && (isProto || !hasOwnProperty$8.call(object, key)))) {
      result.push(key);
    }
  }

  return result;
}

var _baseKeysIn = baseKeysIn;

function keysIn$1(object) {
  return isArrayLike_1(object) ? _arrayLikeKeys(object, true) : _baseKeysIn(object);
}

var keysIn_1 = keysIn$1;

function baseAssignIn(object, source) {
  return object && _copyObject(source, keysIn_1(source), object);
}

var _baseAssignIn = baseAssignIn;

var _cloneBuffer = createCommonjsModule(function (module, exports) {
  var freeExports =  exports && !exports.nodeType && exports;
  var freeModule = freeExports && 'object' == 'object' && module && !module.nodeType && module;
  var moduleExports = freeModule && freeModule.exports === freeExports;
  var Buffer = moduleExports ? _root.Buffer : undefined,
      allocUnsafe = Buffer ? Buffer.allocUnsafe : undefined;

  function cloneBuffer(buffer, isDeep) {
    if (isDeep) {
      return buffer.slice();
    }

    var length = buffer.length,
        result = allocUnsafe ? allocUnsafe(length) : new buffer.constructor(length);
    buffer.copy(result);
    return result;
  }

  module.exports = cloneBuffer;
});

function copyArray(source, array) {
  var index = -1,
      length = source.length;
  array || (array = Array(length));

  while (++index < length) {
    array[index] = source[index];
  }

  return array;
}

var _copyArray = copyArray;

function arrayFilter(array, predicate) {
  var index = -1,
      length = array == null ? 0 : array.length,
      resIndex = 0,
      result = [];

  while (++index < length) {
    var value = array[index];

    if (predicate(value, index, array)) {
      result[resIndex++] = value;
    }
  }

  return result;
}

var _arrayFilter = arrayFilter;

function stubArray() {
  return [];
}

var stubArray_1 = stubArray;

var objectProto$b = Object.prototype;
var propertyIsEnumerable$1 = objectProto$b.propertyIsEnumerable;
var nativeGetSymbols = Object.getOwnPropertySymbols;
var getSymbols = !nativeGetSymbols ? stubArray_1 : function (object) {
  if (object == null) {
    return [];
  }

  object = Object(object);
  return _arrayFilter(nativeGetSymbols(object), function (symbol) {
    return propertyIsEnumerable$1.call(object, symbol);
  });
};
var _getSymbols = getSymbols;

function copySymbols(source, object) {
  return _copyObject(source, _getSymbols(source), object);
}

var _copySymbols = copySymbols;

function arrayPush(array, values) {
  var index = -1,
      length = values.length,
      offset = array.length;

  while (++index < length) {
    array[offset + index] = values[index];
  }

  return array;
}

var _arrayPush = arrayPush;

var getPrototype = _overArg(Object.getPrototypeOf, Object);
var _getPrototype = getPrototype;

var nativeGetSymbols$1 = Object.getOwnPropertySymbols;
var getSymbolsIn = !nativeGetSymbols$1 ? stubArray_1 : function (object) {
  var result = [];

  while (object) {
    _arrayPush(result, _getSymbols(object));
    object = _getPrototype(object);
  }

  return result;
};
var _getSymbolsIn = getSymbolsIn;

function copySymbolsIn(source, object) {
  return _copyObject(source, _getSymbolsIn(source), object);
}

var _copySymbolsIn = copySymbolsIn;

function baseGetAllKeys(object, keysFunc, symbolsFunc) {
  var result = keysFunc(object);
  return isArray_1(object) ? result : _arrayPush(result, symbolsFunc(object));
}

var _baseGetAllKeys = baseGetAllKeys;

function getAllKeys(object) {
  return _baseGetAllKeys(object, keys_1, _getSymbols);
}

var _getAllKeys = getAllKeys;

function getAllKeysIn(object) {
  return _baseGetAllKeys(object, keysIn_1, _getSymbolsIn);
}

var _getAllKeysIn = getAllKeysIn;

var DataView = _getNative(_root, 'DataView');
var _DataView = DataView;

var Promise$1 = _getNative(_root, 'Promise');
var _Promise = Promise$1;

var Set$1 = _getNative(_root, 'Set');
var _Set = Set$1;

var WeakMap = _getNative(_root, 'WeakMap');
var _WeakMap = WeakMap;

var mapTag$1 = '[object Map]',
    objectTag$1 = '[object Object]',
    promiseTag = '[object Promise]',
    setTag$1 = '[object Set]',
    weakMapTag$1 = '[object WeakMap]';
var dataViewTag$1 = '[object DataView]';
var dataViewCtorString = _toSource(_DataView),
    mapCtorString = _toSource(_Map),
    promiseCtorString = _toSource(_Promise),
    setCtorString = _toSource(_Set),
    weakMapCtorString = _toSource(_WeakMap);
var getTag = _baseGetTag;

if (_DataView && getTag(new _DataView(new ArrayBuffer(1))) != dataViewTag$1 || _Map && getTag(new _Map()) != mapTag$1 || _Promise && getTag(_Promise.resolve()) != promiseTag || _Set && getTag(new _Set()) != setTag$1 || _WeakMap && getTag(new _WeakMap()) != weakMapTag$1) {
  getTag = function getTag(value) {
    var result = _baseGetTag(value),
        Ctor = result == objectTag$1 ? value.constructor : undefined,
        ctorString = Ctor ? _toSource(Ctor) : '';

    if (ctorString) {
      switch (ctorString) {
        case dataViewCtorString:
          return dataViewTag$1;

        case mapCtorString:
          return mapTag$1;

        case promiseCtorString:
          return promiseTag;

        case setCtorString:
          return setTag$1;

        case weakMapCtorString:
          return weakMapTag$1;
      }
    }

    return result;
  };
}

var _getTag = getTag;

var objectProto$c = Object.prototype;
var hasOwnProperty$9 = objectProto$c.hasOwnProperty;

function initCloneArray(array) {
  var length = array.length,
      result = new array.constructor(length);

  if (length && typeof array[0] == 'string' && hasOwnProperty$9.call(array, 'index')) {
    result.index = array.index;
    result.input = array.input;
  }

  return result;
}

var _initCloneArray = initCloneArray;

var Uint8Array = _root.Uint8Array;
var _Uint8Array = Uint8Array;

function cloneArrayBuffer(arrayBuffer) {
  var result = new arrayBuffer.constructor(arrayBuffer.byteLength);
  new _Uint8Array(result).set(new _Uint8Array(arrayBuffer));
  return result;
}

var _cloneArrayBuffer = cloneArrayBuffer;

function cloneDataView(dataView, isDeep) {
  var buffer = isDeep ? _cloneArrayBuffer(dataView.buffer) : dataView.buffer;
  return new dataView.constructor(buffer, dataView.byteOffset, dataView.byteLength);
}

var _cloneDataView = cloneDataView;

var reFlags = /\w*$/;

function cloneRegExp(regexp) {
  var result = new regexp.constructor(regexp.source, reFlags.exec(regexp));
  result.lastIndex = regexp.lastIndex;
  return result;
}

var _cloneRegExp = cloneRegExp;

var symbolProto = _Symbol ? _Symbol.prototype : undefined,
    symbolValueOf = symbolProto ? symbolProto.valueOf : undefined;

function cloneSymbol(symbol) {
  return symbolValueOf ? Object(symbolValueOf.call(symbol)) : {};
}

var _cloneSymbol = cloneSymbol;

function cloneTypedArray(typedArray, isDeep) {
  var buffer = isDeep ? _cloneArrayBuffer(typedArray.buffer) : typedArray.buffer;
  return new typedArray.constructor(buffer, typedArray.byteOffset, typedArray.length);
}

var _cloneTypedArray = cloneTypedArray;

var boolTag$1 = '[object Boolean]',
    dateTag$1 = '[object Date]',
    mapTag$2 = '[object Map]',
    numberTag$2 = '[object Number]',
    regexpTag$1 = '[object RegExp]',
    setTag$2 = '[object Set]',
    stringTag$1 = '[object String]',
    symbolTag = '[object Symbol]';
var arrayBufferTag$1 = '[object ArrayBuffer]',
    dataViewTag$2 = '[object DataView]',
    float32Tag$1 = '[object Float32Array]',
    float64Tag$1 = '[object Float64Array]',
    int8Tag$1 = '[object Int8Array]',
    int16Tag$1 = '[object Int16Array]',
    int32Tag$1 = '[object Int32Array]',
    uint8Tag$1 = '[object Uint8Array]',
    uint8ClampedTag$1 = '[object Uint8ClampedArray]',
    uint16Tag$1 = '[object Uint16Array]',
    uint32Tag$1 = '[object Uint32Array]';

function initCloneByTag(object, tag, isDeep) {
  var Ctor = object.constructor;

  switch (tag) {
    case arrayBufferTag$1:
      return _cloneArrayBuffer(object);

    case boolTag$1:
    case dateTag$1:
      return new Ctor(+object);

    case dataViewTag$2:
      return _cloneDataView(object, isDeep);

    case float32Tag$1:
    case float64Tag$1:
    case int8Tag$1:
    case int16Tag$1:
    case int32Tag$1:
    case uint8Tag$1:
    case uint8ClampedTag$1:
    case uint16Tag$1:
    case uint32Tag$1:
      return _cloneTypedArray(object, isDeep);

    case mapTag$2:
      return new Ctor();

    case numberTag$2:
    case stringTag$1:
      return new Ctor(object);

    case regexpTag$1:
      return _cloneRegExp(object);

    case setTag$2:
      return new Ctor();

    case symbolTag:
      return _cloneSymbol(object);
  }
}

var _initCloneByTag = initCloneByTag;

var objectCreate = Object.create;

var baseCreate = function () {
  function object() {}

  return function (proto) {
    if (!isObject_1(proto)) {
      return {};
    }

    if (objectCreate) {
      return objectCreate(proto);
    }

    object.prototype = proto;
    var result = new object();
    object.prototype = undefined;
    return result;
  };
}();

var _baseCreate = baseCreate;

function initCloneObject(object) {
  return typeof object.constructor == 'function' && !_isPrototype(object) ? _baseCreate(_getPrototype(object)) : {};
}

var _initCloneObject = initCloneObject;

var mapTag$3 = '[object Map]';

function baseIsMap(value) {
  return isObjectLike_1(value) && _getTag(value) == mapTag$3;
}

var _baseIsMap = baseIsMap;

var nodeIsMap = _nodeUtil && _nodeUtil.isMap;
var isMap = nodeIsMap ? _baseUnary(nodeIsMap) : _baseIsMap;
var isMap_1 = isMap;

var setTag$3 = '[object Set]';

function baseIsSet(value) {
  return isObjectLike_1(value) && _getTag(value) == setTag$3;
}

var _baseIsSet = baseIsSet;

var nodeIsSet = _nodeUtil && _nodeUtil.isSet;
var isSet = nodeIsSet ? _baseUnary(nodeIsSet) : _baseIsSet;
var isSet_1 = isSet;

var CLONE_DEEP_FLAG = 1,
    CLONE_FLAT_FLAG = 2,
    CLONE_SYMBOLS_FLAG = 4;
var argsTag$2 = '[object Arguments]',
    arrayTag$1 = '[object Array]',
    boolTag$2 = '[object Boolean]',
    dateTag$2 = '[object Date]',
    errorTag$1 = '[object Error]',
    funcTag$2 = '[object Function]',
    genTag$1 = '[object GeneratorFunction]',
    mapTag$4 = '[object Map]',
    numberTag$3 = '[object Number]',
    objectTag$2 = '[object Object]',
    regexpTag$2 = '[object RegExp]',
    setTag$4 = '[object Set]',
    stringTag$2 = '[object String]',
    symbolTag$1 = '[object Symbol]',
    weakMapTag$2 = '[object WeakMap]';
var arrayBufferTag$2 = '[object ArrayBuffer]',
    dataViewTag$3 = '[object DataView]',
    float32Tag$2 = '[object Float32Array]',
    float64Tag$2 = '[object Float64Array]',
    int8Tag$2 = '[object Int8Array]',
    int16Tag$2 = '[object Int16Array]',
    int32Tag$2 = '[object Int32Array]',
    uint8Tag$2 = '[object Uint8Array]',
    uint8ClampedTag$2 = '[object Uint8ClampedArray]',
    uint16Tag$2 = '[object Uint16Array]',
    uint32Tag$2 = '[object Uint32Array]';
var cloneableTags = {};
cloneableTags[argsTag$2] = cloneableTags[arrayTag$1] = cloneableTags[arrayBufferTag$2] = cloneableTags[dataViewTag$3] = cloneableTags[boolTag$2] = cloneableTags[dateTag$2] = cloneableTags[float32Tag$2] = cloneableTags[float64Tag$2] = cloneableTags[int8Tag$2] = cloneableTags[int16Tag$2] = cloneableTags[int32Tag$2] = cloneableTags[mapTag$4] = cloneableTags[numberTag$3] = cloneableTags[objectTag$2] = cloneableTags[regexpTag$2] = cloneableTags[setTag$4] = cloneableTags[stringTag$2] = cloneableTags[symbolTag$1] = cloneableTags[uint8Tag$2] = cloneableTags[uint8ClampedTag$2] = cloneableTags[uint16Tag$2] = cloneableTags[uint32Tag$2] = true;
cloneableTags[errorTag$1] = cloneableTags[funcTag$2] = cloneableTags[weakMapTag$2] = false;

function baseClone(value, bitmask, customizer, key, object, stack) {
  var result,
      isDeep = bitmask & CLONE_DEEP_FLAG,
      isFlat = bitmask & CLONE_FLAT_FLAG,
      isFull = bitmask & CLONE_SYMBOLS_FLAG;

  if (customizer) {
    result = object ? customizer(value, key, object, stack) : customizer(value);
  }

  if (result !== undefined) {
    return result;
  }

  if (!isObject_1(value)) {
    return value;
  }

  var isArr = isArray_1(value);

  if (isArr) {
    result = _initCloneArray(value);

    if (!isDeep) {
      return _copyArray(value, result);
    }
  } else {
    var tag = _getTag(value),
        isFunc = tag == funcTag$2 || tag == genTag$1;

    if (isBuffer_1(value)) {
      return _cloneBuffer(value, isDeep);
    }

    if (tag == objectTag$2 || tag == argsTag$2 || isFunc && !object) {
      result = isFlat || isFunc ? {} : _initCloneObject(value);

      if (!isDeep) {
        return isFlat ? _copySymbolsIn(value, _baseAssignIn(result, value)) : _copySymbols(value, _baseAssign(result, value));
      }
    } else {
      if (!cloneableTags[tag]) {
        return object ? value : {};
      }

      result = _initCloneByTag(value, tag, isDeep);
    }
  }

  stack || (stack = new _Stack());
  var stacked = stack.get(value);

  if (stacked) {
    return stacked;
  }

  stack.set(value, result);

  if (isSet_1(value)) {
    value.forEach(function (subValue) {
      result.add(baseClone(subValue, bitmask, customizer, subValue, value, stack));
    });
  } else if (isMap_1(value)) {
    value.forEach(function (subValue, key) {
      result.set(key, baseClone(subValue, bitmask, customizer, key, value, stack));
    });
  }

  var keysFunc = isFull ? isFlat ? _getAllKeysIn : _getAllKeys : isFlat ? keysIn : keys_1;
  var props = isArr ? undefined : keysFunc(value);
  _arrayEach(props || value, function (subValue, key) {
    if (props) {
      key = subValue;
      subValue = value[key];
    }

    _assignValue(result, key, baseClone(subValue, bitmask, customizer, key, value, stack));
  });
  return result;
}

var _baseClone = baseClone;

var CLONE_DEEP_FLAG$1 = 1,
    CLONE_SYMBOLS_FLAG$1 = 4;

function cloneDeep(value) {
  return _baseClone(value, CLONE_DEEP_FLAG$1 | CLONE_SYMBOLS_FLAG$1);
}

var cloneDeep_1 = cloneDeep;

function leafletMarkerClusterGroup(L) {
  if (L.MarkerClusterGroup) {
    return;
  }

  var MarkerClusterGroup = L.MarkerClusterGroup = L.FeatureGroup.extend({
    options: {
      maxClusterRadius: 80,
      iconCreateFunction: null,
      clusterPane: L.Marker.prototype.options.pane,
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: true,
      zoomToBoundsOnClick: true,
      singleMarkerMode: false,
      shouldSpiderfy: null,
      unspiderfyOnMapClick: true,
      disableClusteringAtZoom: null,
      removeOutsideVisibleBounds: true,
      animate: true,
      animateAddingMarkers: false,
      spiderfyDistanceMultiplier: 1,
      spiderLegPolylineOptions: {
        weight: 1.5,
        color: '#222',
        opacity: 0.5
      },
      chunkedLoading: false,
      chunkInterval: 200,
      chunkDelay: 50,
      chunkProgress: null,
      polygonOptions: {}
    },
    initialize: function initialize(options) {
      L.Util.setOptions(this, options);

      if (!this.options.iconCreateFunction) {
        this.options.iconCreateFunction = this._defaultIconCreateFunction;
      }

      this._featureGroup = L.featureGroup();

      this._featureGroup.addEventParent(this);

      this._nonPointGroup = L.featureGroup();

      this._nonPointGroup.addEventParent(this);

      this._inZoomAnimation = 0;
      this._needsClustering = [];
      this._needsRemoving = [];
      this._currentShownBounds = null;
      this._queue = [];
      this._childMarkerEventHandlers = {
        'dragstart': this._childMarkerDragStart,
        'move': this._childMarkerMoved,
        'dragend': this._childMarkerDragEnd
      };
      var animate = L.DomUtil.TRANSITION && this.options.animate;
      L.extend(this, animate ? this._withAnimation : this._noAnimation);
      this._markerCluster = animate ? L.MarkerCluster : L.MarkerClusterNonAnimated;
    },
    refresh: function refresh() {
      this._topClusterLevel._recursivelySpiderfy(this._map._zoom, this._currentShownBounds, this.options.shouldSpiderfy);

      this._topClusterLevel._recursivelyRemoveChildrenFromMap(this._currentShownBounds, this._zoom, this._currentShownBounds);

      this._topClusterLevel._recursivelyAddChildrenToMap(null, this._map._zoom, this._currentShownBounds);
    },
    addLayer: function addLayer(layer) {
      if (layer instanceof L.LayerGroup) {
        return this.addLayers([layer]);
      }

      if (!layer.getLatLng) {
        this._nonPointGroup.addLayer(layer);

        this.fire('layeradd', {
          layer: layer
        });
        return this;
      }

      if (!this._map) {
        this._needsClustering.push(layer);

        this.fire('layeradd', {
          layer: layer
        });
        return this;
      }

      if (this.hasLayer(layer)) {
        return this;
      }

      if (this._unspiderfy) {
        this._unspiderfy();
      }

      this._addLayer(layer, this._maxZoom);

      this.fire('layeradd', {
        layer: layer
      });

      this._topClusterLevel._recalculateBounds();

      this._refreshClustersIcons();

      var visibleLayer = layer,
          currentZoom = this._zoom;

      if (layer.__parent) {
        while (visibleLayer.__parent._zoom >= currentZoom) {
          visibleLayer = visibleLayer.__parent;
        }
      }

      if (this._currentShownBounds.contains(visibleLayer.getLatLng())) {
        if (this.options.animateAddingMarkers) {
          this._animationAddLayer(layer, visibleLayer);
        } else {
          this._animationAddLayerNonAnimated(layer, visibleLayer);
        }
      }

      return this;
    },
    removeLayer: function removeLayer(layer) {
      if (layer instanceof L.LayerGroup) {
        return this.removeLayers([layer]);
      }

      if (!layer.getLatLng) {
        this._nonPointGroup.removeLayer(layer);

        this.fire('layerremove', {
          layer: layer
        });
        return this;
      }

      if (!this._map) {
        if (!this._arraySplice(this._needsClustering, layer) && this.hasLayer(layer)) {
          this._needsRemoving.push({
            layer: layer,
            latlng: layer._latlng
          });
        }

        this.fire('layerremove', {
          layer: layer
        });
        return this;
      }

      if (!layer.__parent) {
        return this;
      }

      if (this._unspiderfy) {
        this._unspiderfy();

        this._unspiderfyLayer(layer);
      }

      this._removeLayer(layer, true);

      this.fire('layerremove', {
        layer: layer
      });

      this._topClusterLevel._recalculateBounds();

      this._refreshClustersIcons();

      layer.off(this._childMarkerEventHandlers, this);

      if (this._featureGroup.hasLayer(layer)) {
        this._featureGroup.removeLayer(layer);

        if (layer.clusterShow) {
          layer.clusterShow();
        }
      }

      return this;
    },
    addLayers: function addLayers(layersArray, skipLayerAddEvent) {
      if (!L.Util.isArray(layersArray)) {
        return this.addLayer(layersArray);
      }

      var fg = this._featureGroup,
          npg = this._nonPointGroup,
          chunked = this.options.chunkedLoading,
          chunkInterval = this.options.chunkInterval,
          chunkProgress = this.options.chunkProgress,
          l = layersArray.length,
          offset = 0,
          originalArray = true,
          m;

      if (this._map) {
        var started = new Date().getTime();
        var process = L.bind(function () {
          var start = new Date().getTime();

          for (; offset < l; offset++) {
            if (chunked && offset % 200 === 0) {
              var elapsed = new Date().getTime() - start;

              if (elapsed > chunkInterval) {
                break;
              }
            }

            m = layersArray[offset];

            if (m instanceof L.LayerGroup) {
              if (originalArray) {
                layersArray = layersArray.slice();
                originalArray = false;
              }

              this._extractNonGroupLayers(m, layersArray);

              l = layersArray.length;
              continue;
            }

            if (!m.getLatLng) {
              npg.addLayer(m);

              if (!skipLayerAddEvent) {
                this.fire('layeradd', {
                  layer: m
                });
              }

              continue;
            }

            if (this.hasLayer(m)) {
              continue;
            }

            this._addLayer(m, this._maxZoom);

            if (!skipLayerAddEvent) {
              this.fire('layeradd', {
                layer: m
              });
            }

            if (m.__parent) {
              if (m.__parent.getChildCount() === 2) {
                var markers = m.__parent.getAllChildMarkers(),
                    otherMarker = markers[0] === m ? markers[1] : markers[0];

                fg.removeLayer(otherMarker);
              }
            }
          }

          if (chunkProgress) {
            chunkProgress(offset, l, new Date().getTime() - started);
          }

          if (offset === l) {
            this._topClusterLevel._recalculateBounds();

            this._refreshClustersIcons();

            this._topClusterLevel._recursivelyAddChildrenToMap(null, this._zoom, this._currentShownBounds);
          } else {
            setTimeout(process, this.options.chunkDelay);
          }
        }, this);
        process();
      } else {
        var needsClustering = this._needsClustering;

        for (; offset < l; offset++) {
          m = layersArray[offset];

          if (m instanceof L.LayerGroup) {
            if (originalArray) {
              layersArray = layersArray.slice();
              originalArray = false;
            }

            this._extractNonGroupLayers(m, layersArray);

            l = layersArray.length;
            continue;
          }

          if (!m.getLatLng) {
            npg.addLayer(m);
            continue;
          }

          if (this.hasLayer(m)) {
            continue;
          }

          needsClustering.push(m);
        }
      }

      return this;
    },
    removeLayers: function removeLayers(layersArray) {
      var i,
          m,
          l = layersArray.length,
          fg = this._featureGroup,
          npg = this._nonPointGroup,
          originalArray = true;

      if (!this._map) {
        for (i = 0; i < l; i++) {
          m = layersArray[i];

          if (m instanceof L.LayerGroup) {
            if (originalArray) {
              layersArray = layersArray.slice();
              originalArray = false;
            }

            this._extractNonGroupLayers(m, layersArray);

            l = layersArray.length;
            continue;
          }

          this._arraySplice(this._needsClustering, m);

          npg.removeLayer(m);

          if (this.hasLayer(m)) {
            this._needsRemoving.push({
              layer: m,
              latlng: m._latlng
            });
          }

          this.fire('layerremove', {
            layer: m
          });
        }

        return this;
      }

      if (this._unspiderfy) {
        this._unspiderfy();

        var layersArray2 = layersArray.slice(),
            l2 = l;

        for (i = 0; i < l2; i++) {
          m = layersArray2[i];

          if (m instanceof L.LayerGroup) {
            this._extractNonGroupLayers(m, layersArray2);

            l2 = layersArray2.length;
            continue;
          }

          this._unspiderfyLayer(m);
        }
      }

      for (i = 0; i < l; i++) {
        m = layersArray[i];

        if (m instanceof L.LayerGroup) {
          if (originalArray) {
            layersArray = layersArray.slice();
            originalArray = false;
          }

          this._extractNonGroupLayers(m, layersArray);

          l = layersArray.length;
          continue;
        }

        if (!m.__parent) {
          npg.removeLayer(m);
          this.fire('layerremove', {
            layer: m
          });
          continue;
        }

        this._removeLayer(m, true, true);

        this.fire('layerremove', {
          layer: m
        });

        if (fg.hasLayer(m)) {
          fg.removeLayer(m);

          if (m.clusterShow) {
            m.clusterShow();
          }
        }
      }

      this._topClusterLevel._recalculateBounds();

      this._refreshClustersIcons();

      this._topClusterLevel._recursivelyAddChildrenToMap(null, this._zoom, this._currentShownBounds);

      return this;
    },
    clearLayers: function clearLayers() {
      if (!this._map) {
        this._needsClustering = [];
        this._needsRemoving = [];
        delete this._gridClusters;
        delete this._gridUnclustered;
      }

      if (this._noanimationUnspiderfy) {
        this._noanimationUnspiderfy();
      }

      this._featureGroup.clearLayers();

      this._nonPointGroup.clearLayers();

      this.eachLayer(function (marker) {
        marker.off(this._childMarkerEventHandlers, this);
        delete marker.__parent;
      }, this);

      if (this._map) {
        this._generateInitialClusters();
      }

      return this;
    },
    getBounds: function getBounds() {
      var bounds = new L.LatLngBounds();

      if (this._topClusterLevel) {
        bounds.extend(this._topClusterLevel._bounds);
      }

      for (var i = this._needsClustering.length - 1; i >= 0; i--) {
        bounds.extend(this._needsClustering[i].getLatLng());
      }

      bounds.extend(this._nonPointGroup.getBounds());
      return bounds;
    },
    eachLayer: function eachLayer(method, context) {
      var markers = this._needsClustering.slice(),
          needsRemoving = this._needsRemoving,
          thisNeedsRemoving,
          i,
          j;

      if (this._topClusterLevel) {
        this._topClusterLevel.getAllChildMarkers(markers);
      }

      for (i = markers.length - 1; i >= 0; i--) {
        thisNeedsRemoving = true;

        for (j = needsRemoving.length - 1; j >= 0; j--) {
          if (needsRemoving[j].layer === markers[i]) {
            thisNeedsRemoving = false;
            break;
          }
        }

        if (thisNeedsRemoving) {
          method.call(context, markers[i]);
        }
      }

      this._nonPointGroup.eachLayer(method, context);
    },
    getLayers: function getLayers() {
      var layers = [];
      this.eachLayer(function (l) {
        layers.push(l);
      });
      return layers;
    },
    getLayer: function getLayer(id) {
      var result = null;
      id = parseInt(id, 10);
      this.eachLayer(function (l) {
        if (L.stamp(l) === id) {
          result = l;
        }
      });
      return result;
    },
    hasLayer: function hasLayer(layer) {
      if (!layer) {
        return false;
      }

      var i,
          anArray = this._needsClustering;

      for (i = anArray.length - 1; i >= 0; i--) {
        if (anArray[i] === layer) {
          return true;
        }
      }

      anArray = this._needsRemoving;

      for (i = anArray.length - 1; i >= 0; i--) {
        if (anArray[i].layer === layer) {
          return false;
        }
      }

      return !!(layer.__parent && layer.__parent._group === this) || this._nonPointGroup.hasLayer(layer);
    },
    zoomToShowLayer: function zoomToShowLayer(layer, callback) {
      if (typeof callback !== 'function') {
        callback = function callback() {};
      }

      var showMarker = function showMarker() {
        if ((layer._icon || layer.__parent._icon) && !this._inZoomAnimation) {
          this._map.off('moveend', showMarker, this);

          this.off('animationend', showMarker, this);

          if (layer._icon) {
            callback();
          } else if (layer.__parent._icon) {
            this.once('spiderfied', callback, this);

            layer.__parent.spiderfy();
          }
        }
      };

      if (layer._icon && this._map.getBounds().contains(layer.getLatLng())) {
        callback();
      } else if (layer.__parent._zoom < this._map._zoom) {
        this._map.on('moveend', showMarker, this);

        this._map.panTo(layer.getLatLng());
      } else {
        this._map.on('moveend', showMarker, this);

        this.on('animationend', showMarker, this);

        layer.__parent.zoomToBounds();
      }
    },
    onAdd: function onAdd(map) {
      this._map = map;
      var i, l, layer;

      if (!isFinite(this._map.getMaxZoom())) {
        throw "Map has no maxZoom specified";
      }

      this._featureGroup.addTo(map);

      this._nonPointGroup.addTo(map);

      if (!this._gridClusters) {
        this._generateInitialClusters();
      }

      this._maxLat = map.options.crs.projection.MAX_LATITUDE;

      for (i = 0, l = this._needsRemoving.length; i < l; i++) {
        layer = this._needsRemoving[i];
        layer.newlatlng = layer.layer._latlng;
        layer.layer._latlng = layer.latlng;
      }

      for (i = 0, l = this._needsRemoving.length; i < l; i++) {
        layer = this._needsRemoving[i];

        this._removeLayer(layer.layer, true);

        layer.layer._latlng = layer.newlatlng;
      }

      this._needsRemoving = [];
      this._zoom = this._map._zoom;
      this._currentShownBounds = this._getExpandedVisibleBounds();

      this._map.on('zoomend', this._zoomEnd, this);

      this._map.on('moveend', this._moveEnd, this);

      if (this._spiderfierOnAdd) {
        this._spiderfierOnAdd();
      }

      this._bindEvents();

      l = this._needsClustering;
      this._needsClustering = [];
      this.addLayers(l, true);

      this._topClusterLevel._recursivelySpiderfy(this._map._zoom, this._currentShownBounds, this.options.shouldSpiderfy);
    },
    onRemove: function onRemove(map) {
      map.off('zoomend', this._zoomEnd, this);
      map.off('moveend', this._moveEnd, this);

      this._unbindEvents();

      this._map._mapPane.className = this._map._mapPane.className.replace(' leaflet-cluster-anim', '');

      if (this._spiderfierOnRemove) {
        this._spiderfierOnRemove();
      }

      delete this._maxLat;

      this._hideCoverage();

      this._featureGroup.remove();

      this._nonPointGroup.remove();

      this._featureGroup.clearLayers();

      this._map = null;
    },
    getVisibleParent: function getVisibleParent(marker) {
      var vMarker = marker;

      while (vMarker && !vMarker._icon) {
        vMarker = vMarker.__parent;
      }

      return vMarker || null;
    },
    _arraySplice: function _arraySplice(anArray, obj) {
      for (var i = anArray.length - 1; i >= 0; i--) {
        if (anArray[i] === obj) {
          anArray.splice(i, 1);
          return true;
        }
      }
    },
    _removeFromGridUnclustered: function _removeFromGridUnclustered(marker, z) {
      var map = this._map,
          gridUnclustered = this._gridUnclustered,
          minZoom = Math.floor(this._map.getMinZoom());

      for (; z >= minZoom; z--) {
        if (!gridUnclustered[z].removeObject(marker, map.project(marker.getLatLng(), z))) {
          break;
        }
      }
    },
    _childMarkerDragStart: function _childMarkerDragStart(e) {
      e.target.__dragStart = e.target._latlng;
    },
    _childMarkerMoved: function _childMarkerMoved(e) {
      if (!this._ignoreMove && !e.target.__dragStart) {
        var isPopupOpen = e.target._popup && e.target._popup.isOpen();

        this._moveChild(e.target, e.oldLatLng, e.latlng);

        if (isPopupOpen) {
          e.target.openPopup();
        }
      }
    },
    _moveChild: function _moveChild(layer, from, to) {
      layer._latlng = from;
      this.removeLayer(layer);
      layer._latlng = to;
      this.addLayer(layer);
    },
    _childMarkerDragEnd: function _childMarkerDragEnd(e) {
      var dragStart = e.target.__dragStart;
      delete e.target.__dragStart;

      if (dragStart) {
        this._moveChild(e.target, dragStart, e.target._latlng);
      }
    },
    _removeLayer: function _removeLayer(marker, removeFromDistanceGrid, dontUpdateMap) {
      var gridClusters = this._gridClusters,
          gridUnclustered = this._gridUnclustered,
          fg = this._featureGroup,
          map = this._map,
          minZoom = Math.floor(this._map.getMinZoom());

      if (removeFromDistanceGrid) {
        this._removeFromGridUnclustered(marker, this._maxZoom);
      }

      var cluster = marker.__parent,
          markers = cluster._markers,
          otherMarker;

      this._arraySplice(markers, marker);

      while (cluster) {
        cluster._childCount--;
        cluster._boundsNeedUpdate = true;

        if (cluster._zoom < minZoom) {
          break;
        } else if (removeFromDistanceGrid && cluster._childCount <= 1) {
          otherMarker = cluster._markers[0] === marker ? cluster._markers[1] : cluster._markers[0];

          gridClusters[cluster._zoom].removeObject(cluster, map.project(cluster._cLatLng, cluster._zoom));

          gridUnclustered[cluster._zoom].addObject(otherMarker, map.project(otherMarker.getLatLng(), cluster._zoom));

          this._arraySplice(cluster.__parent._childClusters, cluster);

          cluster.__parent._markers.push(otherMarker);

          otherMarker.__parent = cluster.__parent;

          if (cluster._icon) {
            fg.removeLayer(cluster);

            if (!dontUpdateMap) {
              fg.addLayer(otherMarker);
            }
          }
        } else {
          cluster._iconNeedsUpdate = true;
        }

        cluster = cluster.__parent;
      }

      delete marker.__parent;
    },
    _isOrIsParent: function _isOrIsParent(el, oel) {
      while (oel) {
        if (el === oel) {
          return true;
        }

        oel = oel.parentNode;
      }

      return false;
    },
    fire: function fire(type, data, propagate) {
      if (data && data.layer instanceof L.MarkerCluster) {
        if (data.originalEvent && this._isOrIsParent(data.layer._icon, data.originalEvent.relatedTarget)) {
          return;
        }

        type = 'cluster' + type;
      }

      L.FeatureGroup.prototype.fire.call(this, type, data, propagate);
    },
    listens: function listens(type, propagate) {
      return L.FeatureGroup.prototype.listens.call(this, type, propagate) || L.FeatureGroup.prototype.listens.call(this, 'cluster' + type, propagate);
    },
    _defaultIconCreateFunction: function _defaultIconCreateFunction(cluster) {
      var childCount = cluster.getChildCount();
      var c = ' marker-cluster-';

      if (childCount < 10) {
        c += 'small';
      } else if (childCount < 100) {
        c += 'medium';
      } else {
        c += 'large';
      }

      return new L.DivIcon({
        html: '<div><span>' + childCount + '</span></div>',
        className: 'marker-cluster' + c,
        iconSize: new L.Point(40, 40)
      });
    },
    _bindEvents: function _bindEvents() {
      var map = this._map,
          spiderfyOnMaxZoom = this.options.spiderfyOnMaxZoom,
          showCoverageOnHover = this.options.showCoverageOnHover,
          zoomToBoundsOnClick = this.options.zoomToBoundsOnClick;

      if (spiderfyOnMaxZoom || zoomToBoundsOnClick) {
        this.on('clusterclick', this._zoomOrSpiderfy, this);
      }

      if (showCoverageOnHover) {
        this.on('clustermouseover', this._showCoverage, this);
        this.on('clustermouseout', this._hideCoverage, this);
        map.on('zoomend', this._hideCoverage, this);
      }
    },
    _zoomOrSpiderfy: function _zoomOrSpiderfy(e) {
      var cluster = e.layer,
          bottomCluster = cluster;

      while (bottomCluster._childClusters.length === 1) {
        bottomCluster = bottomCluster._childClusters[0];
      }

      if (bottomCluster._zoom === this._maxZoom && bottomCluster._childCount === cluster._childCount && this.options.spiderfyOnMaxZoom) {
        cluster.spiderfy();
      } else if (this.options.zoomToBoundsOnClick) {
        cluster.zoomToBounds();
      }

      if (e.originalEvent && e.originalEvent.keyCode === 13) {
        this._map._container.focus();
      }
    },
    _showCoverage: function _showCoverage(e) {
      var map = this._map;

      if (this._inZoomAnimation) {
        return;
      }

      if (this._shownPolygon) {
        map.removeLayer(this._shownPolygon);
      }

      if (e.layer.getChildCount() > 2 && e.layer !== this._spiderfied) {
        this._shownPolygon = new L.Polygon(e.layer.getConvexHull(), this.options.polygonOptions);
        map.addLayer(this._shownPolygon);
      }
    },
    _hideCoverage: function _hideCoverage() {
      if (this._shownPolygon) {
        this._map.removeLayer(this._shownPolygon);

        this._shownPolygon = null;
      }
    },
    _unbindEvents: function _unbindEvents() {
      var spiderfyOnMaxZoom = this.options.spiderfyOnMaxZoom,
          showCoverageOnHover = this.options.showCoverageOnHover,
          zoomToBoundsOnClick = this.options.zoomToBoundsOnClick,
          map = this._map;

      if (spiderfyOnMaxZoom || zoomToBoundsOnClick) {
        this.off('clusterclick', this._zoomOrSpiderfy, this);
      }

      if (showCoverageOnHover) {
        this.off('clustermouseover', this._showCoverage, this);
        this.off('clustermouseout', this._hideCoverage, this);
        map.off('zoomend', this._hideCoverage, this);
      }
    },
    _zoomEnd: function _zoomEnd() {
      if (!this._map) {
        return;
      }

      this._mergeSplitClusters();

      this._zoom = this._map._zoom;
      this._currentShownBounds = this._getExpandedVisibleBounds();

      this._topClusterLevel._recursivelySpiderfy(this._map._zoom, this._currentShownBounds, this.options.shouldSpiderfy);

      this._topClusterLevel._recursivelyRemoveChildrenFromMap(this._currentShownBounds, this._zoom, this._currentShownBounds);

      this._topClusterLevel._recursivelyAddChildrenToMap(null, this._map._zoom, this._currentShownBounds);
    },
    _moveEnd: function _moveEnd() {
      if (this._inZoomAnimation) {
        return;
      }

      var newBounds = this._getExpandedVisibleBounds();

      this._topClusterLevel._recursivelyRemoveChildrenFromMap(this._currentShownBounds, Math.floor(this._map.getMinZoom()), this._zoom, newBounds);

      this._topClusterLevel._recursivelyAddChildrenToMap(null, this._map._zoom, newBounds);

      this._currentShownBounds = newBounds;
      return;
    },
    _generateInitialClusters: function _generateInitialClusters() {
      var maxZoom = this._map.getMaxZoom(),
          minZoom = this._map.getMinZoom(),
          radius = this.options.maxClusterRadius,
          radiusFn = radius;

      if (typeof radius !== "function") {
        radiusFn = function radiusFn() {
          return radius;
        };
      }

      if (this.options.disableClusteringAtZoom !== null) {
        maxZoom = this.options.disableClusteringAtZoom - 1;
      }

      this._maxZoom = maxZoom;
      this._gridClusters = {};
      this._gridUnclustered = {};

      for (var zoom = maxZoom; zoom >= minZoom; zoom--) {
        this._gridClusters[zoom] = new L.DistanceGrid(radiusFn(zoom));
        this._gridUnclustered[zoom] = new L.DistanceGrid(radiusFn(zoom));
      }

      this._topClusterLevel = new this._markerCluster(this, minZoom - 1);
    },
    _addLayer: function _addLayer(layer, zoom) {
      var gridClusters = this._gridClusters,
          gridUnclustered = this._gridUnclustered,
          minZoom = Math.floor(this._map.getMinZoom()),
          markerPoint,
          z;

      if (this.options.singleMarkerMode) {
        this._overrideMarkerIcon(layer);
      }

      layer.on(this._childMarkerEventHandlers, this);

      for (; zoom >= minZoom; zoom--) {
        markerPoint = this._map.project(layer.getLatLng(), zoom);
        var closest = gridClusters[zoom].getNearObject(markerPoint);

        if (closest) {
          closest._addChild(layer);

          layer.__parent = closest;
          return;
        }

        closest = gridUnclustered[zoom].getNearObject(markerPoint);

        if (closest) {
          var parent = closest.__parent;

          if (parent) {
            this._removeLayer(closest, false);
          }

          var newCluster = new this._markerCluster(this, zoom, closest, layer);
          gridClusters[zoom].addObject(newCluster, this._map.project(newCluster._cLatLng, zoom));
          closest.__parent = newCluster;
          layer.__parent = newCluster;
          var lastParent = newCluster;

          for (z = zoom - 1; z > parent._zoom; z--) {
            lastParent = new this._markerCluster(this, z, lastParent);
            gridClusters[z].addObject(lastParent, this._map.project(closest.getLatLng(), z));
          }

          parent._addChild(lastParent);

          this._removeFromGridUnclustered(closest, zoom);

          return;
        }

        gridUnclustered[zoom].addObject(layer, markerPoint);
      }

      this._topClusterLevel._addChild(layer);

      layer.__parent = this._topClusterLevel;
      return;
    },
    _refreshClustersIcons: function _refreshClustersIcons() {
      this._featureGroup.eachLayer(function (c) {
        if (c instanceof L.MarkerCluster && c._iconNeedsUpdate) {
          c._updateIcon();
        }
      });
    },
    _enqueue: function _enqueue(fn) {
      this._queue.push(fn);

      if (!this._queueTimeout) {
        this._queueTimeout = setTimeout(L.bind(this._processQueue, this), 300);
      }
    },
    _processQueue: function _processQueue() {
      for (var i = 0; i < this._queue.length; i++) {
        this._queue[i].call(this);
      }

      this._queue.length = 0;
      clearTimeout(this._queueTimeout);
      this._queueTimeout = null;
    },
    _mergeSplitClusters: function _mergeSplitClusters() {
      var mapZoom = this._map._zoom;

      this._processQueue();

      if (this._zoom < mapZoom && this._currentShownBounds.intersects(this._getExpandedVisibleBounds())) {
        this._animationStart();

        this._topClusterLevel._recursivelyRemoveChildrenFromMap(this._currentShownBounds, this._map.getMinZoom(), this._zoom, this._getExpandedVisibleBounds());

        this._animationZoomIn(this._zoom, mapZoom);
      } else if (this._zoom > mapZoom) {
        this._animationStart();

        this._animationZoomOut(this._zoom, mapZoom);
      } else {
        this._moveEnd();
      }
    },
    _getExpandedVisibleBounds: function _getExpandedVisibleBounds() {
      if (!this.options.removeOutsideVisibleBounds) {
        return this._mapBoundsInfinite;
      } else if (L.Browser.mobile) {
        return this._checkBoundsMaxLat(this._map.getBounds());
      }

      return this._checkBoundsMaxLat(this._map.getBounds().pad(1));
    },
    _checkBoundsMaxLat: function _checkBoundsMaxLat(bounds) {
      var maxLat = this._maxLat;

      if (maxLat !== undefined) {
        if (bounds.getNorth() >= maxLat) {
          bounds._northEast.lat = Infinity;
        }

        if (bounds.getSouth() <= -maxLat) {
          bounds._southWest.lat = -Infinity;
        }
      }

      return bounds;
    },
    _animationAddLayerNonAnimated: function _animationAddLayerNonAnimated(layer, newCluster) {
      if (newCluster === layer) {
        this._featureGroup.addLayer(layer);
      } else if (newCluster._childCount === 2) {
        newCluster._addToMap();

        var markers = newCluster.getAllChildMarkers();

        this._featureGroup.removeLayer(markers[0]);

        this._featureGroup.removeLayer(markers[1]);
      } else {
        newCluster._updateIcon();
      }
    },
    _extractNonGroupLayers: function _extractNonGroupLayers(group, output) {
      var layers = group.getLayers(),
          i = 0,
          layer;
      output = output || [];

      for (; i < layers.length; i++) {
        layer = layers[i];

        if (layer instanceof L.LayerGroup) {
          this._extractNonGroupLayers(layer, output);

          continue;
        }

        output.push(layer);
      }

      return output;
    },
    _overrideMarkerIcon: function _overrideMarkerIcon(layer) {
      var icon = layer.options.icon = this.options.iconCreateFunction({
        getChildCount: function getChildCount() {
          return 1;
        },
        getAllChildMarkers: function getAllChildMarkers() {
          return [layer];
        }
      });
      return icon;
    }
  });
  L.MarkerClusterGroup.include({
    _mapBoundsInfinite: new L.LatLngBounds(new L.LatLng(-Infinity, -Infinity), new L.LatLng(Infinity, Infinity))
  });
  L.MarkerClusterGroup.include({
    _noAnimation: {
      _animationStart: function _animationStart() {},
      _animationZoomIn: function _animationZoomIn(previousZoomLevel, newZoomLevel) {
        this._topClusterLevel._recursivelyRemoveChildrenFromMap(this._currentShownBounds, this._map.getMinZoom(), previousZoomLevel);

        this._topClusterLevel._recursivelyAddChildrenToMap(null, newZoomLevel, this._getExpandedVisibleBounds());

        this.fire('animationend');
      },
      _animationZoomOut: function _animationZoomOut(previousZoomLevel, newZoomLevel) {
        this._topClusterLevel._recursivelyRemoveChildrenFromMap(this._currentShownBounds, this._map.getMinZoom(), previousZoomLevel);

        this._topClusterLevel._recursivelyAddChildrenToMap(null, newZoomLevel, this._getExpandedVisibleBounds());

        this.fire('animationend');
      },
      _animationAddLayer: function _animationAddLayer(layer, newCluster) {
        this._animationAddLayerNonAnimated(layer, newCluster);
      }
    },
    _withAnimation: {
      _animationStart: function _animationStart() {
        this._map._mapPane.className += ' leaflet-cluster-anim';
        this._inZoomAnimation++;
      },
      _animationZoomIn: function _animationZoomIn(previousZoomLevel, newZoomLevel) {
        var bounds = this._getExpandedVisibleBounds(),
            fg = this._featureGroup,
            minZoom = this._map.getMinZoom(),
            i;

        this._ignoreMove = true;

        this._topClusterLevel._recursively(bounds, previousZoomLevel, minZoom, function (c) {
          var startPos = c._latlng,
              markers = c._markers,
              m;

          if (!bounds.contains(startPos)) {
            startPos = null;
          }

          if (c._isSingleParent() && previousZoomLevel + 1 === newZoomLevel) {
            fg.removeLayer(c);

            c._recursivelyAddChildrenToMap(null, newZoomLevel, bounds);
          } else {
            c.clusterHide();

            c._recursivelyAddChildrenToMap(startPos, newZoomLevel, bounds);
          }

          for (i = markers.length - 1; i >= 0; i--) {
            m = markers[i];

            if (!bounds.contains(m._latlng)) {
              fg.removeLayer(m);
            }
          }
        });

        this._forceLayout();

        this._topClusterLevel._recursivelyBecomeVisible(bounds, newZoomLevel);

        fg.eachLayer(function (n) {
          if (!(n instanceof L.MarkerCluster) && n._icon) {
            n.clusterShow();
          }
        });

        this._topClusterLevel._recursively(bounds, previousZoomLevel, newZoomLevel, function (c) {
          c._recursivelyRestoreChildPositions(newZoomLevel);
        });

        this._ignoreMove = false;

        this._enqueue(function () {
          this._topClusterLevel._recursively(bounds, previousZoomLevel, minZoom, function (c) {
            fg.removeLayer(c);
            c.clusterShow();
          });

          this._animationEnd();
        });
      },
      _animationZoomOut: function _animationZoomOut(previousZoomLevel, newZoomLevel) {
        this._animationZoomOutSingle(this._topClusterLevel, previousZoomLevel - 1, newZoomLevel);

        this._topClusterLevel._recursivelyAddChildrenToMap(null, newZoomLevel, this._getExpandedVisibleBounds());

        this._topClusterLevel._recursivelyRemoveChildrenFromMap(this._currentShownBounds, this._map.getMinZoom(), previousZoomLevel, this._getExpandedVisibleBounds());
      },
      _animationAddLayer: function _animationAddLayer(layer, newCluster) {
        var me = this,
            fg = this._featureGroup;
        fg.addLayer(layer);

        if (newCluster !== layer) {
          if (newCluster._childCount > 2) {
            newCluster._updateIcon();

            this._forceLayout();

            this._animationStart();

            layer._setPos(this._map.latLngToLayerPoint(newCluster.getLatLng()));

            layer.clusterHide();

            this._enqueue(function () {
              fg.removeLayer(layer);
              layer.clusterShow();

              me._animationEnd();
            });
          } else {
            this._forceLayout();

            me._animationStart();

            me._animationZoomOutSingle(newCluster, this._map.getMaxZoom(), this._zoom);
          }
        }
      }
    },
    _animationZoomOutSingle: function _animationZoomOutSingle(cluster, previousZoomLevel, newZoomLevel) {
      var bounds = this._getExpandedVisibleBounds(),
          minZoom = this._map.getMinZoom();

      cluster._recursivelyAnimateChildrenInAndAddSelfToMap(bounds, minZoom, previousZoomLevel + 1, newZoomLevel);

      var me = this;

      this._forceLayout();

      cluster._recursivelyBecomeVisible(bounds, newZoomLevel);

      this._enqueue(function () {
        if (cluster._childCount === 1) {
          var m = cluster._markers[0];
          this._ignoreMove = true;
          m.setLatLng(m.getLatLng());
          this._ignoreMove = false;

          if (m.clusterShow) {
            m.clusterShow();
          }
        } else {
          cluster._recursively(bounds, newZoomLevel, minZoom, function (c) {
            c._recursivelyRemoveChildrenFromMap(bounds, minZoom, previousZoomLevel + 1);
          });
        }

        me._animationEnd();
      });
    },
    _animationEnd: function _animationEnd() {
      if (this._map) {
        this._map._mapPane.className = this._map._mapPane.className.replace(' leaflet-cluster-anim', '');
      }

      this._inZoomAnimation--;
      this.fire('animationend');
    },
    _forceLayout: function _forceLayout() {
      L.Util.falseFn(document.body.offsetWidth);
    }
  });

  L.markerClusterGroup = function (options) {
    return new L.MarkerClusterGroup(options);
  };

  var MarkerCluster = L.MarkerCluster = L.Marker.extend({
    options: L.Icon.prototype.options,
    initialize: function initialize(group, zoom, a, b) {
      L.Marker.prototype.initialize.call(this, a ? a._cLatLng || a.getLatLng() : new L.LatLng(0, 0), {
        icon: this,
        pane: group.options.clusterPane
      });
      this._group = group;
      this._zoom = zoom;
      this._markers = [];
      this._childClusters = [];
      this._childCount = 0;
      this._iconNeedsUpdate = true;
      this._boundsNeedUpdate = true;
      this._bounds = new L.LatLngBounds();

      if (a) {
        this._addChild(a);
      }

      if (b) {
        this._addChild(b);
      }
    },
    getAllChildMarkers: function getAllChildMarkers(storageArray, ignoreDraggedMarker) {
      storageArray = storageArray || [];

      for (var i = this._childClusters.length - 1; i >= 0; i--) {
        this._childClusters[i].getAllChildMarkers(storageArray);
      }

      for (var j = this._markers.length - 1; j >= 0; j--) {
        if (ignoreDraggedMarker && this._markers[j].__dragStart) {
          continue;
        }

        storageArray.push(this._markers[j]);
      }

      return storageArray;
    },
    getChildCount: function getChildCount() {
      return this._childCount;
    },
    zoomToBounds: function zoomToBounds(fitBoundsOptions) {
      var childClusters = this._childClusters.slice(),
          map = this._group._map,
          boundsZoom = map.getBoundsZoom(this._bounds),
          zoom = this._zoom + 1,
          mapZoom = map.getZoom(),
          i;

      while (childClusters.length > 0 && boundsZoom > zoom) {
        zoom++;
        var newClusters = [];

        for (i = 0; i < childClusters.length; i++) {
          newClusters = newClusters.concat(childClusters[i]._childClusters);
        }

        childClusters = newClusters;
      }

      if (boundsZoom > zoom) {
        this._group._map.setView(this._latlng, zoom);
      } else if (boundsZoom <= mapZoom) {
        this._group._map.setView(this._latlng, mapZoom + 1);
      } else {
        this._group._map.fitBounds(this._bounds, fitBoundsOptions);
      }
    },
    getBounds: function getBounds() {
      var bounds = new L.LatLngBounds();
      bounds.extend(this._bounds);
      return bounds;
    },
    _updateIcon: function _updateIcon() {
      this._iconNeedsUpdate = true;

      if (this._icon) {
        this.setIcon(this);
      }
    },
    createIcon: function createIcon() {
      if (this._iconNeedsUpdate) {
        this._iconObj = this._group.options.iconCreateFunction(this);
        this._iconNeedsUpdate = false;
      }

      return this._iconObj.createIcon();
    },
    createShadow: function createShadow() {
      return this._iconObj.createShadow();
    },
    _addChild: function _addChild(new1, isNotificationFromChild) {
      this._iconNeedsUpdate = true;
      this._boundsNeedUpdate = true;

      this._setClusterCenter(new1);

      if (new1 instanceof L.MarkerCluster) {
        if (!isNotificationFromChild) {
          this._childClusters.push(new1);

          new1.__parent = this;
        }

        this._childCount += new1._childCount;
      } else {
        if (!isNotificationFromChild) {
          this._markers.push(new1);
        }

        this._childCount++;
      }

      if (this.__parent) {
        this.__parent._addChild(new1, true);
      }
    },
    _setClusterCenter: function _setClusterCenter(child) {
      if (!this._cLatLng) {
        this._cLatLng = child._cLatLng || child._latlng;
      }
    },
    _resetBounds: function _resetBounds() {
      var bounds = this._bounds;

      if (bounds._southWest) {
        bounds._southWest.lat = Infinity;
        bounds._southWest.lng = Infinity;
      }

      if (bounds._northEast) {
        bounds._northEast.lat = -Infinity;
        bounds._northEast.lng = -Infinity;
      }
    },
    _recalculateBounds: function _recalculateBounds() {
      var markers = this._markers,
          childClusters = this._childClusters,
          latSum = 0,
          lngSum = 0,
          totalCount = this._childCount,
          i,
          child,
          childLatLng,
          childCount;

      if (totalCount === 0) {
        return;
      }

      this._resetBounds();

      for (i = 0; i < markers.length; i++) {
        childLatLng = markers[i]._latlng;

        this._bounds.extend(childLatLng);

        latSum += childLatLng.lat;
        lngSum += childLatLng.lng;
      }

      for (i = 0; i < childClusters.length; i++) {
        child = childClusters[i];

        if (child._boundsNeedUpdate) {
          child._recalculateBounds();
        }

        this._bounds.extend(child._bounds);

        childLatLng = child._wLatLng;
        childCount = child._childCount;
        latSum += childLatLng.lat * childCount;
        lngSum += childLatLng.lng * childCount;
      }

      this._latlng = this._wLatLng = new L.LatLng(latSum / totalCount, lngSum / totalCount);
      this._boundsNeedUpdate = false;
    },
    _addToMap: function _addToMap(startPos) {
      if (startPos) {
        this._backupLatlng = this._latlng;
        this.setLatLng(startPos);
      }

      this._group._featureGroup.addLayer(this);
    },
    _recursivelyAnimateChildrenIn: function _recursivelyAnimateChildrenIn(bounds, center, maxZoom) {
      this._recursively(bounds, this._group._map.getMinZoom(), maxZoom - 1, function (c) {
        var markers = c._markers,
            i,
            m;

        for (i = markers.length - 1; i >= 0; i--) {
          m = markers[i];

          if (m._icon) {
            m._setPos(center);

            m.clusterHide();
          }
        }
      }, function (c) {
        var childClusters = c._childClusters,
            j,
            cm;

        for (j = childClusters.length - 1; j >= 0; j--) {
          cm = childClusters[j];

          if (cm._icon) {
            cm._setPos(center);

            cm.clusterHide();
          }
        }
      });
    },
    _recursivelyAnimateChildrenInAndAddSelfToMap: function _recursivelyAnimateChildrenInAndAddSelfToMap(bounds, mapMinZoom, previousZoomLevel, newZoomLevel) {
      this._recursively(bounds, newZoomLevel, mapMinZoom, function (c) {
        c._recursivelyAnimateChildrenIn(bounds, c._group._map.latLngToLayerPoint(c.getLatLng()).round(), previousZoomLevel);

        if (c._isSingleParent() && previousZoomLevel - 1 === newZoomLevel) {
          c.clusterShow();

          c._recursivelyRemoveChildrenFromMap(bounds, mapMinZoom, previousZoomLevel);
        } else {
          c.clusterHide();
        }

        c._addToMap();
      });
    },
    _recursivelyBecomeVisible: function _recursivelyBecomeVisible(bounds, zoomLevel) {
      this._recursively(bounds, this._group._map.getMinZoom(), zoomLevel, null, function (c) {
        c.clusterShow();
      });
    },
    _recursivelyAddChildrenToMap: function _recursivelyAddChildrenToMap(startPos, zoomLevel, bounds) {
      this._recursively(bounds, this._group._map.getMinZoom() - 1, zoomLevel, function (c) {
        if (zoomLevel === c._zoom) {
          return;
        }

        for (var i = c._markers.length - 1; i >= 0; i--) {
          var nm = c._markers[i];

          if (!bounds.contains(nm._latlng)) {
            continue;
          }

          if (startPos) {
            nm._backupLatlng = nm.getLatLng();
            nm.setLatLng(startPos);

            if (nm.clusterHide) {
              nm.clusterHide();
            }
          }

          c._group._featureGroup.addLayer(nm);
        }
      }, function (c) {
        c._addToMap(startPos);
      });
    },
    _recursivelySpiderfy: function _recursivelySpiderfy(zoomLevel, bounds, shouldSpiderfy) {
      this._recursively(bounds, -1, zoomLevel, function (c) {
        c._noanimationUnspiderfy();
      }, function (c) {
        if (shouldSpiderfy && shouldSpiderfy(c, zoomLevel)) {
          c.spiderfy();
        }
      });
    },
    _recursivelyRestoreChildPositions: function _recursivelyRestoreChildPositions(zoomLevel) {
      for (var i = this._markers.length - 1; i >= 0; i--) {
        var nm = this._markers[i];

        if (nm._backupLatlng) {
          nm.setLatLng(nm._backupLatlng);
          delete nm._backupLatlng;
        }
      }

      if (zoomLevel - 1 === this._zoom) {
        for (var j = this._childClusters.length - 1; j >= 0; j--) {
          this._childClusters[j]._restorePosition();
        }
      } else {
        for (var k = this._childClusters.length - 1; k >= 0; k--) {
          this._childClusters[k]._recursivelyRestoreChildPositions(zoomLevel);
        }
      }
    },
    _restorePosition: function _restorePosition() {
      if (this._backupLatlng) {
        this.setLatLng(this._backupLatlng);
        delete this._backupLatlng;
      }
    },
    _recursivelyRemoveChildrenFromMap: function _recursivelyRemoveChildrenFromMap(previousBounds, mapMinZoom, zoomLevel, exceptBounds) {
      var m, i;

      this._recursively(previousBounds, mapMinZoom - 1, zoomLevel - 1, function (c) {
        for (i = c._markers.length - 1; i >= 0; i--) {
          m = c._markers[i];

          if (!exceptBounds || !exceptBounds.contains(m._latlng)) {
            c._group._featureGroup.removeLayer(m);

            if (m.clusterShow) {
              m.clusterShow();
            }
          }
        }
      }, function (c) {
        for (i = c._childClusters.length - 1; i >= 0; i--) {
          m = c._childClusters[i];

          if (!exceptBounds || !exceptBounds.contains(m._latlng)) {
            c._group._featureGroup.removeLayer(m);

            if (m.clusterShow) {
              m.clusterShow();
            }
          }
        }
      });
    },
    _recursively: function _recursively(boundsToApplyTo, zoomLevelToStart, zoomLevelToStop, runAtEveryLevel, runAtBottomLevel) {
      var childClusters = this._childClusters,
          zoom = this._zoom,
          i,
          c;

      if (zoomLevelToStart <= zoom) {
        if (runAtEveryLevel) {
          runAtEveryLevel(this);
        }

        if (runAtBottomLevel && zoom === zoomLevelToStop) {
          runAtBottomLevel(this);
        }
      }

      if (zoom < zoomLevelToStart || zoom < zoomLevelToStop) {
        for (i = childClusters.length - 1; i >= 0; i--) {
          c = childClusters[i];

          if (c._boundsNeedUpdate) {
            c._recalculateBounds();
          }

          if (boundsToApplyTo.intersects(c._bounds)) {
            c._recursively(boundsToApplyTo, zoomLevelToStart, zoomLevelToStop, runAtEveryLevel, runAtBottomLevel);
          }
        }
      }
    },
    _isSingleParent: function _isSingleParent() {
      return this._childClusters.length > 0 && this._childClusters[0]._childCount === this._childCount;
    }
  });
  L.Marker.include({
    clusterHide: function clusterHide() {
      var backup = this.options.opacity;
      this.setOpacity(0);
      this.options.opacity = backup;
      return this;
    },
    clusterShow: function clusterShow() {
      return this.setOpacity(this.options.opacity);
    }
  });

  L.DistanceGrid = function (cellSize) {
    this._cellSize = cellSize;
    this._sqCellSize = cellSize * cellSize;
    this._grid = {};
    this._objectPoint = {};
  };

  L.DistanceGrid.prototype = {
    addObject: function addObject(obj, point) {
      var x = this._getCoord(point.x),
          y = this._getCoord(point.y),
          grid = this._grid,
          row = grid[y] = grid[y] || {},
          cell = row[x] = row[x] || [],
          stamp = L.Util.stamp(obj);

      this._objectPoint[stamp] = point;
      cell.push(obj);
    },
    updateObject: function updateObject(obj, point) {
      this.removeObject(obj);
      this.addObject(obj, point);
    },
    removeObject: function removeObject(obj, point) {
      var x = this._getCoord(point.x),
          y = this._getCoord(point.y),
          grid = this._grid,
          row = grid[y] = grid[y] || {},
          cell = row[x] = row[x] || [],
          i,
          len;

      delete this._objectPoint[L.Util.stamp(obj)];

      for (i = 0, len = cell.length; i < len; i++) {
        if (cell[i] === obj) {
          cell.splice(i, 1);

          if (len === 1) {
            delete row[x];
          }

          return true;
        }
      }
    },
    eachObject: function eachObject(fn, context) {
      var i,
          j,
          k,
          len,
          row,
          cell,
          removed,
          grid = this._grid;

      for (i in grid) {
        row = grid[i];

        for (j in row) {
          cell = row[j];

          for (k = 0, len = cell.length; k < len; k++) {
            removed = fn.call(context, cell[k]);

            if (removed) {
              k--;
              len--;
            }
          }
        }
      }
    },
    getNearObject: function getNearObject(point) {
      var x = this._getCoord(point.x),
          y = this._getCoord(point.y),
          i,
          j,
          k,
          row,
          cell,
          len,
          obj,
          dist,
          objectPoint = this._objectPoint,
          closestDistSq = this._sqCellSize,
          closest = null;

      for (i = y - 1; i <= y + 1; i++) {
        row = this._grid[i];

        if (row) {
          for (j = x - 1; j <= x + 1; j++) {
            cell = row[j];

            if (cell) {
              for (k = 0, len = cell.length; k < len; k++) {
                obj = cell[k];
                dist = this._sqDist(objectPoint[L.Util.stamp(obj)], point);

                if (dist < closestDistSq || dist <= closestDistSq && closest === null) {
                  closestDistSq = dist;
                  closest = obj;
                }
              }
            }
          }
        }
      }

      return closest;
    },
    _getCoord: function _getCoord(x) {
      var coord = Math.floor(x / this._cellSize);
      return isFinite(coord) ? coord : x;
    },
    _sqDist: function _sqDist(p, p2) {
      var dx = p2.x - p.x,
          dy = p2.y - p.y;
      return dx * dx + dy * dy;
    }
  };

  (function () {
    L.QuickHull = {
      getDistant: function getDistant(cpt, bl) {
        var vY = bl[1].lat - bl[0].lat,
            vX = bl[0].lng - bl[1].lng;
        return vX * (cpt.lat - bl[0].lat) + vY * (cpt.lng - bl[0].lng);
      },
      findMostDistantPointFromBaseLine: function findMostDistantPointFromBaseLine(baseLine, latLngs) {
        var maxD = 0,
            maxPt = null,
            newPoints = [],
            i,
            pt,
            d;

        for (i = latLngs.length - 1; i >= 0; i--) {
          pt = latLngs[i];
          d = this.getDistant(pt, baseLine);

          if (d > 0) {
            newPoints.push(pt);
          } else {
            continue;
          }

          if (d > maxD) {
            maxD = d;
            maxPt = pt;
          }
        }

        return {
          maxPoint: maxPt,
          newPoints: newPoints
        };
      },
      buildConvexHull: function buildConvexHull(baseLine, latLngs) {
        var convexHullBaseLines = [],
            t = this.findMostDistantPointFromBaseLine(baseLine, latLngs);

        if (t.maxPoint) {
          convexHullBaseLines = convexHullBaseLines.concat(this.buildConvexHull([baseLine[0], t.maxPoint], t.newPoints));
          convexHullBaseLines = convexHullBaseLines.concat(this.buildConvexHull([t.maxPoint, baseLine[1]], t.newPoints));
          return convexHullBaseLines;
        } else {
          return [baseLine[0]];
        }
      },
      getConvexHull: function getConvexHull(latLngs) {
        var maxLat = false,
            minLat = false,
            maxLng = false,
            minLng = false,
            maxLatPt = null,
            minLatPt = null,
            maxLngPt = null,
            minLngPt = null,
            maxPt = null,
            minPt = null,
            i;

        for (i = latLngs.length - 1; i >= 0; i--) {
          var pt = latLngs[i];

          if (maxLat === false || pt.lat > maxLat) {
            maxLatPt = pt;
            maxLat = pt.lat;
          }

          if (minLat === false || pt.lat < minLat) {
            minLatPt = pt;
            minLat = pt.lat;
          }

          if (maxLng === false || pt.lng > maxLng) {
            maxLngPt = pt;
            maxLng = pt.lng;
          }

          if (minLng === false || pt.lng < minLng) {
            minLngPt = pt;
            minLng = pt.lng;
          }
        }

        if (minLat !== maxLat) {
          minPt = minLatPt;
          maxPt = maxLatPt;
        } else {
          minPt = minLngPt;
          maxPt = maxLngPt;
        }

        var ch = [].concat(this.buildConvexHull([minPt, maxPt], latLngs), this.buildConvexHull([maxPt, minPt], latLngs));
        return ch;
      }
    };
  })();

  L.MarkerCluster.include({
    getConvexHull: function getConvexHull() {
      var childMarkers = this.getAllChildMarkers(),
          points = [],
          p,
          i;

      for (i = childMarkers.length - 1; i >= 0; i--) {
        p = childMarkers[i].getLatLng();
        points.push(p);
      }

      return L.QuickHull.getConvexHull(points);
    }
  });
  L.MarkerCluster.include({
    _2PI: Math.PI * 2,
    _circleFootSeparation: 25,
    _circleStartAngle: Math.PI / 6,
    _spiralFootSeparation: 28,
    _spiralLengthStart: 11,
    _spiralLengthFactor: 5,
    _circleSpiralSwitchover: 9,
    spiderfy: function spiderfy() {
      if (this._group._isSpiderfied(this)) {
        return;
      }

      var childMarkers = this.getAllChildMarkers(null, true),
          group = this._group,
          map = group._map,
          center = map.latLngToLayerPoint(this._latlng),
          positions;

      this._group._addSpiderfied(this);

      if (childMarkers.length >= this._circleSpiralSwitchover) {
        positions = this._generatePointsSpiral(childMarkers.length, center);
      } else {
        center.y += 10;
        positions = this._generatePointsCircle(childMarkers.length, center);
      }

      this._animationSpiderfy(childMarkers, positions);
    },
    unspiderfy: function unspiderfy(zoomDetails) {
      this._animationUnspiderfy(zoomDetails);

      this._group._removeSpiderfied(this);
    },
    _generatePointsCircle: function _generatePointsCircle(count, centerPt) {
      var circumference = this._group.options.spiderfyDistanceMultiplier * this._circleFootSeparation * (2 + count),
          legLength = circumference / this._2PI,
          angleStep = this._2PI / count,
          res = [],
          i,
          angle;
      res.length = count;

      for (i = 0; i < count; i++) {
        angle = this._circleStartAngle + i * angleStep;
        res[i] = new L.Point(centerPt.x + legLength * Math.cos(angle), centerPt.y + legLength * Math.sin(angle))._round();
      }

      return res;
    },
    _generatePointsSpiral: function _generatePointsSpiral(count, centerPt) {
      var spiderfyDistanceMultiplier = this._group.options.spiderfyDistanceMultiplier,
          legLength = spiderfyDistanceMultiplier * this._spiralLengthStart,
          separation = spiderfyDistanceMultiplier * this._spiralFootSeparation,
          lengthFactor = spiderfyDistanceMultiplier * this._spiralLengthFactor * this._2PI,
          angle = 0,
          res = [],
          i;
      res.length = count;

      for (i = count; i >= 0; i--) {
        if (i < count) {
          res[i] = new L.Point(centerPt.x + legLength * Math.cos(angle), centerPt.y + legLength * Math.sin(angle))._round();
        }

        angle += separation / legLength + i * 0.0005;
        legLength += lengthFactor / angle;
      }

      return res;
    },
    _noanimationUnspiderfy: function _noanimationUnspiderfy() {
      var group = this._group,
          map = group._map,
          fg = group._featureGroup,
          childMarkers = this.getAllChildMarkers(null, true),
          m,
          i;
      group._ignoreMove = true;
      this.setOpacity(1);

      for (i = childMarkers.length - 1; i >= 0; i--) {
        m = childMarkers[i];
        fg.removeLayer(m);

        if (m._preSpiderfyLatlng) {
          m.setLatLng(m._preSpiderfyLatlng);
          delete m._preSpiderfyLatlng;
        }

        if (m.setZIndexOffset) {
          m.setZIndexOffset(0);
        }

        if (m._spiderLeg) {
          map.removeLayer(m._spiderLeg);
          delete m._spiderLeg;
        }
      }

      group.fire('unspiderfied', {
        cluster: this,
        markers: childMarkers
      });

      group._removeSpiderfied(this);
    }
  });
  L.MarkerClusterNonAnimated = L.MarkerCluster.extend({
    _animationSpiderfy: function _animationSpiderfy(childMarkers, positions) {
      var group = this._group,
          map = group._map,
          fg = group._featureGroup,
          legOptions = this._group.options.spiderLegPolylineOptions,
          i,
          m,
          leg,
          newPos;

      for (i = 0; i < childMarkers.length; i++) {
        newPos = map.layerPointToLatLng(positions[i]);
        m = childMarkers[i];
        leg = new L.Polyline([this._latlng, newPos], legOptions);
        map.addLayer(leg);
        m._spiderLeg = leg;
        m._preSpiderfyLatlng = m._latlng;
        m.setLatLng(newPos);

        if (m.setZIndexOffset) {
          m.setZIndexOffset(1000000);
        }

        fg.addLayer(m);
      }

      this.setOpacity(0.3);
      group.fire('spiderfied', {
        cluster: this,
        markers: childMarkers
      });
    },
    _animationSpiderfyOld: function _animationSpiderfyOld(childMarkers, positions) {
      var group = this._group,
          map = group._map,
          fg = group._featureGroup,
          legOptions = this._group.options.spiderLegPolylineOptions,
          i,
          m,
          leg,
          newPos;
      group._ignoreMove = true;

      for (i = 0; i < childMarkers.length; i++) {
        newPos = map.layerPointToLatLng(positions[i]);
        m = childMarkers[i];
        leg = new L.Polyline([this._latlng, newPos], legOptions);
        map.addLayer(leg);
        m._spiderLeg = leg;
        m._preSpiderfyLatlng = m._latlng;
        m.setLatLng(newPos);

        if (m.setZIndexOffset) {
          m.setZIndexOffset(1000000);
        }

        fg.addLayer(m);
      }

      this.setOpacity(0.3);
      group._ignoreMove = false;
      group.fire('spiderfied', {
        cluster: this,
        markers: childMarkers
      });
    },
    _animationUnspiderfy: function _animationUnspiderfy() {
      this._noanimationUnspiderfy();
    }
  });
  L.MarkerCluster.include({
    _animationSpiderfy: function _animationSpiderfy(childMarkers, positions) {
      var me = this,
          group = this._group,
          map = group._map,
          fg = group._featureGroup,
          thisLayerLatLng = this._latlng,
          thisLayerPos = map.latLngToLayerPoint(thisLayerLatLng),
          svg = L.Path.SVG,
          legOptions = L.extend({}, this._group.options.spiderLegPolylineOptions),
          finalLegOpacity = legOptions.opacity,
          i,
          m,
          leg,
          legPath,
          legLength,
          newPos;

      if (finalLegOpacity === undefined) {
        finalLegOpacity = L.MarkerClusterGroup.prototype.options.spiderLegPolylineOptions.opacity;
      }

      if (svg) {
        legOptions.opacity = 0;
        legOptions.className = (legOptions.className || '') + ' leaflet-cluster-spider-leg';
      } else {
        legOptions.opacity = finalLegOpacity;
      }

      group._ignoreMove = true;

      for (i = 0; i < childMarkers.length; i++) {
        m = childMarkers[i];
        newPos = map.layerPointToLatLng(positions[i]);
        leg = new L.Polyline([thisLayerLatLng, newPos], legOptions);
        map.addLayer(leg);
        m._spiderLeg = leg;

        if (svg) {
          legPath = leg._path;
          legLength = legPath.getTotalLength() + 0.1;
          legPath.style.strokeDasharray = legLength;
          legPath.style.strokeDashoffset = legLength;
        }

        if (m.setZIndexOffset) {
          m.setZIndexOffset(1000000);
        }

        if (m.clusterHide) {
          m.clusterHide();
        }

        fg.addLayer(m);

        if (m._setPos) {
          m._setPos(thisLayerPos);
        }
      }

      group._forceLayout();

      group._animationStart();

      for (i = childMarkers.length - 1; i >= 0; i--) {
        newPos = map.layerPointToLatLng(positions[i]);
        m = childMarkers[i];
        m._preSpiderfyLatlng = m._latlng;
        m.setLatLng(newPos);

        if (m.clusterShow) {
          m.clusterShow();
        }

        if (svg) {
          leg = m._spiderLeg;
          legPath = leg._path;
          legPath.style.strokeDashoffset = 0;
          leg.setStyle({
            opacity: finalLegOpacity
          });
        }
      }

      this.setOpacity(0.3);
      group._ignoreMove = false;
      setTimeout(function () {
        group._animationEnd();

        group.fire('spiderfied', {
          cluster: me,
          markers: childMarkers
        });
      }, 200);
    },
    _animationUnspiderfy: function _animationUnspiderfy(zoomDetails) {
      var me = this,
          group = this._group,
          map = group._map,
          fg = group._featureGroup,
          thisLayerPos = zoomDetails ? map._latLngToNewLayerPoint(this._latlng, zoomDetails.zoom, zoomDetails.center) : map.latLngToLayerPoint(this._latlng),
          childMarkers = this.getAllChildMarkers(null, true),
          svg = L.Path.SVG,
          m,
          i,
          leg,
          legPath,
          legLength,
          nonAnimatable;
      group._ignoreMove = true;

      group._animationStart();

      this.setOpacity(1);

      for (i = childMarkers.length - 1; i >= 0; i--) {
        m = childMarkers[i];

        if (!m._preSpiderfyLatlng) {
          continue;
        }

        m.closePopup();
        m.setLatLng(m._preSpiderfyLatlng);
        delete m._preSpiderfyLatlng;
        nonAnimatable = true;

        if (m._setPos) {
          m._setPos(thisLayerPos);

          nonAnimatable = false;
        }

        if (m.clusterHide) {
          m.clusterHide();
          nonAnimatable = false;
        }

        if (nonAnimatable) {
          fg.removeLayer(m);
        }

        if (svg) {
          leg = m._spiderLeg;
          legPath = leg._path;
          legLength = legPath.getTotalLength() + 0.1;
          legPath.style.strokeDashoffset = legLength;
          leg.setStyle({
            opacity: 0
          });
        }
      }

      group._ignoreMove = false;
      setTimeout(function () {
        var stillThereChildCount = 0;

        for (i = childMarkers.length - 1; i >= 0; i--) {
          m = childMarkers[i];

          if (m._spiderLeg) {
            stillThereChildCount++;
          }
        }

        for (i = childMarkers.length - 1; i >= 0; i--) {
          m = childMarkers[i];

          if (!m._spiderLeg) {
            continue;
          }

          if (m.clusterShow) {
            m.clusterShow();
          }

          if (m.setZIndexOffset) {
            m.setZIndexOffset(0);
          }

          if (stillThereChildCount > 1) {
            fg.removeLayer(m);
          }

          map.removeLayer(m._spiderLeg);
          delete m._spiderLeg;
        }

        group._animationEnd();

        group.fire('unspiderfied', {
          cluster: me,
          markers: childMarkers
        });
      }, 200);
    }
  });
  L.MarkerClusterGroup.include({
    _spiderfied: {},
    _isSpiderfied: function _isSpiderfied(marker) {
      return this._spiderfied[marker._leaflet_id];
    },
    _addSpiderfied: function _addSpiderfied(marker) {
      this._spiderfied[marker._leaflet_id] = marker;
    },
    _removeSpiderfied: function _removeSpiderfied(marker) {
      delete this._spiderfied[marker._leaflet_id];
    },
    _spiderfierOnAdd: function _spiderfierOnAdd() {
      if (this.options.unspiderfyOnMapClick) {
        this._map.on('click', this._unspiderfyWrapper, this);
      }
    },
    _spiderfierOnRemove: function _spiderfierOnRemove() {
      this._map.off('click', this._unspiderfyWrapper, this);

      this._map.off('zoomstart', this._unspiderfyZoomStart, this);

      this._map.off('zoomanim', this._unspiderfyZoomAnim, this);

      this._map.off('zoomend', this._noanimationUnspiderfy, this);

      this._noanimationUnspiderfy();
    },
    _unspiderfyZoomStart: function _unspiderfyZoomStart() {
      if (!this._map) {
        return;
      }

      this._map.on('zoomanim', this._unspiderfyZoomAnim, this);
    },
    _unspiderfyZoomAnim: function _unspiderfyZoomAnim() {
      if (L.DomUtil.hasClass(this._map._mapPane, 'leaflet-touching')) {
        return;
      }

      this._map.off('zoomanim', this._unspiderfyZoomAnim, this);
    },
    _unspiderfyWrapper: function _unspiderfyWrapper() {
      this._unspiderfy();
    },
    _unspiderfy: function _unspiderfy(zoomDetails) {
      for (var mKey in this._spiderfied) {
        if (this._spiderfied.hasOwnProperty(mKey)) {
          this._spiderfied[mKey].unspiderfy(zoomDetails);
        }
      }
    },
    _noanimationUnspiderfy: function _noanimationUnspiderfy() {
      for (var mKey in this._spiderfied) {
        if (this._spiderfied.hasOwnProperty(mKey)) {
          this._spiderfied[mKey]._noanimationUnspiderfy();
        }
      }
    },
    _unspiderfyLayer: function _unspiderfyLayer(layer) {
      if (layer._spiderLeg) {
        this._featureGroup.removeLayer(layer);

        if (layer.clusterShow) {
          layer.clusterShow();
        }

        if (layer.setZIndexOffset) {
          layer.setZIndexOffset(0);
        }

        this._map.removeLayer(layer._spiderLeg);

        delete layer._spiderLeg;
      }
    }
  });
  L.MarkerClusterGroup.include({
    refreshClusters: function refreshClusters(layers) {
      if (!layers) {
        layers = this._topClusterLevel.getAllChildMarkers();
      } else if (layers instanceof L.MarkerClusterGroup) {
        layers = layers._topClusterLevel.getAllChildMarkers();
      } else if (layers instanceof L.LayerGroup) {
        layers = layers._layers;
      } else if (layers instanceof L.MarkerCluster) {
        layers = layers.getAllChildMarkers();
      } else if (layers instanceof L.Marker) {
        layers = [layers];
      }

      this._flagParentsIconsNeedUpdate(layers);

      this._refreshClustersIcons();

      if (this.options.singleMarkerMode) {
        this._refreshSingleMarkerModeMarkers(layers);
      }

      return this;
    },
    _flagParentsIconsNeedUpdate: function _flagParentsIconsNeedUpdate(layers) {
      var id, parent;

      for (id in layers) {
        parent = layers[id].__parent;

        while (parent) {
          parent._iconNeedsUpdate = true;
          parent = parent.__parent;
        }
      }
    },
    _refreshSingleMarkerModeMarkers: function _refreshSingleMarkerModeMarkers(layers) {
      var id, layer;

      for (id in layers) {
        layer = layers[id];

        if (this.hasLayer(layer)) {
          layer.setIcon(this._overrideMarkerIcon(layer));
        }
      }
    }
  });
  L.Marker.include({
    refreshIconOptions: function refreshIconOptions(options, directlyRefreshClusters) {
      var icon = this.options.icon;
      L.setOptions(icon, options);
      this.setIcon(icon);

      if (directlyRefreshClusters && this.__parent) {
        this.__parent._group.refreshClusters(this);
      }

      return this;
    }
  });
}

function getColorsByLastPostedInMinutes(feature) {
  var minutes = feature.properties.lastPostedInMinutes;

  if (minutes === undefined) {
    return stateColorCode[0];
  }

  if (minutes <= 30) {
    return stateColorCode[5];
  }

  if (minutes > 30 && minutes <= 120) {
    return stateColorCode[3];
  }

  if (minutes > 120) {
    return stateColorCode[2];
  }

  if (minutes === -1) {
    return stateColorCode[1];
  }

  return stateColorCode[1];
}

function prepareMarker(feature, latlng, presentationMode, isSelected) {
  var opts = {};
  opts.markerColor = '#fff';
  opts.iconColor = '#000';
  opts.selected = false;

  switch (presentationMode) {
    case 'initial':
      {
        opts.markerColor = 'gray';
        opts.iconColor = 'white';
        break;
      }

    case 'soilstatus':
      {
        if (['aquacheck_soil', 'pixl_soil'].includes(feature.properties.type) && feature.properties.block) {
          var soilState = feature.properties.soilState;

          if (!soilState) {
            opts.markerColor = 'gray';
            opts.iconColor = 'white';
            break;
          }

          opts.markerColor = getColorCodeForSoilState(soilState);
          opts.iconColor = 'white';
          break;
        }

        opts.markerColor = 'white';
        opts.iconColor = 'black';
        break;
      }

    case 'connectivity':
      {
        var color = getColorsByLastPostedInMinutes(feature);
        opts.markerColor = color.markerColor;
        opts.iconColor = 'white';
        break;
      }

    case 'waterpressure':
      {
        opts.markerColor = 'gray';
        opts.iconColor = 'black';

        if (feature.properties.waterPressure !== undefined) {
          opts.data = {};
          opts.data.value = feature.properties.waterPressure;
          opts.data.valueColor = feature.properties.waterPressureRatio < 0.5 ? 'black' : 'white';

          if (feature.properties.waterPressureColor) {
            opts.markerColor = feature.properties.waterPressureColor;
          }
        }

        break;
      }

    case 'control':
      {
        if (feature.properties.online) {
          opts.markerColor = 'white';
          opts.iconColor = 'black';
        } else {
          opts.markerColor = 'gray';
          opts.iconColor = 'black';
        }

        if (feature.properties.type === 'water_pressure') {
          if (feature.properties.waterPressure !== undefined) {
            opts.data = {};
            opts.data.value = feature.properties.waterPressure;
            opts.data.valueColor = feature.properties.waterPressureRatio < 0.5 ? 'black' : 'white';

            if (feature.properties.waterPressureColor) {
              opts.markerColor = feature.properties.waterPressureColor;
            }
          }
        } else if (feature.properties.controlSateON) {
          opts.markerColor = '#3C61B4';
          opts.iconColor = 'white';
        }

        break;
      }

    case 'provision':
      {
        opts.iconColor = 'white';
        opts.markerColor = '#e23549';

        if (feature.properties.provisioned) {
          opts.markerColor = '#3dc873';
        }

        break;
      }

    case 'anomaly':
      {
        opts.markerColor = getAnomalyIconColorBySeverity(feature.properties.anomaly_severity);
        opts.iconColor = '#fff';
        break;
      }

    case 'cropHealth':
      {
        var _ref = feature && feature.properties ? feature.properties : {},
            cropStress = _ref.cropStress;

        var cropState = getCropStateByStress(new Set([cropStress]));
        var cropHealthColor = stateColorCode[cropState] || {};
        opts.iconColor = cropHealthColor.iconColor;
        opts.markerColor = cropHealthColor.markerColor;
        break;
      }

    default:
      {
        opts.markerColor = '#fff';
        opts.iconColor = '#000';
        opts.selected = false;
      }
  }

  if (feature.properties.alarmCount) {
    opts.markerBorderColor = 'red';
  }

  var markerIcon = L$1.VectorMarkers.icon({
    icon: presentationMode === 'anomaly' ? getAnomalyIcon(feature.properties.anomaly_type) : icons[feature.properties.type] || 'icon-radio-waves',
    prefix: 'icon',
    markerColor: opts.markerColor,
    iconColor: opts.iconColor,
    dataTestId: feature.id.split('/').join('-'),
    selected: opts.selected,
    selectedClass: isSelected ? 'marker-is-selected' : '',
    data: opts.data,
    markerBorderColor: opts.markerBorderColor
  });
  var marker = new L$1.marker(latlng, {
    icon: markerIcon
  });
  return marker;
}
function prepareSingleMarkerIcon() {
  var opts = {};
  opts.markerColor = '#fff';
  opts.iconColor = '#000';
  opts.selected = false;
  return L$1.VectorMarkers.icon({
    icon: 'icon-radio-waves',
    prefix: 'icon',
    markerColor: opts.markerColor,
    iconColor: opts.iconColor,
    dataTestId: 'single-marker',
    selected: opts.selected
  });
}

var createDivIcon = function createDivIcon(markersLength, c) {
  return new L$1.DivIcon({
    html: "<div><span>" + markersLength + "</span></div>",
    className: "marker-cluster " + c,
    iconSize: new L$1.Point(40, 40)
  });
};

var prepareMarkerClusterIcon = function prepareMarkerClusterIcon(presentationMode) {
  return function (cluster) {
    var childCount = cluster.getChildCount();
    var c = 'marker-cluster-';

    if (childCount < 10) {
      c += 'small';
    } else if (childCount < 100) {
      c += 'medium';
    } else {
      c += 'large';
    }

    var markers = cluster.getAllChildMarkers();
    var markersLength = markers.length;

    var _calculateGroupStateF = calculateGroupStateForPresentationModes(markers, true),
        connectivityState = _calculateGroupStateF.connectivityState,
        waterPressureState = _calculateGroupStateF.waterPressureState,
        provisionState = _calculateGroupStateF.provisionState,
        soilState = _calculateGroupStateF.soilState,
        controlState = _calculateGroupStateF.controlState;

    if (presentationMode === 'initial') {
      c = 'marker-cluster-unknown';
      return createDivIcon(markersLength, c);
    }

    if (presentationMode === 'soilstatus') {
      switch (soilState) {
        case 1:
          c = 'marker-cluster-critical';
          break;

        case 2:
          c = 'marker-cluster-bad';
          break;

        case 3:
          c = 'marker-cluster-warning';
          break;

        case 4:
          c = 'marker-cluster-over';
          break;

        case 5:
          c = 'marker-cluster-good';
          break;

        default:
          c = 'marker-cluster-unknown';
      }

      return createDivIcon(markersLength, c);
    }

    if (presentationMode === 'connectivity') {
      switch (connectivityState) {
        case 0:
          c = 'marker-cluster-unknown';
          break;

        case -1:
          c = 'marker-cluster-offline';
          break;

        case 2:
          c = 'marker-cluster-critical';
          break;

        case 3:
          c = 'marker-cluster-warning';
          break;

        case 5:
          c = 'marker-cluster-good';
          break;

        default:
          c = 'marker-cluster-unknown';
      }

      return createDivIcon(markersLength, c);
    }

    if (presentationMode === 'waterpressure') {
      switch (waterPressureState) {
        case 0:
          c = 'marker-cluster-unknown';
          break;

        case 4:
          c = 'marker-cluster-over';
          break;

        default:
          c = 'marker-cluster-unknown';
      }

      return createDivIcon(markersLength, c);
    }

    if (presentationMode === 'control') {
      switch (controlState) {
        case 0:
          c = 'marker-cluster-unknown';
          break;

        case 4:
          c = 'marker-cluster-over';
          break;

        default:
          c = 'marker-cluster-unknown';
      }

      return createDivIcon(markersLength, c);
    }

    if (presentationMode === 'provision') {
      switch (provisionState) {
        case 2:
          c = 'marker-cluster-critical';
          break;

        case 5:
          c = 'marker-cluster-good';
          break;

        default:
          c = 'marker-cluster-unknown';
      }

      return createDivIcon(markersLength, c);
    }

    return createDivIcon(markersLength, c);
  };
};

leafletMarkerClusterGroup(L$1);

var shouldSpiderfy = function shouldSpiderfy(c) {
  return c.getChildCount() < 7;
};

function MarkerCluster(_ref) {
  var sensorsGeoJSON = _ref.sensorsGeoJSON,
      showAllSensorLabels = _ref.showAllSensorLabels,
      activePopUp = _ref.activePopUp,
      selectSensor = _ref.selectSensor,
      showPopUpOnMarkerClick = _ref.showPopUpOnMarkerClick,
      presentationMode = _ref.presentationMode,
      selectedSensor = _ref.selectedSensor,
      refreshMap = _ref.refreshMap;
  var marketClusterGroup = L$1.markerClusterGroup({
    maxClusterRadius: 35,
    unspiderfyOnMapClick: false,
    shouldSpiderfy: shouldSpiderfy,
    animate: false,
    iconCreateFunction: prepareMarkerClusterIcon(presentationMode),
    spiderLegPolylineOptions: {
      weight: 1.5,
      color: '#eee',
      opacity: 0.8
    },
    polygonOptions: {
      stroke: false,
      fill: false
    }
  });

  var _useLeaflet = useLeaflet(),
      map = _useLeaflet.map;

  var prevsensorsGeoJSON = useRef(JSON.stringify({}));
  var markers = useRef([]);

  var onMarkerPopupOpen = function onMarkerPopupOpen(_ref2) {
    var _ref2$target$feature$ = _ref2.target.feature.properties,
        type = _ref2$target$feature$.type,
        identifier = _ref2$target$feature$.identifier;
    selectSensor(type, identifier);
  };

  useEffect(function () {
    var destroy = function destroy() {
      markers.current.forEach(function (marker) {
        marker.off('popupopen', onMarkerPopupOpen);
      });
      marketClusterGroup.clearLayers();
    };

    return destroy;
  }, []);
  useEffect(function () {
    markers.current.forEach(function (marker) {
      if (marker.unbindTooltip) {
        marker.unbindTooltip();
      }
    });

    if (showAllSensorLabels) {
      markers.current.forEach(function (marker) {
        marker.bindTooltip(getMarkerTooltipByPresentationMode(marker.feature, presentationMode), {
          direction: 'auto',
          permanent: true,
          className: marker.feature.properties.type + "-" + marker.feature.properties.identifier
        });
      });
    }
  }, [showAllSensorLabels]);
  useEffect(function () {
    if (typeof sensorsGeoJSON === 'undefined') {
      marketClusterGroup.clearLayers();
      markers.current = [];
      return;
    }

    var sensorsGeoJSONString = JSON.stringify(sensorsGeoJSON);

    if (prevsensorsGeoJSON.current !== sensorsGeoJSONString) {
      marketClusterGroup.clearLayers();
      markers.current = [];
      sensorsGeoJSON.features = sensorsGeoJSON.features.filter(function (feature) {
        return feature.geometry.coordinates[0] && feature.geometry.coordinates[1];
      });

      if (!sensorsGeoJSON.features.length) {
        return;
      }

      var geojson = L$1.geoJSON(sensorsGeoJSON, {
        pointToLayer: function pointToLayer(feature, latlng) {
          var marker = prepareMarker(feature, latlng, presentationMode);
          markers.current.push(marker);

          if (showPopUpOnMarkerClick) {
            var popupContent = "<div class=\"popup-inner\" data-test-id=\"popup-" + feature.id + "\">\n              <div class=\"popup-title\">" + feature.properties.type + "</div>\n              <div>" + feature.properties.identifier + "</div>\n              <div class=\"popup-location\">" + feature.geometry.coordinates[1] + ", " + feature.geometry.coordinates[0] + "</div>\n              </div>";
            marker.bindPopup(popupContent, {
              autoPan: false
            });
          }

          if (showAllSensorLabels) {
            marker.bindTooltip(getMarkerTooltipByPresentationMode(feature, presentationMode), {
              direction: 'auto',
              permanent: true,
              className: feature.properties.type + "-" + feature.properties.identifier
            });
          } else if (presentationMode === 'connectivity') {
            marker.bindTooltip(feature.properties.name || 'NO NAME', {
              direction: 'auto',
              permanent: false,
              className: feature.properties.type + "-" + feature.properties.identifier
            });
          }

          marker.on('click', onMarkerPopupOpen);
          return marker;
        }
      });
      marketClusterGroup.addLayer(geojson);
      map.addLayer(marketClusterGroup);
      map.setZoom(Math.round(map.getZoom()));
      prevsensorsGeoJSON.current = sensorsGeoJSONString;
    }
  }, [sensorsGeoJSON]);
  useEffect(function () {
    if (activePopUp === Object(activePopUp) && activePopUp.identifier) {
      var identifier = activePopUp.identifier;
      var markersLength = markers.current.length;

      for (var i = 0; i < markersLength; i += 1) {
        var marker = markers.current[i];

        if (marker.feature.properties.identifier === identifier) {
          marker.openPopup();
          break;
        }
      }
    }
  }, [activePopUp, map]);
  useEffect(function () {
    markers.current.forEach(function (marker) {
      var iconOpts = marker.getIcon().options;

      if (selectedSensor && marker.feature.properties.identifier === selectedSensor.properties.identifier && marker.feature.properties.type === selectedSensor.properties.type) {
        marker.setIcon(L$1.VectorMarkers.icon(_extends({}, iconOpts, {
          selected: true
        })));
      } else if (iconOpts.selected) {
        marker.setIcon(L$1.VectorMarkers.icon(_extends({}, iconOpts, {
          selected: false
        })));
      }
    });
  }, [selectedSensor]);
  refreshMap();
  return null;
}
MarkerCluster.propTypes = {
  activePopUp: PropTypes.object,
  selectSensor: PropTypes.func,
  showAllSensorLabels: PropTypes.bool,
  sensorsGeoJSON: PropTypes.shape({
    type: PropTypes.string,
    features: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      type: PropTypes.string,
      geometry: PropTypes.shape({
        coordinates: PropTypes.arrayOf(PropTypes.number),
        type: PropTypes.string
      }),
      properties: PropTypes.object
    }))
  }),
  showPopUpOnMarkerClick: PropTypes.bool,
  presentationMode: PropTypes.string,
  refreshMap: PropTypes.func.isRequired,
  selectedSensor: PropTypes.any
};
MarkerCluster.defaultProps = {
  activePopUp: undefined,
  selectSensor: undefined,
  showAllSensorLabels: undefined,
  sensorsGeoJSON: undefined,
  showPopUpOnMarkerClick: true,
  presentationMode: undefined,
  selectedSensor: undefined
};

function leafletVectorMarkers(L) {
  if (L.VectorMarkers) {
    return;
  }

  L.VectorMarkers = {};
  L.VectorMarkers.version = "1.0.0";
  L.VectorMarkers.MAP_PIN = 'M6.19,28.81a16,16,0,1,1,22.63,0L17.5,40.13Z';
  L.VectorMarkers.PIN_INNER = 'M7.9,27.1a13.58,13.58,0,1,1,19.21,0l-9.6,9.6Z';
  L.VectorMarkers.Icon = L.Icon.extend({
    options: {
      iconSize: [32, 40],
      iconAnchor: [16, 40],
      popupAnchor: [2, -40],
      shadowAnchor: [24, 24],
      shadowSize: [32, 40],
      className: "vector-marker",
      prefix: "fa",
      spinClass: "fa-spin",
      extraClasses: "",
      extraClassesSvg: "",
      icon: "home",
      markerColor: "blue",
      iconColor: "white",
      selected: false,
      selectedClass: '',
      data: undefined
    },
    initialize: function initialize(options) {
      return options = L.Util.setOptions(this, options);
    },
    createIcon: function createIcon(oldIcon) {
      var div, icon, options, pin_path, pin_inner;
      div = oldIcon && oldIcon.tagName === "DIV" ? oldIcon : document.createElement("div");
      options = this.options;
      div.setAttribute('data-test-id', "marker-" + options.dataTestId);

      if (options.data && typeof options.data.value !== 'undefiend') {
        var x = 10;

        if (Number(options.data.value) === 0) {
          x = 14;
        } else if (Number(options.data.value) >= 100) {
          x = 7;
        } else if (Number(options.data.value) >= 10) {
          x = 11;
        } else if (String(options.data.value).split('').length === 1) {
          x = 14;
        } else {
          x = 10;
        }

        icon = "<text x=\"" + x + "\" y=\"22\" fill=\"" + options.data.valueColor + "\" font-weight=\"bold\">" + options.data.value + "</text>";
      } else if (options.icon) {
        icon = this._createInner();
      }

      pin_path = L.VectorMarkers.MAP_PIN;
      pin_inner = L.VectorMarkers.PIN_INNER;
      var borderColor = options.markerBorderColor || options.markerColor;

      if (icon) {
        div.innerHTML = '<svg width="35px" height="40px" ' + ("class=\"" + options.extraClassesSvg + "\"") + ' viewBox="0 0 35 40" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">' + '<path class="pin-outer ' + ("marker-pin-outer-" + options.dataTestId) + '" d="' + pin_path + '" fill="' + borderColor + '"></path>' + '<path class="pin-inner ' + ("marker-pin-inner-" + options.dataTestId) + '" d="' + pin_inner + '" fill="' + options.markerColor + '"></path>' + icon + '</svg>';
      } else {
        div.innerHTML = '<svg width="35px" height="40px" ' + ("class=\"" + options.extraClassesSvg + "\"") + ' viewBox="0 0 35 40" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">' + '<path class="pin-outer ' + ("marker-pin-outer-" + options.dataTestId) + '" d="' + pin_path + '" fill="gray"></path>' + '<path class="pin-inner ' + ("marker-pin-inner-" + options.dataTestId) + '" d="' + pin_inner + '" fill="gray"></path>' + '<text x="7" y="22" fill="white" font-weight="bold">N/A</text>' + '</svg>';
      }

      this._setIconStyles(div, "icon");

      if (options.selected) {
        this._setIconStyles(div, " selected-icon");
      }

      return div;
    },
    _createInner: function _createInner() {
      var iconClass, iconColorClass, iconColorStyle, iconSpinClass, options;
      iconClass = void 0;
      iconSpinClass = "";
      iconColorClass = "";
      iconColorStyle = "";
      var options = this.options;

      if (options.data) {
        return;
      }

      if (options.icon.slice(0, options.prefix.length + 1) === options.prefix + "-") {
        iconClass = options.icon;
      } else {
        iconClass = options.prefix + "-" + options.icon;
      }

      if (options.spin && typeof options.spinClass === "string") {
        iconSpinClass = options.spinClass;
      }

      if (options.iconColor) {
        if (options.iconColor === "white" || options.iconColor === "black") {
          iconColorClass = "icon-" + options.iconColor;
        } else {
          iconColorStyle = "style='color: " + options.iconColor + "' ";
        }
      }

      return "<i " + iconColorStyle + "class='" + options.extraClasses + " " + options.prefix + " " + iconClass + " " + iconSpinClass + " " + iconColorClass + "'></i>";
    },
    _setIconStyles: function _setIconStyles(img, name) {
      var anchor, options, size;
      options = this.options;
      size = L.point(options[name === "shadow" ? "shadowSize" : "iconSize"]);
      anchor = void 0;

      if (name === "shadow") {
        anchor = L.point(options.shadowAnchor || options.iconAnchor);
      } else {
        anchor = L.point(options.iconAnchor);
      }

      if (!anchor && size) {
        anchor = size.divideBy(2, true);
      }

      img.className = "vector-marker" + name + " " + options.className;

      if (options.selectedClass) {
        img.classList.add(options.selectedClass);
      }

      if (anchor) {
        img.style.marginLeft = -anchor.x + "px";
        img.style.marginTop = -anchor.y + "px";
      }

      if (size) {
        img.style.width = size.x + "px";
        return img.style.height = size.y + "px";
      }
    },
    createShadow: function createShadow() {
      var div;
      div = document.createElement("div");

      this._setIconStyles(div, "shadow");

      return div;
    }
  });

  L.VectorMarkers.icon = function (options) {
    return new L.VectorMarkers.Icon(options);
  };
}

(function () {
  var originalInitTile = L.GridLayer.prototype._initTile;
  L.GridLayer.include({
    _initTile: function _initTile(tile) {
      originalInitTile.call(this, tile);
      var tileSize = this.getTileSize();
      tile.style.width = tileSize.x + 1 + 'px';
      tile.style.height = tileSize.y + 1 + 'px';
    }
  });
})();

function AnomalyLayer(props) {
  var anomalyGeoJSON = props.anomalyGeoJSON,
      presentationMode = props.presentationMode,
      isShowingImagery = props.isShowingImagery,
      zoomShowSensorsState = props.zoomShowSensorsState,
      onClickBlock = props.onClickBlock,
      setState = props.setState,
      mapState = props.mapState,
      mapRef = props.mapRef,
      previousZoom = props.previousZoom,
      deleteAnomaly = props.deleteAnomaly,
      onSelectAnomaly = props.onSelectAnomaly,
      createAnomaly = props.createAnomaly,
      onDrawAnomaly = props.onDrawAnomaly,
      userAreaUnit = props.userAreaUnit;
  var selectedPolygons = useRef([]);
  var isDelete = useRef(false);
  var polygonDrawer = useRef(false);
  var feature = anomalyGeoJSON;
  var style = {
    color: (feature === null || feature === void 0 ? void 0 : feature.properties) && feature.properties.anomaly_severity ? getAnomalyIconColorBySeverity(feature.properties.anomaly_severity) : '#3388ff',
    dashArray: '8 8',
    weight: 3,
    opacity: 1.0,
    fillColor: '#000000',
    fillOpacity: 0.0,
    editing: {
      dashArray: '10, 10',
      fill: true,
      fillColor: '#fe57a1',
      fillOpacity: 0.1,
      maintainColor: false
    }
  };
  var selected = false;
  var dataToRender = (feature === null || feature === void 0 ? void 0 : feature.type) === 'FeatureCollection' ? feature.features.filter(function (d) {
    return d && d.properties.anomaly_type;
  }) : [feature];

  function setElementStyle(elem, styleObj) {
    elem.setAttribute('stroke', styleObj.stroke);
    elem.setAttribute('fill', styleObj.fill);
    elem.setAttribute('fill-opacity', styleObj.opacity);
  }

  function onClickAnomaly(d) {
    if (d && isDelete.current) {
      var _ids;

      var ids = [];

      if (selectedPolygons.current.length) {
        ids = selectedPolygons.current.map(function (e) {
          return e._leaflet_id;
        });
      }

      if (!((_ids = ids) === null || _ids === void 0 ? void 0 : _ids.includes(d._leaflet_id))) {
        selectedPolygons.current.push(d);
        var elem = d._path;
        setElementStyle(elem, {
          stroke: '#fe57a1',
          fill: '#fe57a1',
          opacity: 0.5
        });

        if (selectedPolygons.current.length && onSelectAnomaly) {
          onSelectAnomaly({
            enable: false
          });
        }
      } else {
        var _elem = d._path;
        setElementStyle(_elem, {
          stroke: '#3388ff',
          fill: '#000000',
          opacity: 0
        });
        selectedPolygons.current = selectedPolygons.current.filter(function (e) {
          return e._leaflet_id !== d._leaflet_id;
        });

        if (!selectedPolygons.current.length && onSelectAnomaly) {
          onSelectAnomaly({
            enable: true
          });
        }
      }
    }
  }

  function whenClicked(e) {
    onClickAnomaly(e === null || e === void 0 ? void 0 : e.target);
  }

  function _onEachFeature(fe, layer) {
    layer.on({
      click: whenClicked
    });
  }

  function clearTooltip() {
    var element = document.querySelector('.leaflet-draw-tooltip');

    if (element) {
      element.style.display = 'none';
    }
  }

  useEffect(function () {
    if (!anomalyGeoJSON && previousZoom.current) {
      setState(_extends({}, mapState, {
        viewBox: previousZoom.current
      }));
      clearTooltip();
    }
  }, [anomalyGeoJSON]);
  useEffect(function () {
    isDelete.current = deleteAnomaly === null || deleteAnomaly === void 0 ? void 0 : deleteAnomaly["delete"];
  }, [deleteAnomaly, isDelete]);

  function updateTooltipText(element) {
    var childElementText = element.firstChild.innerHTML;
    var changedText = childElementText.split(' ')[0].split('<br>')[1];

    if (changedText) {
      var defaultUnit = childElementText.split(' ')[1];
      var defaultText = document.querySelector('.leaflet-draw-tooltip').firstChild.innerHTML;
      var tooltipText = defaultText.replace(defaultUnit, userAreaUnit);
      document.querySelector('.leaflet-draw-tooltip-subtext').innerHTML = tooltipText;
    }
  }

  function customTooltip() {
    var element = document.querySelector('.leaflet-draw-tooltip');

    if (element) {
      updateTooltipText(element);
    }
  }

  useEffect(function () {
    if (createAnomaly === null || createAnomaly === void 0 ? void 0 : createAnomaly.visible) {
      if ((createAnomaly === null || createAnomaly === void 0 ? void 0 : createAnomaly.type) === 'create') {
        polygonDrawer.current = new L$1.Draw.Polygon(mapRef.current, {
          showArea: true,
          allowIntersection: false,
          color: '#3388ff',
          dashArray: '8 8',
          weight: 3,
          opacity: 1.0,
          fillColor: '#000000',
          fillOpacity: 0.0,
          shapeOptions: {
            stroke: true,
            color: '#3388ff',
            weight: 3,
            opacity: 1.0,
            fillColor: '#000000',
            fillOpacity: 0.0,
            dashArray: '8 8',
            fill: false,
            clickable: false,
            editing: {
              dashArray: '10, 10',
              fill: true,
              fillColor: '#fe57a1',
              fillOpacity: 0.1,
              maintainColor: false
            }
          }
        });
        polygonDrawer.current.enable();
        L$1.DomUtil.addClass(mapRef.current._container, 'crosshair-cursor-enabled');
      }

      if ((createAnomaly === null || createAnomaly === void 0 ? void 0 : createAnomaly.type) === 'edit') {
        var editableLayer;
        mapRef.current.eachLayer(function (layer) {
          var _layer$options, _layer$options2, _layer$options2$data$, _layer$options2$data$2, _anomalyGeoJSON$prope;

          if ((layer === null || layer === void 0 ? void 0 : (_layer$options = layer.options) === null || _layer$options === void 0 ? void 0 : _layer$options.data) && (layer === null || layer === void 0 ? void 0 : (_layer$options2 = layer.options) === null || _layer$options2 === void 0 ? void 0 : (_layer$options2$data$ = _layer$options2.data[0]) === null || _layer$options2$data$ === void 0 ? void 0 : (_layer$options2$data$2 = _layer$options2$data$.properties) === null || _layer$options2$data$2 === void 0 ? void 0 : _layer$options2$data$2.id) === (anomalyGeoJSON === null || anomalyGeoJSON === void 0 ? void 0 : (_anomalyGeoJSON$prope = anomalyGeoJSON.properties) === null || _anomalyGeoJSON$prope === void 0 ? void 0 : _anomalyGeoJSON$prope.id)) {
            editableLayer = layer;
          }
        });

        if (editableLayer) {
          polygonDrawer.current = new L$1.EditToolbar.Edit(mapRef.current, {
            featureGroup: editableLayer,
            dashArray: '10, 10',
            fill: true,
            fillColor: '#fe57a1',
            fillOpacity: 0.1,
            maintainColor: false,
            selectedPathOptions: {
              dashArray: '10, 10',
              fill: true,
              fillColor: '#fe57a1',
              fillOpacity: 0.1,
              maintainColor: false
            },
            shapeOptions: {
              stroke: true,
              color: '#3388ff',
              weight: 3,
              opacity: 1.0,
              fillColor: '#000000',
              fillOpacity: 0.0,
              dashArray: '8 8',
              fill: false,
              clickable: false
            }
          });
          polygonDrawer.current.enable();
        } else if (onDrawAnomaly) onDrawAnomaly({
          type: 'close',
          visible: false
        });
      }

      if ((createAnomaly === null || createAnomaly === void 0 ? void 0 : createAnomaly.type) === 'undo') {
        customTooltip();
        polygonDrawer.current.deleteLastVertex();
        customTooltip();

        if (polygonDrawer.current._poly._latlngs.length < 2) {
          if (onDrawAnomaly) onDrawAnomaly({
            type: 'hide-undo'
          });
          L$1.DomUtil.removeClass(mapRef.current._container, 'crosshair-cursor-enabled');
        }
      }
    } else if (!(createAnomaly === null || createAnomaly === void 0 ? void 0 : createAnomaly.visible) && polygonDrawer.current) {
      if ((createAnomaly === null || createAnomaly === void 0 ? void 0 : createAnomaly.type) === 'save') {
        if (createAnomaly === null || createAnomaly === void 0 ? void 0 : createAnomaly.isAnomalyCreate) {
          if (onDrawAnomaly) onDrawAnomaly({
            type: 'create'
          });
          mapRef.current.eachLayer(function (e) {
            var _e$_path, _e$_path$outerHTML;

            if (e === null || e === void 0 ? void 0 : (_e$_path = e._path) === null || _e$_path === void 0 ? void 0 : (_e$_path$outerHTML = _e$_path.outerHTML) === null || _e$_path$outerHTML === void 0 ? void 0 : _e$_path$outerHTML.includes('class="leaflet-interactive"')) {
              mapRef.current.removeLayer(e);
            }
          });
        } else {
          polygonDrawer.current.save();
          polygonDrawer.current.disable();
        }
      }

      if ((createAnomaly === null || createAnomaly === void 0 ? void 0 : createAnomaly.type) === 'cancel') {
        mapRef.current.eachLayer(function (e) {
          var _e$_path2, _e$_path2$outerHTML;

          if (e === null || e === void 0 ? void 0 : (_e$_path2 = e._path) === null || _e$_path2 === void 0 ? void 0 : (_e$_path2$outerHTML = _e$_path2.outerHTML) === null || _e$_path2$outerHTML === void 0 ? void 0 : _e$_path2$outerHTML.includes('class="leaflet-interactive"')) {
            mapRef.current.removeLayer(e);
            if (onDrawAnomaly) onDrawAnomaly();
            L$1.DomUtil.removeClass(mapRef.current._container, 'crosshair-cursor-enabled');
          }
        });
        if (polygonDrawer.current.revertLayers) polygonDrawer.current.revertLayers();
        polygonDrawer.current.disable();
        L$1.DomUtil.removeClass(mapRef.current._container, 'crosshair-cursor-enabled');
        clearTooltip();
      }
    }
  }, [createAnomaly, anomalyGeoJSON]);

  if (document.querySelector('div.map-desktop-container')) {
    document.addEventListener('mousemove', function () {
      customTooltip();
    });
    document.addEventListener('mousedown', function () {
      customTooltip();
    });
  }

  if (document.querySelector('div.map-mobile-container')) {
    document.addEventListener('touchstart', function () {
      customTooltip();
    });
    document.addEventListener('touchmove', function () {
      customTooltip();
    });
    document.addEventListener('touchend', function () {
      customTooltip();
    });
    document.addEventListener('click', function () {
      customTooltip();
    });
  }

  useEffect(function () {
    var _selectedPolygons$cur;

    if (deleteAnomaly === null || deleteAnomaly === void 0 ? void 0 : deleteAnomaly.save) {
      if (onSelectAnomaly) onSelectAnomaly({
        enable: true,
        data: selectedPolygons.current
      });
      selectedPolygons.current.forEach(function (d) {
        mapRef.current.removeLayer(d);
      });
      selectedPolygons.current = [];
    } else if (!(deleteAnomaly === null || deleteAnomaly === void 0 ? void 0 : deleteAnomaly["delete"]) && !(deleteAnomaly === null || deleteAnomaly === void 0 ? void 0 : deleteAnomaly.save) && ((_selectedPolygons$cur = selectedPolygons.current) === null || _selectedPolygons$cur === void 0 ? void 0 : _selectedPolygons$cur.length)) {
      selectedPolygons.current.forEach(function (d) {
        var elem = d._path;
        setElementStyle(elem, {
          stroke: '#3388ff',
          fill: '#000000',
          opacity: 0
        });
      });
      selectedPolygons.current = [];
      if (onSelectAnomaly) onSelectAnomaly({
        enable: true
      });
    }
  }, [deleteAnomaly]);
  useEffect(function () {
    if (!mapRef.current) return;
    mapRef.current.on('draw:drawvertex', function (e) {
      var layers = e.layers;

      if (Object.values(layers._layers).length > 1) {
        if (onDrawAnomaly) onDrawAnomaly({
          type: 'show-undo'
        });
      } else if (Object.values(layers._layers).length) {
        if (onDrawAnomaly) onDrawAnomaly({
          type: 'show-cancel'
        });
      }
    });
    mapRef.current.on('draw:created', function (e) {
      var layer = e.layer;
      layer.addTo(mapRef.current);
      if (onDrawAnomaly) onDrawAnomaly({
        type: 'stop-draw',
        layer: layer
      });
      L$1.DomUtil.removeClass(mapRef.current._container, 'crosshair-cursor-enabled');
    });
    mapRef.current.on('draw:edited', function (e) {
      var layers = e.layers;
      layers.eachLayer(function (layer) {
        var _layer$toGeoJSON;

        var newObj = _extends({}, (_layer$toGeoJSON = layer.toGeoJSON()) === null || _layer$toGeoJSON === void 0 ? void 0 : _layer$toGeoJSON.properties);

        var coordinates = layer.toGeoJSON().geometry.coordinates;
        var editedCoordinates = coordinates[0];
        newObj.bounds.coordinates[0] = editedCoordinates;
        newObj.center.coordinates[0] = layer.getBounds().getCenter().lng;
        newObj.center.coordinates[1] = layer.getBounds().getCenter().lat;
        if (onDrawAnomaly) onDrawAnomaly({
          type: 'edit',
          layer: newObj
        });
        mapRef.current.removeLayer(layer);
      });
    });
  }, []);
  return dataToRender.map(function (d) {
    var _d$geometry;

    return d === null || d === void 0 ? void 0 : (_d$geometry = d.geometry) === null || _d$geometry === void 0 ? void 0 : _d$geometry.coordinates.map(function (c) {
      return /*#__PURE__*/React.createElement(GeoJSON, {
        className: "map-ranch-block-anomaly-boundary-" + feature.name,
        style: style,
        key: "" + presentationMode + JSON.stringify(feature.properties) + "\n        " + selected + isShowingImagery.visible + zoomShowSensorsState,
        data: [_extends({}, d, {
          geometry: {
            type: 'Polygon',
            coordinates: [c]
          }
        })],
        onEachFeature: function onEachFeature(fe, la) {
          return _onEachFeature(fe, la);
        },
        onClick: function onClick() {
          if (onClickBlock) {
            onClickBlock(feature.properties.id);
          }
        }
      });
    });
  });
}

(function () {
  function r(e, n, t) {
    function o(i, f) {
      if (!n[i]) {
        if (!e[i]) {
          var c = "function" == typeof require && require;
          if (!f && c) return c(i, !0);
          if (u) return u(i, !0);
          var a = new Error("Cannot find module '" + i + "'");
          throw a.code = "MODULE_NOT_FOUND", a;
        }

        var p = n[i] = {
          exports: {}
        };
        e[i][0].call(p.exports, function (r) {
          var n = e[i][1][r];
          return o(n || r);
        }, p, p.exports, r, e, n, t);
      }

      return n[i].exports;
    }

    for (var u = "function" == typeof require && require, i = 0; i < t.length; i++) {
      o(t[i]);
    }

    return o;
  }

  return r;
})()({
  1: [function (require, module, exports) {
    (function (global) {
      var L = typeof window !== "undefined" ? window["L"] : typeof global !== "undefined" ? global["L"] : null;

      require("./layout.css");

      require("./range.css");

      var mapWasDragEnabled;
      var mapWasTapEnabled;

      function on(el, types, fn, context) {
        types.split(" ").forEach(function (type) {
          L.DomEvent.on(el, type, fn, context);
        });
      }

      function off(el, types, fn, context) {
        types.split(" ").forEach(function (type) {
          L.DomEvent.off(el, type, fn, context);
        });
      }

      function getRangeEvent(rangeInput) {
        return "oninput" in rangeInput ? "input" : "change";
      }

      function cancelMapDrag() {
        mapWasDragEnabled = this._map.dragging.enabled();
        mapWasTapEnabled = this._map.tap && this._map.tap.enabled();

        this._map.dragging.disable();

        this._map.tap && this._map.tap.disable();
      }

      function uncancelMapDrag(e) {
        this._refocusOnMap(e);

        if (mapWasDragEnabled) {
          this._map.dragging.enable();
        }

        if (mapWasTapEnabled) {
          this._map.tap.enable();
        }
      }

      function asArray(arg) {
        return arg === "undefined" ? [] : Array.isArray(arg) ? arg : [arg];
      }

      function noop() {}

      function isTouch() {
        return "ontouchstart" in window || navigator.MaxTouchPoints > 0 || navigator.msMaxTouchPoints > 0;
      }

      L.Control.SideBySide = L.Control.extend({
        options: {
          thumbSize: 42,
          padding: 0
        },
        initialize: function initialize(leftLayers, rightLayers, options) {
          this.setLeftLayers(leftLayers);
          this.setRightLayers(rightLayers);
          L.setOptions(this, options);
        },
        getPosition: function getPosition() {
          var rangeValue = this._range.value;
          var offset = (0.5 - rangeValue) * (2 * this.options.padding + this.options.thumbSize);
          return this._map.getSize().x * rangeValue + offset;
        },
        setPosition: noop,
        includes: L.Evented.prototype || L.Mixin.Events,
        addTo: function addTo(map) {
          this.remove();
          this._map = map;
          var container = this._container = L.DomUtil.create("div", "leaflet-sbs", map._controlContainer);
          this._divider = L.DomUtil.create("div", "leaflet-sbs-divider", container);
          var range = this._range = L.DomUtil.create("input", "leaflet-sbs-range", container);
          range.type = "range";
          range.min = 0;
          range.max = 1;
          range.step = "any";
          range.value = 0.5;
          range.style.paddingLeft = range.style.paddingRight = this.options.padding + "px";

          this._addEvents();

          this._updateLayers();

          return this;
        },
        remove: function remove() {
          if (!this._map) {
            return this;
          }

          if (this._leftLayer) {
            this._leftLayer.getPane().style.clip = "";
          }

          if (this._rightLayer) {
            this._rightLayer.getPane().style.clip = "";
          }

          this._removeEvents();

          L.DomUtil.remove(this._container);
          this._map = null;
          return this;
        },
        setLeftLayers: function setLeftLayers(leftLayers) {
          this._leftLayers = asArray(leftLayers);

          this._updateLayers();

          return this;
        },
        setRightLayers: function setRightLayers(rightLayers) {
          this._rightLayers = asArray(rightLayers);

          this._updateLayers();

          return this;
        },
        _updateClip: function _updateClip() {
          var map = this._map;
          var nw = map.containerPointToLayerPoint([0, 0]);
          var se = map.containerPointToLayerPoint(map.getSize());
          var clipX = nw.x + this.getPosition();
          var dividerX = this.getPosition();
          this._divider.style.left = dividerX + "px";
          this.fire("dividermove", {
            x: dividerX
          });
          var clipLeft = "rect(" + [nw.y, clipX, se.y, nw.x].join("px,") + "px)";
          var clipRight = "rect(" + [nw.y, se.x, se.y, clipX].join("px,") + "px)";

          if (this._leftLayer) {
            this._leftLayer.getPane().style.clip = clipLeft;
          }

          if (this._rightLayer) {
            this._rightLayer.getPane().style.clip = clipRight;
          }
        },
        _updateLayers: function _updateLayers() {
          if (!this._map) {
            return this;
          }

          var prevLeft = this._leftLayer;
          var prevRight = this._rightLayer;
          this._leftLayer = this._rightLayer = null;

          this._leftLayers.forEach(function (layer) {
            if (this._map.hasLayer(layer)) {
              this._leftLayer = layer;
            }
          }, this);

          this._rightLayers.forEach(function (layer) {
            if (this._map.hasLayer(layer)) {
              this._rightLayer = layer;
            }
          }, this);

          if (prevLeft !== this._leftLayer) {
            prevLeft && this.fire("leftlayerremove", {
              layer: prevLeft
            });
            this._leftLayer && this.fire("leftlayeradd", {
              layer: this._leftLayer
            });
          }

          if (prevRight !== this._rightLayer) {
            prevRight && this.fire("rightlayerremove", {
              layer: prevRight
            });
            this._rightLayer && this.fire("rightlayeradd", {
              layer: this._rightLayer
            });
          }

          this._updateClip();
        },
        _addEvents: function _addEvents() {
          var range = this._range;
          var map = this._map;
          if (!map || !range) return;
          map.on("move", this._updateClip, this);
          map.on("layeradd layerremove", this._updateLayers, this);
          on(range, getRangeEvent(range), this._updateClip, this);
          on(range, isTouch() ? "touchstart" : "mousedown", cancelMapDrag, this);
          on(range, isTouch() ? "touchend" : "mouseup", uncancelMapDrag, this);
        },
        _removeEvents: function _removeEvents() {
          var range = this._range;
          var map = this._map;

          if (range) {
            off(range, getRangeEvent(range), this._updateClip, this);
            off(range, L.Browser.touch ? "touchstart" : "mousedown", cancelMapDrag, this);
            off(range, L.Browser.touch ? "touchend" : "mouseup", uncancelMapDrag, this);
          }

          if (map) {
            map.off("layeradd layerremove", this._updateLayers, this);
            map.off("move", this._updateClip, this);
          }
        }
      });

      L.control.sideBySide = function (leftLayers, rightLayers, options) {
        return new L.Control.SideBySide(leftLayers, rightLayers, options);
      };

      module.exports = L.Control.SideBySide;
    }).call(this, typeof global !== "undefined" ? global : typeof self !== "undefined" ? self : typeof window !== "undefined" ? window : {});
  }, {
    "./layout.css": 2,
    "./range.css": 4
  }],
  2: [function (require, module, exports) {
    var inject = require("./node_modules/cssify");

    var css = ".leaflet-sbs-range {\r\n    position: absolute;\r\n    top: 50%;\r\n    width: 100%;\r\n    z-index: 999;\r\n}\r\n.leaflet-sbs-divider {\r\n    position: absolute;\r\n    top: 0;\r\n    bottom: 0;\r\n    left: 50%;\r\n    margin-left: -2px;\r\n    width: 4px;\r\n    background-color: #fff;\r\n    pointer-events: none;\r\n    z-index: 999;\r\n}\r\n";
    inject(css, undefined, "_i6aomd");
    module.exports = css;
  }, {
    "./node_modules/cssify": 3
  }],
  3: [function (require, module, exports) {

    function injectStyleTag(document, fileName, cb) {
      var style = document.getElementById(fileName);

      if (style) {
        cb(style);
      } else {
        var head = document.getElementsByTagName("head")[0];
        style = document.createElement("style");
        if (fileName != null) style.id = fileName;
        cb(style);
        head.appendChild(style);
      }

      return style;
    }

    module.exports = function (css, customDocument, fileName) {
      var doc = customDocument || document;

      if (doc.createStyleSheet) {
        var sheet = doc.createStyleSheet();
        sheet.cssText = css;
        return sheet.ownerNode;
      } else {
        return injectStyleTag(doc, fileName, function (style) {
          if (style.styleSheet) {
            style.styleSheet.cssText = css;
          } else {
            style.innerHTML = css;
          }
        });
      }
    };

    module.exports.byUrl = function (url) {
      if (document.createStyleSheet) {
        return document.createStyleSheet(url).ownerNode;
      } else {
        var head = document.getElementsByTagName("head")[0];
        var link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = url;
        head.appendChild(link);
        return link;
      }
    };
  }, {}],
  4: [function (require, module, exports) {
    var inject = require("./node_modules/cssify");

    var css = '.leaflet-sbs-range {\r\n    -webkit-appearance: none;\r\n    display: inline-block!important;\r\n    vertical-align: middle;\r\n    height: 0;\r\n    padding: 0;\r\n    margin: 0;\r\n    border: 0;\r\n    background: rgba(0, 0, 0, 0.25);\r\n    min-width: 100px;\r\n    cursor: pointer;\r\n    pointer-events: none;\r\n    z-index: 999;\r\n}\r\n.leaflet-sbs-range::-ms-fill-upper {\r\n    background: transparent;\r\n}\r\n.leaflet-sbs-range::-ms-fill-lower {\r\n    background: rgba(255, 255, 255, 0.25);\r\n}\r\n/* Browser thingies */\r\n\r\n.leaflet-sbs-range::-moz-range-track {\r\n    opacity: 0;\r\n}\r\n.leaflet-sbs-range::-ms-track {\r\n    opacity: 0;\r\n}\r\n.leaflet-sbs-range::-ms-tooltip {\r\n    display: none;\r\n}\r\n/* For whatever reason, these need to be defined\r\n * on their own so dont group them */\r\n\r\n.leaflet-sbs-range::-webkit-slider-thumb {\r\n    -webkit-appearance: none;\r\n    margin: 0;\r\n    padding: 0;\r\n    background: #fff;\r\n    height: 40px;\r\n    width: 40px;\r\n    border-radius: 20px;\r\n    cursor: ew-resize;\r\n    pointer-events: auto;\r\n    border: 1px solid #ddd;\r\n    background-image: url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAFAAAABQCAMAAAC5zwKfAAAABlBMVEV9fX3///+Kct39AAAAAnRSTlP/AOW3MEoAAAA9SURBVFjD7dehDQAwDANBZ/+l2wmKoiqR7pHRcaeaCxAIBAL/g7k9JxAIBAKBQCAQCAQC14H+MhAIBE4CD3fOFvGVBzhZAAAAAElFTkSuQmCC");\r\n    background-position: 50% 50%;\r\n    background-repeat: no-repeat;\r\n    background-size: 40px 40px;\r\n}\r\n.leaflet-sbs-range::-ms-thumb {\r\n    margin: 0;\r\n    padding: 0;\r\n    background: #fff;\r\n    height: 40px;\r\n    width: 40px;\r\n    border-radius: 20px;\r\n    cursor: ew-resize;\r\n    pointer-events: auto;\r\n    border: 1px solid #ddd;\r\n    background-image: url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAFAAAABQCAMAAAC5zwKfAAAABlBMVEV9fX3///+Kct39AAAAAnRSTlP/AOW3MEoAAAA9SURBVFjD7dehDQAwDANBZ/+l2wmKoiqR7pHRcaeaCxAIBAL/g7k9JxAIBAKBQCAQCAQC14H+MhAIBE4CD3fOFvGVBzhZAAAAAElFTkSuQmCC");\r\n    background-position: 50% 50%;\r\n    background-repeat: no-repeat;\r\n    background-size: 40px 40px;\r\n}\r\n.leaflet-sbs-range::-moz-range-thumb {\r\n    padding: 0;\r\n    right: 0    ;\r\n    background: #fff;\r\n    height: 40px;\r\n    width: 40px;\r\n    border-radius: 20px;\r\n    cursor: ew-resize;\r\n    pointer-events: auto;\r\n    border: 1px solid #ddd;\r\n    background-image: url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAFAAAABQCAMAAAC5zwKfAAAABlBMVEV9fX3///+Kct39AAAAAnRSTlP/AOW3MEoAAAA9SURBVFjD7dehDQAwDANBZ/+l2wmKoiqR7pHRcaeaCxAIBAL/g7k9JxAIBAKBQCAQCAQC14H+MhAIBE4CD3fOFvGVBzhZAAAAAElFTkSuQmCC");\r\n    background-position: 50% 50%;\r\n    background-repeat: no-repeat;\r\n    background-size: 40px 40px;\r\n}\r\n.leaflet-sbs-range:disabled::-moz-range-thumb {\r\n    cursor: default;\r\n}\r\n.leaflet-sbs-range:disabled::-ms-thumb {\r\n    cursor: default;\r\n}\r\n.leaflet-sbs-range:disabled::-webkit-slider-thumb {\r\n    cursor: default;\r\n}\r\n.leaflet-sbs-range:disabled {\r\n    cursor: default;\r\n}\r\n.leaflet-sbs-range:focus {\r\n    outline: none!important;\r\n}\r\n.leaflet-sbs-range::-moz-focus-outer {\r\n    border: 0;\r\n}\r\n\r\n';
    inject(css, undefined, "_1tlt668");
    module.exports = css;
  }, {
    "./node_modules/cssify": 3
  }]
}, {}, [1]);

var isEqual = require('react-fast-compare');

var useMapSideBySideView = function useMapSideBySideView(mapRef, showSideBySideMap, layersControl, isShowingImagery) {
  var sideBySideView = useRef(null);

  function getTileLayer(type, name, side) {
    var tileLayerByType;

    if (type === 'soil') {
      tileLayerByType = L$1.tileLayer('https://map.farmx.co/api/soil/wms/?x={x}&y={y}&z={z}', {
        zIndex: 5,
        showOnSelector: false,
        minZoom: 0,
        maxZoom: 19,
        name: name,
        pane: side
      });
    }

    if (type === 'satellite') {
      tileLayerByType = L$1.tileLayer('', {
        tileSize: 512,
        zoomOffset: -1,
        attributionControl: false,
        name: name,
        pane: side,
        zIndex: 0
      });
    }

    return tileLayerByType;
  }

  useEffect(function () {
    var _layersControl$soil, _layersControl$soil2, _layersControl$soil2$, _isShowingImagery$oth;

    if (!mapRef.current) return;

    function removeSideBySideLayerAndSlider() {
      mapRef.current.eachLayer(function (layer) {
        if (layer.options.attribution === 'satellite') {
          mapRef.current.removeLayer(layer);
        }
      });
      var mapElem = document.querySelectorAll('.leaflet-sbs');
      if (mapElem === null || mapElem === void 0 ? void 0 : mapElem.length) mapElem.forEach(function (d) {
        d.remove();
      });
      var leafletLayerElem = document.querySelectorAll('.leaflet-layer');

      if (leafletLayerElem === null || leafletLayerElem === void 0 ? void 0 : leafletLayerElem.length) {
        leafletLayerElem.forEach(function (d) {
          var _d$style;

          if (d === null || d === void 0 ? void 0 : (_d$style = d.style) === null || _d$style === void 0 ? void 0 : _d$style.clip) d.remove();
        });
      }
    }

    function removePanes() {
      var lPane = mapRef.current.getPane('left');
      var rPane = mapRef.current.getPane('right');

      if (lPane) {
        L$1.DomUtil.remove(lPane);
      }

      if (rPane) {
        L$1.DomUtil.remove(rPane);
      }
    }

    if (mapRef.current && !showSideBySideMap) {
      removePanes();
      removeSideBySideLayerAndSlider();
      return;
    }

    if (sideBySideView.current && showSideBySideMap) {
      removePanes();
      removeSideBySideLayerAndSlider();
    }

    mapRef.current.createPane('left');
    mapRef.current.createPane('right');
    var streetLayer = getTileLayer('satellite', 'leftLayer', 'left').addTo(mapRef.current);
    var satelliteLayer = getTileLayer('satellite', 'rightLayer', 'right').addTo(mapRef.current);
    var soilLayerLeft = L$1.featureGroup();
    var soilLayerRight = L$1.featureGroup();

    if (layersControl === null || layersControl === void 0 ? void 0 : (_layersControl$soil = layersControl.soil) === null || _layersControl$soil === void 0 ? void 0 : _layersControl$soil.left.visible) {
      soilLayerLeft = getTileLayer('soil', 'soil', 'left').addTo(mapRef.current);
    }

    if (layersControl === null || layersControl === void 0 ? void 0 : (_layersControl$soil2 = layersControl.soil) === null || _layersControl$soil2 === void 0 ? void 0 : (_layersControl$soil2$ = _layersControl$soil2.right) === null || _layersControl$soil2$ === void 0 ? void 0 : _layersControl$soil2$.visible) {
      soilLayerRight = getTileLayer('soil', 'soil', 'right').addTo(mapRef.current);
    }

    var leftLayer = [];
    var rightLayer = [];

    if ((isShowingImagery === null || isShowingImagery === void 0 ? void 0 : isShowingImagery.data) || (isShowingImagery === null || isShowingImagery === void 0 ? void 0 : (_isShowingImagery$oth = isShowingImagery.otherSide) === null || _isShowingImagery$oth === void 0 ? void 0 : _isShowingImagery$oth.data)) {
      var _isShowingImagery$oth2, _isShowingImagery$oth3;

      if ((isShowingImagery === null || isShowingImagery === void 0 ? void 0 : isShowingImagery.data) && (isShowingImagery === null || isShowingImagery === void 0 ? void 0 : isShowingImagery.visible)) {
        isShowingImagery.data.forEach(function (d) {
          var latlng = L$1.GeoJSON.coordsToLatLngs(JSON.parse(d.bounds).coordinates, 1);
          var bounds = new L$1.LatLngBounds(latlng);
          var imageUrl = d.png_url;
          var options = {
            opacity: 90 / 100,
            alt: 'satellite imagery',
            attribution: 'satellite',
            pane: 'left'
          };
          var imageLayer = L$1.imageOverlay(imageUrl, bounds, options);
          imageLayer.addTo(mapRef.current).bringToBack();
          leftLayer.push(imageLayer);
        });
      }

      if ((isShowingImagery === null || isShowingImagery === void 0 ? void 0 : (_isShowingImagery$oth2 = isShowingImagery.otherSide) === null || _isShowingImagery$oth2 === void 0 ? void 0 : _isShowingImagery$oth2.data) && (isShowingImagery === null || isShowingImagery === void 0 ? void 0 : (_isShowingImagery$oth3 = isShowingImagery.otherSide) === null || _isShowingImagery$oth3 === void 0 ? void 0 : _isShowingImagery$oth3.visible)) {
        isShowingImagery.otherSide.data.forEach(function (d) {
          var latlng = L$1.GeoJSON.coordsToLatLngs(JSON.parse(d.bounds).coordinates, 1);
          var bounds = new L$1.LatLngBounds(latlng);
          var imageUrl = d.png_url;
          var options = {
            opacity: 90 / 100,
            alt: 'satellite imagery',
            attribution: 'satellite',
            pane: 'right'
          };
          var imageLayer = L$1.imageOverlay(imageUrl, bounds, options);
          imageLayer.addTo(mapRef.current).bringToBack();
          rightLayer.push(imageLayer);
        });
      }
    }

    sideBySideView.current = L$1.control.sideBySide([streetLayer, soilLayerLeft, leftLayer], [satelliteLayer, soilLayerRight, rightLayer]).addTo(mapRef.current);
  }, [isShowingImagery, layersControl, mapRef, showSideBySideMap]);
};
var useMapGeoJSONData = function useMapGeoJSONData(mapRef, mounted, exposeZoomChange, ranchesGeoJSON, recenterGeoJSON, expandedGeoJSON, dynamicBoundsGeoJSON, sensorsGeoJSON, singleMarkerGeoJSON, mapBoxPadding, width, height, state, anomalyGeoJSON, maxZoom, setState, previousZoom) {
  useEffect(function () {
    var _anomalyGeoJSON$prope, _anomalyGeoJSON$prope2, _anomalyGeoJSON$featu, _anomalyGeoJSON$featu2, _anomalyGeoJSON$featu3, _anomalyGeoJSON$featu4;

    if (!mounted.current) {
      return;
    }

    if (typeof exposeZoomChange === 'function' && mapRef.current) {
      exposeZoomChange(mapRef.current.getZoom(), mapRef.current);
    }

    var incomingState = {
      recenterGeoJSON: recenterGeoJSON,
      expandedGeoJSON: expandedGeoJSON,
      ranchesGeoJSON: ranchesGeoJSON,
      dynamicBoundsGeoJSON: dynamicBoundsGeoJSON,
      sensorsGeoJSON: sensorsGeoJSON,
      mapBoxPadding: mapBoxPadding,
      width: width,
      height: height,
      viewBox: state.viewBox
    };

    if (isEqual(incomingState, state) && !anomalyGeoJSON) {
      return;
    }

    if (recenterGeoJSON && recenterGeoJSON.features.length && recenterGeoJSON.features[0]) {
      try {
        if (!width || !height) {
          return;
        }

        var _bbox = bbox(recenterGeoJSON),
            minLng = _bbox[0],
            minLat = _bbox[1],
            maxLng = _bbox[2],
            maxLat = _bbox[3];

        var webMercatorViewport = new WebMercatorViewport(_extends({}, state.viewBox, {
          width: width,
          height: height
        }));

        var _webMercatorViewport$ = webMercatorViewport.fitBounds([[minLng, minLat], [maxLng, maxLat]], {
          padding: {
            top: mapBoxPadding.top,
            bottom: mapBoxPadding.bottom,
            left: mapBoxPadding.left,
            right: mapBoxPadding.right
          }
        }),
            longitude = _webMercatorViewport$.longitude,
            latitude = _webMercatorViewport$.latitude,
            zoom = _webMercatorViewport$.zoom;

        var newState = _extends({}, state, {
          recenterGeoJSON: recenterGeoJSON,
          expandedGeoJSON: expandedGeoJSON,
          ranchesGeoJSON: ranchesGeoJSON,
          dynamicBoundsGeoJSON: dynamicBoundsGeoJSON,
          sensorsGeoJSON: sensorsGeoJSON,
          mapBoxPadding: mapBoxPadding,
          viewBox: {
            longitude: longitude,
            latitude: latitude,
            zoom: zoom > maxZoom ? maxZoom : zoom
          },
          width: width,
          height: height
        });

        previousZoom.current = newState.viewBox;
        setState(newState);
      } catch (error) {
        console.log('WebMercatorViewport recenterGeoJSON', error);
      }

      if (!anomalyGeoJSON) {
        return;
      }
    }

    if (anomalyGeoJSON && ((anomalyGeoJSON === null || anomalyGeoJSON === void 0 ? void 0 : (_anomalyGeoJSON$prope = anomalyGeoJSON.properties) === null || _anomalyGeoJSON$prope === void 0 ? void 0 : (_anomalyGeoJSON$prope2 = _anomalyGeoJSON$prope.center) === null || _anomalyGeoJSON$prope2 === void 0 ? void 0 : _anomalyGeoJSON$prope2.coordinates) || (anomalyGeoJSON === null || anomalyGeoJSON === void 0 ? void 0 : (_anomalyGeoJSON$featu = anomalyGeoJSON.features) === null || _anomalyGeoJSON$featu === void 0 ? void 0 : (_anomalyGeoJSON$featu2 = _anomalyGeoJSON$featu[0]) === null || _anomalyGeoJSON$featu2 === void 0 ? void 0 : (_anomalyGeoJSON$featu3 = _anomalyGeoJSON$featu2.properties) === null || _anomalyGeoJSON$featu3 === void 0 ? void 0 : (_anomalyGeoJSON$featu4 = _anomalyGeoJSON$featu3.center) === null || _anomalyGeoJSON$featu4 === void 0 ? void 0 : _anomalyGeoJSON$featu4.coordinates))) {
      var _anomalyGeoJSON$featu5;

      var _bbox2 = bbox(anomalyGeoJSON),
          _minLng = _bbox2[0],
          _minLat = _bbox2[1],
          _maxLng = _bbox2[2],
          _maxLat = _bbox2[3];

      var _webMercatorViewport = new WebMercatorViewport(_extends({}, state.viewBox, {
        width: width,
        height: height
      }));

      if (!(anomalyGeoJSON === null || anomalyGeoJSON === void 0 ? void 0 : anomalyGeoJSON.features) || (anomalyGeoJSON === null || anomalyGeoJSON === void 0 ? void 0 : (_anomalyGeoJSON$featu5 = anomalyGeoJSON.features) === null || _anomalyGeoJSON$featu5 === void 0 ? void 0 : _anomalyGeoJSON$featu5.length)) {
        var _webMercatorViewport$2 = _webMercatorViewport.fitBounds([[_minLng, _minLat], [_maxLng, _maxLat]], {
          padding: {
            top: mapBoxPadding.top,
            bottom: mapBoxPadding.bottom,
            left: mapBoxPadding.left,
            right: mapBoxPadding.right
          }
        }),
            _longitude = _webMercatorViewport$2.longitude,
            _latitude = _webMercatorViewport$2.latitude,
            _zoom = _webMercatorViewport$2.zoom;

        var customZoomSize = _zoom;
        if (anomalyGeoJSON === null || anomalyGeoJSON === void 0 ? void 0 : anomalyGeoJSON.customZoom) customZoomSize = _zoom + 0.6;
        var zoomChanged = _zoom > maxZoom ? maxZoom : customZoomSize;

        if (anomalyGeoJSON === null || anomalyGeoJSON === void 0 ? void 0 : anomalyGeoJSON.features) {
          zoomChanged = _zoom > maxZoom ? maxZoom : _zoom + 0.75;
        }

        var changedViewBox = {
          latitude: _latitude,
          longitude: _longitude,
          zoom: zoomChanged
        };
        setState(_extends({}, state, {
          viewBox: changedViewBox
        }));
      }

      return;
    }

    if (expandedGeoJSON && expandedGeoJSON.features.length) {
      try {
        if (!width || !height) {
          return;
        }

        var _bbox3 = bbox(expandedGeoJSON),
            _minLng2 = _bbox3[0],
            _minLat2 = _bbox3[1],
            _maxLng2 = _bbox3[2],
            _maxLat2 = _bbox3[3];

        var _webMercatorViewport2 = new WebMercatorViewport(_extends({}, state.viewBox, {
          width: width,
          height: height
        }));

        var _webMercatorViewport3 = _webMercatorViewport2.fitBounds([[_minLng2, _minLat2], [_maxLng2, _maxLat2]], {
          padding: {
            top: mapBoxPadding.top,
            bottom: mapBoxPadding.bottom,
            left: mapBoxPadding.left,
            right: mapBoxPadding.right
          }
        }),
            _longitude2 = _webMercatorViewport3.longitude,
            _latitude2 = _webMercatorViewport3.latitude,
            _zoom2 = _webMercatorViewport3.zoom;

        var _newState = _extends({}, state, {
          recenterGeoJSON: recenterGeoJSON,
          expandedGeoJSON: expandedGeoJSON,
          ranchesGeoJSON: ranchesGeoJSON,
          dynamicBoundsGeoJSON: dynamicBoundsGeoJSON,
          sensorsGeoJSON: sensorsGeoJSON,
          mapBoxPadding: mapBoxPadding,
          viewBox: {
            longitude: _longitude2,
            latitude: _latitude2,
            zoom: _zoom2 > maxZoom ? maxZoom : _zoom2
          },
          width: width,
          height: height
        });

        setState(_newState);
      } catch (error) {
        console.log('WebMercatorViewport expandedGeoJSON', error);
      }

      return;
    }

    if (!ranchesGeoJSON && !sensorsGeoJSON && singleMarkerGeoJSON) {
      try {
        if (!width || !height) {
          return;
        }

        var _newState2 = _extends({}, state, {
          recenterGeoJSON: recenterGeoJSON,
          expandedGeoJSON: expandedGeoJSON,
          ranchesGeoJSON: ranchesGeoJSON,
          dynamicBoundsGeoJSON: dynamicBoundsGeoJSON,
          sensorsGeoJSON: sensorsGeoJSON,
          mapBoxPadding: mapBoxPadding,
          singleMarkerGeoJSON: singleMarkerGeoJSON,
          viewBox: {
            longitude: singleMarkerGeoJSON.geometry.coordinates[1],
            latitude: singleMarkerGeoJSON.geometry.coordinates[0],
            zoom: 17
          },
          width: width,
          height: height
        });

        setState(_newState2);
        return;
      } catch (error) {
        console.log('WebMercatorViewport singleMarkerGeoJSON', error);
      }
    }

    if (dynamicBoundsGeoJSON && dynamicBoundsGeoJSON.features.length) {
      try {
        if (!width || !height) {
          return;
        }

        var _bbox4 = bbox(dynamicBoundsGeoJSON),
            _minLng3 = _bbox4[0],
            _minLat3 = _bbox4[1],
            _maxLng3 = _bbox4[2],
            _maxLat3 = _bbox4[3];

        var _webMercatorViewport4 = new WebMercatorViewport(_extends({}, state.viewBox, {
          width: width,
          height: height
        }));

        var _webMercatorViewport5 = _webMercatorViewport4.fitBounds([[_minLng3, _minLat3], [_maxLng3, _maxLat3]], {
          padding: {
            top: mapBoxPadding.top,
            bottom: mapBoxPadding.bottom,
            left: mapBoxPadding.left,
            right: mapBoxPadding.right
          }
        }),
            _longitude3 = _webMercatorViewport5.longitude,
            _latitude3 = _webMercatorViewport5.latitude,
            _zoom3 = _webMercatorViewport5.zoom;

        var _newState3 = _extends({}, state, {
          recenterGeoJSON: recenterGeoJSON,
          expandedGeoJSON: expandedGeoJSON,
          ranchesGeoJSON: ranchesGeoJSON,
          dynamicBoundsGeoJSON: dynamicBoundsGeoJSON,
          sensorsGeoJSON: sensorsGeoJSON,
          mapBoxPadding: mapBoxPadding,
          viewBox: {
            longitude: _longitude3,
            latitude: _latitude3,
            zoom: _zoom3 > maxZoom ? maxZoom : _zoom3
          },
          width: width,
          height: height
        });

        setState(_newState3);
      } catch (error) {
        console.log('WebMercatorViewport ranchesGeoJSON', error);
      }

      return;
    }

    if (ranchesGeoJSON && ranchesGeoJSON.features.length && ranchesGeoJSON.features[0]) {
      try {
        if (!width || !height) {
          return;
        }

        var _bbox5 = bbox(ranchesGeoJSON),
            _minLng4 = _bbox5[0],
            _minLat4 = _bbox5[1],
            _maxLng4 = _bbox5[2],
            _maxLat4 = _bbox5[3];

        var _webMercatorViewport6 = new WebMercatorViewport(_extends({}, state.viewBox, {
          width: width,
          height: height
        }));

        var _webMercatorViewport7 = _webMercatorViewport6.fitBounds([[_minLng4, _minLat4], [_maxLng4, _maxLat4]], {
          padding: {
            top: mapBoxPadding.top,
            bottom: mapBoxPadding.bottom,
            left: mapBoxPadding.left,
            right: mapBoxPadding.right
          }
        }),
            _longitude4 = _webMercatorViewport7.longitude,
            _latitude4 = _webMercatorViewport7.latitude,
            _zoom4 = _webMercatorViewport7.zoom;

        var _newState4 = _extends({}, state, {
          recenterGeoJSON: recenterGeoJSON,
          expandedGeoJSON: expandedGeoJSON,
          ranchesGeoJSON: ranchesGeoJSON,
          dynamicBoundsGeoJSON: dynamicBoundsGeoJSON,
          sensorsGeoJSON: sensorsGeoJSON,
          mapBoxPadding: mapBoxPadding,
          viewBox: {
            longitude: _longitude4,
            latitude: _latitude4,
            zoom: _zoom4 > maxZoom ? maxZoom : _zoom4
          },
          width: width,
          height: height
        });

        setState(_newState4);
      } catch (error) {
        console.log('WebMercatorViewport ranchesGeoJSON', error);
      }

      return;
    }

    if (!sensorsGeoJSON) {
      setState(_extends({}, state, {
        recenterGeoJSON: recenterGeoJSON,
        expandedGeoJSON: expandedGeoJSON,
        ranchesGeoJSON: ranchesGeoJSON,
        dynamicBoundsGeoJSON: dynamicBoundsGeoJSON,
        sensorsGeoJSON: sensorsGeoJSON,
        mapBoxPadding: mapBoxPadding
      }));
      return;
    }

    sensorsGeoJSON.features = sensorsGeoJSON.features.filter(function (feature) {
      return feature.geometry.coordinates[0] && feature.geometry.coordinates[1];
    });

    if (!sensorsGeoJSON.features.length) {
      setState(_extends({}, state, {
        recenterGeoJSON: recenterGeoJSON,
        expandedGeoJSON: expandedGeoJSON,
        ranchesGeoJSON: ranchesGeoJSON,
        dynamicBoundsGeoJSON: dynamicBoundsGeoJSON,
        sensorsGeoJSON: sensorsGeoJSON,
        mapBoxPadding: mapBoxPadding
      }));
      return;
    }

    try {
      if (!width || !height) {
        return;
      }

      var _bbox6 = bbox(sensorsGeoJSON),
          _minLng5 = _bbox6[0],
          _minLat5 = _bbox6[1],
          _maxLng5 = _bbox6[2],
          _maxLat5 = _bbox6[3];

      var _webMercatorViewport8 = new WebMercatorViewport(_extends({}, state.viewBox, {
        width: width,
        height: height
      }));

      var _webMercatorViewport9 = _webMercatorViewport8.fitBounds([[_minLng5, _minLat5], [_maxLng5, _maxLat5]], {
        padding: {
          top: mapBoxPadding.top,
          bottom: mapBoxPadding.bottom,
          left: mapBoxPadding.left,
          right: mapBoxPadding.right
        }
      }),
          _longitude5 = _webMercatorViewport9.longitude,
          _latitude5 = _webMercatorViewport9.latitude,
          _zoom5 = _webMercatorViewport9.zoom;

      var _newState5 = _extends({}, state, {
        recenterGeoJSON: recenterGeoJSON,
        expandedGeoJSON: expandedGeoJSON,
        ranchesGeoJSON: ranchesGeoJSON,
        dynamicBoundsGeoJSON: dynamicBoundsGeoJSON,
        sensorsGeoJSON: sensorsGeoJSON,
        mapBoxPadding: mapBoxPadding,
        viewBox: {
          longitude: _longitude5,
          latitude: _latitude5,
          zoom: _zoom5 > maxZoom ? maxZoom : _zoom5
        },
        width: width,
        height: height
      });

      setState(_newState5);
    } catch (error) {
      console.log('WebMercatorViewport sensorsGeoJSON', error);
    }
  }, [recenterGeoJSON, expandedGeoJSON, singleMarkerGeoJSON, ranchesGeoJSON, dynamicBoundsGeoJSON, sensorsGeoJSON, mapBoxPadding, width, height, anomalyGeoJSON]);
};

function RanchLayer(props) {
  var ranchesGeoJSON = props.ranchesGeoJSON,
      presentationMode = props.presentationMode,
      expandedGeoJSON = props.expandedGeoJSON,
      colors = props.colors,
      onClickRanch = props.onClickRanch,
      zoomShowSensorsState = props.zoomShowSensorsState;
  return ranchesGeoJSON.features.map(function (feature) {
    var selected = false;
    var featureRanch = cloneDeep_1(feature);
    featureRanch.geometry.type = 'MultiLineString';
    var style = {
      color: 'white',
      weight: 2
    };

    try {
      if ((expandedGeoJSON === null || expandedGeoJSON === void 0 ? void 0 : expandedGeoJSON.features) && expandedGeoJSON.features.length === 1 && expandedGeoJSON.features[0].properties.id === feature.properties.id) {
        delete style.dashArray;
        selected = true;
        style.weight = 4;
        style.dashArray = [10, 10];
      }

      if (presentationMode) {
        switch (presentationMode) {
          case 'soilstatus':
            {
              if (feature.properties.state && feature.properties.state.state) {
                style.color = (colors || stateColorCode)[feature.properties.state.state].markerColor;
              }

              break;
            }

          case 'connectivity':
            {
              if (feature.properties.connectivityState) {
                style.color = (colors || stateColorCode)[feature.properties.connectivityState].markerColor;
              }

              break;
            }

          case 'waterpressure':
            {
              if (feature.properties.waterPressureState) {
                style.color = (colors || stateColorCode)[feature.properties.waterPressureState].markerColor;
              }

              break;
            }

          case 'control':
            {
              if (feature.properties.controlState) {
                style.color = (colors || stateColorCode)[feature.properties.controlState].markerColor;
              }

              break;
            }

          case 'provision':
            {
              if (feature.properties.provisionState) {
                style.color = (colors || stateColorCode)[feature.properties.provisionState].markerColor;
              }

              break;
            }

          case 'cropHealth':
            {
              if (feature.properties.cropState) {
                style.color = (colors || stateColorCode)[feature.properties.cropState].markerColor;
              }

              break;
            }

          default:
            style.color = 'white';
        }
      }
    } catch (error) {
      console.log('ERROR in Map.jsx ranch data anomaly', error);
    }

    return /*#__PURE__*/React.createElement(GeoJSON, {
      className: "map-ranch-boundary-" + feature.id,
      style: style,
      key: "" + JSON.stringify(feature) + selected + presentationMode + zoomShowSensorsState,
      data: [featureRanch],
      onClick: function onClick() {
        if (onClickRanch) {
          onClickRanch(feature.properties.id);
        }
      }
    });
  });
}

var LabelMarker = function LabelMarker(_ref) {
  var position = _ref.position,
      name = _ref.name,
      className = _ref.className,
      onClick = _ref.onClick;
  var icon = L$1.divIcon({
    className: 'custom-icon',
    html: ReactDOMServer.renderToString( /*#__PURE__*/React.createElement("div", {
      className: className + " label-marker-text"
    }, name))
  });
  return /*#__PURE__*/React.createElement(Marker, {
    onClick: onClick,
    position: position,
    icon: icon
  });
};
LabelMarker.propTypes = {
  position: PropTypes.arrayOf(PropTypes.number).isRequired,
  name: PropTypes.string.isRequired,
  className: PropTypes.string.isRequired,
  onClick: PropTypes.func
};
LabelMarker.defaultProps = {
  onClick: function onClick() {}
};

function BlocksLayer(props) {
  var _isShowingImagery$oth;

  var blocksGeoJSON = props.blocksGeoJSON,
      presentationMode = props.presentationMode,
      expandedGeoJSON = props.expandedGeoJSON,
      colors = props.colors,
      onClickBlock = props.onClickBlock,
      isShowingImagery = props.isShowingImagery,
      mapRef = props.mapRef,
      zoomShowSensorsState = props.zoomShowSensorsState;
  var CONDITION_CHECK = !isShowingImagery.visible && !(isShowingImagery === null || isShowingImagery === void 0 ? void 0 : (_isShowingImagery$oth = isShowingImagery.otherSide) === null || _isShowingImagery$oth === void 0 ? void 0 : _isShowingImagery$oth.visible);
  return blocksGeoJSON.features.map(function (feature) {
    var _bbox = bbox(feature),
        minLng = _bbox[0],
        minLat = _bbox[1],
        maxLng = _bbox[2],
        maxLat = _bbox[3];

    var position = [(minLat + maxLat) / 2, (minLng + maxLng) / 2];
    var style = {
      color: 'white',
      weight: 2,
      fillOpacity: 0
    };
    var selected = false;

    try {
      if ((expandedGeoJSON === null || expandedGeoJSON === void 0 ? void 0 : expandedGeoJSON.features) && expandedGeoJSON.features.length === 1 && expandedGeoJSON.features[0].properties.id === feature.properties.id) {
        style.weight = 4;
        style.fillOpacity = 0;
        style.dashArray = [10, 10];
        selected = true;
      }

      if (presentationMode) {
        switch (presentationMode) {
          case 'soilstatus':
            {
              var soilStateStringOfBlock = feature.properties.soilStateStringOfBlock;
              var color = getColorCodeForSoilState(soilStateStringOfBlock);
              style.color = color;

              if (CONDITION_CHECK) {
                style.fillColor = color;
                style.fillOpacity = 0.5;
              }

              break;
            }

          case 'connectivity':
            {
              if (feature.properties.connectivityState) {
                style.color = (colors || stateColorCode)[feature.properties.connectivityState].markerColor;

                if (CONDITION_CHECK) {
                  style.fillColor = (colors || stateColorCode)[feature.properties.connectivityState].markerColor;
                  style.fillOpacity = 0.5;
                }
              }

              break;
            }

          case 'waterpressure':
            {
              if (typeof feature.properties.waterPressureState !== 'undefined') {
                style.color = (colors || stateColorCode)[feature.properties.waterPressureState].markerColor;

                if (CONDITION_CHECK) {
                  style.fillColor = (colors || stateColorCode)[feature.properties.waterPressureState].markerColor;
                  style.fillOpacity = 0.5;
                }
              }

              break;
            }

          case 'control':
            {
              if (typeof feature.properties.controlState !== 'undefined') {
                style.color = (colors || stateColorCode)[feature.properties.waterPressureState].markerColor;

                if (CONDITION_CHECK) {
                  style.fillColor = (colors || stateColorCode)[feature.properties.waterPressureState].markerColor;
                  style.fillOpacity = 0.5;
                }
              }

              break;
            }

          case 'provision':
            {
              if (feature.properties.provisionState) {
                style.color = (colors || stateColorCode)[feature.properties.provisionState].markerColor;

                if (CONDITION_CHECK) {
                  style.fillColor = (colors || stateColorCode)[feature.properties.provisionState].markerColor;
                  style.fillOpacity = 0.5;
                }
              }

              break;
            }

          case 'irrigationRecommendation':
            {
              if (feature.properties.recommendationInfo && feature.properties.recommendationInfo.state) {
                var _color = getColorCodeForIrrigationState(feature.properties.recommendationInfo.state);

                style.color = _color;

                if (CONDITION_CHECK) {
                  style.fillColor = _color;
                  style.fillOpacity = 0.5;
                }
              } else {
                var _color2 = getColorCodeForIrrigationState();

                style.color = getColorCodeForIrrigationState();

                if (CONDITION_CHECK) {
                  style.fillColor = _color2;
                  style.fillOpacity = 0.5;
                }
              }

              break;
            }

          case 'cropHealth':
            {
              if (typeof feature.properties.cropState !== 'undefined') {
                style.color = (colors || stateColorCode)[feature.properties.cropState].markerColor;

                if (CONDITION_CHECK) {
                  style.fillColor = (colors || stateColorCode)[feature.properties.cropState].markerColor;
                  style.fillOpacity = 0.5;
                }
              }

              break;
            }

          default:
            style.color = 'white';
        }
      }
    } catch (error) {
      console.log('ERROR in Map.jsx block data anomaly', error);
    }

    return /*#__PURE__*/React.createElement(GeoJSON, {
      className: "map-ranch-block-b-" + feature.properties.name,
      style: style,
      key: "" + presentationMode + JSON.stringify(feature.properties) + selected + isShowingImagery.visible + zoomShowSensorsState,
      data: [feature],
      onClick: function onClick() {
        if (onClickBlock) {
          onClickBlock(feature.properties.id);
        }
      }
    }, mapRef.current && mapRef.current.getZoom() > 13 && /*#__PURE__*/React.createElement(LabelMarker, {
      position: position,
      name: feature.properties.name,
      className: "map-ranch-block-i-" + feature.properties.name,
      onClick: function onClick() {
        if (onClickBlock) {
          onClickBlock(feature.properties.id);
        }
      }
    }));
  });
}

var antIcon = /*#__PURE__*/React.createElement(LoadingOutlined, {
  style: {
    fontSize: 24
  },
  spin: true
});

var isEqual$1 = require('react-fast-compare');

leafletVectorMarkers(L$1);
var accessToken = process.env.REACT_APP_MAPBOX_TOKEN;
var initialState = {
  viewBox: {
    latitude: 37.646,
    longitude: -121.016,
    zoom: 6
  },
  sensorsGeoJSON: {
    features: []
  },
  ranchesGeoJSON: {
    features: []
  }
};

function SingleMarker(singleMarkerGeoJSON) {
  return /*#__PURE__*/React.createElement(Marker, {
    position: [singleMarkerGeoJSON.geometry.coordinates[0], singleMarkerGeoJSON.geometry.coordinates[1]],
    icon: prepareSingleMarkerIcon()
  });
}

var maxZoom = 23;

function Map$1(_ref) {
  var _dynamicBoundsGeoJSON;

  var children = _ref.children,
      sensorsGeoJSON = _ref.sensorsGeoJSON,
      mapBoxPadding = _ref.mapBoxPadding,
      width = _ref.width,
      height = _ref.height,
      activePopUp = _ref.activePopUp,
      selectSensor = _ref.selectSensor,
      showAllSensorLabels = _ref.showAllSensorLabels,
      recenterGeoJSON = _ref.recenterGeoJSON,
      expandedGeoJSON = _ref.expandedGeoJSON,
      ranchesGeoJSON = _ref.ranchesGeoJSON,
      blocksGeoJSON = _ref.blocksGeoJSON,
      showPopUpOnMarkerClick = _ref.showPopUpOnMarkerClick,
      presentationMode = _ref.presentationMode,
      selectedSensor = _ref.selectedSensor,
      isDataLoading = _ref.isDataLoading,
      onClickRanch = _ref.onClickRanch,
      onClickBlock = _ref.onClickBlock,
      onClickMap = _ref.onClickMap,
      singleMarkerGeoJSON = _ref.singleMarkerGeoJSON,
      dynamicBoundsGeoJSON = _ref.dynamicBoundsGeoJSON,
      onClickDynamicBounds = _ref.onClickDynamicBounds,
      isShowingImagery = _ref.isShowingImagery,
      exposeZoomChange = _ref.exposeZoomChange,
      colors = _ref.colors,
      anomalyGeoJSON = _ref.anomalyGeoJSON,
      deleteAnomaly = _ref.deleteAnomaly,
      onSelectAnomaly = _ref.onSelectAnomaly,
      createAnomaly = _ref.createAnomaly,
      onDrawAnomaly = _ref.onDrawAnomaly,
      userAreaUnit = _ref.userAreaUnit,
      showSideBySideMap = _ref.showSideBySideMap,
      layersControl = _ref.layersControl,
      showHighResBaseMap = _ref.showHighResBaseMap;

  if (dynamicBoundsGeoJSON && !((_dynamicBoundsGeoJSON = dynamicBoundsGeoJSON) === null || _dynamicBoundsGeoJSON === void 0 ? void 0 : _dynamicBoundsGeoJSON.features)) {
    var dynamicBoundsGeoJSONBuffer = {
      type: 'FeatureCollection',
      features: []
    };
    dynamicBoundsGeoJSONBuffer.features.push(cloneDeep_1(dynamicBoundsGeoJSON));
    dynamicBoundsGeoJSON = dynamicBoundsGeoJSONBuffer;
  }

  var _useState = useState(initialState),
      state = _useState[0],
      setState = _useState[1];

  var mounted = useRef(false);
  var mapRef = useRef(null);
  var previousZoom = useRef(state.viewBox);
  var el = document;
  var event = document.createEvent('HTMLEvents');
  event.initEvent('resize', true, false);
  useEffect(function () {
    delete L$1.Icon.Default.prototype._getIconUrl;
    L$1.Icon.Default.mergeOptions({
      iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
      iconUrl: require('leaflet/dist/images/marker-icon.png'),
      shadowUrl: require('leaflet/dist/images/marker-shadow.png')
    });
    mounted.current = true;
    el.dispatchEvent(event);
    return function () {
      mounted.current = false;
    };
  }, []);
  useMapGeoJSONData(mapRef, mounted, exposeZoomChange, ranchesGeoJSON, recenterGeoJSON, expandedGeoJSON, dynamicBoundsGeoJSON, sensorsGeoJSON, singleMarkerGeoJSON, mapBoxPadding, width, height, state, anomalyGeoJSON, maxZoom, setState, previousZoom);
  useMapSideBySideView(mapRef, showSideBySideMap, layersControl, isShowingImagery);
  useEffect(function () {
    if (!mapRef.current) return;

    if (!isMobile) {
      L$1.control.zoom({
        position: 'topright'
      }).addTo(mapRef.current);
    }
  }, []);

  var _useState2 = useState(true),
      zoomShowSensorsState = _useState2[0],
      setZoomShowSensorsState = _useState2[1];

  function onViewPortChanged(viewport) {
    if (viewport && viewport.center) {
      if (viewport.center[0] !== state.viewBox.latitude || viewport.center[1] !== state.viewBox.longitude || state.viewBox.zoom !== viewport.zoom) {
        var zoom = viewport.zoom > maxZoom ? maxZoom : viewport.zoom;
        setState(_extends({}, state, {
          viewBox: {
            latitude: viewport.center[0],
            longitude: viewport.center[1],
            zoom: zoom
          }
        }));
        var zoomShowSensors = zoom > 11;

        if (zoomShowSensors !== zoomShowSensorsState) {
          setZoomShowSensorsState(zoomShowSensors);
        }

        if (typeof exposeZoomChange === 'function') {
          exposeZoomChange(zoom, mapRef.current);
        }
      }
    }

    try {
      el.dispatchEvent(event);
    } catch (error) {
      console.log('Error map viewport changed', error);
    }
  }

  return /*#__PURE__*/React.createElement(Fragment, null, isDataLoading && /*#__PURE__*/React.createElement("div", {
    style: {
      position: 'absolute',
      zIndex: '9999'
    },
    className: "map-data-is-loading"
  }, /*#__PURE__*/React.createElement(Spin, {
    indicator: antIcon
  })), /*#__PURE__*/React.createElement(Map$2, {
    maxZoom: maxZoom,
    ref: function ref(r) {
      if (r && r.leafletElement) {
        mapRef.current = r.leafletElement;
      } else {
        mapRef.current = null;
      }
    },
    doubleClickZoom: false,
    onClick: onClickMap,
    attributionControl: false,
    className: "map-leaflet",
    viewport: {
      center: [state.viewBox.latitude, state.viewBox.longitude],
      zoom: state.viewBox.zoom > maxZoom ? maxZoom : state.viewBox.zoom
    },
    onViewportChanged: function onViewportChanged(viewport) {
      return onViewPortChanged(viewport);
    },
    zoomControl: false
  }, Boolean(showHighResBaseMap) &&
  /*#__PURE__*/
  React.createElement(TileLayer, {
    url: "https://map.ammc.automodality.com/api/projects/814/tasks/467e4be5-dae0-413a-a859-bd6517980f99/orthophoto/tiles/{z}/{x}/{y}?bands=RGB&rescale=0,0.231",
    zIndex: 100
  }), /*#__PURE__*/React.createElement(TileLayer, {
    url: "https://api.mapbox.com/styles/v1/farmx/cinjdgdic0015adnjk1bl7775/tiles/{z}/{x}/{y}?access_token={accessToken}",
    accessToken: accessToken,
    id: "farmx.cinjdgdic0015adnjk1bl7775",
    tileSize: 512,
    zoomOffset: -1
  }), Boolean(singleMarkerGeoJSON) && SingleMarker(singleMarkerGeoJSON), Boolean(state.sensorsGeoJSON) && Boolean(state.sensorsGeoJSON.features.length) && /*#__PURE__*/React.createElement(MarkerCluster, {
    key: JSON.stringify({
      presentationMode: presentationMode,
      s: state.sensorsGeoJSON
    }),
    sensorsGeoJSON: state.sensorsGeoJSON,
    activePopUp: activePopUp || {},
    selectSensor: selectSensor,
    showAllSensorLabels: showAllSensorLabels || false,
    showPopUpOnMarkerClick: showPopUpOnMarkerClick,
    presentationMode: presentationMode,
    selectedSensor: selectedSensor,
    refreshMap: function refreshMap() {
      el.dispatchEvent(event);
    }
  }), Boolean(dynamicBoundsGeoJSON) && Boolean(dynamicBoundsGeoJSON.features.length) && dynamicBoundsGeoJSON.features.map(function (feature) {
    return /*#__PURE__*/React.createElement(GeoJSON, {
      className: "map-dynamic-boundary-" + feature.id,
      style: {
        color: '#FF0000',
        weight: 4,
        fillOpacity: 0,
        fillColor: '#e23549',
        dashArray: [10, 10]
      },
      key: JSON.stringify(feature),
      data: [feature],
      onClick: onClickDynamicBounds
    });
  }), Boolean(ranchesGeoJSON) && Boolean(ranchesGeoJSON.features.length) && ranchesGeoJSON.features[0] && /*#__PURE__*/React.createElement(RanchLayer, {
    ranchesGeoJSON: ranchesGeoJSON,
    presentationMode: presentationMode,
    expandedGeoJSON: expandedGeoJSON,
    colors: colors,
    onClickRanch: onClickRanch,
    zoomShowSensorsState: zoomShowSensorsState
  }), Boolean(blocksGeoJSON) && Boolean(blocksGeoJSON.features.length) && blocksGeoJSON.features[0] && /*#__PURE__*/React.createElement(BlocksLayer, {
    blocksGeoJSON: blocksGeoJSON,
    presentationMode: presentationMode,
    expandedGeoJSON: expandedGeoJSON,
    colors: colors,
    onClickBlock: onClickBlock,
    isShowingImagery: isShowingImagery,
    mapRef: mapRef,
    zoomShowSensorsState: zoomShowSensorsState
  }), presentationMode === 'anomaly' && /*#__PURE__*/React.createElement(AnomalyLayer, {
    anomalyGeoJSON: anomalyGeoJSON,
    presentationMode: presentationMode,
    isShowingImagery: isShowingImagery,
    zoomShowSensorsState: zoomShowSensorsState,
    onClickBlock: onClickBlock,
    setState: setState,
    mapState: state,
    mapRef: mapRef,
    previousZoom: previousZoom,
    deleteAnomaly: deleteAnomaly,
    onSelectAnomaly: onSelectAnomaly,
    createAnomaly: createAnomaly,
    onDrawAnomaly: onDrawAnomaly,
    userAreaUnit: userAreaUnit
  }), children));
}

Map$1.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  width: PropTypes.number,
  height: PropTypes.number,
  activePopUp: PropTypes.object,
  selectSensor: PropTypes.func,
  showAllSensorLabels: PropTypes.bool,
  presentationMode: PropTypes.string,
  recenterGeoJSON: PropTypes.shape({
    type: PropTypes.string,
    features: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      type: PropTypes.string,
      geometry: PropTypes.shape({
        coordinates: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.number), PropTypes.array]),
        type: PropTypes.string
      }),
      properties: PropTypes.object
    }))
  }),
  expandedGeoJSON: PropTypes.shape({
    type: PropTypes.string,
    features: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      type: PropTypes.string,
      geometry: PropTypes.shape({
        coordinates: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.number), PropTypes.array]),
        type: PropTypes.string
      }),
      properties: PropTypes.object
    }))
  }),
  ranchesGeoJSON: PropTypes.shape({
    type: PropTypes.string,
    features: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      type: PropTypes.string,
      geometry: PropTypes.shape({
        coordinates: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.number), PropTypes.array]),
        type: PropTypes.string
      }),
      properties: PropTypes.object
    }))
  }),
  dynamicBoundsGeoJSON: PropTypes.shape({
    type: PropTypes.string,
    features: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      type: PropTypes.string,
      geometry: PropTypes.shape({
        coordinates: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.number), PropTypes.array]),
        type: PropTypes.string
      }),
      properties: PropTypes.object
    }))
  }),
  blocksGeoJSON: PropTypes.shape({
    type: PropTypes.string,
    features: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      type: PropTypes.string,
      geometry: PropTypes.shape({
        coordinates: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.number), PropTypes.array]),
        type: PropTypes.string
      }),
      properties: PropTypes.object
    }))
  }),
  sensorsGeoJSON: PropTypes.shape({
    type: PropTypes.string,
    features: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      type: PropTypes.string,
      geometry: PropTypes.shape({
        coordinates: PropTypes.arrayOf(PropTypes.number),
        type: PropTypes.string
      }),
      properties: PropTypes.object
    }))
  }),
  singleMarkerGeoJSON: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    type: PropTypes.string,
    geometry: PropTypes.shape({
      coordinates: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.number), PropTypes.array]),
      type: PropTypes.string
    }),
    properties: PropTypes.object
  }),
  mapBoxPadding: PropTypes.shape({
    top: PropTypes.number,
    bottom: PropTypes.number,
    left: PropTypes.number,
    right: PropTypes.number
  }),
  showPopUpOnMarkerClick: PropTypes.bool,
  selectedSensor: PropTypes.any,
  isDataLoading: PropTypes.bool,
  onClickRanch: PropTypes.func,
  onClickBlock: PropTypes.func,
  onClickMap: PropTypes.func,
  onClickDynamicBounds: PropTypes.func,
  isShowingImagery: PropTypes.shape({
    visible: PropTypes.bool,
    data: PropTypes.any
  }),
  exposeZoomChange: PropTypes.func,
  colors: PropTypes.arrayOf(PropTypes.shape({})),
  anomalyGeoJSON: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    type: PropTypes.string,
    geometry: PropTypes.shape({
      coordinates: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.number), PropTypes.array]),
      type: PropTypes.string
    }),
    properties: PropTypes.object,
    zoom: PropTypes.number
  }),
  deleteAnomaly: PropTypes.shape({}),
  onSelectAnomaly: PropTypes.func,
  createAnomaly: PropTypes.shape(),
  onDrawAnomaly: PropTypes.func,
  userAreaUnit: PropTypes.string,
  showSideBySideMap: PropTypes.bool,
  layersControl: PropTypes.shape({}),
  showHighResBaseMap: PropTypes.bool
};
Map$1.defaultProps = {
  children: null,
  width: 500,
  height: 500,
  activePopUp: undefined,
  selectSensor: undefined,
  showAllSensorLabels: undefined,
  ranchesGeoJSON: undefined,
  dynamicBoundsGeoJSON: undefined,
  recenterGeoJSON: undefined,
  expandedGeoJSON: undefined,
  blocksGeoJSON: undefined,
  sensorsGeoJSON: undefined,
  presentationMode: undefined,
  singleMarkerGeoJSON: undefined,
  mapBoxPadding: {
    top: 100,
    bottom: 100,
    left: 100,
    right: 100
  },
  showPopUpOnMarkerClick: true,
  selectedSensor: undefined,
  isDataLoading: false,
  onClickRanch: undefined,
  onClickBlock: undefined,
  onClickMap: undefined,
  onClickDynamicBounds: undefined,
  isShowingImagery: {
    visible: false,
    data: undefined
  },
  exposeZoomChange: undefined,
  colors: null,
  anomalyGeoJSON: undefined,
  deleteAnomaly: undefined,
  onSelectAnomaly: null,
  createAnomaly: null,
  onDrawAnomaly: null,
  userAreaUnit: null,
  showSideBySideMap: false,
  layersControl: null,
  showHighResBaseMap: false
};
function ResizeDetector(props) {
  var _useState3 = useState({
    width: 500,
    height: 500
  }),
      dimensions = _useState3[0],
      setDimensions = _useState3[1];

  return /*#__PURE__*/React.createElement("div", {
    className: "map-wrapper"
  }, /*#__PURE__*/React.createElement("div", {
    className: "map-inner-container"
  }, /*#__PURE__*/React.createElement(ReactResizeDetector, {
    onResize: function onResize(width, height) {
      if (!isEqual$1(dimensions, {
        width: width,
        height: height
      })) {
        setDimensions({
          width: width,
          height: height
        });
      }
    },
    handleWidth: true,
    handleHeight: true,
    refreshMode: "debounce",
    refreshRate: 633
  }), /*#__PURE__*/React.createElement(Map$1, _extends({
    key: JSON.stringify(props === null || props === void 0 ? void 0 : props.sensorsGeoJSON)
  }, _extends({}, props), {
    width: Math.round(dimensions.width),
    height: Math.round(dimensions.height)
  }))));
}

function prepareZeroIslandSensors(sensorsGeoJSON) {
  var sensors = cloneDeep_1(sensorsGeoJSON);
  var zeroIslands = [];
  var gw = {};
  sensors.features.forEach(function (s, i) {
    var coordinates = s.geometry.coordinates;
    var _s$properties = s.properties,
        type = _s$properties.type,
        id = _s$properties.id;

    if (type === 'gateway') {
      gw[id] = coordinates;
    }

    if (!coordinates[0] && !coordinates[1]) {
      zeroIslands.push({
        gw: s.properties.gateway,
        idx: i
      });
    }
  });

  if (!zeroIslands.length) {
    return sensorsGeoJSON;
  }

  zeroIslands.forEach(function (z) {
    if (gw[z.gw]) {
      sensors.features[z.idx].geometry.coordinates = gw[z.gw];
    }
  });
  return sensors;
}

function prepareRanchMarker(feature, latlng) {
  var opts = {
    markerColor: '#fff',
    iconColor: '#000'
  };

  if (feature.properties.state && feature.properties.state.state) {
    var state = feature.properties.state.state;
    var markerColor = stateColorCode[state].markerColor;
    opts.markerColor = markerColor;
    opts.iconColor = 'white';
  }

  var markerIcon = L$1.VectorMarkers.icon({
    icon: 'icon-ranch',
    prefix: 'icon',
    markerColor: opts.markerColor,
    iconColor: opts.iconColor,
    dataTestId: feature.properties.id,
    selected: false,
    selectedClass: ''
  });
  var marker = new L$1.marker(latlng, {
    icon: markerIcon
  });
  return marker;
}

function LogoutRedirect() {
  var _useState = useState(true),
      processing = _useState[0],
      setProcessing = _useState[1];

  var dispatch = useDispatch();
  useEffect(function () {
    authService.logout(dispatch).then(function () {
      setProcessing(false);
    });
  }, [setProcessing, dispatch]);
  if (processing) return null;
  return /*#__PURE__*/React.createElement(Redirect, {
    to: "/"
  });
}

var _iteratorSymbol = /*#__PURE__*/typeof Symbol !== "undefined" ? Symbol.iterator || (Symbol.iterator = Symbol("Symbol.iterator")) : "@@iterator";
var _asyncIteratorSymbol = /*#__PURE__*/typeof Symbol !== "undefined" ? Symbol.asyncIterator || (Symbol.asyncIterator = Symbol("Symbol.asyncIterator")) : "@@asyncIterator";
function _catch(body, recover) {
  try {
    var result = body();
  } catch (e) {
    return recover(e);
  }

  if (result && result.then) {
    return result.then(void 0, recover);
  }

  return result;
}

var changePassword = userApi.changePassword;
var layout = {
  labelCol: {
    span: 12
  },
  wrapperCol: {
    span: 16
  }
};
var tailLayout = {
  wrapperCol: {
    offset: 12,
    span: 16
  }
};
function ChangePasswordPage() {
  var onFinish = function onFinish(values) {
    try {
      var currentPassword = values.currentPassword,
          newPassword = values.newPassword;

      var _temp2 = _catch(function () {
        return Promise.resolve(changePassword(currentPassword, newPassword)).then(function (response) {
          if (response.status === 200) {
            setAlertData({
              message: t('Password changed'),
              type: 'success'
            });
            form.resetFields();
          }
        });
      }, function (error) {
        var response = error.response;
        var status = response.status;

        if (status === 400) {
          setAlertData({
            message: t('Incorrect password'),
            type: 'error'
          });
        } else {
          setAlertData({
            message: t('Failed to change password'),
            type: 'error'
          });
        }
      });

      return Promise.resolve(_temp2 && _temp2.then ? _temp2.then(function () {}) : void 0);
    } catch (e) {
      return Promise.reject(e);
    }
  };

  var _Form$useForm = Form.useForm(),
      form = _Form$useForm[0];

  var _useTranslation = useTranslation(),
      t = _useTranslation.t;

  var _useState = useState(null),
      alertData = _useState[0],
      setAlertData = _useState[1];

  function renderAlert() {
    if (!alertData) return null;
    var message = alertData.message,
        type = alertData.type;
    return /*#__PURE__*/React.createElement(Form.Item, {
      wrapperCol: {
        span: 28
      }
    }, /*#__PURE__*/React.createElement(Alert, {
      message: message,
      type: type,
      showIcon: true
    }));
  }

  return /*#__PURE__*/React.createElement(Form, _extends({
    className: "change-password-form",
    onFinish: onFinish,
    form: form
  }, layout), renderAlert(), /*#__PURE__*/React.createElement(Form.Item, {
    name: "currentPassword",
    label: t('Current password'),
    rules: [{
      required: true,
      message: t('Please input your current password')
    }]
  }, /*#__PURE__*/React.createElement(Input.Password, {
    placeholder: t('Current Password'),
    autoComplete: "current-password"
  })), /*#__PURE__*/React.createElement(Form.Item, {
    name: "newPassword",
    label: t('New password'),
    rules: [{
      required: true,
      message: t('Please input your new password')
    }]
  }, /*#__PURE__*/React.createElement(Input.Password, {
    placeholder: t('New Password'),
    autoComplete: "new-password"
  })), /*#__PURE__*/React.createElement(Form.Item, {
    name: "confirmNewPassword",
    dependencies: ['newPassword'],
    label: t('Confirm new password'),
    rules: [{
      required: true,
      message: t('Please enter your new password again')
    }, function (_ref) {
      var getFieldValue = _ref.getFieldValue;
      return {
        validator: function validator(_, value) {
          if (!value || getFieldValue('newPassword') === value) {
            return Promise.resolve();
          }

          return Promise.reject(Error('The two passwords that you entered do not match!'));
        }
      };
    }]
  }, /*#__PURE__*/React.createElement(Input.Password, {
    placeholder: t('Confirm New Password'),
    autoComplete: "new-password"
  })), /*#__PURE__*/React.createElement(Form.Item, tailLayout, /*#__PURE__*/React.createElement(Button, {
    type: "primary",
    htmlType: "submit",
    className: "change-password-button",
    "data-test-id": "change-password-submit"
  }, t('Change Password'))));
}

var _window = window,
    scheduler = _window.scheduler,
    dhtmlXTooltip = _window.dhtmlXTooltip;

var Timeline = /*#__PURE__*/function (_Component) {
  _inheritsLoose(Timeline, _Component);

  function Timeline(props) {
    var _this;

    _this = _Component.call(this, props) || this;
    _this.state = {
      intervalId: null,
      spanID: null,
      timeoutId: null
    };
    return _this;
  }

  var _proto = Timeline.prototype;

  _proto.componentDidMount = function componentDidMount() {
    var _this$state = this.state,
        intervalId = _this$state.intervalId,
        timeoutId = _this$state.timeoutId,
        spanID = _this$state.spanID;
    this.createConfig();

    if (intervalId) {
      clearInterval(intervalId);
    }

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    if (spanID) {
      this.deleteCurrentDateIndicator();
    }

    this.customEventTextStyles();
    this.updateCurrentDateIndicator();
  };

  _proto.componentDidUpdate = function componentDidUpdate(prevProps) {
    var _this$props = this.props,
        blocks = _this$props.blocks,
        scheduleData = _this$props.scheduleData,
        config = _this$props.config;
    var hasBlocksChanged = JSON.stringify(prevProps.blocks) !== JSON.stringify(blocks);

    if (config.x_length > 14) {
      scheduler.config.start_on_monday = true;
    } else {
      scheduler.config.start_on_monday = false;
    }

    scheduler.clearAll();

    if (hasBlocksChanged || scheduleData && scheduleData.length) {
      this.redrawChart();
    }

    this.customEventTextStyles();

    if (hasBlocksChanged) {
      this.deleteCurrentDateIndicator();
      this.updateCurrentDateIndicator();
    }
  };

  _proto.componentWillUnmount = function componentWillUnmount() {
    var _this$state2 = this.state,
        intervalId = _this$state2.intervalId,
        timeoutId = _this$state2.timeoutId;

    if (intervalId) {
      clearInterval(intervalId);
    }

    if (timeoutId) {
      clearTimeout(timeoutId);
      this.setState({
        intervalId: null
      });
    }

    this.deleteCurrentDateIndicator();
  };

  _proto.currentDateIndicator = function currentDateIndicator() {
    var _this$props2 = this.props,
        scheduleData = _this$props2.scheduleData,
        initDate = _this$props2.initDate,
        blocks = _this$props2.blocks;
    var spanID = scheduler.addMarkedTimespan({
      start_date: new Date(),
      end_date: new Date(),
      html: "<span id=\"currTime\" class=\"currTime\">" + moment().format('YYYY-MM-DD HH:mm') + "</span>",
      css: 'column_selection',
      type: 'indicator',
      sections: {
        timeline: scheduleData[0] ? [scheduleData[0].section_id] : [blocks[0]]
      }
    });
    this.setState({
      spanID: spanID
    });
    scheduler.setCurrentView(initDate);
  };

  _proto.deleteCurrentDateIndicator = function deleteCurrentDateIndicator() {
    var spanID = this.state.spanID;

    if (spanID) {
      scheduler.deleteMarkedTimespan(spanID);
    }
  };

  _proto.updateCurrentDateIndicator = function updateCurrentDateIndicator() {
    this.currentDateIndicator();
    var d = new Date();
    d.setTime(d.getTime() + 60000);
    var dms = d.getMilliseconds();
    var dh = d.getHours();
    var dm = d.getMinutes();
    var intervalTime = new Date().setHours(dh, dm, 0, dms) - new Date();
    var temp = this;
    var timeoutId = setTimeout(function () {
      temp.deleteCurrentDateIndicator();
      temp.currentDateIndicator();
      scheduler.updateView();
      var intervalId = setInterval(function () {
        temp.deleteCurrentDateIndicator();
        temp.currentDateIndicator();
        scheduler.updateView();
      }, 60000);
      temp.setState({
        intervalId: intervalId
      });
    }, intervalTime);
    temp.setState({
      timeoutId: timeoutId
    });
  };

  _proto.customEventTextStyles = function customEventTextStyles() {
    var initDate = this.props.initDate;

    scheduler.templates.event_bar_text = function (startTime, endTime, ev) {
      var duration = null;
      var actualDuration = moment.duration(moment(endTime).diff(moment(startTime)));

      if (moment(startTime).isBefore(initDate)) {
        duration = moment.duration(moment(endTime).diff(moment(initDate)));
      } else {
        duration = actualDuration;
      }

      var eventHours = duration.asHours();
      var divElem = document.getElementById('scheduler-container');
      var containerWidth;
      if (divElem) containerWidth = divElem.clientWidth;
      var displayText = Number(eventHours) > 4 && containerWidth > 1026 || Number(eventHours) > 7 && containerWidth === 1026 || Number(eventHours) > 9;
      return "<span class=" + (Number(actualDuration) >= 10 ? 'event_text' : '') + " id=" + ev.bid + ">\n        " + (displayText ? ev.text : '') + "</span>";
    };

    scheduler.setCurrentView(initDate);
  };

  _proto.redrawChart = function redrawChart() {
    var _this$props3 = this.props,
        config = _this$props3.config,
        initDate = _this$props3.initDate,
        scheduleData = _this$props3.scheduleData;
    var timeline = scheduler.getView();
    timeline.x_date = config.x_date;
    timeline.x_unit = config.x_unit;
    timeline.x_length = config.x_length;
    timeline.x_start = config.x_start;
    timeline.x_size = config.x_size;
    timeline.second_scale = config.second_scale;
    timeline.y_unit = config.y_unit;
    scheduler.createTimelineView(timeline);
    scheduler.setCurrentView(initDate);
    scheduler.parse(scheduleData, 'json');
  };

  _proto.createConfig = function createConfig() {
    var _this$props4 = this.props,
        initDate = _this$props4.initDate,
        scheduleData = _this$props4.scheduleData,
        config = _this$props4.config,
        onClickChart = _this$props4.onClickChart;
    scheduler.skin = 'material';
    scheduler.config.header = [];
    scheduler.xy.scale_height = 20;
    scheduler.createTimelineView(config);
    scheduler.attachEvent('onEmptyClick', function (date, e) {
      if (!e.target.innerHTML) {
        onClickChart({
          blockId: scheduler.getActionData(e).section,
          startDate: moment(date).format('YYYY-MM-DD HH:mm')
        });
      }
    });
    scheduler.attachEvent('onClick', function (id, e) {
      var eventObj = scheduler.getEvent(id);
      onClickChart({
        blockId: scheduler.getActionData(e).section,
        scheduleId: parseInt(id, 10),
        eventIndex: eventObj && eventObj.event_index
      });
      dhtmlXTooltip.hide();
      return true;
    });
    scheduler.attachEvent('onBeforeDrag', function () {
      return false;
    });
    scheduler.config.xml_date = '%Y-%m-%d %h:%i';
    scheduler.config.drag_event_body = false;
    scheduler.config.drag_resize = false;
    scheduler.config.mark_now = true;
    scheduler.config.full_day = true;
    scheduler.config.drag_move = false;
    scheduler.config.drag_resize = false;
    scheduler.locale.labels.timeline_scale_header = 'Blocks';
    scheduler.config.dblclick_create = false;
    scheduler.locale.labels.icon_myicon = 'Text';
    scheduler.config.multisection = true;
    dhtmlXTooltip.config.className = 'dhtmlXTooltip tooltip';
    dhtmlXTooltip.config.timeout_to_display = 0;
    dhtmlXTooltip.config.timeout_to_hide = 10;
    dhtmlXTooltip.config.delta_x = 15;
    dhtmlXTooltip.config.delta_y = -20;
    var format = scheduler.date.date_to_str('%Y-%m-%d %H:%i');

    scheduler.templates.tooltip_text = function (start, end, event) {
      return event.name + "<br/>Event:\n     " + event.text + "<br/>From: " + format(start) + "<br/>To: " + format(end) + "<br/>Total runtime (in view):";
    };

    scheduler.onMouseMoveEventId = 0;
    scheduler.attachEvent('onMouseMove', function (id, e) {
      if (scheduler.onMouseMoveEventId !== id) {
        scheduler.onMouseMoveEventId = id;
        scheduler.clientX = e.clientX;
        scheduler.clientY = e.clientY;
      }
    });

    dhtmlXTooltip.position = function (e) {
      return {
        x: scheduler.clientX || e.clientX,
        y: scheduler.clientY || e.clientY
      };
    };

    scheduler.init(this.schedulerContainer, initDate, 'timeline');
    scheduler.parse(scheduleData, 'json');
  };

  _proto.render = function render() {
    var _this2 = this;

    return /*#__PURE__*/React.createElement("div", {
      ref: function ref(input) {
        _this2.schedulerContainer = input;
      },
      style: {
        width: '100%',
        height: '100%'
      }
    });
  };

  return Timeline;
}(Component);
Timeline.propTypes = {
  config: PropTypes.shape({
    x_date: PropTypes.string,
    x_length: PropTypes.number,
    x_unit: PropTypes.string,
    y_unit: PropTypes.arrayOf(PropTypes.any),
    x_start: PropTypes.number,
    x_size: PropTypes.number,
    second_scale: PropTypes.shape({})
  }),
  scheduleData: PropTypes.arrayOf(PropTypes.any),
  initDate: PropTypes.shape({}),
  onClickChart: PropTypes.func,
  blocks: PropTypes.arrayOf(PropTypes.any)
};
Timeline.defaultProps = {
  config: {},
  scheduleData: [],
  initDate: null,
  onClickChart: function onClickChart() {
    return null;
  },
  blocks: []
};

function Schedule(props) {
  var dates = props.dates,
      blocks = props.blocks,
      onClickChart = props.onClickChart,
      scheduleData = props.scheduleData;

  function getXaxisConfig(daterange) {
    var dateDiff = 0;
    var dte = moment().format('YYYY-MM-DD').split('-').map(function (d) {
      return Number(d);
    });

    if (daterange && daterange.length) {
      dateDiff = daterange[1].diff(daterange[0], 'day');
      dte = moment(daterange[0]).format('YYYY-MM-DD').split('-').map(function (d) {
        return Number(d);
      });
    }

    var obj = {
      x_size: dateDiff ? dateDiff + 1 : 7,
      x_start: 0,
      x_length: dateDiff ? dateDiff + 1 : 7,
      x_unit: 'day',
      x_date: '%d %M %Y',
      second_scale: {
        x_unit: 'week',
        x_date: 'Week %W'
      }
    };
    var initDate = new Date(dte[0], dte[1] - 1, dte[2]);

    if (dateDiff > 7 && dateDiff <= 14) {
      obj.x_size = dateDiff + 1;
      obj.x_length = dateDiff + 1;
      obj.x_date = '%d %M';
      obj.second_scale = {
        x_unit: 'week',
        x_date: 'Week %W'
      };
    } else if (dateDiff > 14) {
      obj.x_size = dateDiff + 1;
      obj.x_length = dateDiff + 1;
      obj.x_date = '%d';
    } else if (!dateDiff) {
      initDate = new Date(dte[0], dte[1] - 1, dte[2] - 1);
      var hourDiff = daterange[1].diff(daterange[0], 'hour');
      obj.x_size = hourDiff + 1;
      obj.x_start = hourDiff + 1;
      obj.x_length = hourDiff + 1;
      obj.x_unit = 'hour';
      obj.x_date = '%H:%i';
      obj.second_scale = {
        x_unit: 'day',
        x_date: '%d %M %Y'
      };
    }

    return {
      configObj: obj,
      initDate: initDate
    };
  }

  var callFunc = getXaxisConfig(dates);

  var config = _extends({
    name: 'timeline'
  }, callFunc.configObj, {
    y_unit: scheduleData.sections,
    fit_events: true,
    y_property: 'section_id',
    render: 'bar',
    dy: 100,
    dx: 130,
    event_dy: 'full',
    section_autoheight: true,
    sort: function sort(a, b) {
      if (a.irrId > b.irrId) {
        return -1;
      }

      if (a.irrId < b.irrId) {
        return 1;
      }

      return +a.start_date > +b.start_date ? 1 : -1;
    }
  });

  function getClass(daterange) {
    var dateDiff = daterange[1].diff(daterange[0], 'day');
    var styleStr = '';

    if (dateDiff > 7 && dateDiff <= 14) {
      styleStr = 'twoweeks-mode';
    } else if (dateDiff > 14) {
      styleStr = 'month-mode';
    } else if (!dateDiff) {
      styleStr = 'day-mode';
    } else if (dateDiff && dateDiff <= 7) {
      styleStr = 'week-mode';
    }

    return styleStr;
  }

  var customClass = getClass(dates);
  return /*#__PURE__*/React.createElement("div", {
    className: "scheduler-container " + customClass,
    id: "scheduler-container"
  }, /*#__PURE__*/React.createElement(Timeline, {
    config: config,
    scheduleData: scheduleData.data,
    initDate: callFunc.initDate,
    onClickChart: onClickChart,
    blocks: blocks
  }));
}
Schedule.propTypes = {
  blocks: PropTypes.arrayOf(PropTypes.any),
  dates: PropTypes.arrayOf(PropTypes.any),
  onClickChart: PropTypes.func,
  scheduleData: PropTypes.shape({
    data: PropTypes.arrayOf(PropTypes.any),
    sections: PropTypes.arrayOf(PropTypes.any)
  })
};
Schedule.defaultProps = {
  blocks: null,
  dates: null,
  onClickChart: function onClickChart() {
    return null;
  },
  scheduleData: {
    data: [],
    sections: []
  }
};

var _window$1 = window,
    scheduler$1 = _window$1.scheduler,
    dhtmlXTooltip$1 = _window$1.dhtmlXTooltip;
var SWIPE_SIZE = 40;

var EventCalendar = /*#__PURE__*/function (_Component) {
  _inheritsLoose(EventCalendar, _Component);

  function EventCalendar() {
    return _Component.apply(this, arguments) || this;
  }

  var _proto = EventCalendar.prototype;

  _proto.componentDidMount = function componentDidMount() {
    var _this$props = this.props,
        scheduleData = _this$props.scheduleData,
        schedulerObject = _this$props.schedulerObject,
        onModechange = _this$props.onModechange,
        hourFormat = _this$props.hourFormat,
        onClickChart = _this$props.onClickChart;
    this.getHeader();
    var schedulerMode = schedulerObject.schedulerMode,
        schedulerStartDate = schedulerObject.schedulerStartDate;
    scheduler$1.skin = 'material';
    scheduler$1.config.hour_date = hourFormat;
    scheduler$1.xy.min_event_height = 1;
    scheduler$1.xy.scale_width = 20;
    var height = document.querySelector('.scheduler-container-mobile');
    var screenHeight = window.screen.availHeight;

    if (height && height.clientHeight >= 500) {
      scheduler$1.chartDivHeight = height.clientHeight;
    } else if (!scheduler$1.chartDivHeight && screenHeight !== window.innerHeight) {
      scheduler$1.chartDivHeight = screenHeight - Math.round(screenHeight * 0.33);
    } else {
      scheduler$1.chartDivHeight = window.innerHeight - Math.round(window.innerHeight * 0.25);
    }

    scheduler$1.config.hour_size_px = (scheduler$1.chartDivHeight - 83) / 24;
    scheduler$1.config.all_timed = true;
    scheduler$1.config.icons_select = ['icon_details', 'icon_edit'];
    scheduler$1.attachEvent('onBeforeLightbox', function () {
      return false;
    });
    scheduler$1.attachEvent('onBeforeTooltip', function () {
      return false;
    });

    scheduler$1.templates.event_class = function () {
      return 'my_event';
    };

    scheduler$1.xy.scale_height = 40;
    scheduler$1.xy.nav_height = 40;
    scheduler$1.config.start_on_monday = false;
    scheduler$1.config.touch_swipe_dates = true;
    var headerDate = scheduler$1.date.date_to_str('%F');
    var formatMonthScale = scheduler$1.date.date_to_str('%D');
    var formatDayScale = scheduler$1.date.date_to_str('%l, %F %j, %Y');
    scheduler$1.xy.menu_width = 0;
    scheduler$1.config.drag_event_body = false;
    scheduler$1.config.drag_resize = false;
    scheduler$1.config.drag_move = false;
    scheduler$1.config.drag_create = false;
    scheduler$1.config.drag_lightbox = false;
    scheduler$1.config.touch_tip = false;
    scheduler$1.config.touch_tooltip = false;
    scheduler$1.config.dblclick_create = false;
    scheduler$1.config.detail_on_dblclick = false;
    scheduler$1.onEmptyClick = scheduler$1.attachEvent('onEmptyClick', function (date) {
      scheduler$1.detachEvent(scheduler$1.onClick);

      var _scheduler$getState = scheduler$1.getState(),
          mode = _scheduler$getState.mode;

      if (mode === 'day' || mode === 'week') {
        onClickChart({
          blockId: [],
          startDate: moment(date).format('YYYY-MM-DD HH:mm')
        });
        onModechange({
          mode: mode,
          date: date
        });
      } else {
        onModechange({
          mode: 'day',
          date: date
        });
        scheduler$1.setCurrentView(date, 'day');
      }
    });
    scheduler$1.attachEvent('onClick', function (id) {
      var eventObj = scheduler$1.getEvent(id);
      onClickChart(eventObj);
      return true;
    });

    scheduler$1.templates.event_header = function (start, end, event) {
      var _scheduler$getState2 = scheduler$1.getState(),
          mode = _scheduler$getState2.mode;

      if (mode === 'day') {
        var diff = moment.duration(moment(end).diff(moment(start))).asHours();

        if (diff >= 1) {
          return event.text;
        }
      }

      return '';
    };

    scheduler$1.templates.day_scale_date = function (date) {
      return formatDayScale(date);
    };

    scheduler$1.templates.week_date = function (date) {
      if (scheduler$1.getState().mode === 'week') {
        scheduler$1.detachEvent(scheduler$1.onClick);
      }

      return headerDate(date);
    };

    scheduler$1.templates.day_date = function (date) {
      if (scheduler$1.getState().mode === 'day') {
        scheduler$1.detachEvent(scheduler$1.onClick);
      }

      return headerDate(date);
    };

    scheduler$1.templates.month_date = function (date) {
      document.querySelector('.dhx_minical_icon').style.display = 'none';

      if (scheduler$1.getState().mode === 'month') {
        scheduler$1.detachEvent(scheduler$1.onClick);
        scheduler$1.onClick = scheduler$1.attachEvent('onClick', function (id) {
          if (scheduler$1.getState().mode === 'month') {
            var eventObj = scheduler$1.getEvent(id);
            var sDate = eventObj.start_date;
            onModechange({
              mode: 'day',
              sDate: sDate
            });
            scheduler$1.setCurrentView(new Date(eventObj.start_date), 'day');
          }
        });
      }

      return headerDate(date);
    };

    scheduler$1.templates.month_scale_date = function monthScaleDate(date) {
      return formatMonthScale(date)[0];
    };

    scheduler$1.attachEvent('onBeforeViewChange', function () {
      scheduler$1.deleteMarkedTimespan();
      return true;
    });

    function addEvCount() {
      var startDate = scheduler$1.getState().min_date;
      var endMonthDate = scheduler$1.getState().max_date;

      while (startDate.getTime() < endMonthDate.getTime()) {
        var endDayDate = scheduler$1.date.add(startDate, 1, 'day');
        var evs = scheduler$1.getEvents(startDate, endDayDate);

        if (evs.length) {
          scheduler$1.addMarkedTimespan({
            start_date: startDate,
            end_date: endDayDate,
            html: "<div style=\"text-align:center;\"><b>" + evs.length + "</b><svg height=\"30px\" width=\"30px\"  fill=\"#1890ff\" xmlns=\"http://www.w3.org/2000/svg\" xmlns:xlink=\"http://www.w3.org/1999/xlink\" version=\"1.1\" x=\"0px\" y=\"0px\" viewBox=\"0 0 100 100\" enable-background=\"new 0 0 100 100\" xml:space=\"preserve\"><path d=\"M82.66,62.34C82.66,80.378,68.037,95,50,95c-18.038,0-32.66-14.622-32.66-32.66C17.34,34.309,50,5,50,5  S82.66,34.309,82.66,62.34z\"></path></svg></div>"
          });
        }

        startDate = endDayDate;
      }

      scheduler$1.updateView();
    }

    scheduler$1.attachEvent('onViewChange', function (newMode) {
      if (newMode === 'month') {
        addEvCount();
      }
    });

    scheduler$1.filter_month = function () {
      return false;
    };

    scheduler$1.templates.event_bar_date = function () {
      return '';
    };

    var dateFormat = scheduler$1.date.date_to_str('%j');
    var formatweekScale = scheduler$1.date.date_to_str('%D');

    scheduler$1.templates.week_scale_date = function (date) {
      var today = scheduler$1.date.date_part(new Date());

      if (date.valueOf() === today.valueOf()) {
        return " <div><span>" + formatweekScale(date)[0] + "<span>\n        <br>\n        <span\n        style='border-radius: 50%;\n        justify-content: center;\n        background: #1890ff;\n        border: 1px solid #1890ff;\n        color: white;\n        display:flex;\n        align-items:center;\n        height: 22px;\n        width: 22px;'\n        >" + dateFormat(date) + "</span></div>";
      }

      return " <div><span>" + formatweekScale(date)[0] + "<span><br>\n      <span>" + dateFormat(date) + "</span></div>";
    };

    scheduler$1.init(this.schedulerContainer, new Date(moment(schedulerStartDate)), schedulerMode);
    scheduler$1.clearAll();
    scheduler$1.parse(scheduleData);
    scheduler$1.config.max_month_events = 1;
    var xStart = null;
    var xEnd = null;
    var yStart = null;
    var yEnd = null;

    scheduler$1.touchStart = function handleTouchStart(evt) {
      xStart = evt.targetTouches[0].clientX;
      yStart = evt.targetTouches[0].clientY;
    };

    scheduler$1.touchMove = function handleTouchMove(evt) {
      xEnd = evt.targetTouches[0].clientX;
      yEnd = evt.targetTouches[0].clientY;
    };

    scheduler$1.touchEnd = function handleTouchEnd() {
      var _scheduler$getState3 = scheduler$1.getState(),
          mode = _scheduler$getState3.mode;

      var xScale = xStart - xEnd;
      var yScale = yStart - yEnd;

      if (xStart && xEnd && xScale > SWIPE_SIZE && xScale > yScale) {
        var newDate = moment(scheduler$1.currDate).add(1, mode);
        onModechange({
          date: newDate,
          mode: mode
        });
      }

      if (xStart && xEnd && xScale < -SWIPE_SIZE && xScale < -yScale) {
        var _newDate = moment(scheduler$1.currDate).subtract(1, mode);

        onModechange({
          date: _newDate,
          mode: mode
        });
      }

      xStart = null;
      xEnd = null;
      yStart = null;
      yEnd = null;
    };

    this.addTouchEvent();
  };

  _proto.componentDidUpdate = function componentDidUpdate() {
    this.removeTouchEvent();

    if (scheduler$1.chartDivHeight) {
      scheduler$1.config.hour_size_px = (scheduler$1.chartDivHeight - 83) / 24;
    }

    scheduler$1.detachEvent(scheduler$1.onClick);
    scheduler$1.clearAll();
    var allEvents = scheduler$1.getEvents();
    var allSectionIds = allEvents.map(function (item) {
      return item.section_id;
    }).filter(function (v, i, a) {
      return a.indexOf(v) === i;
    });
    var _this$props2 = this.props,
        scheduleData = _this$props2.scheduleData,
        schedulerObject = _this$props2.schedulerObject;
    var schedulerMode = schedulerObject.schedulerMode,
        schedulerStartDate = schedulerObject.schedulerStartDate;
    var newSectionIds = scheduleData.map(function (item) {
      return item.section_id;
    }).filter(function (v, i, a) {
      return a.indexOf(v) === i;
    });

    if (newSectionIds.length > 0) {
      var idsToDelete = allSectionIds.filter(function (element) {
        return !newSectionIds.includes(element);
      });
      var eventsToDelete = allEvents.filter(function (d) {
        return d.section_id === idsToDelete[0];
      });

      if (idsToDelete.length === 1) {
        for (var i = 0; i < eventsToDelete.length; i += 1) {
          scheduler$1.deleteEvent(eventsToDelete[i].id, true);
        }
      }
    }

    scheduler$1.init(this.schedulerContainer, new Date(moment(schedulerStartDate)), schedulerMode);
    this.getHeader();
    scheduler$1.parse(scheduleData);
    scheduler$1.render();
    scheduler$1.attachEvent('onBeforeViewChange', function () {
      scheduler$1.deleteMarkedTimespan();
      return true;
    });
    this.addTouchEvent();
  };

  _proto.componentWillUnmount = function componentWillUnmount() {
    dhtmlXTooltip$1.hide();
    scheduler$1.detachEvent(scheduler$1.onEmptyClick);
    scheduler$1.detachEvent(scheduler$1.onClick);
    scheduler$1.chartDivHeight = 0;
  };

  _proto.setTimeFormat = function setTimeFormat() {
    var hourFormat = this.props.hourFormat;
    scheduler$1.config.hour_date = hourFormat;
    scheduler$1.templates.hour_scale = scheduler$1.date.date_to_str(scheduler$1.config.hour_date);
  };

  _proto.getHeader = function getHeader() {
    var _this$props3 = this.props,
        schedulerObject = _this$props3.schedulerObject,
        onModechange = _this$props3.onModechange;
    var schedulerMode = schedulerObject.schedulerMode;

    function customModeChange(mode) {
      onModechange({
        mode: mode,
        date: scheduler$1.getState().date
      });
    }

    scheduler$1.config.header = [{
      html: "<text class=" + (schedulerMode === 'day' ? 'selected' : '') + ">day</text>",
      click: function click() {
        customModeChange('day');
      }
    }, {
      html: "<text class=" + (schedulerMode === 'week' ? 'selected' : '') + ">week</text>",
      click: function click() {
        customModeChange('week');
      }
    }, {
      html: "<text class=" + (schedulerMode === 'month' ? 'selected' : '') + ">month</text>",
      click: function click() {
        customModeChange('month');
      }
    }, {
      view: 'minicalendar',
      click: function click() {
        if (scheduler$1.isCalendarVisible()) {
          scheduler$1.destroyCalendar();
        } else {
          scheduler$1.renderCalendar({
            position: this,
            date: scheduler$1.getState().date,
            navigation: true,
            handler: function handler(date) {
              scheduler$1.setCurrentView(date);
              scheduler$1.destroyCalendar();
            }
          });
        }
      }
    }, 'date'];
  };

  _proto.addTouchEvent = function addTouchEvent() {
    if (this.schedulerContainer) {
      var divElem = this.schedulerContainer.lastChild;
      divElem.addEventListener('touchstart', scheduler$1.touchStart, false);
      divElem.addEventListener('touchmove', scheduler$1.touchMove, false);
      divElem.addEventListener('touchend', scheduler$1.touchEnd, false);
    }
  };

  _proto.removeTouchEvent = function removeTouchEvent() {
    if (this.schedulerContainer) {
      var divElem = this.schedulerContainer.lastChild;
      divElem.removeEventListener('touchstart', scheduler$1.touchStart, false);
      divElem.removeEventListener('touchmove', scheduler$1.touchMove, false);
      divElem.removeEventListener('touchend', scheduler$1.touchEnd, false);
    }
  };

  _proto.render = function render() {
    var _this = this;

    var schedulerObject = this.props.schedulerObject;
    var schedulerStartDate = schedulerObject.schedulerStartDate;
    scheduler$1.currDate = schedulerStartDate;
    this.setTimeFormat();
    return /*#__PURE__*/React.createElement("div", {
      ref: function ref(input) {
        _this.schedulerContainer = input;
      },
      style: {
        width: '100%',
        height: '100%'
      }
    });
  };

  return EventCalendar;
}(Component);
EventCalendar.propTypes = {
  scheduleData: PropTypes.arrayOf(PropTypes.any),
  hourFormat: PropTypes.string,
  onClickChart: PropTypes.func,
  schedulerObject: PropTypes.shape({
    schedulerMode: PropTypes.string,
    schedulerStartDate: PropTypes.any
  }),
  onModechange: PropTypes.func
};
EventCalendar.defaultProps = {
  scheduleData: null,
  hourFormat: '',
  onClickChart: function onClickChart() {
    return null;
  },
  schedulerObject: {},
  onModechange: function onModechange() {
    return null;
  }
};

var initialMobileScheduler = {
  schedulerMode: 'week',
  schedulerStartDate: new Date()
};
function SchedulerMobile(props) {
  var dates = props.dates,
      onClickChart = props.onClickChart,
      scheduleData = props.scheduleData,
      is24Hour = props.is24Hour;
  var hourFormat = is24Hour ? '%G' : '%g';

  var _useState = useState(initialMobileScheduler),
      mobileScheduler = _useState[0],
      setMobileScheduler = _useState[1];

  function onModeChange(obj) {
    var mode = obj.mode,
        date = obj.date;
    var dataObj = {
      schedulerMode: mode,
      schedulerStartDate: new Date(date)
    };
    setMobileScheduler(dataObj);
    props.setScheduler(dataObj);
    props.onModeChange(obj);
  }

  return /*#__PURE__*/React.createElement("div", {
    className: "scheduler-container-mobile"
  }, /*#__PURE__*/React.createElement(EventCalendar, {
    scheduleData: scheduleData,
    dates: dates,
    hourFormat: hourFormat,
    onClickChart: onClickChart,
    schedulerObject: mobileScheduler,
    onModechange: onModeChange
  }));
}
SchedulerMobile.propTypes = {
  dates: PropTypes.arrayOf(PropTypes.any),
  onClickChart: PropTypes.func,
  colorObj: PropTypes.shape({}),
  scheduleData: PropTypes.arrayOf(PropTypes.any),
  is24Hour: PropTypes.bool,
  onModeChange: PropTypes.func,
  setScheduler: PropTypes.func
};
SchedulerMobile.defaultProps = {
  dates: null,
  onClickChart: function onClickChart() {
    return null;
  },
  colorObj: {},
  scheduleData: [],
  is24Hour: true,
  onModeChange: function onModeChange() {
    return null;
  },
  setScheduler: function setScheduler() {
    return null;
  }
};

var styles$6 = {"entity-header":"_3Aaht","ranch-header":"_1WYA7","block-header":"_3Gc7e","block-name":"_1n7BN","select-all":"_2q6e5","ant-typography":"_3e__W","no-block":"_3TDid","no-ranch":"_3uXsn","selected":"_3-3ZQ"};

var TreeNode = TreeSelect.TreeNode;
var Text$5 = Typography.Text;
var mobileScreen$2 = window.innerWidth < 599;
var loadFarmData$4 = actions.loadFarmData,
    setUrlLoad = actions.setUrlLoad;
var useEntityRanchBlock = hooks.useEntityRanchBlock;

function getTreeNode(tnValue, tnKey, tnName, titleKey, titleClass, titleContent, selectable, tnContent, tnId) {
  return /*#__PURE__*/React.createElement(TreeNode, {
    key: tnValue || tnKey,
    value: tnValue || tnKey,
    name: tnName,
    title: /*#__PURE__*/React.createElement(Text$5, {
      key: titleKey,
      id: tnId,
      className: titleClass
    }, titleContent),
    selectable: selectable
  }, tnContent);
}

function getClassName(id, index, selectedValue) {
  var stylestr = '';

  if (index % 2 && selectedValue && selectedValue === String(id)) {
    stylestr = styles$6['block-header'] + " " + styles$6.selected;
  } else if (index % 2 && selectedValue && selectedValue !== String(id)) {
    stylestr = styles$6['block-header'];
  } else if (!(index % 2) && selectedValue && selectedValue === String(id)) {
    stylestr = styles$6['block-name'] + " " + styles$6.selected;
  } else if (!(index % 2) && selectedValue && selectedValue !== String(id)) {
    stylestr = styles$6['block-name'];
  }

  return stylestr;
}

function getNoRanchTreeNode(data, t) {
  var expandKeys = [];
  var nrKey = "No_Ranch_" + data[0].id + "_" + data[0].name;
  expandKeys.push(nrKey);
  return {
    nodes: getTreeNode(null, nrKey, 'No Ranch', "No_Ranch_" + data[0].ranches.length + "_" + data[0].name, styles$6['no-ranch'], t('No Ranch'), false, undefined, "No_Ranch_" + data[0].id),
    expandKeys: expandKeys
  };
}

function getBlockContent(data, rIndex, selectedValue) {
  if (data === void 0) {
    data = [];
  }

  var expandKeys = [];
  return {
    nodes: data.map(function (block, bIndex) {
      if (block && block.id) {
        var bKey = "" + block.id;
        expandKeys.push(bKey);
        return getTreeNode(bKey, null, block.name, String(rIndex) + "_" + block.id, getClassName(block.id, bIndex, selectedValue), block.name, true, undefined, "Block_" + block.id);
      }

      return null;
    }),
    expandKeys: expandKeys
  };
}

function getRanchContent(data, eId, eIndex, selectedValue, t) {
  if (data === void 0) {
    data = [];
  }

  var expandKeys = [];
  return {
    nodes: data.map(function (ranch, rIndex) {
      if (ranch && ranch.id) {
        var rKey = eId + "_" + ranch.id;
        var blockTreeNodes = getBlockContent(ranch.blocks, rIndex, selectedValue);
        expandKeys.push(rKey);
        expandKeys = [].concat(expandKeys, blockTreeNodes.expandKeys);
        return getTreeNode(rKey, null, ranch.name, eId + "_" + String(eIndex) + "_" + ranch.id, selectedValue && [selectedValue].includes(String(ranch.id)) ? styles$6['ranch-header'] + " " + styles$6.selected : styles$6['ranch-header'], ranch.name, true, ranch.blocks.length ? blockTreeNodes.nodes : getTreeNode(null, "No_Block_" + String(eIndex) + "_" + String(rIndex), 'No Block', String(eIndex) + "_" + String(rIndex), styles$6['no-block'], t('No Block'), false, undefined, "No_Block_" + String(eIndex) + "_" + String(rIndex)), "Ranch_" + ranch.id);
      }

      return null;
    }),
    expandKeys: expandKeys
  };
}

function getEntityLevelHierarchy(data, selectedValue, t) {
  if (data === void 0) {
    data = [];
  }

  var expandKeys = [];
  return {
    nodes: data.map(function (entity, eIndex) {
      if (entity && entity.id) {
        var eKey = entity.name + "_" + entity.id + "_" + String(eIndex);
        var ranchTreeNodes = getRanchContent(entity.ranches, entity.id, eIndex, selectedValue, t);
        expandKeys.push(eKey);
        expandKeys = [].concat(expandKeys, ranchTreeNodes.expandKeys);
        return getTreeNode(null, eKey, entity.name, entity.id + "_" + String(eIndex), styles$6['entity-header'], entity.name, false, entity.ranches.length ? ranchTreeNodes.nodes : getTreeNode(null, "No_Ranch_" + entity.id + "_" + entity.name, 'No Ranch', "" + String(eIndex), styles$6['no-ranch'], t('No Ranch'), false, undefined, "No_Ranch_" + entity.id + "_" + entity.name), "Entity_" + entity.id);
      }

      return null;
    }),
    expandKeys: expandKeys
  };
}

function getRanchLevelHierarchy(data, selectedValue, t) {
  if (data === void 0) {
    data = [];
  }

  var expandKeys = [];
  return {
    nodes: data.map(function (ranch, rIndex) {
      if (ranch && ranch.id) {
        var rKey = String(rIndex) + "_" + ranch.id;
        var ranchTreeNodes = getRanchContent(data[0].ranches, data[0].id, 0, selectedValue, t);
        expandKeys.push(rKey);
        expandKeys = [].concat(expandKeys, ranchTreeNodes.expandKeys);
        return ranchTreeNodes.nodes;
      }

      return null;
    }),
    expandKeys: expandKeys
  };
}

function getCustomSelectOption(data, selectedValue, t) {
  var processedData = {};

  if (data && data.length) {
    if (data.length > 1) {
      processedData = getEntityLevelHierarchy(data, selectedValue, t);
    } else if (data[0] && data[0].ranches.length) {
      processedData = getRanchLevelHierarchy(data, selectedValue, t);
    } else {
      processedData = getNoRanchTreeNode(data, t);
    }
  }

  return processedData;
}

var RanchBlockSelect = function RanchBlockSelect(_ref) {
  var onSelect = _ref.onSelect,
      selected = _ref.selected,
      getSelectedRanchBlock = _ref.getSelectedRanchBlock,
      title = _ref.title,
      admin = _ref.admin,
      showFullHeight = _ref.showFullHeight;

  var _useState = useState(false),
      modal = _useState[0],
      setModalState = _useState[1];

  var _useState2 = useState(false),
      showSearch = _useState2[0],
      setShowSearch = _useState2[1];

  var _useState3 = useState(''),
      searchText = _useState3[0],
      setSearchText = _useState3[1];

  var dispatch = useDispatch();

  var _useTranslation = useTranslation(),
      t = _useTranslation.t;

  var _useState4 = useState([]),
      options = _useState4[0],
      setOptions = _useState4[1];

  useEffect(function () {
    setTimeout(function () {
      dispatch(loadFarmData$4(true));
    }, 0);
  }, [dispatch]);
  var finalData = useEntityRanchBlock(options);
  useEffect(function () {
    if (JSON.stringify(finalData) !== JSON.stringify(options)) {
      setOptions(finalData);
    }
  }, [finalData, options]);

  var onChange = function onChange(value) {
    if (!mobileScreen$2) dispatch(setUrlLoad(false));

    if (value) {
      var ids = value.split('_');
      var obj = {
        key: value,
        value: ids[ids.length - 1],
        type: ids.length === 2 ? 'ranch' : 'block'
      };
      if (onSelect) onSelect(obj);

      if (getSelectedRanchBlock instanceof Function) {
        getSelectedRanchBlock(obj);
      }
    } else {
      var _obj = {
        key: 'All Ranches',
        value: value,
        type: 'All Ranches'
      };
      if (onSelect) onSelect(_obj);

      if (getSelectedRanchBlock instanceof Function) {
        getSelectedRanchBlock(_obj);
      }
    }
  };

  var scrollElementId;
  if (selected.type && selected.type === 'ranch') scrollElementId = "Ranch_" + selected.value;
  if (selected.type && selected.type === 'block') scrollElementId = "Block_" + selected.value;
  var scrollElem;

  var filterTreeNode = function filterTreeNode(txt, node) {
    var filterTitle = node.title.props.children;
    var result = !!(filterTitle && String(filterTitle).toLowerCase().indexOf(txt.toLowerCase()) !== -1);
    scrollElem = document.getElementById(scrollElementId);
    return result;
  };

  useEffect(function () {
    if (scrollElem) scrollElem.scrollIntoView({
      block: 'start'
    });
  });

  var handleOnClick = function handleOnClick(d) {
    if (d.target && !d.target.id.includes('Entity') && !d.target.id.includes('No_')) {
      setModalState(false);
      setSearchText();
      setShowSearch(false);
    }
  };

  var selectObj = getCustomSelectOption(options, selected.value || [], t);
  var listHeight = calculatedHeightForMobile();
  var selectedValue = selected.key && selected.key.toLowerCase() === 'all ranches' ? t('All Ranches') : selected.key;
  var changedListHeight = 330;
  if (mobileScreen$2) changedListHeight = listHeight - 70;
  if (showFullHeight) changedListHeight = listHeight - 180;
  return /*#__PURE__*/React.createElement("div", {
    style: {
      margin: 2
    }
  }, /*#__PURE__*/React.createElement(TreeSelect, {
    showSearch: mobileScreen$2 || showFullHeight ? showSearch : !showSearch,
    style: {
      width: '100%'
    },
    bordered: false,
    value: selectedValue,
    placeholder: t('Please select'),
    multiple: false,
    treeDefaultExpandAll: true,
    onChange: onChange,
    onSearch: function onSearch(d) {
      return setSearchText(d);
    },
    listHeight: changedListHeight,
    onDropdownVisibleChange: function onDropdownVisibleChange(d) {
      if (mobileScreen$2) setModalState(true);else setModalState(d);
    },
    open: modal,
    filterTreeNode: filterTreeNode,
    treeNodeFilterProp: "value",
    treeNodeLabelProp: "name",
    searchValue: searchText,
    treeExpandedKeys: selectObj.expandKeys,
    dropdownRender: mobileScreen$2 || showFullHeight ? function (menu) {
      return /*#__PURE__*/React.createElement(DropdownModal, {
        title: title || 'Select Ranch or Block',
        displayOptions: menu,
        mobileScreen: mobileScreen$2 || showFullHeight,
        onClick: handleOnClick,
        onChange: function onChange(d) {
          setSearchText(d.target.value);
          setShowSearch(true);
        },
        displaySearch: true,
        buttonText: "cancel",
        size: mobileScreen$2 || showFullHeight ? 'large' : 'middle',
        showFullHeight: showFullHeight
      });
    } : undefined,
    dropdownClassName: showFullHeight && !mobileScreen$2 ? 'desktop-sidebar-container' : undefined,
    getPopupContainer: mobileScreen$2 || showFullHeight ? null : function (triggerNode) {
      return triggerNode.parentNode;
    }
  }, !admin ? /*#__PURE__*/React.createElement(TreeNode, {
    value: null,
    name: "All Ranches",
    key: null,
    title: /*#__PURE__*/React.createElement(Text$5, {
      key: "all_ranches",
      className: selected.value === null ? styles$6['select-all'] + " " + styles$6.selected : styles$6['select-all']
    }, t('All Ranches'))
  }) : null, selectObj.nodes));
};

RanchBlockSelect.propTypes = {
  getSelectedRanchBlock: PropTypes.func,
  onSelect: PropTypes.func,
  selected: PropTypes.shape({
    value: PropTypes.any,
    key: PropTypes.any,
    type: PropTypes.any
  }),
  title: PropTypes.string,
  admin: PropTypes.bool,
  showFullHeight: PropTypes.bool
};
RanchBlockSelect.defaultProps = {
  getSelectedRanchBlock: null,
  onSelect: null,
  selected: {},
  title: null,
  admin: false,
  showFullHeight: false
};

NoDataToDisplay(Highcharts);
var defaultOptions$1 = {
  title: null,
  chart: {
    type: 'column'
  },
  xAxis: {},
  yAxis: {},
  series: [],
  credits: {
    enabled: false
  },
  lang: {
    noData: 'No data'
  },
  noData: {
    style: {
      fontWeight: 'bold',
      fontSize: '15px',
      color: '#303030'
    }
  },
  time: {
    useUTC: false
  }
};

function BarHighchart(props) {
  var highchartsComponentRef = React.createRef();
  var options = props.options,
      constructorType = props.constructorType,
      chartElem = props.chartElem;
  var plotLineId = 'y2';
  var draggablePlotLine = useCallback(function (axis) {
    var clickY;

    var getPlotLine = function getPlotLine() {
      for (var i = 0; i < axis.plotLinesAndBands.length; i += 1) {
        if (axis.plotLinesAndBands[i].id === plotLineId) {
          return axis.plotLinesAndBands[i];
        }
      }

      return null;
    };

    var getValue = function getValue() {
      var plotLine = getPlotLine();
      var translation = plotLine.svgElem.translateY;
      var newValue = axis.toValue(translation) - axis.toValue(0) + plotLine.options.value;
      newValue = Math.max(axis.min, Math.min(axis.max, newValue));
      return newValue;
    };

    var dragStep = function dragStep(e) {
      var plotLine = getPlotLine();
      var newTranslation = e.pageY ? e.pageY - clickY : e.changedTouches['0'].pageY - clickY;
      var newValue = axis.toValue(newTranslation) - axis.toValue(0) + plotLine.options.value;
      newValue = Math.max(axis.min, Math.min(axis.max, newValue));
      newTranslation = axis.toPixels(newValue + axis.toValue(0) - plotLine.options.value);
      plotLine.svgElem.translate(0, newTranslation);

      if (getPlotLine().label) {
        plotLine.label.translate(0, newTranslation);
      }

      if (plotLine.options.onDragChange) {
        plotLine.options.onDragChange(newValue);
      }

      if (plotLine.label && Math.round(newValue) > axis.min && Math.round(newValue) < axis.max) {
        plotLine.label.attr({
          y: (e.pageY ? e.pageY : e.changedTouches['0'].pageY) - newValue
        });
      }
    };

    var _dragStop = null;

    var dragStart = function dragStart(e) {
      document.addEventListener('mousemove', dragStep, false);
      document.addEventListener('mouseup', _dragStop, false);
      document.addEventListener('touchstart', dragStep, false);
      document.addEventListener('touchmove', dragStep, false);
      document.addEventListener('touchend', _dragStop, false);
      var plotLine = getPlotLine();
      clickY = (e.pageY ? e.pageY : e.changedTouches['0'].pageY) - plotLine.svgElem.translateY;

      if (plotLine.options.onDragStart) {
        plotLine.options.onDragStart(getValue());
      }
    };

    _dragStop = function dragStop(e) {
      document.removeEventListener('mousemove', dragStep, false);
      document.removeEventListener('mouseup', _dragStop, false);
      document.removeEventListener('touchstart', dragStep, false);
      document.removeEventListener('touchmove', dragStep, false);
      document.removeEventListener('touchend', _dragStop, false);
      var plotLine = getPlotLine();

      if (plotLine) {
        var plotLineOptions = plotLine.options;

        if (Object.prototype.hasOwnProperty.call(plotLine.svgElem, 'translateX')) {
          plotLineOptions.value = getValue();
          axis.removePlotLine(plotLineOptions.id);
          axis.addPlotLine(plotLineOptions);

          if (plotLineOptions.onDragFinish) {
            plotLineOptions.onDragFinish(plotLineOptions.value);
          }
        }

        var range = getValue();

        if (plotLine.label && range < axis.max && range > axis.min) {
          plotLine.label.attr({
            y: (e.pageY ? e.pageY : e.changedTouches['0'].pageY) - range
          });
        }

        getPlotLine().svgElem.css({
          cursor: 'pointer'
        }).translate(0, 0).on('touchstart', dragStart).on('mousedown', dragStart);

        if (getPlotLine().label) {
          getPlotLine().label.css({
            cursor: 'pointer'
          }).translate(0, 0).on('touchstart', dragStart).on('mousedown', dragStart);
        }
      }
    };

    _dragStop();
  }, []);
  useEffect(function () {
    var chart = highchartsComponentRef.current.chart;

    if (chart && chart.yAxis[0]) {
      draggablePlotLine(chart.yAxis[0]);
      chartElem(highchartsComponentRef.current);
    }
  }, [highchartsComponentRef, chartElem, draggablePlotLine]);
  return /*#__PURE__*/React.createElement(HighchartsReact, {
    constructorType: constructorType || 'chart',
    containerProps: {
      style: {
        height: '100%'
      }
    },
    highcharts: Highcharts,
    options: _extends({}, defaultOptions$1, {}, options),
    ref: highchartsComponentRef
  });
}

BarHighchart.propTypes = {
  options: PropTypes.shape({}),
  constructorType: PropTypes.string,
  chartElem: PropTypes.func,
  chartRef: PropTypes.shape({
    current: PropTypes.any
  })
};
BarHighchart.defaultProps = {
  options: null,
  chartElem: null,
  constructorType: null,
  chartRef: null
};

var styles$7 = {"icon":"_1vsgf"};

function BarChart(props) {
  var chartData = props.chartData,
      xCategories = props.xCategories,
      onChange = props.onChange;
  var chartRef = useRef(null);

  var _useState = useState(null),
      options = _useState[0],
      setOptions = _useState[1];

  var _useState2 = useState(chartData),
      newData = _useState2[0],
      setNewData = _useState2[1];

  var handleOnChange = useCallback(function (d) {
    if (onChange) onChange(d);
    var changedData = newData.map(function (e) {
      var obj = {};

      if (typeof e === 'object' && e.y <= d) {
        obj = e;
        obj.color = 'pink';
        return obj;
      }

      if (e.y > d) {
        obj = e;
        obj.color = '#7cb5ec';
        return obj;
      }

      obj = {
        y: e,
        color: e <= d ? 'pink' : '#7cb5ec'
      };
      return obj;
    });
    setNewData(changedData);
  }, [newData, onChange]);
  var chartConfig = useCallback(function () {
    return {
      title: 'Bar Chart With Draggable Line',
      chart: {
        type: 'column'
      },
      legend: {
        enabled: false
      },
      xAxis: {
        categories: xCategories
      },
      yAxis: {
        plotLines: [{
          id: 'y2',
          color: 'skyblue',
          width: 2,
          value: 1,
          zIndex: 5,
          dashStyle: 'dash',
          label: {
            align: 'right',
            y: -12,
            x: 7,
            useHTML: true,
            text: "<svg class=" + styles$7.icon + ">\n              <path style=\" stroke:none;fill-rule:nonzero;\n              fill:rgb(29.019608%,16.470588%,78.039216%);fill-opacity:1;\" \n              d=\"M 9.179688 49.894531 L 9.179688 0.09375 L 41.976562 0.09375 L 41.976562 \n              49.894531 Z M 9.179688 49.894531 \"/>\n              <path style=\" stroke:none;fill-rule:evenodd;\n              fill:rgb(100%,100%,100%);fill-opacity:1;\" \n              d=\"M 33.53125 20.722656 L 17.359375 20.722656 L 25.445312 6.636719 Z M \n              33.53125 20.722656 \"/>\n              <path style=\" stroke:none;fill-rule:evenodd;\n              fill:rgb(100%,100%,100%);fill-opacity:1;\" \n              d=\"M 33.433594 29.132812 L 17.722656 29.132812 L 25.578125 42.683594 Z M \n              33.433594 29.132812 \"/>\n              <path style=\" stroke:none;fill-rule:nonzero;\n              fill:rgb(100%,100%,100%);fill-opacity:1;\" \n              d=\"M 15.355469 25.492188 L 35.800781 25.492188 L 35.800781 24.507812 L \n              15.355469 24.507812 Z M 15.355469 25.492188 \"/>\n              </svg>"
          },
          onDragStart: function onDragStart() {
            return null;
          },
          onDragChange: function onDragChange() {
            return null;
          },
          onDragFinish: function onDragFinish(newValue) {
            handleOnChange(newValue);
          }
        }]
      },
      plotOptions: {
        series: {
          borderRadius: 8
        }
      },
      series: [{
        showInLegend: 'false',
        data: newData
      }]
    };
  }, [newData, handleOnChange, xCategories]);

  function getChartElem(chartElem) {
    chartRef.current = chartElem;
  }

  useEffect(function () {
    if (chartRef.current) {
      var chart = chartRef.current.chart;

      if (chart) {
        chart.update({
          series: [{
            data: newData
          }],
          xAxis: {
            categories: xCategories
          }
        });
      }
    }
  }, [newData, xCategories]);
  useEffect(function () {
    var config = chartConfig();

    if (config && !options) {
      setOptions(config);
    }
  }, [options, chartConfig]);
  return /*#__PURE__*/React.createElement(BarHighchart, {
    options: options,
    chartElem: getChartElem
  });
}

BarChart.propTypes = {
  chartData: PropTypes.arrayOf(PropTypes.any),
  onChange: PropTypes.func,
  xCategories: PropTypes.arrayOf(PropTypes.any)
};
BarChart.defaultProps = {
  chartData: null,
  onChange: null,
  xCategories: null
};

function TreeSelectList(props) {
  var data = props.data,
      value = props.value,
      onSelect = props.onSelect;
  return data.map(function (entity, index) {
    var _entity$ranches;

    return /*#__PURE__*/React.createElement("div", {
      key: "option_" + String(index),
      id: "option_" + index
    }, (entity === null || entity === void 0 ? void 0 : entity.name) === 'All Ranches' ? /*#__PURE__*/React.createElement("li", {
      id: entity.id,
      value: null,
      onClick: function onClick(e) {
        return onSelect(e);
      },
      "aria-hidden": "true",
      className: !value ? styles$6['select-all'] + " " + styles$6.selected : "" + styles$6['select-all']
    }, "All Ranches") : /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("li", {
      id: entity === null || entity === void 0 ? void 0 : entity.id,
      onClick: function onClick(e) {
        return onSelect(e);
      },
      "aria-hidden": "true",
      type: "entity",
      className: "" + styles$6['entity-header']
    }, entity.name), (entity === null || entity === void 0 ? void 0 : (_entity$ranches = entity.ranches) === null || _entity$ranches === void 0 ? void 0 : _entity$ranches.length) ? entity.ranches.map(function (ranch) {
      var _ranch$blocks;

      return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("li", {
        key: (entity === null || entity === void 0 ? void 0 : entity.id) + "_" + (ranch === null || ranch === void 0 ? void 0 : ranch.id),
        id: ranch.id,
        onClick: function onClick(e) {
          return onSelect(e);
        },
        "aria-hidden": "true",
        type: "ranch",
        className: value === ranch.id ? styles$6['ranch-header'] + " " + styles$6.selected : "" + styles$6['ranch-header']
      }, ranch.name), (ranch === null || ranch === void 0 ? void 0 : (_ranch$blocks = ranch.blocks) === null || _ranch$blocks === void 0 ? void 0 : _ranch$blocks.length) ? ranch.blocks.map(function (block) {
        return /*#__PURE__*/React.createElement("li", {
          id: block.id,
          key: "" + block.id,
          onClick: function onClick(e) {
            return onSelect(e);
          },
          "aria-hidden": "true",
          type: "block",
          className: value === block.id ? styles$6['block-header'] + " " + styles$6.selected : "" + styles$6['block-header']
        }, block.name);
      }) : /*#__PURE__*/React.createElement("li", {
        id: "No_Block",
        onClick: function onClick(e) {
          return onSelect(e);
        },
        "aria-hidden": "true",
        type: "no_block",
        className: "" + styles$6['no-block']
      }, "No Block"));
    }) : /*#__PURE__*/React.createElement("li", {
      id: "No_Ranch",
      onClick: function onClick(e) {
        return onSelect(e);
      },
      "aria-hidden": "true",
      type: "no_ranch",
      className: "" + styles$6['no-ranch']
    }, "No Ranch")));
  });
}

var useBlockNames$1 = hooks.useBlockNames,
    useRanchNames = hooks.useRanchNames;

var blockTagRender$1 = function blockTagRender(args) {
  var label = args.label,
      _onClose = args.onClose,
      closable = args.closable;
  return /*#__PURE__*/React.createElement(Tag, {
    closable: closable,
    onClose: function onClose(e) {
      e.preventDefault();

      _onClose(e);
    }
  }, label.props ? label.props.block.name : label);
};

var ModalSelect = function ModalSelect(props) {
  var defaultOption = props.defaultOption,
      onSelect = props.onSelect,
      type = props.type,
      mode = props.mode,
      data = props.data,
      title = props.title,
      placeholder = props.placeholder,
      admin = props.admin,
      selected = props.selected;

  var _useTranslation = useTranslation(),
      t = _useTranslation.t;

  var _useState = useState(defaultOption),
      value = _useState[0],
      setValue = _useState[1];

  var _useState2 = useState(Number(selected === null || selected === void 0 ? void 0 : selected.value)),
      rbValue = _useState2[0],
      setRBValue = _useState2[1];

  var _useState3 = useState(''),
      searchText = _useState3[0],
      setSearchText = _useState3[1];

  var allRanches = !admin && !title ? {
    name: 'All Ranches',
    id: 'null',
    isLoading: false
  } : null;
  var originalData = useRef([]);

  var _useState4 = useState(false),
      modal = _useState4[0],
      setModal = _useState4[1];

  var blockNames = useBlockNames$1(value.length ? value.map(function (d) {
    return Number(d);
  }) : [rbValue]);
  var ranchNames = useRanchNames(value.length ? value.map(function (d) {
    return Number(d);
  }) : [rbValue]);
  var selectedName;
  if ((selected === null || selected === void 0 ? void 0 : selected.type) === 'block') selectedName = blockNames[rbValue];
  if ((selected === null || selected === void 0 ? void 0 : selected.type) === 'ranch') selectedName = ranchNames[rbValue];
  if ((selected === null || selected === void 0 ? void 0 : selected.type) === 'All Ranches') selectedName = 'All Ranches';
  useEffect(function () {
    var allData = allRanches ? [allRanches].concat(data) : data;

    if (JSON.stringify(originalData.current) !== JSON.stringify(allData)) {
      originalData.current = allData;
    }
  }, [allRanches, data]);
  useEffect(function () {
    if (selected.value) setRBValue(Number(selected === null || selected === void 0 ? void 0 : selected.value));
  }, [selected]);
  var RBSelect = type === 'RanchBlockSelect';

  var onChange = function onChange(d) {
    var _d$target, _d$target2, _d$target3;

    var val = [d.target.id];
    var conditionCheck = (d === null || d === void 0 ? void 0 : (_d$target = d.target) === null || _d$target === void 0 ? void 0 : _d$target.type) !== 'entity' && (d === null || d === void 0 ? void 0 : (_d$target2 = d.target) === null || _d$target2 === void 0 ? void 0 : _d$target2.type) !== 'no_ranch' && (d === null || d === void 0 ? void 0 : (_d$target3 = d.target) === null || _d$target3 === void 0 ? void 0 : _d$target3.type) !== 'no_block';

    if (RBSelect && conditionCheck) {
      setRBValue(Number(d.target.id));
      val = {
        type: d.target.type,
        value: d.target.id,
        key: d.target.key
      };
      setModal(false);
    }

    setSearchText('');
    if (onSelect && conditionCheck) onSelect(val);
  };

  var onButtonClick = function onButtonClick(val) {
    setModal(val);
  };

  function isStringExists(name, filterText) {
    return (name === null || name === void 0 ? void 0 : name.toLowerCase().indexOf(filterText)) > -1;
  }

  var scrollElementId = "" + selected.value;
  var scrollElem;

  function customFilter(input, option) {
    if (option === void 0) {
      option = [];
    }

    scrollElem = document.getElementById(scrollElementId);
    var str = input === null || input === void 0 ? void 0 : input.toLowerCase();

    if (RBSelect) {
      if (str === null || str === void 0 ? void 0 : str.length) {
        var finalData = [];
        var allData = originalData.current;
        allData.forEach(function (d) {
          var obj = {
            id: d.id,
            name: d.name,
            isLoading: d.isLoading,
            ranches: []
          };

          if (isStringExists(d.name, str)) {
            obj.ranches = d.ranches;
            finalData.push(obj);
            return;
          }

          if (d.ranches && d.ranches.length) {
            d.ranches.forEach(function (ra) {
              var rObj = {
                id: ra.id,
                name: ra.name,
                isLoading: ra.isLoading,
                blocks: []
              };

              if (isStringExists(ra.name, str)) {
                rObj.blocks = ra.blocks;
                obj.ranches.push(rObj);
                finalData.push(obj);
              } else if (ra.blocks && ra.blocks.length) {
                var blocks = ra.blocks.filter(function (b) {
                  return isStringExists(b.name, str);
                });

                if (blocks.length) {
                  rObj.blocks = blocks;
                  obj.ranches.push(rObj);
                  finalData.push(obj);
                }
              }
            });
          }
        });
        return finalData.filter(function (v, i, a) {
          return a.indexOf(v) === i;
        });
      }

      return option;
    }

    return option;
  }

  useEffect(function () {
    if (scrollElem) scrollElem.scrollIntoView({
      block: 'start'
    });
  });
  var listHeight = calculatedHeightForMobile();
  var divHeight = listHeight - 70 + "px";

  function getOptions() {
    var lastY = 0;
    return /*#__PURE__*/React.createElement("div", {
      className: styles$1['flex-container'] + " " + styles$1['tree-select-dropdown']
    }, isMobile && /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
      className: styles$1['modal-select-header']
    }, /*#__PURE__*/React.createElement("div", {
      className: styles$1['modal-select-header-text']
    }, t((title === null || title === void 0 ? void 0 : title.length) ? title : 'Select Ranch or Block'))), /*#__PURE__*/React.createElement(Input, {
      className: styles$1['search-box'],
      placeholder: t('Type to Search..'),
      onChange: function onChange(d) {
        var _d$target4;

        return setSearchText(d === null || d === void 0 ? void 0 : (_d$target4 = d.target) === null || _d$target4 === void 0 ? void 0 : _d$target4.value);
      },
      onKeyDown: function onKeyDown(e) {
        return e.stopPropagation();
      },
      ref: function ref(input) {
        return input && input.focus();
      },
      allowClear: true
    })), /*#__PURE__*/React.createElement("div", {
      className: styles$1['sensor-option-div'] + " " + styles$1['mobile-select-scrollable-div'],
      style: {
        height: divHeight
      },
      onTouchStart: function onTouchStart(e) {
        e.currentTarget.style.touchAction = 'manipulation';
        lastY = e.touches[0].clientY;
      },
      onTouchMove: function onTouchMove(e) {
        var top = e.touches[0].clientY;
        var _e$currentTarget = e.currentTarget,
            scrollTop = _e$currentTarget.scrollTop,
            scrollHeight = _e$currentTarget.scrollHeight,
            offsetHeight = _e$currentTarget.offsetHeight;
        var maxScrollTop = scrollHeight - offsetHeight;
        var direction = lastY - top < 0 ? 'up' : 'down';

        if (scrollTop === 0 && direction === 'up' || scrollTop >= maxScrollTop && direction === 'down') {
          e.preventDefault();

          if (e.currentTarget.style.touchAction !== 'none') {
            e.currentTarget.style.touchAction = 'none';
          }
        }

        lastY = top;
      }
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        height: divHeight
      }
    }, /*#__PURE__*/React.createElement(TreeSelectList, {
      data: customFilter(searchText, originalData.current),
      onSelect: onChange,
      value: rbValue
    }))), isMobile && /*#__PURE__*/React.createElement("div", {
      className: styles$1['button-centered'] + " " + styles$1['modal-custom-button']
    }, /*#__PURE__*/React.createElement(Button, {
      type: "primary",
      onClick: function onClick() {
        return onButtonClick(false);
      },
      className: styles$1['cancel-button-color'] + " \n            " + styles$1['mobile-dropdown-modal-button'],
      size: "middle",
      block: true
    }, mode !== 'multiple' && RBSelect ? 'Cancel' : 'Done')));
  }

  return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Select, {
    showSearch: true,
    onSearch: function onSearch(d) {
      return setSearchText(d);
    },
    mode: mode,
    allowClear: !RBSelect,
    style: {
      width: '100%'
    },
    placeholder: t(placeholder || 'Please select'),
    value: selectedName,
    onChange: function onChange(d) {
      if (!RBSelect) {
        if (!d) setValue([]);else setValue(d);
      }
    },
    onMouseDown: function onMouseDown(e) {
      e.preventDefault();
      e.stopPropagation();
    },
    tagRender: blockTagRender$1,
    dropdownClassName: styles$1['mobile-select-dropdown-style'],
    bordered: false,
    open: modal,
    onDropdownVisibleChange: function onDropdownVisibleChange() {
      return setModal(true);
    },
    dropdownRender: function dropdownRender() {
      return getOptions();
    },
    dropdownStyle: {
      height: divHeight + "px"
    }
  })));
};

ModalSelect.propTypes = {
  mode: PropTypes.string,
  data: PropTypes.arrayOf(PropTypes.object),
  onSelect: PropTypes.func,
  placeholder: PropTypes.string,
  defaultOption: PropTypes.arrayOf(PropTypes.any),
  title: PropTypes.string,
  type: PropTypes.string,
  admin: PropTypes.bool,
  selected: PropTypes.shape({
    value: PropTypes.any,
    key: PropTypes.any,
    type: PropTypes.any
  })
};
ModalSelect.defaultProps = {
  mode: null,
  data: null,
  onSelect: null,
  placeholder: null,
  defaultOption: [],
  title: '',
  type: null,
  admin: false,
  selected: {}
};

var loadFarmData$5 = actions.loadFarmData;
var useEntityRanchBlock$1 = hooks.useEntityRanchBlock;

var RanchBlockWrapper = function RanchBlockWrapper(props) {
  var mode = props.mode,
      loadItems = props.loadItems,
      type = props.type,
      placeholder = props.placeholder,
      onSelect = props.onSelect,
      value = props.value,
      title = props.title,
      defaultOption = props.defaultOption,
      selected = props.selected,
      getSelectedRanchBlock = props.getSelectedRanchBlock,
      admin = props.admin;
  var dispatch = useDispatch();

  var _useState = useState([]),
      options = _useState[0],
      setOptions = _useState[1];

  var isRanchBlockSelect = type === 'RanchBlockSelect';
  useEffect(function () {
    if (loadItems) loadItems();else dispatch(loadFarmData$5(true));
  }, [dispatch, loadItems]);
  var finalData = useEntityRanchBlock$1(options);
  useEffect(function () {
    if (JSON.stringify(finalData) !== JSON.stringify(options)) {
      setOptions(finalData);
    }
  }, [finalData, options]);

  var customOnSelect = function customOnSelect(val) {
    if (isRanchBlockSelect) {
      var id = val.value;
      var obj = {
        key: val[0],
        value: id,
        type: val.type
      };

      if (val.value === 'null') {
        obj = {
          key: 'All Ranches',
          value: null,
          type: 'All Ranches'
        };
      }

      if (onSelect) onSelect(obj);

      if (getSelectedRanchBlock instanceof Function) {
        getSelectedRanchBlock(obj);
      }
    }
  };

  return /*#__PURE__*/React.createElement("div", {
    className: "mobile-select-modal"
  }, /*#__PURE__*/React.createElement(ModalSelect, {
    data: isRanchBlockSelect ? options : [],
    defaultOption: defaultOption || [],
    onSelect: customOnSelect,
    placeholder: placeholder,
    value: value || [],
    title: title,
    type: type,
    mode: mode,
    admin: admin,
    selected: selected
  }));
};

RanchBlockWrapper.propTypes = {
  mode: PropTypes.string,
  loadItems: PropTypes.func,
  items: PropTypes.arrayOf(PropTypes.object),
  onSelect: PropTypes.func,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  defaultOption: PropTypes.arrayOf(PropTypes.any),
  value: PropTypes.arrayOf(PropTypes.any),
  title: PropTypes.string,
  type: PropTypes.string,
  selected: PropTypes.shape({
    value: PropTypes.any,
    key: PropTypes.any,
    type: PropTypes.any
  }),
  getSelectedRanchBlock: PropTypes.func,
  admin: PropTypes.bool
};
RanchBlockWrapper.defaultProps = {
  mode: 'single',
  loadItems: null,
  items: null,
  onSelect: null,
  onChange: null,
  placeholder: null,
  defaultOption: [],
  value: null,
  title: '',
  type: 'RanchBlockSelect',
  selected: {},
  getSelectedRanchBlock: null,
  admin: false
};

export { AppTemplate, BarChart, BlockControlList, BlockSelect, BooleanTag, ChangePasswordPage as ChangePasswordForm, ChartComponent, CollapsableSidebar, RefreshContext as ControlSummaryRefreshContext, ControlSystemStatus, EntityList, EntitySelect, FilterListItem, HighchartsSchedule, InputTime, InputVolume, InstallStateTag, LogoutRedirect, ResizeDetector as Map, MapSettingsMenu, PrivateRoute, PumpControlList, RanchBlockSelect, RanchBlockWrapper as RanchBlockSelectMobile, RanchSelect, SchedulerMobile, Schedule as SchedulerTimeline, Searchbox, SensorAlarmButton, SensorSelect, SensorTypeSelect, SensorTypeTag, Slider, StoplightTag, UserMenu, ValveControlList, getAnomalyIcon, getAnomalyIconColor, getColorCodeForIrrigationState, getMarkerTooltipByPresentationMode, icons, mapPresentationModes, prepareMarker, prepareMarkerClusterIcon, prepareNumberForDisplay, prepareRanchMarker, prepareZeroIslandSensors, sensorTitles, sensorTypeAllowedForMode, stateColorCode, stateTagColorCodes, stateTagTextColorCodes, useWindowSize };
//# sourceMappingURL=index.modern.js.map
